import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './CourseBuilder.css';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import AssignmentSelectorModal from '../components/AssignmentSelectorModal';
import TaxonomyTooltip from '../components/TaxonomyTooltip';
import HourAwardsTooltip from '../components/HourAwardsTooltip';
import CourseBuilderTaxonomyModal from '../components/CourseBuilderTaxonomyModal';
import LoadingSpinner from '../components/LoadingSpinner';
import UploadMediaOpenInNewTabLink from '../components/UploadMediaOpenInNewTabLink';
import CourseSubjectManagerModal from '../components/CourseSubjectManagerModal';
import CourseAssistant from '../components/CourseAssistant';
import CourseAssignmentDraftModal from '../components/CourseAssignmentDraftModal';
import { collectAiAssignmentSlots } from '../utils/courseOutlineAssignments';
import { mergePathwayRoots } from '../utils/pathwayTaxonomy';

/** Grade codes for TK–12 course targeting (top-level Course only). */
const COURSE_GRADE_LEVEL_OPTIONS = ['TK', 'K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

const sortGradeLevelsSelected = (arr) =>
  [...arr].sort(
    (a, b) => COURSE_GRADE_LEVEL_OPTIONS.indexOf(a) - COURSE_GRADE_LEVEL_OPTIONS.indexOf(b)
  );

/** Defaults for new discussion boards and editor fallbacks. */
const DEFAULT_DISCUSSION_BOARD_SETTINGS = {
  allowMainResponse: true,
  allowCommentResponse: true,
  notifyMeOnResponses: false,
  allowAssigneesViewResponses: true,
  hideresponsesuntilresponded: true
};

/**
 * Email notifications for discussion replies are not implemented server-side yet.
 * When true, the "Notify Me on Responses" toggle is interactive and persisted as chosen.
 */
const DISCUSSION_NOTIFY_ON_RESPONSES_UI_ENABLED = false;

/** Who gets LMS *staff* completion emails (components.notification_settings). Not related to discussion notifyMeOnResponses. */
function defaultLmsNotifyBaseForType(componentType) {
  const t = String(componentType || '');
  if (t === 'Assignment') {
    return {
      notify_superparent_creator: true,
      notify_creator: false,
      notify_item_creator: false
    };
  }
  return {
    notify_superparent_creator: false,
    notify_creator: false,
    notify_item_creator: false
  };
}

function mergeLmsNotificationSettingsFromStored(stored, componentType) {
  const base = defaultLmsNotifyBaseForType(componentType);
  const o =
    stored && typeof stored === 'object' && !Array.isArray(stored) ? stored : {};
  return {
    notify_superparent_creator: Object.prototype.hasOwnProperty.call(
      o,
      'notify_superparent_creator'
    )
      ? o.notify_superparent_creator !== false
      : base.notify_superparent_creator,
    notify_creator: Object.prototype.hasOwnProperty.call(o, 'notify_creator')
      ? o.notify_creator === true
      : base.notify_creator,
    notify_item_creator: Object.prototype.hasOwnProperty.call(o, 'notify_item_creator')
      ? o.notify_item_creator === true
      : base.notify_item_creator
  };
}

function walkSectionsForLmsNotifyItems(sections) {
  const out = [];
  const types = ['Assignment', 'Content', 'Discussion Board'];
  const visit = (node, path) => {
    if (!node || !types.includes(node.type)) return;
    out.push({
      id: node.id,
      type: node.type,
      title: node.text || node.title || `Untitled ${node.type}`,
      path,
      updateddate: node.updateddate || null,
      notification_settings: node.notification_settings
    });
  };
  (sections || []).forEach((section, si) => {
    visit(section, `${si + 1}.`);
    section.subsections?.forEach((sub, sj) => {
      visit(sub, `${si + 1}.${sj + 1}.`);
      sub.nestedSections?.forEach((nest, nk) => {
        visit(nest, `${si + 1}.${sj + 1}.${nk + 1}.`);
      });
    });
  });
  const order = (t) => (t === 'Assignment' ? 0 : t === 'Content' ? 1 : 2);
  out.sort((a, b) => order(a.type) - order(b.type) || String(a.path).localeCompare(String(b.path)));
  return out;
}

/** Bulk modal: no completion-email tuning for these types until product enables sending. */
const LMS_NOTIFY_BULK_UI_DISABLED_TYPES = ['Content', 'Discussion Board'];

function applyLmsNotifyUpdatesToSections(sectionsArr, updatesById) {
  if (!updatesById || !Object.keys(updatesById).length) return sectionsArr;
  return (sectionsArr || []).map((section) => {
    let s = section;
    const su = updatesById[section.id];
    if (su) s = { ...section, ...su };
    const patchSub = (sub) => {
      let ss = sub;
      const u = updatesById[sub.id];
      if (u) ss = { ...sub, ...u };
      return {
        ...ss,
        nestedSections: (ss.nestedSections || []).map((n) => {
          const nu = updatesById[n.id];
          return nu ? { ...n, ...nu } : n;
        })
      };
    };
    return {
      ...s,
      subsections: (s.subsections || []).map(patchSub)
    };
  });
}

/** Display name from viewer API row (joined account). */
function formatCourseCreatorFromViewerRow(c) {
  if (!c) return null;
  const fn = (c.instructor_first_name || '').trim();
  const ln = (c.instructor_last_name || '').trim();
  const name = [fn, ln].filter(Boolean).join(' ');
  if (name) return name;
  if (c.instructor_email) return c.instructor_email;
  return null;
}

const CourseBuilder = ({ userData = null }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sections, setSections] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [draggedSubsection, setDraggedSubsection] = useState(null);
  const [dragOverSubsection, setDragOverSubsection] = useState(null);
  const [draggedNestedSection, setDraggedNestedSection] = useState(null);
  const [dragOverNestedSection, setDragOverNestedSection] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState(null); // 'section', 'subsection', 'nested'
  const [popupLevel, setPopupLevel] = useState(null); // sectionIndex, subsectionIndex
  const [showItemSelector, setShowItemSelector] = useState(false);
  const [courseTitle, setCourseTitle] = useState('');
  const [showJsonPreview, setShowJsonPreview] = useState(false);
  const [expandedEditors, setExpandedEditors] = useState({});
  const [contentEditor, setContentEditor] = useState(null);
  const [discussionBoardEditor, setDiscussionBoardEditor] = useState(null);
  const [directionDraft, setDirectionDraft] = useState({ 
    element: 'richText', 
    value: '',
    imageUrl: '',
    imageWidth: '',
    imageHeight: ''
  });
  const [editingContentId, setEditingContentId] = useState(null);
  const [discussionBoardSettings, setDiscussionBoardSettings] = useState({
    ...DEFAULT_DISCUSSION_BOARD_SETTINGS
  });
  const [discussionBoardTitle, setDiscussionBoardTitle] = useState('');
  const [contentTitle, setContentTitle] = useState('');
  const [showDiscussionSettings, setShowDiscussionSettings] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState('');
  const [videoUploadError, setVideoUploadError] = useState('');
  const [userUploads, setUserUploads] = useState([]);
  const [uploadsLoading, setUploadsLoading] = useState(false);
  const [showUploadsModal, setShowUploadsModal] = useState(false);
  const [stickyContainerExpanded, setStickyContainerExpanded] = useState(false);
  const [flagContainerExpanded, setFlagContainerExpanded] = useState(false);
  const [showLmsEmailNotifyModal, setShowLmsEmailNotifyModal] = useState(false);
  const [lmsNotifyExpandedTypes, setLmsNotifyExpandedTypes] = useState({
    Assignment: true,
    Content: false,
    'Discussion Board': false
  });
  const [lmsNotifyDraft, setLmsNotifyDraft] = useState({});
  const [lmsNotifySaving, setLmsNotifySaving] = useState(false);
  const [outlineExpandedSections, setOutlineExpandedSections] = useState({});
  const [outlineExpandedSubsections, setOutlineExpandedSubsections] = useState({});
  const [defaultPrimaryConcentration, setDefaultPrimaryConcentration] = useState('');
  /** Top-level Course only: CTE vs TK–12 classification */
  const [courseTrack, setCourseTrack] = useState('CTE');
  const [tk12Subject, setTk12Subject] = useState('');
  const [gradeLevels, setGradeLevels] = useState([]);
  /** Taxonomy rows with type Course-Subject (id → title for TK–12 subject select) */
  const [courseSubjectsTaxonomy, setCourseSubjectsTaxonomy] = useState([]);
  const [courseSubjectsLoading, setCourseSubjectsLoading] = useState(false);
  const [courseSubjectsFetchError, setCourseSubjectsFetchError] = useState('');
  const [showCourseSubjectManager, setShowCourseSubjectManager] = useState(false);
  const [canManageCourseSubjects, setCanManageCourseSubjects] = useState(false);
  const [canUpdateCourseCreator, setCanUpdateCourseCreator] = useState(false);
  const [showCreatorTransferModal, setShowCreatorTransferModal] = useState(false);
  const [creatorSearchTerm, setCreatorSearchTerm] = useState('');
  const [creatorSearchResults, setCreatorSearchResults] = useState([]);
  const [creatorSearchLoading, setCreatorSearchLoading] = useState(false);
  const [creatorSearchHasRun, setCreatorSearchHasRun] = useState(false);
  const [creatorTransferSaving, setCreatorTransferSaving] = useState(false);
  const [canEditSharedCourseContent, setCanEditSharedCourseContent] = useState(false);
  const [canControlModlock, setCanControlModlock] = useState(false);
  const [canComponentNotificationSettings, setCanComponentNotificationSettings] =
    useState(false);
  const [aiCapabilities, setAiCapabilities] = useState(null);
  const [assignmentDraftModalOpen, setAssignmentDraftModalOpen] = useState(false);
  const [modlockSaving, setModlockSaving] = useState(false);
  /** Set from viewer API when a saved course loads; null = show signed-in user for new/unsaved drafts */
  const [courseCreatorLabelFromServer, setCourseCreatorLabelFromServer] = useState(null);

  
  // Hour awards modal state
  const [hourAwardsModalConfig, setHourAwardsModalConfig] = useState({
    isOpen: false,
    sectionIndex: null,
    subsectionIndex: null,
    nestedIndex: null,
    itemIndex: null,
    type: null, // 'section', 'subsection', 'nested', 'lesson-item'
    currentHourAwards: [],
    onHourAwardsChange: null
  });
  
  // Project types for hour awards (same as ProjectBuilder)
  const projectTypes = [
    { label: "Field Experience", value: "Field Experience" },
    { label: "Lab", value: "Lab" },
    { label: "Theory", value: "Theory" },
    { label: "Capstone", value: "Capstone" },
    { label: "Concentration", value: "Concentration" }
  ];
  
  // Deletion confirmation modal state
  const [deletionModalConfig, setDeletionModalConfig] = useState({
    isOpen: false,
    componentType: null, // 'section', 'subsection', 'nested'
    sectionIndex: null,
    subsectionIndex: null,
    nestedIndex: null,
    componentTitles: [],
    onConfirm: null
  });

  // Track deleted components for save transaction
  const [deletedComponents, setDeletedComponents] = useState([]);
  
  // Store complete component hierarchies before deletion for rollback
  const [deletedComponentHierarchies, setDeletedComponentHierarchies] = useState([]);
  
      // Ensure all sections have required properties
      const ensureAllSectionsHaveProperties = (sections) => {
        sections.forEach((section) => {
          if (!section.hourawards) section.hourawards = [];
          if (!section.gradeSettings) section.gradeSettings = null;
          if (!section.type) section.type = 'Content';
          if (!section.status) section.status = 'Draft';
                if (!section.taxonomy) section.taxonomy = [];
          if (!section.primaryConcentration) section.primaryConcentration = '';
          if (!section.otherConcentrations) section.otherConcentrations = [];
          if (section.type === 'Assignment' && !section.assignments) section.assignments = [];
          if (section.type === 'Assignment' && !section.reference_table) section.reference_table = null;
          if (section.type === 'Assignment' && !section.reference_id) section.reference_id = null;
          
          if (section.subsections) {
            section.subsections.forEach((subsection) => {
              if (!subsection.hourawards) subsection.hourawards = [];
              if (!subsection.gradeSettings) subsection.gradeSettings = null;
              if (!subsection.type) subsection.type = 'Content';
              if (!subsection.status) subsection.status = 'Draft';
                        if (!subsection.taxonomy) subsection.taxonomy = [];
              if (!subsection.primaryConcentration) subsection.primaryConcentration = '';
              if (!subsection.otherConcentrations) subsection.otherConcentrations = [];
              if (subsection.type === 'Assignment' && !subsection.assignments) subsection.assignments = [];
              if (subsection.type === 'Assignment' && !subsection.reference_table) subsection.reference_table = null;
              if (subsection.type === 'Assignment' && !subsection.reference_id) subsection.reference_id = null;
              
              if (subsection.nestedSections) {
                subsection.nestedSections.forEach((nestedSection) => {
                  if (!nestedSection.hourawards) nestedSection.hourawards = [];
                  if (!nestedSection.gradeSettings) nestedSection.gradeSettings = null;
                  if (!nestedSection.type) nestedSection.type = 'Content';
                  if (!nestedSection.status) nestedSection.status = 'Draft';
              if (!nestedSection.taxonomy) nestedSection.taxonomy = [];
              if (!nestedSection.primaryConcentration) nestedSection.primaryConcentration = '';
              if (!nestedSection.otherConcentrations) nestedSection.otherConcentrations = [];
              if (nestedSection.type === 'Assignment' && !nestedSection.assignments) nestedSection.assignments = [];
              if (nestedSection.type === 'Assignment' && !nestedSection.reference_table) nestedSection.reference_table = null;
              if (nestedSection.type === 'Assignment' && !nestedSection.reference_id) nestedSection.reference_id = null;
                });
              }
            });
          }
        });
      };
      
  // Wrapper function to set sections with property initialization
  const setSectionsWithProperties = (newSections) => {
    ensureAllSectionsHaveProperties(newSections);
    setSections(newSections);
  };

  // Initialize outline expansion state when sections change
  useEffect(() => {
    if (sections.length > 0) {
      // Initialize properties for all sections
      ensureAllSectionsHaveProperties(sections);
      
      const expandedSections = {};
      const expandedSubsections = {};
      
      sections.forEach((section, sectionIndex) => {
        expandedSections[sectionIndex] = true; // Always expand sections
        
        if (section.subsections) {
          section.subsections.forEach((subsection, subsectionIndex) => {
            expandedSubsections[`${sectionIndex}-${subsectionIndex}`] = true; // Always expand subsections
          });
        }
      });
      
      setOutlineExpandedSections(expandedSections);
      setOutlineExpandedSubsections(expandedSubsections);
    }
  }, [sections]);
  
  // Taxonomy-related state
  const [concentrations, setConcentrations] = useState([]);
  const [concentrationsLoading, setConcentrationsLoading] = useState(true);
  const [concentrationsError, setConcentrationsError] = useState(null);
  const [taxonomyModalConfig, setTaxonomyModalConfig] = useState({
    isOpen: false,
    sectionIndex: null,
    subsectionIndex: null,
    nestedIndex: null,
    itemIndex: null,
    type: null, // 'section', 'subsection', 'nested', 'lesson-item'
    currentTaxonomy: [],
    primaryConcentration: '',
    otherConcentrations: [],
    onTaxonomyChange: null
  });
  
  // Assignment-related state
  const [allAssignments, setAllAssignments] = useState([]);
  const [assignmentModalConfig, setAssignmentModalConfig] = useState({
    isOpen: false,
    sectionIndex: null,
    subsectionIndex: null,
    nestedIndex: null,
    itemIndex: null,
    selectedAssignments: [],
    onSelectionChange: null
  });

  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);

  const [savedCourses, setSavedCourses] = useState([]);
  const [isLoadingCourses, setIsLoadingCourses] = useState(false);
  const [currentCourseId, setCurrentCourseId] = useState(null); // Track which course is currently loaded
  const [currentCourseStatus, setCurrentCourseStatus] = useState(null); // Track current course status
  const [currentCourseUpdatedDate, setCurrentCourseUpdatedDate] = useState(null); // Track current course version
  const [currentCourseComponent, setCurrentCourseComponent] = useState(null); // Store current course component data
  const [courseComponentState, setCourseComponentState] = useState(null); // Track Course component changes separately from sections
  const [showSavedCoursesModal, setShowSavedCoursesModal] = useState(false);
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [actionOverlay, setActionOverlay] = useState(false);
      const [criticalError, setCriticalError] = useState(null); // { message: string, showReload: boolean }
  const dragRef = useRef(null);
  /** Prevents re-applying default CTE vs General until user starts fresh (New Course / empty template). */
  const defaultCourseTrackAppliedRef = useRef(false);

  // Store original loaded components for change detection
  const [originalComponentsState, setOriginalComponentsState] = useState(null);

  const courseCreatorDisplay = useMemo(() => {
    if (courseCreatorLabelFromServer) return courseCreatorLabelFromServer;
    if (!currentCourseId && userData) {
      const n = [userData.first_name, userData.last_name].filter(Boolean).join(' ').trim();
      if (n) return n;
      if (userData.primary_email) return userData.primary_email;
    }
    return null;
  }, [courseCreatorLabelFromServer, currentCourseId, userData]);

  // Fetch user assignments when component mounts
  useEffect(() => {
    if (userData?.account_id) {
      fetchUserAssignments();
    }
    
    // Initialize modlock status
    window.courseModsLocked = false;
  }, [userData]);

  // Fetch concentrations for taxonomy functionality
  const fetchConcentrations = async () => {
    try {
      setConcentrationsLoading(true);
      setConcentrationsError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      
    
      const response = await axios.get('/api/concentrations', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      
      if (response.data && response.data.concentrations) {
        setConcentrations(response.data.concentrations || []);
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (err) {
      console.error('Error fetching concentrations:', err);
      setConcentrationsError(err.message || 'Failed to load concentrations');
      setConcentrations([]);
    } finally {
      setConcentrationsLoading(false);
    }
  };

  // Load saved courses function
  const loadSavedCourses = async () => {
    try {
      // Ensure we have user data before proceeding
      if (!userData?.account_id) {
        throw new Error('User data not available. Please log in again.');
      }

      setIsLoadingCourses(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      // Fetch courses created by the current user (API already filters by authenticated user)
      const response = await axios.get('/api/components?type=Course', {
        headers: { Authorization: `Bearer ${token}` }
      });

      const allCourses = response.data.components || [];
      setSavedCourses(allCourses.filter(c => c.status === 'Active'));
    } catch (error) {
      console.error('Error loading saved courses:', error);
      setSuccess(`Error loading saved courses: ${error.message}`);
      setTimeout(() => setSuccess(''), 5000);
    } finally {
      setIsLoadingCourses(false);
    }
  };

  // Load concentrations on component mount
  useEffect(() => {
    fetchConcentrations();
  }, []);

  const fetchCourseSubjectsTaxonomy = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setCourseSubjectsLoading(true);
    setCourseSubjectsFetchError('');
    try {
      const { data } = await axios.get('/api/taxonomy/course-subjects', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourseSubjectsTaxonomy(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      console.error('Error loading course subjects taxonomy:', err);
      setCourseSubjectsFetchError(err.response?.data?.error || err.message || 'Failed to load subjects');
      setCourseSubjectsTaxonomy([]);
    } finally {
      setCourseSubjectsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (userData?.account_id) {
      fetchCourseSubjectsTaxonomy();
    }
  }, [userData?.account_id, fetchCourseSubjectsTaxonomy]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('/api/check-permission?pagePath=course-builder', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) {
          setCanManageCourseSubjects(Boolean(res.data.capabilities?.ManageCourseSubjects));
          setCanEditSharedCourseContent(Boolean(res.data.capabilities?.EditSharedCourseContent));
          setCanUpdateCourseCreator(Boolean(res.data.capabilities?.UpdateCreator));
          setCanControlModlock(Boolean(res.data.capabilities?.ModlockControl));
          setCanComponentNotificationSettings(
            Boolean(res.data.capabilities?.ComponentNotificationSettings)
          );
          setAiCapabilities((prev) => ({
            ...(prev || {}),
            hasPage: Boolean(res.data.hasAccess),
            UseAssistant: Boolean(res.data.capabilities?.UseAssistant),
            GenerateOutline: Boolean(res.data.capabilities?.GenerateOutline),
            GenerateAssignmentDraft: Boolean(res.data.capabilities?.GenerateAssignmentDraft),
          }));
        }
      } catch {
        if (!cancelled) {
          setCanManageCourseSubjects(false);
          setCanEditSharedCourseContent(false);
          setCanUpdateCourseCreator(false);
          setCanControlModlock(false);
          setCanComponentNotificationSettings(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userData?.account_id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get('/api/ai/course-capabilities', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          setAiCapabilities((prev) => ({ ...(prev || {}), ...res.data }));
        }
      } catch (err) {
        console.warn('Failed to load course assistant capabilities:', err);
        if (!cancelled) {
          setAiCapabilities((prev) =>
            prev?.UseAssistant
              ? prev
              : { UseAssistant: false, GenerateOutline: false, hasPage: false }
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get('/api/check-permission?pagePath=assignment-builder', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          setAiCapabilities((prev) => ({
            ...(prev || {}),
            assignmentBuilderGenerateDraft: Boolean(res.data.capabilities?.GenerateDraft),
          }));
        }
      } catch {
        /* optional cross-page cap */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!canManageCourseSubjects) setShowCourseSubjectManager(false);
  }, [canManageCourseSubjects]);

  useEffect(() => {
    if (!canComponentNotificationSettings) setShowLmsEmailNotifyModal(false);
  }, [canComponentNotificationSettings]);

  useEffect(() => {
    if (!showLmsEmailNotifyModal) return;
    const items = walkSectionsForLmsNotifyItems(sections);
    const next = {};
    items.forEach((item) => {
      if (LMS_NOTIFY_BULK_UI_DISABLED_TYPES.includes(item.type)) return;
      next[item.id] = mergeLmsNotificationSettingsFromStored(
        item.notification_settings,
        item.type
      );
    });
    setLmsNotifyDraft(next);
  }, [showLmsEmailNotifyModal, sections]);

  const saveLmsEmailNotifySettings = useCallback(async () => {
    if (!canComponentNotificationSettings) return;
    const items = walkSectionsForLmsNotifyItems(sections).filter(
      (i) => !LMS_NOTIFY_BULK_UI_DISABLED_TYPES.includes(i.type)
    );
    if (!items.length) {
      setShowLmsEmailNotifyModal(false);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Not signed in');
      return;
    }
    setLmsNotifySaving(true);
    setError('');
    const updatesById = {};
    try {
      for (const item of items) {
        const draft = lmsNotifyDraft[item.id];
        if (!draft) continue;
        const body = {
          notification_settings: {
            notify_superparent_creator: draft.notify_superparent_creator,
            notify_creator: draft.notify_creator,
            notify_item_creator: draft.notify_item_creator
          }
        };
        if (item.updateddate) {
          body.expectedUpdatedDate =
            typeof item.updateddate === 'string'
              ? item.updateddate
              : new Date(item.updateddate).toISOString();
        }
        const res = await axios.put(`/api/components/${item.id}`, body, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const comp = res.data?.component;
        if (comp) {
          updatesById[item.id] = {
            notification_settings:
              comp.notification_settings ?? body.notification_settings,
            updateddate: comp.updateddate ?? item.updateddate
          };
        }
      }
      if (Object.keys(updatesById).length) {
        setSections((prev) => applyLmsNotifyUpdatesToSections(prev, updatesById));
      }
      setShowLmsEmailNotifyModal(false);
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.message ||
        'Failed to save notification settings';
      setError(msg);
      setTimeout(() => setError(''), 6000);
    } finally {
      setLmsNotifySaving(false);
    }
  }, [sections, lmsNotifyDraft, canComponentNotificationSettings]);

  const handleModlockToggle = useCallback(
    async (nextModlock) => {
      if (!canControlModlock) return;
      const cc = courseComponentState || currentCourseComponent;
      const courseRowId = currentCourseId || cc?.id;
      if (!courseRowId || courseRowId === 'temp' || cc?.status !== 'Active') return;
      setModlockSaving(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('Not signed in');
        const body = { modlock: nextModlock };
        if (cc?.updateddate) {
          const d = cc.updateddate;
          body.expectedUpdatedDate = typeof d === 'string' ? d : new Date(d).toISOString();
        }
        const { data } = await axios.put(`/api/components/${courseRowId}`, body, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const saved = data.component;
        if (saved) {
          setCourseComponentState((prev) =>
            prev ? { ...prev, modlock: saved.modlock, updateddate: saved.updateddate } : prev
          );
          setCurrentCourseComponent((prev) =>
            prev ? { ...prev, modlock: saved.modlock, updateddate: saved.updateddate } : prev
          );
          if (saved.updateddate) {
            setCurrentCourseUpdatedDate(saved.updateddate);
          }
          window.courseModsLocked = saved.modlock === true;
        }
      } catch (err) {
        const msg = err.response?.data?.error || err.message || 'Failed to update modlock';
        setError(msg);
        setTimeout(() => setError(''), 5000);
      } finally {
        setModlockSaving(false);
      }
    },
    [canControlModlock, courseComponentState, currentCourseComponent, currentCourseId]
  );

  const runCreatorSearch = useCallback(async () => {
    const term = creatorSearchTerm.trim();
    if (!canUpdateCourseCreator) return;
    if (term.length < 2) {
      setCreatorSearchResults([]);
      setCreatorSearchHasRun(false);
      return;
    }
    setCreatorSearchHasRun(true);
    setCreatorSearchLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/components/admin/creator-search?q=${encodeURIComponent(term)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setCreatorSearchResults(Array.isArray(res.data?.results) ? res.data.results : []);
    } catch (e) {
      console.error('Creator search failed:', e);
      setError('Failed to search users for creator transfer.');
      setTimeout(() => setError(''), 4500);
    } finally {
      setCreatorSearchLoading(false);
    }
  }, [creatorSearchTerm, canUpdateCourseCreator]);

  const transferCourseCreator = useCallback(async (targetAccountId) => {
    if (!canUpdateCourseCreator) return;
    if (!currentCourseId) {
      setError('Load a saved course before changing creator.');
      setTimeout(() => setError(''), 4000);
      return;
    }
    setCreatorTransferSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `/api/components/${currentCourseId}/creator`,
        { account_id: targetAccountId },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const c = res.data?.creator;
      const n = [c?.first_name, c?.last_name].filter(Boolean).join(' ').trim();
      setCourseCreatorLabelFromServer(n || c?.primary_email || null);
      setSuccess('Course creator updated successfully.');
      setTimeout(() => setSuccess(''), 3000);
      setShowCreatorTransferModal(false);
    } catch (e) {
      console.error('Creator transfer failed:', e);
      const msg = e?.response?.data?.error || 'Failed to change course creator.';
      setError(msg);
      setTimeout(() => setError(''), 5000);
    } finally {
      setCreatorTransferSaving(false);
    }
  }, [canUpdateCourseCreator, currentCourseId]);

  const pathwayRoots = useMemo(
    () => mergePathwayRoots(concentrations, courseSubjectsTaxonomy),
    [concentrations, courseSubjectsTaxonomy]
  );

  const hasCtePathways = useMemo(
    () => pathwayRoots.some((p) => p.pathwayKind === 'concentration'),
    [pathwayRoots]
  );
  const hasSubjectPathways = useMemo(
    () => pathwayRoots.some((p) => p.pathwayKind === 'course_subject'),
    [pathwayRoots]
  );

  const pathwayCatalogReady =
    !concentrationsLoading && (!userData?.account_id || !courseSubjectsLoading);
  const noPathwaysAvailable = pathwayCatalogReady && !concentrationsError && pathwayRoots.length === 0;

  // Compute total hours and breakdown by type from all sections
  const courseHoursBreakdown = useMemo(() => {
    const types = ['Theory', 'Lab', 'Field Experience', 'Concentration', 'Capstone'];
    const breakdown = { Theory: 0, Lab: 0, 'Field Experience': 0, Concentration: 0, Capstone: 0 };
    const collectFrom = (arr) => {
      if (!Array.isArray(arr)) return;
      arr.forEach((item) => {
        const ha = item.hourawards;
        if (Array.isArray(ha)) {
          ha.forEach((a) => {
            const t = a.type || 'Theory';
            const h = typeof a.hours === 'number' ? a.hours : (parseFloat(a.hours) || 0);
            if (breakdown[t] !== undefined) breakdown[t] += h;
            else breakdown.Theory += h;
          });
        }
        if (item.subsections) collectFrom(item.subsections);
        if (item.nestedSections) collectFrom(item.nestedSections);
        if (item.lessonItems) collectFrom(item.lessonItems);
      });
    };
    collectFrom(sections);
    const total = types.reduce((sum, t) => sum + breakdown[t], 0);
    return { total, breakdown, types };
  }, [sections]);

  const gradeLevelsAllSelected = useMemo(
    () => COURSE_GRADE_LEVEL_OPTIONS.every((c) => gradeLevels.includes(c)),
    [gradeLevels]
  );
  const gradeLevelsNoneSelected = useMemo(() => gradeLevels.length === 0, [gradeLevels]);




  // Load saved courses on component mount
  useEffect(() => {
    if (userData?.account_id) {
      loadSavedCourses();
    }
  }, [userData?.account_id]);

  // Helper function to create comment for a component
  const createCommentForComponent = async (component, componentType, level) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Generate UUID for the comment first
      const newCommentId = uuidv4();
      
      // Prepare comment data based on component type
      const commentData = {
        id: newCommentId,
        title: component.text || component.title || '',
        content: componentType === 'Content' ? JSON.stringify(component.content || []) : '',
        type: componentType === 'Content' ? 'Content' : 'Discussion Board',
        reference_id: component.id,
        table: 'components',
        account: userData?.account_id,
        status: 'created',
        relevantRole: 'Instructor',
                            allowmainresponse: false,
        allowcommentresponse: false,
        notifymeonresponses: false,
        allowassigneesviewresponses: false,
            initialnotification: false
      };



      // Create the comment with the pre-generated ID using the component-specific endpoint
      const response = await axios.post('/api/comments/component', commentData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update the component with the comment reference using our pre-generated ID
      component.reference_table = 'comments';
      component.reference_id = newCommentId;
      

      
      return response.data.comment;
    } catch (error) {
      console.error(`❌ Error creating ${componentType} comment:`, {
        error: error.message,
        componentId: component.id,
        componentType,
        level,
        component
      });
      throw error;
    }
  };

  // Helper function to update comment for a component
  const updateCommentForComponent = async (component, componentType) => {
    try {
      // Only update if component has a comment reference
      if (!component.reference_id || component.reference_table !== 'comments') {

        return null;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Prepare comment update data
      const commentUpdateData = {
        title: component.text || component.title || '',
        content: componentType === 'Content' ? JSON.stringify(component.content || []) : '',
        settings: component.settings || {}
      };



      // Update the comment
      const response = await axios.put(`/api/comments/${component.reference_id}`, commentUpdateData, {
        headers: { Authorization: `Bearer ${token}` }
      });



      return response.data.comment;
    } catch (error) {
      console.error(`❌ Error updating comment for component:`, {
        error: error.message,
        componentId: component.id,
        commentId: component.reference_id
      });
      throw error;
    }
  };

  // Helper function to delete comment for a component
  const deleteCommentForComponent = async (component) => {
    try {
      // Only delete if component has a comment reference
      if (!component.reference_id || component.reference_table !== 'comments') {

        return null;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      

      // Delete the comment
      const response = await axios.delete(`/api/comments/${component.reference_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      

      return response.data;
    } catch (error) {
      console.error(`❌ Error deleting comment for component:`, {
        error: error.message,
        componentId: component.id,
        commentId: component.reference_id
      });
      throw error;
    }
  };

  // Helper function to fetch comment data for a component
  const fetchCommentData = async (commentId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await axios.get(`/api/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data.comment;
    } catch (error) {
      console.error('Error fetching comment data:', error);
      return null;
    }
  };

  // Helper function to create comments for all Content and Discussion Board components during save
  const createCommentsForComponents = async (components) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }



      for (const component of components) {
        // Only create comments for Content and Discussion Board components that don't already have them
        if ((component.type === 'Content' || component.type === 'Discussion Board') && 
            (!component.reference_id || component.reference_table !== 'comments')) {
          
          // Generate UUID for the comment first
          const newCommentId = uuidv4();
          
          // Debug: Log the component being processed
          if (component.type === 'Discussion Board') {
            console.log('🔍 Creating comment for Discussion Board component:', {
              id: component.id,
              type: component.type,
              settings: component.settings,
              hasSettings: !!component.settings,
              allowMainResponse: component.settings?.allowMainResponse,
              allowCommentResponse: component.settings?.allowCommentResponse,
              notifyMeOnResponses: component.settings?.notifyMeOnResponses,
              allowAssigneesViewResponses: component.settings?.allowAssigneesViewResponses,
              hideresponsesuntilresponded: component.settings?.hideresponsesuntilresponded
            });
          }
          


          // Prepare comment data based on the component structure from save
          const commentData = {
            id: newCommentId,
            title: component.title || null,
            content: JSON.stringify(component.content || []),
            type: component.type,
            reference_id: component.id,
            table: 'components',
            account: userData?.account_id,
            status: 'created',
            relevantRole: 'CourseBuilder',
            // For Discussion Board components, use the component's settings
            // For Content components, use the default false values
            allowmainresponse: component.type === 'Discussion Board' ? (component.settings?.allowMainResponse ?? true) : false,
            allowcommentresponse: component.type === 'Discussion Board' ? (component.settings?.allowCommentResponse ?? true) : false,
            notifymeonresponses: component.type === 'Discussion Board' ? (component.settings?.notifyMeOnResponses ?? false) : false,
            allowassigneesviewresponses: component.type === 'Discussion Board' ? (component.settings?.allowAssigneesViewResponses ?? true) : false,
            hideresponsesuntilresponded: component.type === 'Discussion Board' ? (component.settings?.hideresponsesuntilresponded ?? true) : false,
            initialnotification: false
          };
          
          // Debug: Log the comment data being sent
          if (component.type === 'Discussion Board') {
            console.log('🔍 Comment data for Discussion Board:', commentData);
          }

          // Create the comment with the pre-generated ID using the component-specific endpoint
          const response = await axios.post('/api/comments/component', commentData, {
            headers: { Authorization: `Bearer ${token}` }
          });

          // Update the component with the comment reference using the actual comment ID from database
          component.reference_table = 'comments';
          component.reference_id = response.data.comment.id;
          
          // Store the comment data for use in JSON generation
          component.commentData = response.data.comment;
          
          console.log('✅ Comment created for component:', {
            componentId: component.id,
            preGeneratedId: newCommentId,
            actualCommentId: response.data.comment.id,
            title: response.data.comment.title,
            note: 'Component now references the actual comment ID from database'
          });


        }
      }


      return components;
    } catch (error) {
      console.error('❌ Error creating comments for components:', error);
      throw error;
    }
  };

  // Helper function to create assignments for all Assignment components during save
  const createAssignmentsForComponents = async (components) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      for (const component of components) {
        // Only create assignments for Assignment components that don't already have them
        if (component.type === 'Assignment' && 
            (!component.reference_id || component.reference_table !== 'assignments')) {
          
          console.log(`🔍 Creating assignment for Assignment component:`, {
            id: component.id,
            type: component.type,
            hasAssignments: !!component.assignments,
            assignmentsCount: component.assignments?.length || 0,
            selectedAssignment: component.assignments?.[0] || null
          });
          
          // Generate UUID for the assignment first
          const newAssignmentId = uuidv4();
          
          // Check if there's a selected assignment to copy from
          const selectedAssignment = component.assignments?.[0];
          
          // Prepare assignment data based on the component structure from save
          const assignmentData = {
            id: newAssignmentId,
            title: selectedAssignment ? selectedAssignment.title : (component.text || component.title || "Untitled Assignment"),
            gradingresulttype: selectedAssignment ? selectedAssignment.gradingresulttype : "Points",
            initialdirections: selectedAssignment ? selectedAssignment.initialdirections : [],
            questions: selectedAssignment ? selectedAssignment.questions : [],
            hours: selectedAssignment ? selectedAssignment.hours : 0,
            maxpoints: selectedAssignment ? selectedAssignment.maxpoints : null,
            concentration: selectedAssignment ? selectedAssignment.concentration : (component.primaryConcentration || null),
            otherconcentrations: selectedAssignment ? selectedAssignment.otherconcentrations : (component.otherConcentrations || []),
            taxonomy: selectedAssignment ? selectedAssignment.taxonomy : (component.taxonomy || []),
            createdby: userData?.account_id,
            reference_id: component.id,
            reference_table: 'components',
            original: selectedAssignment ? selectedAssignment.id : null // Copy from selected assignment if available
          };

          // Create the assignment with the pre-generated ID using the component-specific endpoint
          const response = await axios.post('/api/assignments/component', assignmentData, {
            headers: { Authorization: `Bearer ${token}` }
          });

          // Update the component with the assignment reference using the actual assignment ID from database
          component.reference_table = 'assignments';
          component.reference_id = response.data.assignment.id;
          
          // Store the assignment data for use in JSON generation
          component.assignmentData = response.data.assignment;
          
          console.log('✅ Assignment created for component:', {
            componentId: component.id,
            preGeneratedId: newAssignmentId,
            actualAssignmentId: response.data.assignment.id,
            title: response.data.assignment.title,
            note: 'Component now references the actual assignment ID from database'
          });
        }
      }

      return components;
    } catch (error) {
      console.error('❌ Error creating assignments for components:', error);
      throw error;
    }
  };

  // Note: Draft Assignment components are now handled by createAssignmentsForComponents function
  // Active Assignment components are handled through the transaction system for updates

  const addSection = (type = 'Module') => {
    const newIndex = sections.length;
    const newSection = {
      id: uuidv4(),
      index: newIndex,
      sort_index: newIndex, // Add sort_index for database
      color: `hsl(${Math.random() * 360}, 70%, 80%)`,
      text: `Untitled ${type}`,
      content: type === 'Content' ? [] : '',
      title: type === 'Content' ? `Untitled ${type}` : `Untitled ${type}`,
      type: type,
      subsections: [],
      expanded: true,
      primaryConcentration: '',
      otherConcentrations: [],
      taxonomy: [],
      hourawards: [],
      status: 'Draft',
      parent: currentCourseId || null, // Set parent to course ID
      superparent: currentCourseId || null, // Set superparent to course ID
      level: 'section',
      hasChanges: false, // New sections start with no changes
      assignments: type === 'Assignment' ? [] : undefined, // Initialize assignments array for Assignment type
      reference_table: null, // Will be set when assignment is created during save
      reference_id: null, // Will be set when assignment is created during save
      settings: type === 'Discussion Board' ? { ...DEFAULT_DISCUSSION_BOARD_SETTINGS } : null
    };

    setSections([...sections, newSection]);
    
    // Auto-expand the editor if it's a Content or Discussion Board type
    if (type === 'Content' || type === 'Discussion Board') {
      setExpandedEditors(prev => ({
        ...prev,
        [`section-${newIndex}`]: true
      }));
    }
  };

  const addSubsection = (sectionIndex, type = 'Lesson') => {
    const newSections = [...sections];
    const section = newSections[sectionIndex];
    const newSubsection = {
      id: uuidv4(),
      index: section.subsections.length,
      sort_index: section.subsections.length, // Add sort_index for database
      color: `hsl(${Math.random() * 360}, 60%, 85%)`,
      text: `Untitled ${type}`,
      content: type === 'Content' ? [] : '',
      title: type === 'Content' ? `Untitled ${type}` : `Untitled ${type}`,
      type: type,
      nestedSections: [],
      expanded: true,
      primaryConcentration: '',
      otherConcentrations: [],
      taxonomy: [],
      hourawards: [],
      status: 'Draft',
      parent: section.id, // Set parent to section ID
      superparent: currentCourseId || null, // Set superparent to course ID
      level: 'subsection',
      hasChanges: false, // New subsections start with no changes
      assignments: type === 'Assignment' ? [] : undefined, // Initialize assignments array for Assignment type
      reference_table: null, // Will be set when assignment is created during save
      reference_id: null, // Will be set when assignment is created during save
      settings: type === 'Discussion Board' ? { ...DEFAULT_DISCUSSION_BOARD_SETTINGS } : null
    };

    section.subsections.push(newSubsection);
    setSections(newSections);
    
    // Auto-expand the editor if it's a Content or Discussion Board type
    if (type === 'Content' || type === 'Discussion Board') {
      setExpandedEditors(prev => ({
        ...prev,
        [`subsection-${sectionIndex}-${section.subsections.length - 1}`]: true
      }));
    }
  };

  const addNestedSection = (sectionIndex, subsectionIndex, itemType = 'Assignment') => {
    const newSections = [...sections];
    const section = newSections[sectionIndex];
    const subsection = section.subsections[subsectionIndex];
    const newNestedSection = {
      id: uuidv4(),
      index: subsection.nestedSections.length,
      sort_index: subsection.nestedSections.length, // Add sort_index for database
      color: `hsl(${Math.random() * 360}, 50%, 90%)`,
      text: `Untitled ${itemType}`,
      content: itemType === 'Content' ? [] : '',
      title: itemType === 'Content' ? `Untitled ${itemType}` : `Untitled ${itemType}`,
      type: itemType,
      primaryConcentration: '',
      otherConcentrations: [],
      taxonomy: [],
      hourawards: [],
      status: 'Draft',
      parent: subsection.id, // Set parent to subsection ID
      superparent: currentCourseId || null, // Set superparent to course ID
      level: 'nested',
      hasChanges: false, // New nested sections start with no changes
      assignments: itemType === 'Assignment' ? [] : undefined, // Initialize assignments array for Assignment type
      reference_table: null, // Will be set when assignment is created during save
      reference_id: null, // Will be set when assignment is created during save
      settings: itemType === 'Discussion Board' ? { ...DEFAULT_DISCUSSION_BOARD_SETTINGS } : null
    };

    subsection.nestedSections.push(newNestedSection);
    setSections(newSections);
    
    // Auto-expand the editor if it's a Content or Discussion Board type
    if (itemType === 'Content' || itemType === 'Discussion Board') {
      setExpandedEditors(prev => ({
        ...prev,
        [`nested-${sectionIndex}-${subsectionIndex}-${subsection.nestedSections.length - 1}`]: true
      }));
    }
  };

  const addItemToLesson = (sectionIndex, itemType = 'Assignment') => {
    const newSections = [...sections];
    const section = newSections[sectionIndex];
    const newItem = {
      id: uuidv4(),
      index: section.subsections.length,
      sort_index: section.subsections.length, // Add sort_index for database
      color: `hsl(${Math.random() * 360}, 60%, 85%)`, // Same color as subsections (green)
      text: `Untitled ${itemType}`,
      content: itemType === 'Content' ? [] : '',
      title: itemType === 'Content' ? `Untitled ${itemType}` : `Untitled ${itemType}`,
      type: itemType,
      primaryConcentration: '',
      otherConcentrations: [],
      taxonomy: [],
      hourawards: [],
      status: 'Draft',
      parent: section.id, // Set parent to section ID
      superparent: currentCourseId || null, // Set superparent to course ID
      level: 'subsection',
      hasChanges: false, // New lesson items start with no changes
      assignments: itemType === 'Assignment' ? [] : undefined, // Initialize assignments array for Assignment type
      reference_table: null, // Will be set when assignment is created during save
      reference_id: null, // Will be set when assignment is created during save
      settings: itemType === 'Discussion Board' ? { ...DEFAULT_DISCUSSION_BOARD_SETTINGS } : null
    };

    section.subsections.push(newItem);
    setSections(newSections);
    
    // Auto-expand the editor if it's a Content or Discussion Board type
    if (itemType === 'Content' || itemType === 'Discussion Board') {
      setExpandedEditors(prev => ({
        ...prev,
        [`lesson-item-${sectionIndex}-${section.subsections.length - 1}`]: true
      }));
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    
    // Add dragging class
    if (dragRef.current) {
      dragRef.current.style.opacity = '0.5';
    }
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newSections = [...sections];
    const draggedSection = newSections[draggedIndex];
    
    // Remove the dragged item
    newSections.splice(draggedIndex, 1);
    
    // Insert at new position
    newSections.splice(dropIndex, 0, draggedSection);
    
    // Update indices and mark as modified
    newSections.forEach((section, index) => {
      section.index = index;
      section.sort_index = index; // Also update sort_index for database
      // Changes detected through deep comparison
    });

    setSections(newSections);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDraggedSubsection(null);
    setDragOverSubsection(null);
    setDraggedNestedSection(null);
    setDragOverNestedSection(null);
    if (dragRef.current) {
      dragRef.current.style.opacity = '1';
    }
  };

  // Helper function to find all children of a component (domino style)
  const findAllChildren = (component, componentType, sectionIndex, subsectionIndex = null, nestedIndex = null) => {
    const titles = [];
    
    if (componentType === 'section') {
      const section = sections[sectionIndex];
      if (!section) return titles;
      
      // Add the section itself
      titles.push(`${section.type}: ${section.text || 'Untitled'}`);
      
      // Add referenced item if it exists
      if (section.reference_id && section.reference_table) {
        if (section.reference_table === 'assignments' && section.assignments && section.assignments.length > 0) {
          titles.push(`  📝 Referenced Assignment: ${section.assignments[0].title || 'Untitled Assignment'}`);
        } else if (section.reference_table === 'comments' && section.commentData) {
          titles.push(`  💬 Referenced Comment: ${section.commentData.title || 'Untitled Comment'}`);
        }
      }
      
      // Add all subsections
      if (section.subsections) {
        section.subsections.forEach((subsection, subIndex) => {
          titles.push(`  ${subsection.type}: ${subsection.text || 'Untitled'}`);
          
          // Add referenced item for subsection if it exists
          if (subsection.reference_id && subsection.reference_table) {
            if (subsection.reference_table === 'assignments' && subsection.assignments && subsection.assignments.length > 0) {
              titles.push(`    📝 Referenced Assignment: ${subsection.assignments[0].title || 'Untitled Assignment'}`);
            } else if (subsection.reference_table === 'comments' && subsection.commentData) {
              titles.push(`    💬 Referenced Comment: ${subsection.commentData.title || 'Untitled Comment'}`);
            }
          }
          
          // Add all nested sections
          if (subsection.nestedSections) {
            subsection.nestedSections.forEach((nestedSection, nestedIndex) => {
              titles.push(`    ${nestedSection.type}: ${nestedSection.text || 'Untitled'}`);
              
              // Add referenced item for nested section if it exists
              if (nestedSection.reference_id && nestedSection.reference_table) {
                if (nestedSection.reference_table === 'assignments' && nestedSection.assignments && nestedSection.assignments.length > 0) {
                  titles.push(`      📝 Referenced Assignment: ${nestedSection.assignments[0].title || 'Untitled Assignment'}`);
                } else if (nestedSection.reference_table === 'comments' && nestedSection.commentData) {
                  titles.push(`      💬 Referenced Comment: ${nestedSection.commentData.title || 'Untitled Comment'}`);
                }
              }
            });
          }
        });
      }
    } else if (componentType === 'subsection') {
      const section = sections[sectionIndex];
      if (!section || !section.subsections || !section.subsections[subsectionIndex]) return titles;
      
      const subsection = section.subsections[subsectionIndex];
      
      // Add the subsection itself
      titles.push(`${subsection.type}: ${subsection.text || 'Untitled'}`);
      
      // Add referenced item for subsection if it exists
      if (subsection.reference_id && subsection.reference_table) {
        if (subsection.reference_table === 'assignments' && subsection.assignments && subsection.assignments.length > 0) {
          titles.push(`  📝 Referenced Assignment: ${subsection.assignments[0].title || 'Untitled Assignment'}`);
        } else if (subsection.reference_table === 'comments' && subsection.commentData) {
          titles.push(`  💬 Referenced Comment: ${subsection.commentData.title || 'Untitled Comment'}`);
        }
      }
      
      // Add all nested sections
      if (subsection.nestedSections) {
        subsection.nestedSections.forEach((nestedSection, nestedIndex) => {
          titles.push(`  ${nestedSection.type}: ${nestedSection.text || 'Untitled'}`);
          
          // Add referenced item for nested section if it exists
          if (nestedSection.reference_id && nestedSection.reference_table) {
            if (nestedSection.reference_table === 'assignments' && nestedSection.assignments && nestedSection.assignments.length > 0) {
              titles.push(`    📝 Referenced Assignment: ${nestedSection.assignments[0].title || 'Untitled Assignment'}`);
            } else if (nestedSection.reference_table === 'comments' && nestedSection.commentData) {
              titles.push(`    💬 Referenced Comment: ${nestedSection.commentData.title || 'Untitled Comment'}`);
            }
          }
        });
      }
    } else if (componentType === 'nested') {
      const section = sections[sectionIndex];
      if (!section || !section.subsections || !section.subsections[subsectionIndex] || 
          !section.subsections[subsectionIndex].nestedSections || 
          !section.subsections[subsectionIndex].nestedSections[nestedIndex]) return titles;
      
      const nestedSection = section.subsections[subsectionIndex].nestedSections[nestedIndex];
      
      // Add the nested section itself
      titles.push(`${nestedSection.type}: ${nestedSection.text || 'Untitled'}`);
      
      // Add referenced item for nested section if it exists
      if (nestedSection.reference_id && nestedSection.reference_table) {
        if (nestedSection.reference_table === 'assignments' && nestedSection.assignments && nestedSection.assignments.length > 0) {
          titles.push(`  📝 Referenced Assignment: ${nestedSection.assignments[0].title || 'Untitled Assignment'}`);
        } else if (nestedSection.reference_table === 'comments' && nestedSection.commentData) {
          titles.push(`  💬 Referenced Comment: ${nestedSection.commentData.title || 'Untitled Comment'}`);
        }
      }
    }
    
    return titles;
  };

  // Helper function to check if deletion confirmation is needed
  const shouldShowDeletionConfirmation = (component, componentType, sectionIndex, subsectionIndex = null, nestedIndex = null) => {
    // Only show confirmation if we have an "Active" top-level component
    if (currentCourseStatus !== 'Active') {
      return false;
    }
    
    // Skip confirmation for newly created "Draft" components (no reference_id or createddate)
    if (component.status === 'Draft' && (!component.reference_id && !component.createddate)) {
      return false;
    }
    
    return true;
  };

  const removeSection = (index) => {
    const section = sections[index];
    
    // Check if we need to show deletion confirmation
    if (shouldShowDeletionConfirmation(section, 'section', index)) {
      const componentTitles = findAllChildren(section, 'section', index);
      setDeletionModalConfig({
        isOpen: true,
        componentType: 'section',
        sectionIndex: index,
        subsectionIndex: null,
        nestedIndex: null,
        componentTitles,
        onConfirm: () => {
          const section = sections[index];
          
          // Track deleted component if it has database references
          if (section.reference_id || section.createddate) {
            // Store the complete component hierarchy for rollback
            const completeHierarchy = {
              mainComponent: {
                id: section.id,
                type: section.type,
                level: 'section',
                reference_id: section.reference_id,
                reference_table: section.reference_table,
                createddate: section.createddate,
                updateddate: section.updateddate,
                text: section.text,
                index: section.index
              },
              children: []
            };
            
            // Add all subsections as children (only if they exist in database)
            if (section.subsections) {
              section.subsections.forEach((subsection, subIndex) => {
                // Only include subsections that exist in the database (have reference_id or createddate)
                if (subsection.reference_id || subsection.createddate) {
                  completeHierarchy.children.push({
                    id: subsection.id,
                    type: subsection.type,
                    level: 'subsection',
                    reference_id: subsection.reference_id,
                    reference_table: subsection.reference_table,
                    createddate: subsection.createddate,
                    updateddate: subsection.updateddate,
                    text: subsection.text,
                    index: subIndex,
                    parentSectionIndex: index
                  });
                  
                  // Add all nested sections as grandchildren (only if they exist in database)
                  if (subsection.nestedSections) {
                    subsection.nestedSections.forEach((nestedSection, nestedIndex) => {
                      // Only include nested sections that exist in the database
                      if (nestedSection.reference_id || nestedSection.createddate) {
                        completeHierarchy.children.push({
                          id: nestedSection.id,
                          type: nestedSection.type,
                          level: 'nested',
                          reference_id: nestedSection.reference_id,
                          reference_table: nestedSection.reference_table,
                          createddate: nestedSection.createddate,
                          updateddate: nestedSection.updateddate,
                          text: nestedSection.text,
                          index: nestedIndex,
                          parentSectionIndex: index,
                          parentSubsectionIndex: subIndex
                        });
                      } else {
                        console.log(`⚠️ Skipping Draft nested section ${nestedSection.id} (${nestedSection.type}) - not in database`);
                      }
                    });
                  }
                } else {
                  console.log(`⚠️ Skipping Draft subsection ${subsection.id} (${subsection.type}) - not in database`);
                }
              });
            }
            
            // Debug: Log what we're storing
            console.log(`💾 Storing hierarchy for deletion:`, {
              mainComponent: {
                id: completeHierarchy.mainComponent.id,
                type: completeHierarchy.mainComponent.type,
                reference_id: completeHierarchy.mainComponent.reference_id,
                reference_table: completeHierarchy.mainComponent.reference_table
              },
              children: completeHierarchy.children.map(child => ({
                id: child.id,
                type: child.type,
                reference_id: child.reference_id,
                reference_table: child.reference_table
              }))
            });
            
            // Log summary of what's being deleted vs. what's being skipped
            const totalSubsections = section.subsections ? section.subsections.length : 0;
            const totalNestedSections = section.subsections ? 
              section.subsections.reduce((count, ss) => count + (ss.nestedSections ? ss.nestedSections.length : 0), 0) : 0;
            const databaseSubsections = completeHierarchy.children.filter(c => c.level === 'subsection').length;
            const databaseNestedSections = completeHierarchy.children.filter(c => c.level === 'nested').length;
            
            console.log(`📊 Deletion Summary for Section "${section.text || section.type}":`, {
              totalSubsections,
              totalNestedSections,
              databaseSubsections,
              databaseNestedSections,
              skippedSubsections: totalSubsections - databaseSubsections,
              skippedNestedSections: totalNestedSections - databaseNestedSections,
              reason: 'Draft components (not in database) are skipped automatically'
            });
            
            setDeletedComponents(prev => [...prev, completeHierarchy.mainComponent]);
            setDeletedComponentHierarchies(prev => [...prev, completeHierarchy]);
          }
          
    const newSections = sections.filter((_, i) => i !== index);
    // Update indices and mark remaining sections as modified
    newSections.forEach((section, i) => {
      section.index = i;
      section.sort_index = i; // Also update sort_index for database
      // Changes detected through deep comparison
    });
    setSections(newSections);
        }
      });
    } else {
      // Direct deletion for new Draft components or when course is not Active
      const newSections = sections.filter((_, i) => i !== index);
      // Update indices and mark remaining sections as modified
      newSections.forEach((section, i) => {
        section.index = i;
        section.sort_index = i; // Also update sort_index for database
        // Changes detected through deep comparison
      });
      setSections(newSections);
    }
  };

  const updateSectionText = (index, text) => {
    const newSections = [...sections];
    newSections[index].text = text;
    // Changes detected through deep comparison
    setSections(newSections);
  };

  const updateSectionContent = (index, content, title) => {
    const newSections = [...sections];
    newSections[index].content = content;
    if (title !== undefined) {
      newSections[index].title = title;
    }
    // Changes detected through deep comparison
    setSections(newSections);
  };

  const updateSubsectionText = (sectionIndex, subsectionIndex, text) => {
    const newSections = [...sections];
    newSections[sectionIndex].subsections[subsectionIndex].text = text;
    // Changes detected through deep comparison
    setSections(newSections);
  };

  const updateSubsectionContent = (sectionIndex, subsectionIndex, content, title) => {
    const newSections = [...sections];
    newSections[sectionIndex].subsections[subsectionIndex].content = content;
    if (title !== undefined) {
      newSections[sectionIndex].subsections[subsectionIndex].title = title;
    }
    // Changes detected through deep comparison
    setSections(newSections);
  };

  const removeSubsection = (sectionIndex, subsectionIndex) => {
    const section = sections[sectionIndex];
    const subsection = section.subsections[subsectionIndex];
    
    // Check if we need to show deletion confirmation
    if (shouldShowDeletionConfirmation(subsection, 'subsection', sectionIndex, subsectionIndex)) {
      const componentTitles = findAllChildren(subsection, 'subsection', sectionIndex, subsectionIndex);
      setDeletionModalConfig({
        isOpen: true,
        componentType: 'subsection',
        sectionIndex: sectionIndex,
        subsectionIndex: subsectionIndex,
        nestedIndex: null,
        componentTitles,
        onConfirm: () => {
          const section = sections[sectionIndex];
          const subsection = section.subsections[subsectionIndex];
          
          // Track deleted component if it has database references
          if (subsection.reference_id || subsection.createddate) {
            // Store the complete component hierarchy for rollback
            const completeHierarchy = {
              mainComponent: {
                id: subsection.id,
                type: subsection.type,
                level: 'subsection',
                reference_id: subsection.reference_id,
                reference_table: subsection.reference_table,
                createddate: subsection.createddate,
                updateddate: subsection.updateddate,
                text: subsection.text,
                index: subsectionIndex,
                parentSectionIndex: sectionIndex
              },
              children: []
            };
            
            // Add all nested sections as children (only if they exist in database)
            if (subsection.nestedSections) {
              subsection.nestedSections.forEach((nestedSection, nestedIndex) => {
                // Only include nested sections that exist in the database
                if (nestedSection.reference_id || nestedSection.createddate) {
                  completeHierarchy.children.push({
                    id: nestedSection.id,
                    type: nestedSection.type,
                    level: 'nested',
                    reference_id: nestedSection.reference_id,
                    reference_table: nestedSection.reference_table,
                    createddate: nestedSection.createddate,
                    updateddate: nestedSection.updateddate,
                    text: nestedSection.text,
                    index: nestedIndex,
                    parentSectionIndex: sectionIndex,
                    parentSubsectionIndex: subsectionIndex
                  });
                } else {
                  console.log(`⚠️ Skipping Draft nested section ${nestedSection.id} (${nestedSection.type}) - not in database`);
                }
              });
            }
            
            // Debug: Log what we're storing
            console.log(`💾 Storing subsection hierarchy for deletion:`, {
              mainComponent: {
                id: completeHierarchy.mainComponent.id,
                type: completeHierarchy.mainComponent.type,
                reference_id: completeHierarchy.mainComponent.reference_id,
                reference_table: completeHierarchy.mainComponent.reference_table
              },
              children: completeHierarchy.children.map(child => ({
                id: child.id,
                type: child.type,
                reference_id: child.reference_id,
                reference_table: child.reference_table
              }))
            });
            
            // Log summary of what's being deleted vs. what's being skipped
            const totalNestedSections = subsection.nestedSections ? subsection.nestedSections.length : 0;
            const databaseNestedSections = completeHierarchy.children.filter(c => c.level === 'nested').length;
            
            console.log(`📊 Deletion Summary for Subsection "${subsection.text || subsection.type}":`, {
              totalNestedSections,
              databaseNestedSections,
              skippedNestedSections: totalNestedSections - databaseNestedSections,
              reason: 'Draft components (not in database) are skipped automatically'
            });
            
            setDeletedComponents(prev => [...prev, completeHierarchy.mainComponent]);
            setDeletedComponentHierarchies(prev => [...prev, completeHierarchy]);
          }
          
          const newSections = [...sections];
          const sectionToUpdate = newSections[sectionIndex];
          sectionToUpdate.subsections.splice(subsectionIndex, 1);
          // Update indices and mark as modified
          sectionToUpdate.subsections.forEach((sub, i) => {
            sub.index = i;
            sub.sort_index = i; // Also update sort_index for database
            // Changes detected through deep comparison
          });
          setSections(newSections);
        }
      });
    } else {
      // Direct deletion for new Draft components or when course is not Active
    const newSections = [...sections];
    const section = newSections[sectionIndex];
    section.subsections.splice(subsectionIndex, 1);
    // Update indices and mark as modified
    section.subsections.forEach((sub, i) => {
      sub.index = i;
      sub.sort_index = i; // Also update sort_index for database
      // Changes detected through deep comparison
    });
    setSections(newSections);
    }
  };

  const updateNestedSectionText = (sectionIndex, subsectionIndex, nestedSectionIndex, text) => {
    const newSections = [...sections];
    newSections[sectionIndex].subsections[subsectionIndex].nestedSections[nestedSectionIndex].text = text;
    // Changes detected through deep comparison
    setSections(newSections);
  };

  const updateNestedSectionContent = (sectionIndex, subsectionIndex, nestedSectionIndex, content, title) => {
    const newSections = [...sections];
    newSections[sectionIndex].subsections[subsectionIndex].nestedSections[nestedSectionIndex].content = content;
    if (title !== undefined) {
      newSections[sectionIndex].subsections[subsectionIndex].nestedSections[nestedSectionIndex].title = title;
    }
    // Changes detected through deep comparison
    setSections(newSections);
  };

  const removeNestedSection = (sectionIndex, subsectionIndex, nestedSectionIndex) => {
    const section = sections[sectionIndex];
    const subsection = section.subsections[subsectionIndex];
    const nestedSection = subsection.nestedSections[nestedSectionIndex];
    
    // Check if we need to show deletion confirmation
    if (shouldShowDeletionConfirmation(nestedSection, 'nested', sectionIndex, subsectionIndex, nestedSectionIndex)) {
      const componentTitles = findAllChildren(nestedSection, 'nested', sectionIndex, subsectionIndex, nestedSectionIndex);
      setDeletionModalConfig({
        isOpen: true,
        componentType: 'nested',
        sectionIndex: sectionIndex,
        subsectionIndex: subsectionIndex,
        nestedIndex: nestedSectionIndex,
        componentTitles,
        onConfirm: () => {
          const section = sections[sectionIndex];
          const subsection = section.subsections[subsectionIndex];
          const nestedSection = subsection.nestedSections[nestedSectionIndex];
          
          // Track deleted component if it has database references
          if (nestedSection.reference_id || nestedSection.createddate) {
            // Store the complete component hierarchy for rollback
            const completeHierarchy = {
              mainComponent: {
                id: nestedSection.id,
                type: nestedSection.type,
                level: 'nested',
                reference_id: nestedSection.reference_id,
                reference_table: nestedSection.reference_table,
                createddate: nestedSection.createddate,
                updateddate: nestedSection.updateddate,
                text: nestedSection.text,
                index: nestedSectionIndex,
                parentSectionIndex: sectionIndex,
                parentSubsectionIndex: subsectionIndex
              },
              children: [] // Nested sections have no children
            };
            
            // Debug: Log what we're storing
            console.log(`💾 Storing nested section hierarchy for deletion:`, {
              mainComponent: {
                id: completeHierarchy.mainComponent.id,
                type: completeHierarchy.mainComponent.type,
                reference_id: completeHierarchy.mainComponent.reference_id,
                reference_table: completeHierarchy.mainComponent.reference_table
              },
              children: completeHierarchy.children.map(child => ({
                id: child.id,
                type: child.type,
                reference_id: child.reference_id,
                reference_table: child.reference_table
              }))
            });
            
            setDeletedComponents(prev => [...prev, completeHierarchy.mainComponent]);
            setDeletedComponentHierarchies(prev => [...prev, completeHierarchy]);
          }
          
          const newSections = [...sections];
          const sectionToUpdate = newSections[sectionIndex];
          const subsectionToUpdate = sectionToUpdate.subsections[subsectionIndex];
          subsectionToUpdate.nestedSections.splice(nestedSectionIndex, 1);
          // Update indices and mark as modified
          subsectionToUpdate.nestedSections.forEach((nested, i) => {
            nested.index = i;
          });
          setSections(newSections);
        }
      });
    } else {
      // Direct deletion for new Draft components or when course is not Active
    const newSections = [...sections];
    const section = newSections[sectionIndex];
    const subsection = section.subsections[subsectionIndex];
    subsection.nestedSections.splice(nestedSectionIndex, 1);
    // Update indices and mark as modified
    subsection.nestedSections.forEach((nested, i) => {
      nested.index = i;
    });
    setSections(newSections);
    }
  };

  const toggleExpand = (sectionIndex) => {
    const newSections = [...sections];
    newSections[sectionIndex].expanded = !newSections[sectionIndex].expanded;
    setSections(newSections);
  };

  const toggleSubsectionExpand = (sectionIndex, subsectionIndex) => {
    const newSections = [...sections];
    newSections[sectionIndex].subsections[subsectionIndex].expanded = !newSections[sectionIndex].subsections[subsectionIndex].expanded;
    setSections(newSections);
  };

  const handleSubsectionDragStart = (e, sectionIndex, subsectionIndex) => {
    e.stopPropagation();
    setDraggedSubsection({ sectionIndex, subsectionIndex });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
  };

  const handleSubsectionDragOver = (e, sectionIndex, subsectionIndex) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverSubsection({ sectionIndex, subsectionIndex });
  };

  const handleSubsectionDrop = (e, dropSectionIndex, dropSubsectionIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedSubsection || 
        (draggedSubsection.sectionIndex === dropSectionIndex && 
         draggedSubsection.subsectionIndex === dropSubsectionIndex)) {
      setDraggedSubsection(null);
      setDragOverSubsection(null);
      return;
    }

    const newSections = [...sections];
    const sourceSection = newSections[draggedSubsection.sectionIndex];
    const targetSection = newSections[dropSectionIndex];
    
    // Only allow reordering within the same section for now
    if (draggedSubsection.sectionIndex === dropSectionIndex) {
      const draggedSubsectionItem = sourceSection.subsections[draggedSubsection.subsectionIndex];
      
      // Remove from original position
      sourceSection.subsections.splice(draggedSubsection.subsectionIndex, 1);
      
      // Insert at new position
      sourceSection.subsections.splice(dropSubsectionIndex, 0, draggedSubsectionItem);
      
      // Update indices and mark as modified
      sourceSection.subsections.forEach((sub, i) => {
        sub.index = i;
        sub.sort_index = i; // Also update sort_index for database
        // Changes detected through deep comparison
      });
      
      setSections(newSections);
    }
    
    setDraggedSubsection(null);
    setDragOverSubsection(null);
  };

  const handleNestedSectionDragStart = (e, sectionIndex, subsectionIndex, nestedSectionIndex) => {
    e.stopPropagation();
    setDraggedNestedSection({ sectionIndex, subsectionIndex, nestedSectionIndex });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
  };

  const handleNestedSectionDragOver = (e, sectionIndex, subsectionIndex, nestedSectionIndex) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverNestedSection({ sectionIndex, subsectionIndex, nestedSectionIndex });
  };

  const handleNestedSectionDrop = (e, dropSectionIndex, dropSubsectionIndex, dropNestedSectionIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedNestedSection || 
        (draggedNestedSection.sectionIndex === dropSectionIndex && 
         draggedNestedSection.subsectionIndex === dropSubsectionIndex &&
         draggedNestedSection.nestedSectionIndex === dropNestedSectionIndex)) {
      setDraggedNestedSection(null);
      setDragOverNestedSection(null);
      return;
    }

    const newSections = [...sections];
    const sourceSection = newSections[draggedNestedSection.sectionIndex];
    const sourceSubsection = sourceSection.subsections[draggedNestedSection.subsectionIndex];
    
    // Only allow reordering within the same subsection for now
    if (draggedNestedSection.sectionIndex === dropSectionIndex && 
        draggedNestedSection.subsectionIndex === dropSubsectionIndex) {
      const draggedNestedSectionItem = sourceSubsection.nestedSections[draggedNestedSection.nestedSectionIndex];
      
      // Remove from original position
      sourceSubsection.nestedSections.splice(draggedNestedSection.nestedSectionIndex, 1);
      
      // Insert at new position
      sourceSubsection.nestedSections.splice(dropNestedSectionIndex, 0, draggedNestedSectionItem);
      
      // Update indices and mark as modified
      sourceSubsection.nestedSections.forEach((nested, i) => {
        nested.index = i;
        nested.sort_index = i; // Also update sort_index for database
        // Changes detected through deep comparison
      });
      
      setSections(newSections);
    }
    
    setDraggedNestedSection(null);
    setDragOverNestedSection(null);
  };

  const handleAddButtonClick = (type, level = null) => {
    if (type === 'nested') {
      // For nested sections, immediately show item selector
      setPopupType(type);
      setPopupLevel(level);
      setShowItemSelector(true);
    } else if (type === 'subsection') {
      // Check if we're adding to a Lesson (either at section level or subsection level)
      const section = sections[level];
      if (section.type === 'Lesson') {
        // If it's a Lesson, only show item selector (no Lessons allowed within Lessons)
        setPopupType('nested');
        setPopupLevel({ sectionIndex: level, subsectionIndex: 0 });
        setShowItemSelector(true);
      } else {
        // If it's a Module, show the regular subsection popup
        setPopupType(type);
        setPopupLevel(level);
        setShowPopup(true);
      }
    } else {
      setPopupType(type);
      setPopupLevel(level);
      setShowPopup(true);
    }
  };

  const handlePopupOption = (option) => {
    if (option === 'item') {
      setShowItemSelector(true);
    } else if (option === 'module' && popupType === 'section') {
      addSection();
      setShowPopup(false);
    } else if (option === 'lesson' && popupType === 'section') {
      addSection('Lesson');
      setShowPopup(false);
    } else if (option === 'lesson' && popupType === 'subsection') {
      addSubsection(popupLevel);
      setShowPopup(false);
    }
  };

  const handleItemSelection = (itemType) => {
    if (popupType === 'section') {
      addSection(itemType);
    } else if (popupType === 'subsection') {
      addSubsection(popupLevel, itemType);
    } else if (popupType === 'nested') {
      // Check if this is a Lesson at section level or a regular nested section
      const section = sections[popupLevel.sectionIndex];
      if (section.type === 'Lesson') {
        addItemToLesson(popupLevel.sectionIndex, itemType);
      } else {
        addNestedSection(popupLevel.sectionIndex, popupLevel.subsectionIndex, itemType);
      }
    }
    setShowPopup(false);
    setShowItemSelector(false);
  };

  const closePopup = () => {
    setShowPopup(false);
    setShowItemSelector(false);
  };

  const toggleEditorExpansion = (editorId) => {
    setExpandedEditors(prev => ({
      ...prev,
      [editorId]: !prev[editorId]
    }));
  };

  const toggleOutlineSectionExpansion = (sectionIndex) => {
    setOutlineExpandedSections(prev => ({
      ...prev,
      [sectionIndex]: !prev[sectionIndex]
    }));
  };

  const toggleOutlineSubsectionExpansion = (sectionIndex, subsectionIndex) => {
    setOutlineExpandedSubsections(prev => ({
      ...prev,
      [`${sectionIndex}-${subsectionIndex}`]: !prev[`${sectionIndex}-${subsectionIndex}`]
    }));
  };

  const scrollToSection = (sectionIndex, subsectionIndex = null, nestedIndex = null) => {
    let targetElement = null;
    
    if (nestedIndex !== null) {
      // Scroll to nested section
      targetElement = document.querySelector(`[data-section="${sectionIndex}"][data-subsection="${subsectionIndex}"][data-nested="${nestedIndex}"]`);
    } else if (subsectionIndex !== null) {
      // Scroll to subsection
      targetElement = document.querySelector(`[data-section="${sectionIndex}"][data-subsection="${subsectionIndex}"]`);
    } else {
      // Scroll to main section
      targetElement = document.querySelector(`[data-section="${sectionIndex}"]`);
    }
    
    if (targetElement) {
      targetElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      
      // Add a brief highlight effect
      targetElement.style.transition = 'background-color 0.3s ease';
      targetElement.style.backgroundColor = 'rgba(29, 78, 137, 0.1)';
      setTimeout(() => {
        targetElement.style.backgroundColor = '';
      }, 2000);
    }
  };

  const openContentEditor = (editorData) => {
    setContentEditor(editorData);
    // Load existing title or use empty string
    setContentTitle(editorData.title || '');
    // Always reset to Rich Text with blank content
    setDirectionDraft({
      element: 'richText',
      value: '',
      imageUrl: '',
      imageWidth: '',
      imageHeight: ''
    });
    // Reset editing state
    setEditingContentId(null);
  };

  const closeContentEditor = () => {
    if (contentEditor) {
      // Use default title if none provided
      const finalTitle = contentTitle.trim() || `Untitled ${contentEditor.type || 'Content'}`;

      // Save title to the correct item in the course structure
      const { type, sectionIndex, subsectionIndex, nestedIndex, itemIndex, updateFunction } = contentEditor;
      if (typeof updateFunction === 'function') {
        // Use the updateFunction to persist the title (content is unchanged)
        updateFunction(contentEditor.content || [], finalTitle);
      } else if (type === 'section' && sectionIndex !== undefined) {
        const newSections = [...sections];
        newSections[sectionIndex].title = finalTitle;
        setSections(newSections);
      } else if (type === 'subsection' && sectionIndex !== undefined && subsectionIndex !== undefined) {
        const newSections = [...sections];
        newSections[sectionIndex].subsections[subsectionIndex].title = finalTitle;
        setSections(newSections);
      } else if (type === 'nested' && sectionIndex !== undefined && subsectionIndex !== undefined && nestedIndex !== undefined) {
        const newSections = [...sections];
        newSections[sectionIndex].subsections[subsectionIndex].nestedSections[nestedIndex].title = finalTitle;
        setSections(newSections);
      } else if (type === 'lesson-item' && sectionIndex !== undefined && itemIndex !== undefined) {
        const newSections = [...sections];
        newSections[sectionIndex].subsections[itemIndex].title = finalTitle;
        setSections(newSections);
      }
    }
    setContentEditor(null);
    setContentTitle('');
    // Always reset to Rich Text with blank content
    setDirectionDraft({
      element: 'richText',
      value: '',
      imageUrl: '',
      imageWidth: '',
      imageHeight: ''
    });
    // Reset editing state
    setEditingContentId(null);
  };

  const openDiscussionBoardEditor = (editorData) => {
    setDiscussionBoardEditor(editorData);
    const raw = editorData.settings || editorData.discussionboard_settings;
    const merged = raw
      ? { ...DEFAULT_DISCUSSION_BOARD_SETTINGS, ...raw }
      : { ...DEFAULT_DISCUSSION_BOARD_SETTINGS };
    if (!DISCUSSION_NOTIFY_ON_RESPONSES_UI_ENABLED) {
      merged.notifyMeOnResponses = false;
    }
    if (!merged.allowAssigneesViewResponses) {
      merged.hideresponsesuntilresponded = false;
    }
    setDiscussionBoardSettings(merged);
    // Load existing title or use empty string
    setDiscussionBoardTitle(editorData.title || '');
    // Always reset to Rich Text with blank content
    setDirectionDraft({
      element: 'richText',
      value: '',
      imageUrl: '',
      imageWidth: '',
      imageHeight: ''
    });
    // Reset editing state
    setEditingContentId(null);
  };

  const closeDiscussionBoardEditor = () => {
    if (discussionBoardEditor) {
      let effectiveDiscussionSettings = DISCUSSION_NOTIFY_ON_RESPONSES_UI_ENABLED
        ? discussionBoardSettings
        : { ...discussionBoardSettings, notifyMeOnResponses: false };
      if (!effectiveDiscussionSettings.allowAssigneesViewResponses) {
        effectiveDiscussionSettings = {
          ...effectiveDiscussionSettings,
          hideresponsesuntilresponded: false
        };
      }
      // Use default title if none provided
      const finalTitle = discussionBoardTitle.trim() || `Untitled ${discussionBoardEditor.type || 'Discussion Board'}`;

      // Save settings and title to the correct item in the course structure
      const { type, sectionIndex, subsectionIndex, nestedIndex, itemIndex, updateFunction } = discussionBoardEditor;
      if (typeof updateFunction === 'function') {
        // Use the updateFunction to persist the settings and title (content is unchanged)
        updateFunction(discussionBoardEditor.content || [], effectiveDiscussionSettings, finalTitle);
      } else if (type === 'section' && sectionIndex !== undefined) {
        const newSections = [...sections];
        newSections[sectionIndex].settings = { ...effectiveDiscussionSettings };
        newSections[sectionIndex].discussionboard_settings = { ...effectiveDiscussionSettings };
        newSections[sectionIndex].title = finalTitle;
        setSections(newSections);
      } else if (type === 'subsection' && sectionIndex !== undefined && subsectionIndex !== undefined) {
        const newSections = [...sections];
        newSections[sectionIndex].subsections[subsectionIndex].settings = { ...effectiveDiscussionSettings };
        newSections[sectionIndex].subsections[subsectionIndex].discussionboard_settings = { ...effectiveDiscussionSettings };
        newSections[sectionIndex].subsections[subsectionIndex].title = finalTitle;
        setSections(newSections);
      } else if (type === 'nested' && sectionIndex !== undefined && subsectionIndex !== undefined && nestedIndex !== undefined) {
        const newSections = [...sections];
        newSections[sectionIndex].subsections[subsectionIndex].nestedSections[nestedIndex].settings = { ...effectiveDiscussionSettings };
        newSections[sectionIndex].subsections[subsectionIndex].nestedSections[nestedIndex].discussionboard_settings = { ...effectiveDiscussionSettings };
        newSections[sectionIndex].subsections[subsectionIndex].nestedSections[nestedIndex].title = discussionBoardTitle;
        setSections(newSections);
      } else if (type === 'lesson-item' && sectionIndex !== undefined && itemIndex !== undefined) {
        const newSections = [...sections];
        newSections[sectionIndex].subsections[itemIndex].settings = { ...effectiveDiscussionSettings };
        newSections[sectionIndex].subsections[itemIndex].discussionboard_settings = { ...effectiveDiscussionSettings };
        newSections[sectionIndex].subsections[itemIndex].title = discussionBoardTitle;
        setSections(newSections);
      }
    }
    setDiscussionBoardEditor(null);
    setDiscussionBoardSettings({ ...DEFAULT_DISCUSSION_BOARD_SETTINGS });
    setDiscussionBoardTitle('');
    // Always reset to Rich Text with blank content
    setDirectionDraft({
      element: 'richText',
      value: '',
      imageUrl: '',
      imageWidth: '',
      imageHeight: ''
    });
    // Reset editing state
    setEditingContentId(null);
  };

  // Add or update content element
  const addOrUpdateContentElement = () => {
    let value = '';
    if (directionDraft.element === 'richText') {
      if (!directionDraft.value.trim()) return;
      value = directionDraft.value;
    } else if (directionDraft.element === 'image') {
      if (!directionDraft.imageUrl || !directionDraft.imageWidth || !directionDraft.imageHeight) return;
      value = `<img src="${directionDraft.imageUrl}" width="${directionDraft.imageWidth}" height="${directionDraft.imageHeight}" alt="Image">`;
    } else if (directionDraft.element === 'video') {
      if (!directionDraft.value.trim()) return;
      value = directionDraft.value;
    } else if (directionDraft.element === 'embed') {
      if (!directionDraft.value.trim()) return;
      value = directionDraft.value;
    }

    if (contentEditor) {
      const currentContent = contentEditor.content || [];
      
      if (editingContentId) {
        // Update existing content
        const updatedContent = currentContent.map(item => 
          item._id === editingContentId 
            ? { ...item, element: directionDraft.element, value }
            : item
        );
        contentEditor.updateFunction(updatedContent);
        // Update the contentEditor state to reflect changes immediately
        setContentEditor(prev => ({
          ...prev,
          content: updatedContent
        }));
        setEditingContentId(null);
      } else {
        // Add new content
        const newContent = [...currentContent, { _id: uuidv4(), element: directionDraft.element, value }];
        contentEditor.updateFunction(newContent);
        // Update the contentEditor state to reflect changes immediately
        setContentEditor(prev => ({
          ...prev,
          content: newContent
        }));
      }
    }

    // Reset the draft
    setDirectionDraft({ 
      element: 'richText', 
      value: '', 
      imageUrl: '', 
      imageWidth: '', 
      imageHeight: '' 
    });
  };

  // Add or update discussion board content element
  const addOrUpdateDiscussionBoardElement = () => {
    let value = '';
    if (directionDraft.element === 'richText') {
      if (!directionDraft.value.trim()) return;
      value = directionDraft.value;
    } else if (directionDraft.element === 'image') {
      if (!directionDraft.imageUrl || !directionDraft.imageWidth || !directionDraft.imageHeight) return;
      value = `<img src="${directionDraft.imageUrl}" width="${directionDraft.imageWidth}" height="${directionDraft.imageHeight}" alt="Image">`;
    } else if (directionDraft.element === 'video') {
      if (!directionDraft.value.trim()) return;
      value = directionDraft.value;
    } else if (directionDraft.element === 'embed') {
      if (!directionDraft.value.trim()) return;
      value = directionDraft.value;
    }

    if (discussionBoardEditor) {
      const currentContent = discussionBoardEditor.content || [];
      
      if (editingContentId) {
        // Update existing content
        const updatedContent = currentContent.map(item => 
          item._id === editingContentId 
            ? { ...item, element: directionDraft.element, value }
            : item
        );
        discussionBoardEditor.updateFunction(updatedContent, discussionBoardSettings);
        // Update the discussionBoardEditor state to reflect changes immediately
        setDiscussionBoardEditor(prev => ({
          ...prev,
          content: updatedContent
        }));
        setEditingContentId(null);
      } else {
        // Add new content
        const newContent = [...currentContent, { _id: uuidv4(), element: directionDraft.element, value }];
        discussionBoardEditor.updateFunction(newContent, discussionBoardSettings);
        // Update the discussionBoardEditor state to reflect changes immediately
        setDiscussionBoardEditor(prev => ({
          ...prev,
          content: newContent
        }));
      }
    }

    // Reset the draft
    setDirectionDraft({ 
      element: 'richText', 
      value: '', 
      imageUrl: '', 
      imageWidth: '', 
      imageHeight: '' 
    });
  };

  // Edit content element
  const editContentElement = (item) => {
    if (item.element === 'image') {
      // Parse image tag for url, width, height
      const match = item.value.match(/src=\"([^\"]+)\".*width=\"(\d+)\".*height=\"(\d+)\"/);
      setDirectionDraft({
        element: 'image',
        value: '',
        imageUrl: match ? match[1] : '',
        imageWidth: match ? match[2] : '',
        imageHeight: match ? match[3] : ''
      });
    } else {
      setDirectionDraft({
        element: item.element,
        value: item.value,
        imageUrl: '',
        imageWidth: '',
        imageHeight: ''
      });
    }
    setEditingContentId(item._id);
  };

  // Remove content element
  const removeContentElement = (id) => {
    if (contentEditor) {
      const currentContent = contentEditor.content || [];
      const updatedContent = currentContent.filter(item => item._id !== id);
      contentEditor.updateFunction(updatedContent);
      
      // Update the contentEditor state to reflect changes immediately
      setContentEditor(prev => ({
        ...prev,
        content: updatedContent
      }));
      
      // Clear editor if the deleted item was being edited
      if (editingContentId === id) {
        setEditingContentId(null);
        setDirectionDraft({ 
          element: 'richText', 
          value: '', 
          imageUrl: '', 
          imageWidth: '', 
          imageHeight: '' 
        });
      }
    }
  };

  // Remove discussion board content element
  const removeDiscussionBoardElement = (id) => {
    if (discussionBoardEditor) {
      const currentContent = discussionBoardEditor.content || [];
      const updatedContent = currentContent.filter(item => item._id !== id);
      discussionBoardEditor.updateFunction(updatedContent, discussionBoardSettings);
      
      // Update the discussionBoardEditor state to reflect changes immediately
      setDiscussionBoardEditor(prev => ({
        ...prev,
        content: updatedContent
      }));
      
      // Clear editor if the deleted item was being edited
      if (editingContentId === id) {
        setEditingContentId(null);
        setDirectionDraft({ 
          element: 'richText', 
          value: '', 
          imageUrl: '', 
          imageWidth: '', 
          imageHeight: '' 
        });
      }
    }
  };

  // Move content element up
  const moveContentUp = (idx) => {
    if (idx === 0) return;
    if (contentEditor) {
      const currentContent = [...(contentEditor.content || [])];
      [currentContent[idx - 1], currentContent[idx]] = [currentContent[idx], currentContent[idx - 1]];
      contentEditor.updateFunction(currentContent);
      
      // Update the contentEditor state to reflect changes immediately
      setContentEditor(prev => ({
        ...prev,
        content: currentContent
      }));
    }
  };

  // Move content element down
  const moveContentDown = (idx) => {
    if (contentEditor) {
      const currentContent = [...(contentEditor.content || [])];
      if (idx === currentContent.length - 1) return;
      [currentContent[idx], currentContent[idx + 1]] = [currentContent[idx + 1], currentContent[idx]];
      contentEditor.updateFunction(currentContent);
      
      // Update the contentEditor state to reflect changes immediately
      setContentEditor(prev => ({
        ...prev,
        content: currentContent
      }));
    }
  };

  // Move discussion board content element up
  const moveDiscussionBoardUp = (idx) => {
    if (idx === 0) return;
    if (discussionBoardEditor) {
      const currentContent = [...(discussionBoardEditor.content || [])];
      [currentContent[idx - 1], currentContent[idx]] = [currentContent[idx], currentContent[idx - 1]];
      discussionBoardEditor.updateFunction(currentContent, discussionBoardSettings);
      
      // Update the discussionBoardEditor state to reflect changes immediately
      setDiscussionBoardEditor(prev => ({
        ...prev,
        content: currentContent
      }));
    }
  };

  // Move discussion board content element down
  const moveDiscussionBoardDown = (idx) => {
    if (discussionBoardEditor) {
      const currentContent = [...(discussionBoardEditor.content || [])];
      if (idx === currentContent.length - 1) return;
      [currentContent[idx], currentContent[idx + 1]] = [currentContent[idx + 1], currentContent[idx]];
      discussionBoardEditor.updateFunction(currentContent, discussionBoardSettings);
      
      // Update the discussionBoardEditor state to reflect changes immediately
      setDiscussionBoardEditor(prev => ({
        ...prev,
        content: currentContent
      }));
    }
  };

  // Helper to render image from img tag
  const renderImageFromTag = (imgTag) => {
    const match = imgTag.match(/src=\"([^\"]+)\".*width=\"(\d+)\".*height=\"(\d+)\"/);
    if (match) {
      return <img src={match[1]} width={match[2]} height={match[3]} alt="Image" style={{ border: '1px solid #ccc' }} />;
    }
    return null;
  };

    // Helper function to get image dimensions and calculate web-friendly sizes
  const getImageDimensions = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      
      img.onload = () => {
        URL.revokeObjectURL(url);
        const { naturalWidth, naturalHeight } = img;
        
        // Calculate web-friendly dimensions
        let webWidth = naturalWidth;
        let webHeight = naturalHeight;
        
        // If image is very large, scale it down to reasonable web dimensions
        const maxWidth = 1200; // Maximum width for web
        const maxHeight = 800; // Maximum height for web
        
        if (naturalWidth > maxWidth || naturalHeight > maxHeight) {
          const aspectRatio = naturalWidth / naturalHeight;
          
          if (naturalWidth > naturalHeight) {
            // Landscape image
            webWidth = maxWidth;
            webHeight = Math.round(maxWidth / aspectRatio);
          } else {
            // Portrait image
            webHeight = maxHeight;
            webWidth = Math.round(maxHeight * aspectRatio);
          }
        }
        
        resolve({
          naturalWidth,
          naturalHeight,
          webWidth,
          webHeight
        });
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(url);
        // Fallback to default dimensions if image loading fails
        resolve({
          naturalWidth: 800,
          naturalHeight: 600,
          webWidth: 800,
          webHeight: 600
        });
      };
      
      img.src = url;
    });
  };

  // Helper function to get image dimensions from URL and calculate web-friendly sizes
  const getImageDimensionsFromUrl = (imageUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      
      img.onload = () => {
        const { naturalWidth, naturalHeight } = img;
        
        // Calculate web-friendly dimensions
        let webWidth = naturalWidth;
        let webHeight = naturalHeight;
        
        // If image is very large, scale it down to reasonable web dimensions
        const maxWidth = 1200; // Maximum width for web
        const maxHeight = 800; // Maximum height for web
        
        if (naturalWidth > maxWidth || naturalHeight > maxHeight) {
          const aspectRatio = naturalWidth / naturalHeight;
          
          if (naturalWidth > naturalHeight) {
            // Landscape image
            webWidth = maxWidth;
            webHeight = Math.round(maxWidth / aspectRatio);
          } else {
            // Portrait image
            webHeight = maxHeight;
            webWidth = Math.round(maxHeight * aspectRatio);
          }
        }
        
        resolve({
          naturalWidth,
          naturalHeight,
          webWidth,
          webHeight
        });
      };
      
      img.onerror = () => {
        // Fallback to default dimensions if image loading fails
        resolve({
          naturalWidth: 800,
          naturalHeight: 600,
          webWidth: 800,
          webHeight: 600
        });
      };
      
      img.src = imageUrl;
    });
  };

  // Helper function to check deployment readiness
  const getDeploymentReadinessStatus = () => {
    const issues = [];
    const targetTypes = ['Content', 'Discussion Board', 'Assignment'];
    
    // Helper function to check if grade_settings is null or empty
    const isGradeSettingsEmpty = (gradeSettings) => {
      return !gradeSettings || 
             (typeof gradeSettings === 'object' && Object.keys(gradeSettings).length === 0);
    };
    
    // Check all sections
    sections.forEach((section, sectionIndex) => {
      if (targetTypes.includes(section.type)) {
        const hasHourAwardsIssue = !section.hourawards || section.hourawards.length === 0;
        const hasGradeSettingsIssue = isGradeSettingsEmpty(section.gradeSettings);
        
        if (hasHourAwardsIssue || hasGradeSettingsIssue) {
          const issueTypes = [];
          if (hasHourAwardsIssue) issueTypes.push('hour awards');
          if (hasGradeSettingsIssue) issueTypes.push('grade settings');
          
          issues.push({
            type: 'section',
            level: 'section',
            index: sectionIndex,
            componentType: section.type,
            title: section.text || section.title || `Untitled ${section.type}`,
            path: `${sectionIndex + 1}. ${section.type}`,
            issueTypes: issueTypes
          });
        }
      }
      
      // Check subsections
      if (section.subsections) {
        section.subsections.forEach((subsection, subIndex) => {
          if (targetTypes.includes(subsection.type)) {
            const hasHourAwardsIssue = !subsection.hourawards || subsection.hourawards.length === 0;
            const hasGradeSettingsIssue = isGradeSettingsEmpty(subsection.gradeSettings);
            
            if (hasHourAwardsIssue || hasGradeSettingsIssue) {
              const issueTypes = [];
              if (hasHourAwardsIssue) issueTypes.push('hour awards');
              if (hasGradeSettingsIssue) issueTypes.push('grade settings');
              
              issues.push({
                type: 'subsection',
                level: 'subsection',
                sectionIndex,
                subsectionIndex: subIndex,
                componentType: subsection.type,
                title: subsection.text || subsection.title || `Untitled ${subsection.type}`,
                path: `${sectionIndex + 1}.${subIndex + 1}. ${subsection.type}`,
                issueTypes: issueTypes
              });
            }
          }
          
          // Check nested sections
          if (subsection.nestedSections) {
            subsection.nestedSections.forEach((nestedSection, nestedIndex) => {
              if (targetTypes.includes(nestedSection.type)) {
                const hasHourAwardsIssue = !nestedSection.hourawards || nestedSection.hourawards.length === 0;
                const hasGradeSettingsIssue = isGradeSettingsEmpty(nestedSection.gradeSettings);
                
                if (hasHourAwardsIssue || hasGradeSettingsIssue) {
                  const issueTypes = [];
                  if (hasHourAwardsIssue) issueTypes.push('hour awards');
                  if (hasGradeSettingsIssue) issueTypes.push('grade settings');
                  
                  issues.push({
                    type: 'nested',
                    level: 'nested',
                    sectionIndex,
                    subsectionIndex: subIndex,
                    nestedIndex,
                    componentType: nestedSection.type,
                    title: nestedSection.text || nestedSection.title || `Untitled ${nestedSection.type}`,
                    path: `${sectionIndex + 1}.${subIndex + 1}.${nestedIndex + 1}. ${nestedSection.type}`,
                    issueTypes: issueTypes
                  });
                }
              }
            });
          }
        });
      }
    });
    
    return {
      isReady: issues.length === 0,
      issuesCount: issues.length,
      issues: issues
    };
  };

  // Handle image file upload (uses presigned URL for direct S3 upload - bypasses proxy size limits)
  const handleImageFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      setImageUploadError('Please select an image file.');
      return;
    }

    if (!userData || !userData.account_id) {
      setImageUploadError('User data not available. Please refresh the page.');
      return;
    }

    setImageUploading(true);
    setImageUploadError('');
    
    try {
      // Get image dimensions first
      const dimensions = await getImageDimensions(file);
      const uploadId = uuidv4();
      const token = localStorage.getItem('token');
      
      // Get presigned URL (metadata only - no file through proxy)
      const response = await axios.post('/api/upload-url', {
        filename: file.name,
        contentType: file.type,
        fileSize: file.size,
        uploadId: uploadId,
        details: 'lms component - content'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Upload file directly to S3 using presigned URL (bypasses proxy)
      const uploadResponse = await fetch(response.data.presignedUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type }
      });
      
      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.statusText}`);
      }

      const url = response.data.uploadRecord.link;
      // Update direction draft with the uploaded image and actual dimensions
      setDirectionDraft(prev => ({
        ...prev,
        element: 'image',
        value: `<img src="${url}" width="${dimensions.webWidth}" height="${dimensions.webHeight}" alt="Image">`,
        imageUrl: url,
        imageWidth: dimensions.webWidth.toString(),
        imageHeight: dimensions.webHeight.toString()
      }));

    } catch (err) {
      console.error('Error uploading image:', err);
      setImageUploadError('Failed to upload image.');
    } finally {
      setImageUploading(false);
    }
  };

  // Fetch user's uploads
  const fetchUserUploads = async (type) => {
    if (!userData || !userData.account_id) {
      console.error('User data not available');
      return;
    }

    setUploadsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/uploads?type=${type}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserUploads(response.data.uploads);
      setShowUploadsModal(true);
    } catch (err) {
      console.error('Error fetching uploads:', err);
    } finally {
      setUploadsLoading(false);
    }
  };

  // Handle video file upload (uses presigned URL for direct S3 upload - bypasses proxy size limits)
  const handleVideoFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check if file is a video
    if (!file.type.startsWith('video/')) {
      setVideoUploadError('Please select a video file.');
      return;
    }

    if (!userData || !userData.account_id) {
      setVideoUploadError('User data not available. Please refresh the page.');
      return;
    }

    setVideoUploading(true);
    setVideoUploadError('');
    
    try {
      const uploadId = uuidv4();
      const token = localStorage.getItem('token');
      
      // Get presigned URL (metadata only - no file through proxy)
      const response = await axios.post('/api/upload-url', {
        filename: file.name,
        contentType: file.type,
        fileSize: file.size,
        uploadId: uploadId,
        details: 'lms component - content'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Upload file directly to S3 using presigned URL (bypasses proxy)
      const uploadResponse = await fetch(response.data.presignedUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type }
      });
      
      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.statusText}`);
      }

      // Update direction draft with the uploaded video URL
      const url = response.data.uploadRecord.link;
      setDirectionDraft(prev => ({
        ...prev,
        element: 'video',
        value: url
      }));

    } catch (err) {
      console.error('Error uploading video:', err);
      setVideoUploadError('Failed to upload video.');
    } finally {
      setVideoUploading(false);
    }
  };

  // Helper to render video from URL
  const renderVideoFromUrl = (url) => {
    // Try to embed Vimeo/YouTube, fallback to HTML5 video
    if (/vimeo\.com/.test(url)) {
      const id = url.split('/').pop();
      return <iframe src={`https://player.vimeo.com/video/${id}`} width="320" height="180" frameBorder="0" allow="autoplay; fullscreen" allowFullScreen title="Vimeo Video" />;
    }
    if (/youtube\.com|youtu\.be/.test(url)) {
      let id = url;
      if (url.includes('youtu.be/')) id = url.split('youtu.be/')[1];
      else if (url.includes('v=')) id = url.split('v=')[1].split('&')[0];
      return <iframe width="320" height="180" src={`https://www.youtube.com/embed/${id}`} frameBorder="0" allow="autoplay; encrypted-media" allowFullScreen title="YouTube Video" />;
    }
    // Fallback: HTML5 video
    return <video src={url} width="320" height="180" controls />;
  };

  const getDefaultGradeSettings = (type) => {
    switch (type) {
      case 'Content':
        return {
          type: 'completion',
          criteria: 'click_complete'
        };
      case 'Assignment':
        return {
          type: 'score_threshold',
          criteria: {
            min_score: 70,
            max_score: 100
          }
        };
      case 'Discussion Board':
        return {
          type: 'discussion_board',
          criteria: {
            type: 'respond_to_main',
            min_responses: null
          }
        };
      default:
        return null;
    }
  };

  // Check if it's safe to load the template
  const canLoadTemplate = () => {
    // Check if there are any pending changes
    const hasChanges = getComponentsWithChangesCount() > 0;
    
    // Check if there are any sections (components other than the top-level course component)
    const hasSections = sections.length > 0;
    
    // Check if there are any active components (components that exist in the database)
    const hasActiveComponents = sections.some(section => {
      // Check if section is active
      if (section.status === 'Active' || section.createddate) return true;
      
      // Check subsections
      if (section.subsections) {
        for (const subsection of section.subsections) {
          if (subsection.status === 'Active' || subsection.createddate) return true;
          
          // Check nested sections
          if (subsection.nestedSections) {
            for (const nestedSection of subsection.nestedSections) {
              if (nestedSection.status === 'Active' || nestedSection.createddate) return true;
            }
          }
        }
      }
      
      return false;
    });
    
    return !hasChanges && !hasSections && !hasActiveComponents;
  };

  const loadDefaultTemplate = () => {
    // Safety check: prevent loading template if there are pending changes or existing components
    if (!canLoadTemplate()) {
      const hasChanges = getComponentsWithChangesCount() > 0;
      const hasSections = sections.length > 0;
      const hasActiveComponents = sections.some(section => {
        if (section.status === 'Active' || section.createddate) return true;
        if (section.subsections) {
          for (const subsection of section.subsections) {
            if (subsection.status === 'Active' || subsection.createddate) return true;
            if (subsection.nestedSections) {
              for (const nestedSection of subsection.nestedSections) {
                if (nestedSection.status === 'Active' || nestedSection.createddate) return true;
              }
            }
          }
        }
        return false;
      });
      
      let message = 'Cannot load template because:';
      if (hasChanges) message += ' there are unsaved changes';
      if (hasSections) message += ' there are existing course components';
      if (hasActiveComponents) message += ' there are active components that would be overwritten';
      
      alert(message + '. Please save your changes or start a new course first.');
      return;
    }
    
    const newSections = [];
    
    // Preserve the current course ID if one exists
    const templateCourseId = currentCourseId;
    
    // Create 1 Module
    const module = {
      id: uuidv4(),
      index: 0,
      sort_index: 0,
      color: `hsl(${Math.random() * 360}, 70%, 80%)`,
      text: 'Course Module',
      content: '',
      title: 'Course Module',
      type: 'Module',
      subsections: [],
      expanded: true,
      primaryConcentration: '',
      otherConcentrations: [],
      taxonomy: [],
      hourawards: [],
      gradeSettings: null,
      status: 'Draft',
      parent: templateCourseId || null,
      superparent: templateCourseId || null,
      level: 'section',
      hasChanges: false,
      reference_table: null,
      reference_id: null
    };

    // Create 5 Lessons
    for (let lessonIndex = 0; lessonIndex < 5; lessonIndex++) {
      const lesson = {
        id: uuidv4(),
        index: lessonIndex,
        sort_index: lessonIndex,
        color: `hsl(${Math.random() * 360}, 60%, 85%)`,
        text: `Lesson ${lessonIndex + 1}`,
        content: '',
        title: `Lesson ${lessonIndex + 1}`,
        type: 'Lesson',
        nestedSections: [],
        expanded: true,
        primaryConcentration: '',
        otherConcentrations: [],
        taxonomy: [],
        hourawards: [],
        gradeSettings: null,
        status: 'Draft',
        parent: module.id,
        superparent: templateCourseId || null,
        level: 'subsection',
        hasChanges: false,
        reference_table: null,
        reference_id: null
      };

      // Add 5 items to each lesson
      const items = [
        {
          type: 'Content',
          title: `Lesson ${lessonIndex + 1} - Instructional Video`
        },
        {
          type: 'Discussion Board',
          title: `Lesson ${lessonIndex + 1} - Discussion Board`
        },
        {
          type: 'Assignment',
          title: `Lesson ${lessonIndex + 1} - Assignment`
        },
        {
          type: 'Assignment',
          title: `Lesson ${lessonIndex + 1} - Learning Lab`
        },
        {
          type: 'Assignment',
          title: `Lesson ${lessonIndex + 1} - Quiz`
        }
      ];

      items.forEach((item, itemIndex) => {
        const newItem = {
          id: uuidv4(),
          index: itemIndex,
          sort_index: itemIndex,
          color: `hsl(${Math.random() * 360}, 50%, 90%)`,
          text: item.title,
          content: item.type === 'Content' ? [] : '',
          title: item.title,
          type: item.type,
          primaryConcentration: '',
          otherConcentrations: [],
          taxonomy: [],
          hourawards: [],
          gradeSettings: getDefaultGradeSettings(item.type),
          status: 'Draft',
          parent: lesson.id,
          superparent: templateCourseId || null,
          level: 'nested',
          hasChanges: false,
          reference_table: null,
          reference_id: null,
          settings: item.type === 'Discussion Board' ? { ...DEFAULT_DISCUSSION_BOARD_SETTINGS } : null,
          // Initialize assignments array for Assignment type components
          assignments: item.type === 'Assignment' ? [] : undefined
        };
        lesson.nestedSections.push(newItem);
      });

      module.subsections.push(lesson);
    }

    newSections.push(module);
    setSections(newSections);
    
    // Auto-expand all Content and Discussion Board editors
    const expandedEditorsState = {};
    newSections.forEach((section, sectionIndex) => {
      section.subsections.forEach((subsection, subsectionIndex) => {
        subsection.nestedSections.forEach((nestedSection, nestedIndex) => {
          if (nestedSection.type === 'Content' || nestedSection.type === 'Discussion Board') {
            expandedEditorsState[`nested-${sectionIndex}-${subsectionIndex}-${nestedIndex}`] = true;
          }
        });
      });
    });
    setExpandedEditors(expandedEditorsState);
    
    // Only clear course status if no course was previously loaded
    // This preserves the course ID and status when applying template to existing course
    if (!templateCourseId) {
      defaultCourseTrackAppliedRef.current = false;
      setCurrentCourseId(null);
      setCurrentCourseStatus(null);
      setCurrentCourseUpdatedDate(null);
      setCourseComponentState(null); // Clear course component state for new template
      setCourseCreatorLabelFromServer(null);
    }
    
    // Store the original state for change detection
    // If a course was loaded, preserve the course component in original state
    setOriginalComponentsState({
      course: templateCourseId ? originalComponentsState?.course : null,
      sections: JSON.parse(JSON.stringify(newSections)) // Deep copy
    });
  };

  const generateJsonStructure = () => {
    // Safety check: ensure sections exists
    if (!sections || !Array.isArray(sections)) {
      console.warn('Sections not available for JSON generation, returning empty structure');
      return {
        components: [{
          id: currentCourseId || uuidv4(),
          sort_index: 0,
          type: 'Course',
          title: courseTitle || 'Untitled Course',
          level: 'top',
          status: 'Draft',
          createddate: null,
          updateddate: null
        }]
      };
    }
    
    // Use existing course ID or generate new UUID
    const courseId = currentCourseId || uuidv4();
    
    const components = [];
    
    // Add course as component 0 (hidden from UI since level is 'top')
    // Use stored course component data if available, otherwise use fallback
    if (currentCourseComponent && currentCourseComponent.status === 'Active') {
      const effectiveTrack =
        courseTrack === 'TK12' ? 'TK12' : 'CTE';
      const courseJsonComponent = {
        id: currentCourseComponent.id,
        sort_index: 0,
        type: 'Course',
        title: currentCourseComponent.title || courseTitle || 'Untitled Course',
        level: 'top',
        status: currentCourseComponent.status,
        course_track: effectiveTrack,
        concentration:
          effectiveTrack === 'CTE'
            ? currentCourseComponent.concentration || defaultPrimaryConcentration || null
            : null,
        otherconcentrations:
          effectiveTrack === 'CTE' ? currentCourseComponent.otherconcentrations || [] : [],
        subject: effectiveTrack === 'TK12' ? (tk12Subject || currentCourseComponent.subject || null) : null,
        grade_levels:
          effectiveTrack === 'TK12'
            ? (gradeLevels.length ? gradeLevels : currentCourseComponent.grade_levels || [])
            : [],
        taxonomy: currentCourseComponent.taxonomy || [],
        hourawards: currentCourseComponent.hourawards || [],
        grade_settings: currentCourseComponent.grade_settings || null,
        createddate: currentCourseComponent.createddate !== undefined ? currentCourseComponent.createddate : null,
        updateddate: currentCourseComponent.updateddate !== undefined ? currentCourseComponent.updateddate : null
      };
      
      components.push(courseJsonComponent);
    } else {
    components.push({
      id: courseId,
      sort_index: 0,
      type: 'Course',
      title: courseTitle || 'Untitled Course',
      level: 'top',
        status: 'Draft',
        course_track: courseTrack === 'TK12' ? 'TK12' : 'CTE',
        concentration: courseTrack === 'CTE' ? defaultPrimaryConcentration || null : null,
        otherconcentrations: [],
        subject: courseTrack === 'TK12' ? (tk12Subject || null) : null,
        grade_levels: courseTrack === 'TK12' ? gradeLevels : [],
        taxonomy: [],
        hourawards: [],
        grade_settings: null,
        createddate: null,
        updateddate: null
      });
    }
    
    // Add all sections (only visible levels: section, subsection, nested)
    sections.forEach((section, sectionIndex) => {
      // Skip Course components since they're at 'top' level and should be invisible
      if (section.type === 'Course' || section.level === 'top') {
        return;
      }
      
      // Use the actual status from the section, or 'Draft' if not set
      let componentStatus = section.status || 'Draft';
      
      // For Draft Content/Discussion Board components, show UI building fields
      if (componentStatus === 'Draft' && (section.type === 'Content' || section.type === 'Discussion Board')) {
        // Draft Content/Discussion Board - show UI building fields
        const sectionComponent = {
        id: section.id,
          sort_index: section.index,
        type: section.type,
        title: section.text || null,
          post_title: section.title || null,
        content: section.content && section.content.length > 0 ? section.content : null,
        parent: courseId,
        superparent: courseId,
        level: 'section',
        concentration: section.primaryConcentration || null,
        otherconcentrations: section.otherConcentrations && section.otherConcentrations.length > 0 ? section.otherConcentrations : null,
        taxonomy: section.taxonomy && section.taxonomy.length > 0 ? section.taxonomy : null,
        hourawards: section.hourawards && section.hourawards.length > 0 ? section.hourawards : null,
        grade_settings: section.gradeSettings || null,
        discussionboard_settings: section.type === 'Discussion Board' ? (section.discussionboard_settings || section.settings || { ...DEFAULT_DISCUSSION_BOARD_SETTINGS }) : null,
        status: componentStatus,
          reference_table: 'comments',
          reference_id: section.reference_id || null
        };
        
        components.push(sectionComponent);
      } else if (componentStatus === 'Active' && (section.type === 'Content' || section.type === 'Discussion Board') && section.reference_id) {
        // Active Content/Discussion Board with comments - show database fields + referenced comment data
        const sectionComponent = {
          id: section.id,
          sort_index: section.index,
          type: section.type,
          title: section.text || null,
          parent: courseId,
          superparent: courseId,
          level: 'section',
          concentration: section.primaryConcentration || null,
          otherconcentrations: section.otherConcentrations && section.otherConcentrations.length > 0 ? section.otherConcentrations : null,
          taxonomy: section.taxonomy && section.taxonomy.length > 0 ? section.taxonomy : null,
          hourawards: section.hourawards && section.hourawards.length > 0 ? section.hourawards : null,
          grade_settings: section.gradeSettings || null,
          status: componentStatus,
          reference_table: 'comments',
          reference_id: section.reference_id,
          createddate: section.createddate || null,
          updateddate: section.updateddate || null,
                        referenced_item: {
                id: section.reference_id,
                title: section.title || null,
                content: section.content && section.content.length > 0 ? section.content : null,
                type: section.type,
                status: section.commentData?.status || 'created',
                // Use actual comment dates from the database
                createddate: section.commentData?.createddate || null,
                updateddate: section.commentData?.updateddate || null,
                // For Discussion Board components, include the discussion board settings
                ...(section.type === 'Discussion Board' && section.commentData ? {
                  hideresponsesuntilresponded: section.commentData.hideresponsesuntilresponded,
                  allowMainResponse: section.commentData.allowmainresponse,
                  notifyMeOnResponses: section.commentData.notifymeonresponses,
                  allowAssigneesViewResponses: section.commentData.allowassigneesviewresponses,
                  allowCommentResponse: section.commentData.allowcommentresponse
                } : {})
              }
        };
        
        components.push(sectionComponent);
      } else if (componentStatus === 'Active' && section.type === 'Assignment' && section.reference_id) {
        // Active Assignment with assignment reference - show database fields + referenced assignment data
        const sectionComponent = {
          id: section.id,
          sort_index: section.index,
          type: section.type,
          title: section.text || null,
          parent: courseId,
          superparent: courseId,
          level: 'section',
          concentration: section.primaryConcentration || null,
          otherconcentrations: section.otherConcentrations && section.otherConcentrations.length > 0 ? section.otherConcentrations : null,
          taxonomy: section.taxonomy && section.taxonomy.length > 0 ? section.taxonomy : null,
          hourawards: section.hourawards && section.hourawards.length > 0 ? section.hourawards : null,
          grade_settings: section.gradeSettings || null,
          status: componentStatus,
          reference_table: 'assignments',
          reference_id: section.reference_id,
          createddate: section.createddate || null,
          updateddate: section.updateddate || null,
          referenced_item: {
            id: section.reference_id,
            // The original field shows the ID of the assignment that was copied from
            // This is updated when a different assignment is selected in the assignment modal
            original: section.assignmentData?.original || null
          }
        };
        
        // Debug logging for assignment components
        console.log('🔍 JSON Generation - Assignment component:', {
          componentId: section.id,
          componentTitle: section.text,
          referenceId: section.reference_id,
          assignmentData: section.assignmentData,
          assignmentDataOriginal: section.assignmentData?.original,
          referencedItemOriginal: sectionComponent.referenced_item.original
        });
        
        components.push(sectionComponent);
      } else {
        // For other types (Module, Lesson, Assignment) or non-Content/Discussion Board
        const sectionComponent = {
          id: section.id,
          sort_index: section.index,
          type: section.type,
          title: section.text || null,
          parent: courseId,
          superparent: courseId,
          level: 'section',
          concentration: section.primaryConcentration || null,
          otherconcentrations: section.otherConcentrations && section.otherConcentrations.length > 0 ? section.otherConcentrations : null,
          taxonomy: section.taxonomy && section.taxonomy.length > 0 ? section.taxonomy : null,
          hourawards: section.hourawards && section.hourawards.length > 0 ? section.hourawards : null,
          grade_settings: section.gradeSettings || null,
          status: componentStatus,
          reference_table: section.type === 'Assignment' ? 'assignments' : null,
          reference_id: section.type === 'Assignment' && section.reference_id ? section.reference_id : null,
          createddate: section.createddate || null,
          updateddate: section.updateddate || null
        };
        
        components.push(sectionComponent);
      }
      
              // Add all subsections (only if subsection has subsections property)
              if (section.subsections && Array.isArray(section.subsections)) {
                section.subsections.forEach((subsection, subsectionIndex) => {
          // Use the actual status from the subsection, or 'Draft' if not set
          let componentStatus = subsection.status || 'Draft';
          
          // For Draft Content/Discussion Board components, show UI building fields
          if (componentStatus === 'Draft' && (subsection.type === 'Content' || subsection.type === 'Discussion Board')) {
            // Draft Content/Discussion Board - show UI building fields
            const subsectionComponent = {
            id: subsection.id,
              sort_index: subsection.index,
            type: subsection.type,
            title: subsection.text || null,
              post_title: subsection.title || null,
            content: subsection.content && subsection.content.length > 0 ? subsection.content : null,
            parent: section.id,
            superparent: courseId,
            level: 'subsection',
            concentration: subsection.primaryConcentration || null,
            otherconcentrations: subsection.otherConcentrations && subsection.otherConcentrations.length > 0 ? subsection.otherConcentrations : null,
            taxonomy: subsection.taxonomy && subsection.taxonomy.length > 0 ? subsection.taxonomy : null,
            hourawards: subsection.hourawards && subsection.hourawards.length > 0 ? subsection.hourawards : null,
            grade_settings: subsection.gradeSettings || null,
            discussionboard_settings: subsection.type === 'Discussion Board' ? (subsection.discussionboard_settings || subsection.settings || { ...DEFAULT_DISCUSSION_BOARD_SETTINGS }) : null,
              status: componentStatus,
              reference_table: 'comments',
              reference_id: subsection.reference_id || null
            };
            components.push(subsectionComponent);
          } else if (componentStatus === 'Active' && (subsection.type === 'Content' || subsection.type === 'Discussion Board') && subsection.reference_id) {
            // Active Content/Discussion Board with comments - show database fields + referenced comment data
            const subsectionComponent = {
              id: subsection.id,
              sort_index: subsection.index,
              type: subsection.type,
              title: subsection.text || null,
              parent: section.id,
              superparent: courseId,
              level: 'subsection',
              concentration: subsection.primaryConcentration || null,
              otherconcentrations: subsection.otherConcentrations && subsection.otherConcentrations.length > 0 ? subsection.otherConcentrations : null,
              taxonomy: subsection.taxonomy && subsection.taxonomy.length > 0 ? subsection.taxonomy : null,
              hourawards: subsection.hourawards && subsection.hourawards.length > 0 ? subsection.hourawards : null,
              grade_settings: subsection.gradeSettings || null,
              status: componentStatus,
              reference_table: 'comments',
              reference_id: subsection.reference_id,
              createddate: subsection.createddate || null,
              updateddate: subsection.updateddate || null,
              referenced_item: {
                id: subsection.reference_id,
                title: subsection.title || null,
                content: subsection.content && subsection.content.length > 0 ? subsection.content : null,
                type: subsection.type,
                status: subsection.commentData?.status || 'created',
                // Use actual comment dates from the database
                createddate: subsection.commentData?.createddate || null,
                updateddate: subsection.commentData?.updateddate || null,
                // For Discussion Board components, include the discussion board settings
                ...(subsection.type === 'Discussion Board' && subsection.commentData ? {
                  hideresponsesuntilresponded: subsection.commentData.hideresponsesuntilresponded,
                  allowMainResponse: subsection.commentData.allowmainresponse,
                  notifyMeOnResponses: subsection.commentData.notifymeonresponses,
                  allowAssigneesViewResponses: subsection.commentData.allowassigneesviewresponses,
                  allowCommentResponse: subsection.commentData.allowcommentresponse
                } : {})
              }
            };
            components.push(subsectionComponent);
          } else if (componentStatus === 'Active' && subsection.type === 'Assignment' && subsection.reference_id) {
            // Active Assignment with assignment reference - show database fields + referenced assignment data
            const subsectionComponent = {
              id: subsection.id,
              sort_index: subsection.index,
              type: subsection.type,
              title: subsection.text || null,
              parent: section.id,
              superparent: courseId,
              level: 'subsection',
              concentration: subsection.primaryConcentration || null,
              otherconcentrations: subsection.otherConcentrations && subsection.otherConcentrations.length > 0 ? subsection.otherConcentrations : null,
              taxonomy: subsection.taxonomy && subsection.taxonomy.length > 0 ? subsection.taxonomy : null,
              hourawards: subsection.hourawards && subsection.hourawards.length > 0 ? subsection.hourawards : null,
              grade_settings: subsection.gradeSettings || null,
              status: componentStatus,
              reference_table: 'assignments',
              reference_id: subsection.reference_id,
              createddate: subsection.createddate || null,
              updateddate: subsection.updateddate || null,
              referenced_item: {
                id: subsection.reference_id,
                original: subsection.assignmentData?.original || null
              }
            };
            components.push(subsectionComponent);
          } else {
            // For other types (Module, Lesson, Assignment) or non-Content/Discussion Board
            const subsectionComponent = {
              id: subsection.id,
              sort_index: subsection.index,
              type: subsection.type,
              title: subsection.text || null,
              parent: section.id,
              superparent: courseId,
              level: 'subsection',
              concentration: subsection.primaryConcentration || null,
              otherconcentrations: subsection.otherConcentrations && subsection.otherConcentrations.length > 0 ? subsection.otherConcentrations : null,
              taxonomy: subsection.taxonomy && subsection.taxonomy.length > 0 ? subsection.taxonomy : null,
              hourawards: subsection.hourawards && subsection.hourawards.length > 0 ? subsection.hourawards : null,
              grade_settings: subsection.gradeSettings || null,
            status: componentStatus,
            reference_table: subsection.type === 'Assignment' ? 'assignments' : null,
              reference_id: subsection.type === 'Assignment' && subsection.reference_id ? subsection.reference_id : null,
              createddate: subsection.createddate || null,
              updateddate: subsection.updateddate || null
            };
            components.push(subsectionComponent);
          }
          
                    // Add all nested sections (only if subsection has nestedSections property)
          if (subsection.nestedSections && subsection.nestedSections.length > 0) {
            subsection.nestedSections.forEach((nestedSection, nestedIndex) => {
                          // Use the actual status from the nested section, or 'Draft' if not set
            let componentStatus = nestedSection.status || 'Draft';
              
              // For Draft Content/Discussion Board components, show UI building fields
              if (componentStatus === 'Draft' && (nestedSection.type === 'Content' || nestedSection.type === 'Discussion Board')) {
                // Draft Content/Discussion Board - show UI building fields
                const nestedSectionComponent = {
                id: nestedSection.id,
                  sort_index: nestedSection.index,
                type: nestedSection.type,
                title: nestedSection.text || null,
                  post_title: nestedSection.title || null,
                content: nestedSection.content && nestedSection.content.length > 0 ? nestedSection.content : null,
                parent: subsection.id,
                superparent: courseId,
                level: 'nested',
                concentration: nestedSection.primaryConcentration || null,
                otherconcentrations: nestedSection.otherConcentrations && nestedSection.otherConcentrations.length > 0 ? nestedSection.otherConcentrations : null,
                taxonomy: nestedSection.taxonomy && nestedSection.taxonomy.length > 0 ? nestedSection.taxonomy : null,
                hourawards: nestedSection.hourawards && nestedSection.hourawards.length > 0 ? nestedSection.hourawards : null,
                grade_settings: nestedSection.gradeSettings || null,
                discussionboard_settings: nestedSection.type === 'Discussion Board' ? (nestedSection.discussionboard_settings || nestedSection.settings || { ...DEFAULT_DISCUSSION_BOARD_SETTINGS }) : null,
                  status: componentStatus,
                  reference_table: 'comments',
                  reference_id: nestedSection.reference_id || null
                };
                components.push(nestedSectionComponent);
              } else if (componentStatus === 'Active' && (nestedSection.type === 'Content' || nestedSection.type === 'Discussion Board') && nestedSection.reference_id) {
                // Active Content/Discussion Board with comments - show database fields + referenced comment data
                const nestedSectionComponent = {
                  id: nestedSection.id,
                  sort_index: nestedSection.index,
                  type: nestedSection.type,
                  title: nestedSection.text || null,
                  parent: subsection.id,
                  superparent: courseId,
                  level: 'nested',
                  concentration: nestedSection.primaryConcentration || null,
                  otherconcentrations: nestedSection.otherConcentrations && nestedSection.otherConcentrations.length > 0 ? nestedSection.otherConcentrations : null,
                  taxonomy: nestedSection.taxonomy && nestedSection.taxonomy.length > 0 ? nestedSection.taxonomy : null,
                  hourawards: nestedSection.hourawards && nestedSection.hourawards.length > 0 ? nestedSection.hourawards : null,
                  grade_settings: nestedSection.gradeSettings || null,
                  status: componentStatus,
                  reference_table: 'comments',
                  reference_id: nestedSection.reference_id,
                  createddate: nestedSection.createddate || null,
                  updateddate: nestedSection.updateddate || null,
                  referenced_item: {
                    id: nestedSection.reference_id,
                    title: nestedSection.title || null,
                    content: nestedSection.content && nestedSection.content.length > 0 ? nestedSection.content : null,
                    type: nestedSection.type,
                    status: nestedSection.commentData?.status || 'created',
                    // Use actual comment dates from the database
                    createddate: nestedSection.commentData?.createddate || null,
                    updateddate: nestedSection.commentData?.updateddate || null,
                    // For Discussion Board components, include the discussion board settings
                    ...(nestedSection.type === 'Discussion Board' && nestedSection.commentData ? {
                      hideresponsesuntilresponded: nestedSection.commentData.hideresponsesuntilresponded,
                      allowMainResponse: nestedSection.commentData.allowmainresponse,
                      notifyMeOnResponses: nestedSection.commentData.notifymeonresponses,
                      allowAssigneesViewResponses: nestedSection.commentData.allowassigneesviewresponses,
                      allowCommentResponse: nestedSection.commentData.allowcommentresponse
                    } : {})
                  }
                };
                components.push(nestedSectionComponent);
              } else if (componentStatus === 'Active' && nestedSection.type === 'Assignment' && nestedSection.reference_id) {
                // Active Assignment with assignment reference - show database fields + referenced assignment data
                const nestedSectionComponent = {
                  id: nestedSection.id,
                  sort_index: nestedSection.index,
                  type: nestedSection.type,
                  title: nestedSection.text || null,
                  parent: subsection.id,
                  superparent: courseId,
                  level: 'nested',
                  concentration: nestedSection.primaryConcentration || null,
                  otherconcentrations: nestedSection.otherConcentrations && nestedSection.otherConcentrations.length > 0 ? nestedSection.otherConcentrations : null,
                  taxonomy: nestedSection.taxonomy && nestedSection.taxonomy.length > 0 ? nestedSection.taxonomy : null,
                  hourawards: nestedSection.hourawards && nestedSection.hourawards.length > 0 ? nestedSection.hourawards : null,
                  grade_settings: nestedSection.gradeSettings || null,
                  status: componentStatus,
                  reference_table: 'assignments',
                  reference_id: nestedSection.reference_id,
                  createddate: nestedSection.createddate || null,
                  updateddate: nestedSection.updateddate || null,
                  referenced_item: {
                    id: nestedSection.reference_id,
                    original: nestedSection.assignmentData?.original || null
                  }
                };
                components.push(nestedSectionComponent);
              } else {
                // For other types (Module, Lesson, Assignment) or non-Content/Discussion Board
                const nestedSectionComponent = {
                  id: nestedSection.id,
                  sort_index: nestedSection.index,
                  type: nestedSection.type,
                  title: nestedSection.text || null,
                  parent: subsection.id,
                  superparent: courseId,
                  level: 'nested',
                  concentration: nestedSection.primaryConcentration || null,
                  otherconcentrations: nestedSection.otherConcentrations && nestedSection.otherConcentrations.length > 0 ? nestedSection.otherConcentrations : null,
                  taxonomy: nestedSection.taxonomy && nestedSection.taxonomy.length > 0 ? nestedSection.taxonomy : null,
                  hourawards: nestedSection.hourawards && nestedSection.hourawards.length > 0 ? nestedSection.hourawards : null,
                  grade_settings: nestedSection.gradeSettings || null,
                status: componentStatus,
                reference_table: nestedSection.type === 'Assignment' ? 'assignments' : null,
                  reference_id: nestedSection.type === 'Assignment' && nestedSection.reference_id ? nestedSection.reference_id : null,
                  createddate: nestedSection.createddate || null,
                  updateddate: nestedSection.updateddate || null
                };
                components.push(nestedSectionComponent);
              }
            });
          }
        });
      }
    });
    
    // Course status will be set to Active after successful save to database
    // For now, use the current course status if it exists, otherwise 'Draft'
    if (components.length > 1) { // More than just the course component
      components[0].status = currentCourseStatus || 'Draft';
      
      // Note: Database fields (createddate, updateddate) are already set from currentCourseComponent
      // No need to override them here
    }
    

    
    return {
      components: components
    };
  };

  // Memoize the JSON structure to prevent multiple generations
  const memoizedJsonStructure = useMemo(() => {
    return generateJsonStructure();
  }, [
    sections,
    currentCourseComponent,
    courseTitle,
    currentCourseId,
    courseTrack,
    tk12Subject,
    gradeLevels,
    defaultPrimaryConcentration
  ]);

  // Assignment-related functions
  const fetchUserAssignments = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.get('/api/assignments', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // The API already filters assignments by the current user
      const userAssignments = response.data.assignments || [];
      
      setAllAssignments(userAssignments);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  };

  // Handle taxonomy inheritance from assignments
  const handleTaxonomyInheritance = (assignments) => {

    
    // Collect all unique taxonomy items and concentrations from assignments
    const inheritedTaxonomy = [];
    const inheritedConcentrations = new Set();
    
    assignments.forEach(assignment => {
      // Add assignment's primary concentration
      if (assignment.concentration) {
        inheritedConcentrations.add(assignment.concentration);
      }
      
      // Add assignment's other concentrations
      if (assignment.otherconcentrations && Array.isArray(assignment.otherconcentrations)) {
        assignment.otherconcentrations.forEach(conc => {
          if (conc) inheritedConcentrations.add(conc);
        });
      }
      
      // Add assignment's taxonomy items
      if (assignment.taxonomy && Array.isArray(assignment.taxonomy)) {
        assignment.taxonomy.forEach(taxItem => {
          if (taxItem.taxonomy_id) {
            inheritedTaxonomy.push({
              taxonomy_id: taxItem.taxonomy_id,
              parent_id: taxItem.parent_id || null,
              concentration: taxItem.concentration || assignment.concentration
            });
          }
        });
      }
    });
    
    // Remove duplicates from taxonomy (based on taxonomy_id)
    const uniqueTaxonomy = [];
    const seenTaxonomyIds = new Set();
    inheritedTaxonomy.forEach(taxItem => {
      if (!seenTaxonomyIds.has(taxItem.taxonomy_id)) {
        seenTaxonomyIds.add(taxItem.taxonomy_id);
        uniqueTaxonomy.push(taxItem);
      }
    });
    
    // Convert concentrations Set to array
    const allConcentrations = Array.from(inheritedConcentrations);
    const primaryConcentration = allConcentrations[0] || null;
    const otherConcentrations = allConcentrations.slice(1);
    

    
    // Update the current item with inherited values
    const newSections = [...sections];
    
    if (assignmentModalConfig.sectionIndex !== null && assignmentModalConfig.subsectionIndex === null && assignmentModalConfig.nestedIndex === null) {
      // Section level
      if (!newSections[assignmentModalConfig.sectionIndex].primaryConcentration) newSections[assignmentModalConfig.sectionIndex].primaryConcentration = '';
      if (!newSections[assignmentModalConfig.sectionIndex].otherConcentrations) newSections[assignmentModalConfig.sectionIndex].otherConcentrations = [];
      if (!newSections[assignmentModalConfig.sectionIndex].taxonomy) newSections[assignmentModalConfig.sectionIndex].taxonomy = [];
      
      newSections[assignmentModalConfig.sectionIndex].primaryConcentration = primaryConcentration;
      newSections[assignmentModalConfig.sectionIndex].otherConcentrations = otherConcentrations;
      newSections[assignmentModalConfig.sectionIndex].taxonomy = uniqueTaxonomy;
    } else if (assignmentModalConfig.sectionIndex !== null && assignmentModalConfig.subsectionIndex !== null && assignmentModalConfig.nestedIndex === null) {
      // Subsection level
      if (!newSections[assignmentModalConfig.sectionIndex].subsections[assignmentModalConfig.subsectionIndex].primaryConcentration) newSections[assignmentModalConfig.sectionIndex].subsections[assignmentModalConfig.subsectionIndex].primaryConcentration = '';
      if (!newSections[assignmentModalConfig.sectionIndex].subsections[assignmentModalConfig.subsectionIndex].otherConcentrations) newSections[assignmentModalConfig.sectionIndex].subsections[assignmentModalConfig.subsectionIndex].otherConcentrations = [];
      if (!newSections[assignmentModalConfig.sectionIndex].subsections[assignmentModalConfig.subsectionIndex].taxonomy) newSections[assignmentModalConfig.sectionIndex].subsections[assignmentModalConfig.subsectionIndex].taxonomy = [];
      
      newSections[assignmentModalConfig.sectionIndex].subsections[assignmentModalConfig.subsectionIndex].primaryConcentration = primaryConcentration;
      newSections[assignmentModalConfig.sectionIndex].subsections[assignmentModalConfig.subsectionIndex].otherConcentrations = otherConcentrations;
      newSections[assignmentModalConfig.sectionIndex].subsections[assignmentModalConfig.subsectionIndex].taxonomy = uniqueTaxonomy;
    } else if (assignmentModalConfig.sectionIndex !== null && assignmentModalConfig.subsectionIndex !== null && assignmentModalConfig.nestedIndex !== null) {
      // Nested section level
      if (!newSections[assignmentModalConfig.sectionIndex].subsections[assignmentModalConfig.subsectionIndex].nestedSections[assignmentModalConfig.nestedIndex].primaryConcentration) newSections[assignmentModalConfig.sectionIndex].subsections[assignmentModalConfig.subsectionIndex].nestedSections[assignmentModalConfig.nestedIndex].primaryConcentration = '';
      if (!newSections[assignmentModalConfig.sectionIndex].subsections[assignmentModalConfig.subsectionIndex].nestedSections[assignmentModalConfig.nestedIndex].otherConcentrations) newSections[assignmentModalConfig.sectionIndex].subsections[assignmentModalConfig.subsectionIndex].nestedSections[assignmentModalConfig.nestedIndex].otherConcentrations = [];
      if (!newSections[assignmentModalConfig.sectionIndex].subsections[assignmentModalConfig.subsectionIndex].nestedSections[assignmentModalConfig.nestedIndex].taxonomy) newSections[assignmentModalConfig.sectionIndex].subsections[assignmentModalConfig.subsectionIndex].nestedSections[assignmentModalConfig.nestedIndex].taxonomy = [];
      
      newSections[assignmentModalConfig.sectionIndex].subsections[assignmentModalConfig.subsectionIndex].nestedSections[assignmentModalConfig.nestedIndex].primaryConcentration = primaryConcentration;
      newSections[assignmentModalConfig.sectionIndex].subsections[assignmentModalConfig.subsectionIndex].nestedSections[assignmentModalConfig.nestedIndex].otherConcentrations = otherConcentrations;
      newSections[assignmentModalConfig.sectionIndex].subsections[assignmentModalConfig.subsectionIndex].nestedSections[assignmentModalConfig.nestedIndex].taxonomy = uniqueTaxonomy;
    }
    
    setSections(newSections);
    
    // Ensure all properties are initialized after update
    ensureAllSectionsHaveProperties(newSections);
    
    // Show success message
    setSuccess('Taxonomy and concentrations inherited from assignments!');
    setTimeout(() => setSuccess(''), 3000);
  };

  const openAssignmentModal = (sectionIndex, subsectionIndex = null, nestedIndex = null, itemIndex = null) => {

    
    let currentAssignments = [];
    let updateFunction = null;
    
    if (nestedIndex !== null) {
      // Nested section assignment
      const section = sections[sectionIndex];
      const subsection = section.subsections[subsectionIndex];
      const nestedSection = subsection.nestedSections[nestedIndex];
      currentAssignments = nestedSection.assignments || [];
      updateFunction = (selectedAssignments) => {
        const newSections = [...sections];
        const nestedSection = newSections[sectionIndex].subsections[subsectionIndex].nestedSections[nestedIndex];
        
        // Update assignments array with the actual assignment objects
        // This ensures both the UI and JSON generation use the correct assignment data
        nestedSection.assignments = selectedAssignments;
        
        // For Assignment components, update assignmentData to reflect the newly selected assignment
        if (nestedSection.type === 'Assignment') {
          if (selectedAssignments.length > 0) {
            const selectedAssignment = selectedAssignments[0];
            
            if (nestedSection.status === 'Draft') {
              // For Draft components, don't set reference_id yet - it will be set after creating the copy
              // Just mark that we have a selected assignment to copy from
            nestedSection.reference_table = 'assignments';
              nestedSection.reference_id = null; // Will be set after creating copy during save
            }
            
            // Always update assignmentData for both Draft and Active components
            // This ensures referenced_item.original shows the correct value in the JSON
            // The original field should point to the ID of the assignment being copied from
            const updatedNestedAssignmentData = {
              ...selectedAssignment,
              original: selectedAssignment.id
            };
            nestedSection.assignmentData = updatedNestedAssignmentData;
            
            console.log('🔄 Assignment component - updated assignmentData:', {
              componentId: nestedSection.id,
              status: nestedSection.status,
              referenceId: selectedAssignment.id,
              title: selectedAssignment.title,
              original: selectedAssignment.id,
              assignmentDataOriginal: nestedSection.assignmentData?.original,
              fullAssignmentData: nestedSection.assignmentData
            });
          } else {
            // No assignment selected - clear reference fields for Draft components
            if (nestedSection.status === 'Draft') {
            nestedSection.reference_table = null;
            nestedSection.reference_id = null;
            }
            nestedSection.assignmentData = null;
            console.log('🔄 Assignment component - cleared assignmentData:', {
              componentId: nestedSection.id,
              status: nestedSection.status
            });
          }
        }
        
        setSections(newSections);
        // Ensure all properties are initialized after update
        ensureAllSectionsHaveProperties(newSections);
      };
    } else if (subsectionIndex !== null) {
      // Subsection assignment
      const section = sections[sectionIndex];
      const subsection = section.subsections[subsectionIndex];
      currentAssignments = subsection.assignments || [];
      updateFunction = (selectedAssignments) => {
        const newSections = [...sections];
        const subsection = newSections[sectionIndex].subsections[subsectionIndex];
        
        // Update assignments array with the actual assignment objects
        // This ensures both the UI and JSON generation use the correct assignment data
        subsection.assignments = selectedAssignments;
        
        // For Assignment components, update assignmentData to reflect the newly selected assignment
        if (subsection.type === 'Assignment') {
          if (selectedAssignments.length > 0) {
            const selectedAssignment = selectedAssignments[0];
            
            if (subsection.status === 'Draft') {
              // For Draft components, don't set reference_id yet - it will be set after creating the copy
              // Just mark that we have a selected assignment to copy from
            subsection.reference_table = 'assignments';
              subsection.reference_id = null; // Will be set after creating copy during save
            }
            
            // Always update assignmentData for both Draft and Active components
            // This ensures referenced_item.original shows the correct value in the JSON
            // The original field should point to the ID of the assignment being copied from
            const updatedAssignmentData = {
              ...selectedAssignment,
              original: selectedAssignment.id
            };
            subsection.assignmentData = updatedAssignmentData;
            
            console.log('🔄 Assignment component - updated assignmentData:', {
              componentId: subsection.id,
              status: subsection.status,
              referenceId: selectedAssignment.id,
              title: selectedAssignment.title,
              original: selectedAssignment.id,
              assignmentDataOriginal: subsection.assignmentData?.original,
              fullAssignmentData: subsection.assignmentData
            });
          } else {
            // No assignment selected - clear reference fields for Draft components
            if (subsection.status === 'Draft') {
            subsection.reference_table = null;
            subsection.reference_id = null;
            }
            subsection.assignmentData = null;
            console.log('🔄 Assignment component - cleared assignmentData:', {
              componentId: subsection.id,
              status: subsection.status
            });
          }
        }
        
        setSections(newSections);
        // Ensure all properties are initialized after update
        ensureAllSectionsHaveProperties(newSections);
      };
    } else if (itemIndex !== null) {
      // Lesson item assignment
      const section = sections[sectionIndex];
      const item = section.subsections[itemIndex];
      currentAssignments = item.assignments || [];
      updateFunction = (selectedAssignments) => {
        const newSections = [...sections];
        const item = newSections[sectionIndex].subsections[itemIndex];
        
        // Update assignments array with the actual assignment objects
        // This ensures both the UI and JSON generation use the correct assignment data
        item.assignments = selectedAssignments;
        
        // For Assignment components, update assignmentData to reflect the newly selected assignment
        if (item.type === 'Assignment') {
          if (selectedAssignments.length > 0) {
            const selectedAssignment = selectedAssignments[0];
            
            if (item.status === 'Draft') {
              // For Draft components, don't set reference_id yet - it will be set after creating the copy
              // Just mark that we have a selected assignment to copy from
            item.reference_table = 'assignments';
              item.reference_id = null; // Will be set after creating copy during save
            }
            
            // Always update assignmentData for both Draft and Active components
            // This ensures referenced_item.original shows the correct value in the JSON
            // The original field should point to the ID of the assignment being copied from
            const updatedItemAssignmentData = {
              ...selectedAssignment,
              original: selectedAssignment.id
            };
            item.assignmentData = updatedItemAssignmentData;
            
            console.log('🔄 Assignment component - updated assignmentData:', {
              componentId: item.id,
              status: item.status,
              referenceId: selectedAssignment.id,
              title: selectedAssignment.title,
              original: selectedAssignment.id,
              assignmentDataOriginal: item.assignmentData?.original,
              fullAssignmentData: item.assignmentData
            });
          } else {
            // No assignment selected - clear reference fields for Draft components
            if (item.status === 'Draft') {
            item.reference_table = null;
            item.reference_id = null;
            }
            item.assignmentData = null;
            console.log('🔄 Assignment component - cleared assignmentData:', {
              componentId: item.id,
              status: item.status
            });
          }
        }
        
        setSections(newSections);
        // Ensure all properties are initialized after update
        ensureAllSectionsHaveProperties(newSections);
      };
    } else {
      // Section level assignment
      const section = sections[sectionIndex];
      currentAssignments = section.assignments || [];
      updateFunction = (selectedAssignments) => {
        const newSections = [...sections];
        const section = newSections[sectionIndex];
        
        // Update assignments array with the actual assignment objects
        // This ensures both the UI and JSON generation use the correct assignment data
        section.assignments = selectedAssignments;
        
        // For Assignment components, update assignmentData to reflect the newly selected assignment
        if (section.type === 'Assignment') {
          if (selectedAssignments.length > 0) {
            const selectedAssignment = selectedAssignments[0];
            
            if (section.status === 'Draft') {
              // For Draft components, don't set reference_id yet - it will be set after creating the copy
              // Just mark that we have a selected assignment to copy from
            section.reference_table = 'assignments';
              section.reference_id = null; // Will be set after creating copy during save
            }
            
            // Always update assignmentData for both Draft and Active components
            // This ensures referenced_item.original shows the correct value in the JSON
            // The original field should point to the ID of the assignment being copied from
            const updatedSectionAssignmentData = {
              ...selectedAssignment,
              original: selectedAssignment.id
            };
            section.assignmentData = updatedSectionAssignmentData;
            
            console.log('🔄 Assignment component - updated assignmentData:', {
              componentId: section.id,
              status: section.status,
              referenceId: selectedAssignment.id,
              title: selectedAssignment.title,
              original: selectedAssignment.id,
              assignmentDataOriginal: section.assignmentData?.original,
              fullAssignmentData: section.assignmentData
            });
          } else {
            // No assignment selected - clear reference fields for Draft components
            if (section.status === 'Draft') {
            section.reference_table = null;
            section.reference_id = null;
            }
            section.assignmentData = null;
            console.log('🔄 Assignment component - cleared assignmentData:', {
              componentId: section.id,
              status: section.status
            });
          }
        }
        
        setSections(newSections);
        // Ensure all properties are initialized after update
        ensureAllSectionsHaveProperties(newSections);
      };
    }

    setAssignmentModalConfig({
      isOpen: true,
      sectionIndex,
      subsectionIndex,
      nestedIndex,
      itemIndex,
      selectedAssignments: currentAssignments,
      onSelectionChange: updateFunction
    });
  };

  const closeAssignmentModal = () => {
    setAssignmentModalConfig({
      isOpen: false,
      sectionIndex: null,
      subsectionIndex: null,
      nestedIndex: null,
      itemIndex: null,
      selectedAssignments: [],
      onSelectionChange: null
    });
  };

  // Open taxonomy modal for different types of items
  const openTaxonomyModal = (type, sectionIndex, subsectionIndex = null, nestedIndex = null, itemIndex = null) => {
    let currentTaxonomy = [];
    let primaryConcentration = '';
    let otherConcentrations = [];
    
    // Get current taxonomy and concentration data based on type and indices
    if (type === 'section') {
      currentTaxonomy = sections[sectionIndex]?.taxonomy || [];
      primaryConcentration = sections[sectionIndex]?.primaryConcentration || '';
      otherConcentrations = sections[sectionIndex]?.otherConcentrations || [];
    } else if (type === 'subsection') {
      currentTaxonomy = sections[sectionIndex]?.subsections[subsectionIndex]?.taxonomy || [];
      primaryConcentration = sections[sectionIndex]?.subsections[subsectionIndex]?.primaryConcentration || '';
      otherConcentrations = sections[sectionIndex]?.subsections[subsectionIndex]?.otherConcentrations || [];
    } else if (type === 'nested') {
      currentTaxonomy = sections[sectionIndex]?.subsections[subsectionIndex]?.nestedSections[nestedIndex]?.taxonomy || [];
      primaryConcentration = sections[sectionIndex]?.subsections[subsectionIndex]?.nestedSections[nestedIndex]?.primaryConcentration || '';
      otherConcentrations = sections[sectionIndex]?.subsections[subsectionIndex]?.nestedSections[nestedIndex]?.otherConcentrations || [];
    } else if (type === 'lesson-item') {
      currentTaxonomy = sections[sectionIndex]?.subsections[itemIndex]?.taxonomy || [];
      primaryConcentration = sections[sectionIndex]?.subsections[itemIndex]?.primaryConcentration || '';
      otherConcentrations = sections[sectionIndex]?.subsections[itemIndex]?.otherConcentrations || [];
    }

    // Use default primary concentration if none is set
    if (!primaryConcentration && defaultPrimaryConcentration) {
      primaryConcentration = defaultPrimaryConcentration;
    }
    if (!primaryConcentration && courseTrack === 'TK12' && tk12Subject) {
      primaryConcentration = tk12Subject;
    }

    console.log('🔍 Opening taxonomy modal with data:', {
      type,
      sectionIndex,
      subsectionIndex,
      nestedIndex,
      itemIndex,
      currentTaxonomyLength: currentTaxonomy?.length || 0,
      currentTaxonomy,
      primaryConcentration,
      otherConcentrationsLength: otherConcentrations?.length || 0,
      otherConcentrations
    });

    setTaxonomyModalConfig({
      isOpen: true,
      sectionIndex,
      subsectionIndex,
      nestedIndex,
      itemIndex,
      type,
      currentTaxonomy,
      primaryConcentration,
      otherConcentrations,
      onTaxonomyChange: (newTaxonomy, newPrimaryConcentration, newOtherConcentrations) => {
        // Update the appropriate item with new taxonomy data
        // Use the captured values from when the modal was opened to avoid closure issues
        const targetType = type;
        const targetSectionIndex = sectionIndex;
        const targetSubsectionIndex = subsectionIndex;
        const targetNestedIndex = nestedIndex;
        const targetItemIndex = itemIndex;
        
        console.log('🔍 Taxonomy change callback triggered:', {
          targetType,
          targetSectionIndex,
          targetSubsectionIndex,
          targetNestedIndex,
          targetItemIndex,
          newTaxonomyLength: newTaxonomy?.length || 0,
          newPrimaryConcentration,
          newOtherConcentrationsLength: newOtherConcentrations?.length || 0
        });
        
        const newSections = [...sections];
        
        // Ensure the target item has the required properties before setting them
        if (targetType === 'section') {
          if (!newSections[targetSectionIndex].taxonomy) newSections[targetSectionIndex].taxonomy = [];
          if (!newSections[targetSectionIndex].primaryConcentration) newSections[targetSectionIndex].primaryConcentration = '';
          if (!newSections[targetSectionIndex].otherConcentrations) newSections[targetSectionIndex].otherConcentrations = [];
          
          newSections[targetSectionIndex].taxonomy = newTaxonomy;
          newSections[targetSectionIndex].primaryConcentration = newPrimaryConcentration;
          newSections[targetSectionIndex].otherConcentrations = newOtherConcentrations;
          // Changes detected through deep comparison
        } else if (targetType === 'subsection') {
          if (!newSections[targetSectionIndex].subsections[targetSubsectionIndex].taxonomy) newSections[targetSectionIndex].subsections[targetSubsectionIndex].taxonomy = [];
          if (!newSections[targetSectionIndex].subsections[targetSubsectionIndex].primaryConcentration) newSections[targetSectionIndex].subsections[targetSubsectionIndex].primaryConcentration = '';
          if (!newSections[targetSectionIndex].subsections[targetSubsectionIndex].otherConcentrations) newSections[targetSectionIndex].subsections[targetSubsectionIndex].otherConcentrations = [];
          
          newSections[targetSectionIndex].subsections[targetSubsectionIndex].taxonomy = newTaxonomy;
          newSections[targetSectionIndex].subsections[targetSubsectionIndex].primaryConcentration = newPrimaryConcentration;
          newSections[targetSectionIndex].subsections[targetSubsectionIndex].otherConcentrations = newOtherConcentrations;
          // Changes detected through deep comparison
        } else if (targetType === 'nested') {
          if (!newSections[targetSectionIndex].subsections[targetSubsectionIndex].nestedSections[targetNestedIndex].taxonomy) newSections[targetSectionIndex].subsections[targetSubsectionIndex].nestedSections[targetNestedIndex].taxonomy = [];
          if (!newSections[targetSectionIndex].subsections[targetSubsectionIndex].nestedSections[targetNestedIndex].primaryConcentration) newSections[targetSectionIndex].subsections[targetSubsectionIndex].nestedSections[targetNestedIndex].primaryConcentration = '';
          if (!newSections[targetSectionIndex].subsections[targetSubsectionIndex].nestedSections[targetNestedIndex].otherConcentrations) newSections[targetSectionIndex].subsections[targetSubsectionIndex].nestedSections[targetNestedIndex].otherConcentrations = [];
          
          newSections[targetSectionIndex].subsections[targetSubsectionIndex].nestedSections[targetNestedIndex].taxonomy = newTaxonomy;
          newSections[targetSectionIndex].subsections[targetSubsectionIndex].nestedSections[targetNestedIndex].primaryConcentration = newPrimaryConcentration;
          newSections[targetSectionIndex].subsections[targetSubsectionIndex].nestedSections[targetNestedIndex].otherConcentrations = newOtherConcentrations;
          // Changes detected through deep comparison
        } else if (targetType === 'lesson-item') {
          if (!newSections[targetSectionIndex].subsections[targetItemIndex].taxonomy) newSections[targetSectionIndex].subsections[targetItemIndex].taxonomy = [];
          if (!newSections[targetSectionIndex].subsections[targetItemIndex].primaryConcentration) newSections[targetSectionIndex].subsections[targetItemIndex].primaryConcentration = '';
          if (!newSections[targetSectionIndex].subsections[targetItemIndex].otherConcentrations) newSections[targetSectionIndex].subsections[targetItemIndex].otherConcentrations = [];
          
          newSections[targetSectionIndex].subsections[targetItemIndex].taxonomy = newTaxonomy;
          newSections[targetSectionIndex].subsections[targetItemIndex].primaryConcentration = newPrimaryConcentration;
          newSections[targetSectionIndex].subsections[targetItemIndex].otherConcentrations = newOtherConcentrations;
          // Changes detected through deep comparison
        }
        
        console.log('🔍 Updated sections with taxonomy data for:', {
          type: targetType,
          sectionIndex: targetSectionIndex,
          subsectionIndex: targetSubsectionIndex,
          nestedIndex: targetNestedIndex,
          itemIndex: targetItemIndex
        });
        
        setSections(newSections);
        
        // Ensure all properties are initialized after update
        ensureAllSectionsHaveProperties(newSections);
      }
    });
  };

  // Close taxonomy modal
  const closeTaxonomyModal = () => {
    setTaxonomyModalConfig({
      isOpen: false,
      sectionIndex: null,
      subsectionIndex: null,
      nestedIndex: null,
      itemIndex: null,
      type: null,
      currentTaxonomy: [],
      primaryConcentration: '',
      otherConcentrations: [],
      onTaxonomyChange: null
    });
  };

  // Open hour awards modal for different types of items
  const openHourAwardsModal = (type, sectionIndex, subsectionIndex = null, nestedIndex = null, itemIndex = null) => {
    // Validate that sections array exists and sectionIndex is valid
    if (!sections || !Array.isArray(sections) || sections.length === 0) {
      console.error('Sections array is empty or undefined:', { sections, sectionsLength: sections?.length });
      return;
    }
    
    if (sectionIndex < 0 || sectionIndex >= sections.length) {
      console.error('Invalid section index:', { sectionIndex, sectionsLength: sections.length });
      return;
    }
    
    if (!sections[sectionIndex]) {
      console.error('Section at index is undefined:', { sectionIndex, section: sections[sectionIndex] });
      return;
    }
    
    // Debug logging

    
    // Additional debugging for lesson items
    if (type === 'lesson-item') {

    }
    
    // Ensure the target section has required properties
    const ensureSectionProperties = (section) => {
      if (!section.hourawards) section.hourawards = [];
      if (!section.gradeSettings) section.gradeSettings = null;
      if (!section.type) section.type = 'Content';
    };
    
    // Ensure the target subsection has required properties
    const ensureSubsectionProperties = (subsection) => {
      if (!subsection.hourawards) subsection.hourawards = [];
      if (!subsection.gradeSettings) subsection.gradeSettings = null;
      if (!subsection.type) subsection.type = 'Content';
    };
    
    // Ensure the target nested section has required properties
    const ensureNestedSectionProperties = (nestedSection) => {
      if (!nestedSection.hourawards) nestedSection.hourawards = [];
      if (!nestedSection.gradeSettings) nestedSection.gradeSettings = null;
      if (!nestedSection.type) nestedSection.type = 'Content';
    };
    
    let currentHourAwards = [];
    let currentGradeSettings = null;
    let componentType = null;
    
    // Get current hour awards data based on type and indices
    if (type === 'section') {
      if (sections[sectionIndex]) {
        ensureSectionProperties(sections[sectionIndex]);
        currentHourAwards = sections[sectionIndex].hourawards;
        currentGradeSettings = sections[sectionIndex].gradeSettings;
        componentType = sections[sectionIndex].type;
      }
    } else if (type === 'subsection') {
      if (sections[sectionIndex]?.subsections && subsectionIndex >= 0 && subsectionIndex < sections[sectionIndex].subsections.length) {
        const subsection = sections[sectionIndex].subsections[subsectionIndex];
        ensureSubsectionProperties(subsection);
        currentHourAwards = subsection.hourawards;
        currentGradeSettings = subsection.gradeSettings;
        componentType = subsection.type;
      }
    } else if (type === 'nested') {
      if (sections[sectionIndex]?.subsections?.[subsectionIndex]?.nestedSections && 
          nestedIndex >= 0 && nestedIndex < sections[sectionIndex].subsections[subsectionIndex].nestedSections.length) {
        const nestedSection = sections[sectionIndex].subsections[subsectionIndex].nestedSections[nestedIndex];
        ensureNestedSectionProperties(nestedSection);
        currentHourAwards = nestedSection.hourawards;
        currentGradeSettings = nestedSection.gradeSettings;
        componentType = nestedSection.type;
      }
    } else if (type === 'lesson-item') {
      // For lesson items, we need to check if they exist within the specified subsection
      if (sections[sectionIndex]?.subsections && 
          subsectionIndex >= 0 && subsectionIndex < sections[sectionIndex].subsections.length) {
        
        const subsection = sections[sectionIndex].subsections[subsectionIndex];
        
        // If itemIndex is null or undefined, treat the subsection itself as the lesson item
        if (!itemIndex || itemIndex === null) {
          ensureSubsectionProperties(subsection);
          currentHourAwards = subsection.hourawards;
          currentGradeSettings = subsection.gradeSettings;
          componentType = subsection.type;
        } else {
          // Check if the subsection has lesson items (assignments, content, discussion boards)
          if (subsection.lessonItems && itemIndex >= 0 && itemIndex < subsection.lessonItems.length) {
            const lessonItem = subsection.lessonItems[itemIndex];
            ensureSubsectionProperties(lessonItem);
            currentHourAwards = lessonItem.hourawards;
            currentGradeSettings = lessonItem.gradeSettings;
            componentType = lessonItem.type;
          } else if (subsection.assignments && itemIndex >= 0 && itemIndex < subsection.assignments.length) {
            // Fallback: check if it's in assignments array
            const lessonItem = subsection.assignments[itemIndex];
            ensureSubsectionProperties(lessonItem);
            currentHourAwards = lessonItem.hourawards;
            currentGradeSettings = lessonItem.gradeSettings;
            componentType = lessonItem.type;
          } else {
            // If no lesson items array, treat the subsection itself as the lesson item
            ensureSubsectionProperties(subsection);
            currentHourAwards = subsection.hourawards;
            currentGradeSettings = subsection.gradeSettings;
            componentType = subsection.type;
          }
        }
      }
    }

    // If no hour awards exist, start with 1 default item (Theory)
    if (currentHourAwards.length === 0) {
      currentHourAwards = [{ hours: 0, type: 'Theory' }];
    }

    // If no grade settings exist, get default based on component type
    if (!currentGradeSettings) {
      currentGradeSettings = getDefaultGradeSettings(componentType);
    }

    // Validate that we have all required data
    if (!currentHourAwards || !Array.isArray(currentHourAwards) || componentType === null) {

      return;
    }

    // Only set the modal configuration if we have valid data
    if (componentType !== null) {
    setHourAwardsModalConfig({
      isOpen: true,
      sectionIndex,
      subsectionIndex,
      nestedIndex,
      itemIndex,
      type: componentType, // Use the actual component type for grade settings
      currentHourAwards: [...currentHourAwards],
      currentGradeSettings: currentGradeSettings,
      onHourAwardsChange: (newHourAwards) => {
          try {
        // Update the appropriate item with new hour awards data
        const newSections = [...sections];
            const modalType = type; // Capture the type value
            const modalSectionIndex = sectionIndex; // Capture the sectionIndex value
            const modalSubsectionIndex = subsectionIndex; // Capture the subsectionIndex value
            const modalNestedIndex = nestedIndex; // Capture the nestedIndex value
            const modalItemIndex = itemIndex; // Capture the itemIndex value
            
            // Validate that we have valid data
            if (!newHourAwards || !Array.isArray(newHourAwards)) {
              console.error('Invalid hour awards data:', newHourAwards);
              return;
            }
            
            if (modalType === 'section') {
              if (newSections[modalSectionIndex]) {
                newSections[modalSectionIndex].hourawards = newHourAwards;
                // Changes detected through deep comparison
              } else {
                console.error('Section not found at index:', modalSectionIndex);
              }
            } else if (modalType === 'subsection') {
              if (newSections[modalSectionIndex]?.subsections?.[modalSubsectionIndex]) {
                newSections[modalSectionIndex].subsections[modalSubsectionIndex].hourawards = newHourAwards;
                // Changes detected through deep comparison
              } else {
                console.error('Subsection not found at indices:', modalSectionIndex, modalSubsectionIndex);
              }
            } else if (modalType === 'nested') {
              if (newSections[modalSectionIndex]?.subsections?.[modalSubsectionIndex]?.nestedSections?.[modalNestedIndex]) {
                newSections[modalSectionIndex].subsections[modalSubsectionIndex].nestedSections[modalNestedIndex].hourawards = newHourAwards;
                // Changes detected through deep comparison
              } else {
                console.error('Nested section not found at indices:', modalSectionIndex, modalSubsectionIndex, modalNestedIndex);
              }
            } else if (modalType === 'lesson-item') {
              // For lesson items, we need to update the correct location based on the structure
              if (newSections[modalSectionIndex]?.subsections?.[modalSubsectionIndex]) {
                const subsection = newSections[modalSectionIndex].subsections[modalSubsectionIndex];
                
                // If modalItemIndex is null, update the subsection itself
                if (!modalItemIndex || modalItemIndex === null) {
                  subsection.hourawards = newHourAwards;
                } else {
                  // Check if the subsection has lesson items array
                  if (subsection.lessonItems && modalItemIndex >= 0 && modalItemIndex < subsection.lessonItems.length) {
                    subsection.lessonItems[modalItemIndex].hourawards = newHourAwards;
                  } else if (subsection.assignments && modalItemIndex >= 0 && modalItemIndex < subsection.assignments.length) {
                    // Fallback: check if it's in assignments array
                    subsection.assignments[modalItemIndex].hourawards = newHourAwards;
                  } else {
                    // If no lesson items array, update the subsection itself
                    subsection.hourawards = newHourAwards;
                  }
                }
              } else {
                console.error('Subsection not found at indices:', modalSectionIndex, modalSubsectionIndex);
              }
            }
            
        setSections(newSections);
          } catch (error) {
            console.error('Error in onHourAwardsChange callback:', error);
          }
      }
    });
    } else {
      console.error('Could not determine component type for hour awards modal');
    }
  };

  // Helper function to save hour awards and grade settings to sections state
  const saveHourAwardsAndGradeSettings = (hourAwards, gradeSettings) => {
    const newSections = [...sections];
    const { sectionIndex, subsectionIndex, nestedIndex, itemIndex } = hourAwardsModalConfig;
    
    // Add safety checks to ensure the objects exist before trying to set properties
    if (sectionIndex !== null && subsectionIndex === null && nestedIndex === null) {
      // Section level
      if (newSections[sectionIndex]) {
        newSections[sectionIndex].hourawards = hourAwards;
        newSections[sectionIndex].gradeSettings = gradeSettings;
        // Changes detected through deep comparison
      }
    } else if (sectionIndex !== null && subsectionIndex !== null && nestedIndex === null) {
      // Subsection level
      if (newSections[sectionIndex]?.subsections?.[subsectionIndex]) {
        newSections[sectionIndex].subsections[subsectionIndex].hourawards = hourAwards;
        newSections[sectionIndex].subsections[subsectionIndex].gradeSettings = gradeSettings;
        // Changes detected through deep comparison
      }
    } else if (sectionIndex !== null && subsectionIndex !== null && nestedIndex !== null) {
      // Nested section level
      if (newSections[sectionIndex]?.subsections?.[subsectionIndex]?.nestedSections?.[nestedIndex]) {
        newSections[sectionIndex].subsections[subsectionIndex].nestedSections[nestedIndex].hourawards = hourAwards;
        newSections[sectionIndex].subsections[subsectionIndex].nestedSections[nestedIndex].gradeSettings = gradeSettings;
        // Changes detected through deep comparison
      }
    } else if (sectionIndex !== null && subsectionIndex !== null) {
      // Lesson item level - need both sectionIndex and subsectionIndex
      if (newSections[sectionIndex]?.subsections?.[subsectionIndex]) {
        const subsection = newSections[sectionIndex].subsections[subsectionIndex];
        
        // If itemIndex is null, update the subsection itself
        if (!itemIndex || itemIndex === null) {
          subsection.hourawards = hourAwards;
          subsection.gradeSettings = gradeSettings;
        } else {
          // Check if the subsection has lesson items array
          if (subsection.lessonItems && itemIndex >= 0 && itemIndex < subsection.lessonItems.length) {
            subsection.lessonItems[itemIndex].hourawards = hourAwards;
            subsection.lessonItems[itemIndex].gradeSettings = gradeSettings;
          } else if (subsection.assignments && itemIndex >= 0 && itemIndex < subsection.assignments.length) {
            // Fallback: check if it's in assignments array
            subsection.assignments[itemIndex].hourawards = hourAwards;
            subsection.assignments[itemIndex].gradeSettings = gradeSettings;
          } else {
            // If no lesson items array, update the subsection itself
            subsection.hourawards = hourAwards;
            subsection.gradeSettings = gradeSettings;
          }
        }
      }
    }
    setSections(newSections);
  };

  // Close hour awards modal
  const closeHourAwardsModal = () => {
    // Save the current data before closing
    if (hourAwardsModalConfig.isOpen && hourAwardsModalConfig.onHourAwardsChange && typeof hourAwardsModalConfig.onHourAwardsChange === 'function') {
      try {
      // Save hour awards
      hourAwardsModalConfig.onHourAwardsChange(hourAwardsModalConfig.currentHourAwards);
      
      // Save grade settings
      saveHourAwardsAndGradeSettings(hourAwardsModalConfig.currentHourAwards, hourAwardsModalConfig.currentGradeSettings);
      } catch (error) {
        console.error('Error saving hour awards:', error);
        // Continue with closing the modal even if saving fails
      }
    }
    
    setHourAwardsModalConfig({
      isOpen: false,
      sectionIndex: null,
      subsectionIndex: null,
      nestedIndex: null,
      itemIndex: null,
      type: null,
      currentHourAwards: [],
      currentGradeSettings: null,
      onHourAwardsChange: null
    });
  };

  const openAssignmentPreview = (assignmentId) => {
    window.open(`/assignment-builder?id=${assignmentId}&command=edit`, '_blank');
  };

  // Show critical error overlay if concentrations failed to load
  // Save course function - now saves individual components to database
  const handleSaveCourse = async () => {
    if (isSaving) return; // Prevent multiple simultaneous saves
    if (!courseTitle.trim()) {
      setError('Course title is required before saving.');
      return;
    }
    
    try {
      setError(''); // Clear any previous error
      setActionOverlay(true);
      setIsSaving(true);
      setSuccess('Saving course components...');
      
      // Get authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      // Get or create the course component
      // Always use courseComponentState if it exists, as it contains the latest UI changes
      let courseComponent = courseComponentState || currentCourseComponent;
      
      console.log(`🔍 Course component check:`, {
        found: !!courseComponent,
        status: courseComponent?.status,
        hasCreatedDate: !!courseComponent?.createddate,
        hasUpdatedDate: !!courseComponent?.updateddate,
        currentCourseId: currentCourseId
      });
      
      // If no Course component exists, create one from the current course data
      if (!courseComponent) {
        courseComponent = {
          id: uuidv4(),
          type: 'Course',
          title: courseTitle || 'Untitled Course',
          level: 'top',
          sort_index: 0,
          concentration: defaultPrimaryConcentration || null,
          otherconcentrations: [],
          taxonomy: [],
          hourawards: [],
          grade_settings: null,
          course_track: courseTrack === 'TK12' ? 'TK12' : 'CTE',
          subject: courseTrack === 'TK12' ? (tk12Subject || null) : null,
          grade_levels: courseTrack === 'TK12' ? gradeLevels : [],
          status: 'Draft',
          parent: null,
          original: null,
          draft: null,
          hasChanges: false // New courses start with no changes
        };
        
        // Store Course component in separate state for tracking changes
        setCourseComponentState(courseComponent);
        console.log(`🆕 Created new Course component with hasChanges: false`);
      }else{
        // Query the database to get the most recent version of the course component
        // to ensure modlock status is current before proceeding
        if (courseComponent.id && courseComponent.id !== 'temp') {
          try {
            console.log('🔍 Checking most recent course component version for modlock status...');
            const response = await axios.get(`/api/components/${courseComponent.id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            const latestCourseComponent = response.data.component;
            
            // Check if the latest version has modlock enabled
            if (latestCourseComponent.status === 'Active' && latestCourseComponent.modlock === true) {
              // Hide the saving message and show critical error popup
              setSuccess('');
              setCriticalError({ message: 'Modifications are locked.', showReload: false });
              setIsSaving(false);
              setActionOverlay(false);
              return;
            }
            
            // Version check for top-level course component
            if (latestCourseComponent.updateddate && courseComponent.updateddate) {
              const latestUpdatedDate = new Date(latestCourseComponent.updateddate);
              const currentUpdatedDate = new Date(courseComponent.updateddate);
              
              if (latestUpdatedDate > currentUpdatedDate) {
                console.log('⚠️ Version conflict detected for course component:', {
                  currentVersion: courseComponent.updateddate,
                  latestVersion: latestCourseComponent.updateddate,
                  currentTitle: courseComponent.title,
                  latestTitle: latestCourseComponent.title
                });
                
                // Show version conflict error
                setSuccess('');
                setCriticalError({ 
                  message: `Version conflict detected. The course "${latestCourseComponent.title}" has been modified by another user. Please reload the page to get the latest version.`, 
                  showReload: true 
                });
                setIsSaving(false);
                setActionOverlay(false);
                return;
              }
            }
            
            // Update the local courseComponent with the latest data
            courseComponent = { ...courseComponent, ...latestCourseComponent };
            console.log('✅ Course component updated with latest database version');
            
          } catch (error) {
            console.error('Error fetching latest course component version:', error);
            // If we can't fetch the latest version, proceed with caution
            // but still check the local modlock status
            if (courseComponent.status === 'Active' && courseComponent.modlock === true) {
              // Hide the saving message and show critical error popup
              setSuccess('');
              setCriticalError({ message: 'Modifications are locked.', showReload: false });
              setIsSaving(false);
              setActionOverlay(false);
              return;
            }
          }
        } else {
          // For new/temporary components, check local modlock status
          if (courseComponent.status === 'Active' && courseComponent.modlock === true) {
            // Hide the saving message and show critical error popup
            setSuccess('');
            setCriticalError({ message: 'Modifications are locked.', showReload: false });
            setIsSaving(false);
              setActionOverlay(false);
            return;
          }
        }
      }
      
      // Check if the course component actually has changes that need saving
      // Ensure courseComponent has the latest UI values
      if (courseComponentState) {
        courseComponent = { ...courseComponent, ...courseComponentState };
      }

      // Top-level Course row: apply latest title + CTE/TK–12 fields from UI (source of truth)
      const effectiveTrack = courseTrack === 'TK12' ? 'TK12' : 'CTE';
      courseComponent = {
        ...courseComponent,
        title: courseTitle.trim() || courseComponent.title || 'Untitled Course',
        course_track: effectiveTrack,
        concentration:
          effectiveTrack === 'CTE'
            ? defaultPrimaryConcentration || courseComponent.concentration || null
            : null,
        otherconcentrations: effectiveTrack === 'CTE' ? courseComponent.otherconcentrations || [] : [],
        subject: effectiveTrack === 'TK12' ? (tk12Subject || null) : null,
        grade_levels: effectiveTrack === 'TK12' ? gradeLevels : []
      };
      
      console.log('🔍 Course component change detection:', {
        courseComponentTitle: courseComponent?.title,
        courseComponentConcentration: courseComponent?.concentration,
        originalTitle: originalComponentsState?.course?.title,
        originalConcentration: originalComponentsState?.course?.concentration,
        courseComponentStateTitle: courseComponentState?.title,
        courseComponentStateConcentration: courseComponentState?.concentration
      });
      
      const courseHasChanges = hasComponentChanged(courseComponent, originalComponentsState?.course);
      
      // Course component operations will be handled in the transaction
      // No operations should happen outside the transaction to ensure atomicity
      let courseId = currentCourseId;
      
      // Get all non-Course components from sections state
      const otherComponents = [];
      
      // Collect all sections, subsections, and nested sections
      sections.forEach(section => {
        if (section.type !== 'Course') {
          // Add level property for database operations
          section.level = 'section';
          otherComponents.push(section);
          
          if (section.subsections) {
            section.subsections.forEach(subsection => {
              // Add level property for database operations
              subsection.level = 'subsection';
              otherComponents.push(subsection);
              
              if (subsection.nestedSections) {
                subsection.nestedSections.forEach(nestedSection => {
                  // Add level property for database operations
                  nestedSection.level = 'nested';
                  otherComponents.push(nestedSection);
                });
              }
            });
          }
        }
      });
      
      // Enhanced filtering using detailed change analysis
      const componentsToSave = [];
      const componentsNeedingReferencedItemUpdates = [];
      
      otherComponents.forEach((component) => {
        // If no original state exists, consider all components as having changes
        if (!originalComponentsState?.sections) {
          componentsToSave.push(component);
          return;
        }
        
        // Find the original component for comparison by searching through the structure
        let originalComponent = null;
        
        // Search through sections
        for (const originalSection of originalComponentsState.sections) {
          if (component.level === 'section') {
            if (originalSection.id === component.id) {
              originalComponent = originalSection;
              break;
            }
          } else if (component.level === 'subsection' && originalSection.subsections) {
            // Search subsections
            for (const originalSubsection of originalSection.subsections) {
              if (originalSubsection.id === component.id) {
                originalComponent = originalSubsection;
                break;
              }
            }
            if (originalComponent) break;
          }
        }
        
        // If still not found, search for nested sections
        if (!originalComponent && component.level === 'nested') {
          for (const originalSection of originalComponentsState.sections) {
            if (originalSection.subsections) {
              for (const originalSubsection of originalSection.subsections) {
                if (originalSubsection.nestedSections) {
                  for (const originalNested of originalSubsection.nestedSections) {
                    if (originalNested.id === component.id) {
                      originalComponent = originalNested;
                      break;
                    }
                  }
                  if (originalComponent) break;
                }
              }
              if (originalComponent) break;
            }
          }
        }
        
        // If component not found in original state, it's new and should be saved
        if (!originalComponent) {
          console.log(`🆕 New component detected: ${component.id} (${component.type}) - will be saved`);
          componentsToSave.push(component);
          return;
        }
        
        // Special handling for Draft components - they need to be saved even if no changes detected
        // because they need to be inserted into the components table
        if (component.status === 'Draft') {
          if (component.type === 'Assignment') {
            console.log(`🆕 Draft Assignment component detected: ${component.id} - will be saved to create component record`, {
              hasSelectedAssignment: component.assignments && component.assignments.length > 0,
              selectedAssignmentId: component.assignments?.[0]?.id || null
            });
          } else if (component.type === 'Content' || component.type === 'Discussion Board') {
            console.log(`🆕 Draft ${component.type} component detected: ${component.id} - will be saved to create component record`);
          }
          componentsToSave.push(component);
          return;
        }
        
        // Use detailed change analysis to determine what needs updating
        const changeAnalysis = analyzeComponentChanges(component, originalComponent);
        
        if (changeAnalysis.hasChanges) {
          console.log(`🔄 Component ${component.id} (${component.type}) changes:`, changeAnalysis.changes);
          
          // If component properties changed, add to components to save
          if (changeAnalysis.componentChanged) {
            componentsToSave.push(component);
          }
          
          // If referenced item properties changed, add to referenced item updates
          if (changeAnalysis.referencedItemChanged) {
            componentsNeedingReferencedItemUpdates.push({
              component,
              changeAnalysis
            });
          }
        }
      });
      
      console.log(`🔍 Enhanced save analysis:`, {
        totalComponents: otherComponents.length,
        componentsWithChanges: componentsToSave.length,
        referencedItemUpdates: componentsNeedingReferencedItemUpdates.length,
        unchangedComponents: otherComponents.length - componentsToSave.length - componentsNeedingReferencedItemUpdates.length,
        originalStateExists: !!originalComponentsState?.sections,
        originalSectionsCount: originalComponentsState?.sections?.length || 0
      });
      
      // Debug: Log which components are being saved and why
      if (componentsToSave.length > 0) {
        console.log(`📝 Components to save:`, componentsToSave.map(comp => ({
          id: comp.id,
          type: comp.type,
          level: comp.level,
          text: comp.text,
          title: comp.title
        })));
      }
      
      if (componentsNeedingReferencedItemUpdates.length > 0) {
        console.log(`📝 Referenced items to update:`, componentsNeedingReferencedItemUpdates.map(item => ({
          componentId: item.component.id,
          componentType: item.component.type,
          changes: item.changeAnalysis.changes
        })));
      }

      // Client-side guard: block comment reference updates unless user has shared-edit capability.
      // This is UX protection only; server remains source of truth.
      if (!canEditSharedCourseContent) {
        const hasCommentReferenceUpdates = componentsNeedingReferencedItemUpdates.some(
          (item) =>
            item.component.reference_id &&
            item.component.reference_table === 'comments'
        );
        if (hasCommentReferenceUpdates) {
          setSuccess('');
          setError('You do not have permission to update discussion/content comment data. Remove those changes or ask an admin to grant EditSharedCourseContent.');
          setTimeout(() => setError(''), 6000);
          return;
        }
      }
      
      // Check if we have any changes at all (course + components + referenced items + deletions)
      const hasAnyChanges = courseHasChanges || componentsToSave.length > 0 || componentsNeedingReferencedItemUpdates.length > 0 || deletedComponents.length > 0;
      
      if (!hasAnyChanges) {
        // No changes at all - nothing was saved
        setSuccess('No changes detected. Course is already up to date.');
        setTimeout(() => setSuccess(''), 3000);
        return;
      }
      
      // If we reach here, we have changes to save
      // If only course component has changes, update its original state now
      if (courseHasChanges && componentsToSave.length === 0 && componentsNeedingReferencedItemUpdates.length === 0) {
        console.log(`🔄 Only course component had changes, updating original state`);
        if (originalComponentsState) {
          setOriginalComponentsState(prev => ({
            ...prev,
            course: { ...courseComponent }
          }));
        }
      }
      
      // Create comments for Content and Discussion Board components FIRST
      setSuccess('Creating comments for content components...');
      try {
        await createCommentsForComponents(componentsToSave);
      } catch (commentError) {
        console.error('❌ Error creating comments:', commentError);
        // Clear the success message and show error
        setSuccess('');
        setError('Course saved but some comments could not be created. Please try again.');
        setTimeout(() => setError(''), 5000);
      }

      // Create assignments for Draft Assignment components
      setSuccess('Creating assignments for assignment components...');
      try {
        await createAssignmentsForComponents(componentsToSave);
      } catch (assignmentError) {
        console.error('❌ Error creating assignments:', assignmentError);
        // Clear the success message and show error
        setSuccess('');
        setError('Course saved but some assignments could not be created. Please try again.');
        setTimeout(() => setError(''), 5000);
      }

      // Note: Draft Assignment components are now handled by createAssignmentsForComponents function
      // Active Assignment components are handled through the transaction system for updates
      
      // TRANSACTION-BASED SAVE: All operations must succeed or all must be rolled back
      setSuccess('Preparing transaction for atomic save operations...');
      
      // Prepare all operations that need to be executed
      const transactionOperations = {
        courseUpdate: courseHasChanges ? courseComponent : null,
        componentUpdates: componentsToSave,
        referencedItemUpdates: componentsNeedingReferencedItemUpdates,
        operations: []
      };
      
      // VERSION CHECK: Verify all items haven't been modified by another user/session
      if (componentsToSave.length > 0 || componentsNeedingReferencedItemUpdates.length > 0) {
        setSuccess('Checking item versions for conflicts...');
        
        try {
          // Collect all component IDs that need version checking
          const componentIdsToCheck = componentsToSave
            .filter(comp => comp.createddate && comp.updateddate) // Only check existing components
            .map(comp => comp.id);
          
          console.log(`🔍 Version check: ${componentIdsToCheck.length} components, ${componentsNeedingReferencedItemUpdates.length} referenced items`);
          
          // Initialize version conflict tracking
          let hasVersionConflict = false;
          let conflictDetails = [];
          
          // Query component versions individually (since batch endpoint doesn't exist)
          let componentVersions = {};
          if (componentIdsToCheck.length > 0) {
            console.log(`🔍 Checking versions for ${componentIdsToCheck.length} components...`);
            for (const componentId of componentIdsToCheck) {
              try {
                const componentResponse = await axios.get(`/api/components/${componentId}`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                
                if (componentResponse.data.component) {
                  componentVersions[componentId] = componentResponse.data.component.updateddate;
                }
              } catch (error) {
                console.warn(`⚠️ Could not fetch version for component ${componentId}:`, error.message);
                // If we can't fetch a component, assume it might have been deleted - treat as conflict
                hasVersionConflict = true;
                conflictDetails.push(`Component ${componentId} could not be found (may have been deleted)`);
              }
            }
          }
          
          // Query referenced item versions individually (comments and assignments)
          let commentVersions = {};
          let assignmentVersions = {};
          
          // Collect comment IDs and assignment IDs separately
          const commentIdsToCheck = componentsNeedingReferencedItemUpdates
            .filter(item => item.component.reference_table === 'comments')
            .map(item => item.component.reference_id);
          
          const assignmentIdsToCheck = componentsNeedingReferencedItemUpdates
            .filter(item => item.component.reference_table === 'assignments')
            .map(item => item.component.reference_id);
          
          if (commentIdsToCheck.length > 0) {
            console.log(`🔍 Checking versions for ${commentIdsToCheck.length} comments...`);
            for (const commentId of commentIdsToCheck) {
              try {
                // Use the existing comments endpoint with reference_id and table
                const commentResponse = await axios.get(`/api/comments?reference_id=${commentId}&table=comments`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                
                if (commentResponse.data.comments && commentResponse.data.comments.length > 0) {
                  commentVersions[commentId] = commentResponse.data.comments[0].updateddate;
                }
              } catch (error) {
                console.warn(`⚠️ Could not fetch version for comment ${commentId}:`, error.message);
                // If we can't fetch a comment, assume it might have been deleted - treat as conflict
                hasVersionConflict = true;
                conflictDetails.push(`Comment ${commentId} could not be found (may have been deleted)`);
              }
            }
          }
          
          if (assignmentIdsToCheck.length > 0) {
            console.log(`🔍 Checking versions for ${assignmentIdsToCheck.length} assignments...`);
            for (const assignmentId of assignmentIdsToCheck) {
              try {
                // Use the assignments endpoint
                const assignmentResponse = await axios.get(`/api/assignments/${assignmentId}`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                
                if (assignmentResponse.data.assignment) {
                  assignmentVersions[assignmentId] = assignmentResponse.data.assignment.updateddate;
                }
              } catch (error) {
                console.warn(`⚠️ Could not fetch version for assignment ${assignmentId}:`, error.message);
                // If we can't fetch an assignment, assume it might have been deleted - treat as conflict
                hasVersionConflict = true;
                conflictDetails.push(`Assignment ${assignmentId} could not be found (may have been deleted)`);
              }
            }
          }
          
          // Check component versions
          for (const component of componentsToSave) {
            if (component.createddate && component.updateddate) {
              const currentVersion = componentVersions[component.id];
              if (currentVersion && currentVersion !== component.updateddate) {
                hasVersionConflict = true;
                conflictDetails.push(`Component "${component.title || component.type}" (${component.id}) was modified by another user`);
                console.log(`❌ Version conflict detected for component ${component.id}:`, {
                  localVersion: component.updateddate,
                  currentVersion: currentVersion
                });
              }
            }
          }
          
          // Check referenced item versions (comments and assignments)
          for (const item of componentsNeedingReferencedItemUpdates) {
            const { component } = item;
            if (component.reference_id && component.reference_table === 'comments') {
              const currentVersion = commentVersions[component.reference_id];
              if (currentVersion && component.commentData?.updateddate && currentVersion !== component.commentData.updateddate) {
                hasVersionConflict = true;
                conflictDetails.push(`Content "${component.title || component.type}" (${component.id}) was modified by another user`);
                console.log(`❌ Version conflict detected for comment ${component.id}:`, {
                  localVersion: component.commentData.updateddate,
                  currentVersion: currentVersion
                });
              }
            } else if (component.reference_id && component.reference_table === 'assignments') {
              const currentVersion = assignmentVersions[component.reference_id];
              const localVersion = component.assignmentData?.updateddate;
              
              console.log(`🔍 Assignment version check for component ${component.id}:`, {
                componentTitle: component.title || component.type,
                referenceId: component.reference_id,
                localVersion: localVersion,
                currentVersion: currentVersion,
                hasConflict: currentVersion && localVersion && currentVersion !== localVersion
              });
              
              // For assignments, if we're about to update them, we expect the timestamp to be different
              // This is not a conflict - it's expected behavior when changing assignments
              // Since we're going to overwrite the assignment data anyway, we can skip the version check
              console.log(`⚠️ Assignment timestamp mismatch detected for component ${component.id}:`, {
                localVersion: localVersion,
                currentVersion: currentVersion,
                note: 'Skipping version conflict check since we are about to update this assignment'
              });
              
              // Skip version conflict check for assignments we're about to update
              // The timestamp difference is expected when changing assignments
            }
          }
          
          // If version conflicts detected, cancel the transaction
          if (hasVersionConflict) {
            const conflictMessage = `Version conflicts detected. The following items were modified by another user:\n\n${conflictDetails.join('\n')}\n\nPlease reload the course to see the latest changes.`;
            
            console.error('❌ Transaction cancelled due to version conflicts:', conflictDetails);
            setCriticalError(conflictMessage);
            return; // Exit early, don't proceed with the transaction
          }
          
          console.log('✅ Version check passed - no conflicts detected');
          
        } catch (versionCheckError) {
          console.error('❌ Version check failed:', versionCheckError);
          setCriticalError(`Failed to verify item versions. Please try again or contact support if the problem persists.`);
          return; // Exit early, don't proceed with the transaction
        }
      }
      
      // Build the transaction operations array
      let operationIndex = 0;
      
      // 1. Course component operations (create or update)
      if (courseHasChanges || !courseId) {
        // Set status to Active BEFORE saving to database
        courseComponent.status = 'Active';
        
        if (courseId) {
          // Update existing course
        transactionOperations.operations.push({
            id: `course_update_${operationIndex++}`,
          type: 'course_update',
          data: courseComponent,
          execute: async () => {
            const url = `/api/components/${courseComponent.id}`;
            const payload = { ...courseComponent };
            if (!canComponentNotificationSettings) delete payload.notification_settings;
            console.log(`🔄 Transaction: Updating course component (course_update)`, {
              url,
              method: 'PUT',
              id: courseComponent.id,
              title: courseComponent.title,
              concentration: courseComponent.concentration,
              otherconcentrations: courseComponent.otherconcentrations,
              status: courseComponent.status,
              type: courseComponent.type,
              hasToken: !!token,
              omittingNotificationSettings: !canComponentNotificationSettings,
            });
            const response = await axios.put(url, payload, {
              headers: { Authorization: `Bearer ${token}` }
            });
            return response.data.component;
          }
        });
        } else {
          // Create new course
          transactionOperations.operations.push({
            id: `course_create_${operationIndex++}`,
            type: 'course_create',
            data: courseComponent,
            execute: async () => {
              console.log(`🔄 Transaction: Creating new course component`);
              const payload = { ...courseComponent };
              if (!canComponentNotificationSettings) delete payload.notification_settings;
              const response = await axios.post('/api/components', payload, {
                headers: { Authorization: `Bearer ${token}` }
              });
              return response.data.component;
            }
          });
        }
      }
      
      // 2. Component deletions (if any) - CASCADE DELETE ALL CHILDREN AND REFERENCED ITEMS
      if (deletedComponents.length > 0) {
        console.log(`🗑️ Transaction: Processing ${deletedComponents.length} component deletions with cascade`);
        deletedComponents.forEach((deletedComponent, index) => {
          transactionOperations.operations.push({
            id: `delete_${operationIndex++}`,
            type: 'component_delete',
            data: deletedComponent,
            execute: async () => {
              console.log(`🗑️ Transaction: Starting cascade deletion for component ${deletedComponent.id} (${deletedComponent.type})`);
              
              // Step 1: Find all children components that need to be deleted (domino style)
              // Use the stored component hierarchies that were captured when deletion was confirmed
              const componentsToDelete = [];
              const referencedItemsToDelete = [];
              
              // Add the main component to deletion list
              componentsToDelete.push(deletedComponent);
              
              // Find the stored hierarchy for this component
              const storedHierarchy = deletedComponentHierarchies.find(h => h.mainComponent.id === deletedComponent.id);
              if (storedHierarchy) {
                // Add all children from the stored hierarchy
                storedHierarchy.children.forEach(child => {
                  componentsToDelete.push({
                    id: child.id,
                    type: child.type,
                    level: child.level,
                    reference_id: child.reference_id,
                    reference_table: child.reference_table,
                    createddate: child.createddate,
                    updateddate: child.updateddate
                  });
                });
                
                              console.log(`🗑️ Found stored hierarchy for ${deletedComponent.id}:`, {
                mainComponent: storedHierarchy.mainComponent.id,
                childrenCount: storedHierarchy.children.length,
                children: storedHierarchy.children.map(c => ({ 
                  id: c.id, 
                  type: c.type, 
                  level: c.level,
                  status: c.status || 'Unknown',
                  inDatabase: !!(c.reference_id || c.createddate)
                }))
              });
              
              // Log which components will actually be deleted from database
              const databaseComponents = componentsToDelete.filter(c => c.reference_id || c.createddate);
              const draftComponents = componentsToDelete.filter(c => !(c.reference_id || c.createddate));
              
              console.log(`🗑️ Database Deletion Plan:`, {
                totalComponents: componentsToDelete.length,
                databaseComponents: databaseComponents.length,
                draftComponents: draftComponents.length,
                note: 'Draft components are removed from UI but not from database'
              });
              } else {
                console.warn(`⚠️ No stored hierarchy found for component ${deletedComponent.id} - will only delete main component`);
              }
              
              // Step 2: Collect all referenced items that need to be deleted
              console.log(`🔍 Analyzing ${componentsToDelete.length} components for referenced items...`);
              
              componentsToDelete.forEach(comp => {
                const isInDatabase = !!(comp.reference_id || comp.createddate);
                console.log(`  🔍 Component ${comp.id} (${comp.type}):`, {
                  reference_id: comp.reference_id,
                  reference_table: comp.reference_table,
                  hasReference: !!(comp.reference_id && comp.reference_table),
                  inDatabase: isInDatabase,
                  status: isInDatabase ? 'Active/Saved' : 'Draft (UI only)'
                });
                
                if (comp.reference_id && comp.reference_table) {
                  if (comp.reference_table === 'comments') {
                    referencedItemsToDelete.push({
                      type: 'comment',
                      id: comp.reference_id,
                      componentId: comp.id
                    });
                    console.log(`    💬 Added comment ${comp.reference_id} to deletion list`);
                  } else if (comp.reference_table === 'assignments') {
                    console.log(
                      `    📝 Assignment ${comp.reference_id} skipped here — deleted in same DB transaction as component ${comp.id} (DELETE /api/components)`
                    );
                  } else {
                    console.log(`    ⚠️ Unknown reference table: ${comp.reference_table} for ${comp.reference_id}`);
                  }
                } else if (isInDatabase) {
                  console.log(`    ℹ️ No referenced items for this component`);
                } else {
                  console.log(`    ℹ️ Draft component - no referenced items to delete`);
                }
              });
              
              console.log(`🗑️ Cascade deletion plan:`, {
                mainComponent: deletedComponent.id,
                totalComponentsToDelete: componentsToDelete.length,
                referencedItemsToDelete: referencedItemsToDelete.length,
                components: componentsToDelete.map(c => ({ 
                  id: c.id, 
                  type: c.type, 
                  level: c.level,
                  reference_id: c.reference_id,
                  reference_table: c.reference_table
                })),
                referencedItems: referencedItemsToDelete.map(r => ({ type: r.type, id: r.id }))
              });
              
              // Step 3: Delete referenced comments before components (assignments: removed in DB txn via DELETE /api/components)
              console.log(`🗑️ Starting deletion of ${referencedItemsToDelete.length} referenced items...`);
              
              for (const referencedItem of referencedItemsToDelete) {
                try {
                  console.log(`🗑️ Deleting ${referencedItem.type} ${referencedItem.id} (component: ${referencedItem.componentId})`);
                  
                  if (referencedItem.type === 'comment') {
                    console.log(`  💬 Deleting comment ${referencedItem.id} via /api/comments/${referencedItem.id}`);
                    const response = await axios.delete(`/api/comments/${referencedItem.id}`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    console.log(`  ✅ Successfully deleted comment ${referencedItem.id}:`, response.data);
                  } else if (referencedItem.type === 'assignment') {
                    console.log(`  📝 Deleting assignment ${referencedItem.id} via /api/assignments/${referencedItem.id}`);
                    const response = await axios.delete(`/api/assignments/${referencedItem.id}`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    console.log(`  ✅ Successfully deleted assignment ${referencedItem.id}:`, response.data);
                  }
                } catch (error) {
                  console.error(`❌ Failed to delete ${referencedItem.type} ${referencedItem.id}:`, error.message);
                  if (error.response) {
                    console.error(`  Status: ${error.response.status}`);
                    console.error(`  Data:`, error.response.data);
                  }
                  // Continue with component deletion even if referenced item deletion fails
                  console.warn(`  ⚠️ Continuing with component deletion despite referenced item deletion failure`);
                }
              }
              
              console.log(`✅ Completed deletion of referenced items`);
              
              // Step 4: Delete all components (children first, then parent)
              // Sort by level so children are deleted before parents
              const sortedComponents = componentsToDelete.sort((a, b) => {
                const levelOrder = { 'nested': 0, 'subsection': 1, 'section': 2 };
                return levelOrder[a.level] - levelOrder[b.level];
              });
              
              for (const component of sortedComponents) {
                try {
                  // Check if this component actually exists in the database
                  if (component.reference_id || component.createddate) {
                    console.log(`🗑️ Transaction: Deleting component ${component.id} (${component.type}, level: ${component.level}) from database`);
                    const deleteResponse = await axios.delete(`/api/components/${component.id}`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    if (deleteResponse?.data?.courseUpdatedDate) {
                      courseComponent.updateddate = deleteResponse.data.courseUpdatedDate;
                      setCourseComponentState(prev => (prev ? { ...prev, updateddate: deleteResponse.data.courseUpdatedDate } : prev));
                    }
                    console.log(`✅ Successfully deleted component ${component.id} from database`);
                  } else {
                    console.log(`⚠️ Skipping Draft component ${component.id} (${component.type}, level: ${component.level}) - not in database`);
                  }
                } catch (error) {
                  console.error(`❌ Failed to delete component ${component.id}:`, error.message);
                  if (error.response) {
                    console.error(`❌ DELETE /api/components response ${error.response.status}:`, error.response.data);
                  }
                  throw new Error(`Failed to delete component ${component.id}: ${error.message}`);
                }
              }
              
              console.log(`✅ Cascade deletion completed for component ${deletedComponent.id}`);
              return { 
                deleted: true, 
                componentId: deletedComponent.id,
                totalComponentsDeleted: componentsToDelete.length,
                totalReferencedItemsDeleted: referencedItemsToDelete.length,
                courseUpdatedDate: courseComponent.updateddate || null
              };
            }
          });
        });
      }
      
      // 3. Referenced item updates (comments and assignments)
      componentsNeedingReferencedItemUpdates.forEach((item, index) => {
        const { component, changeAnalysis } = item;
        if (component.reference_id && component.reference_table === 'comments') {
          transactionOperations.operations.push({
            id: `referenced_${operationIndex++}`,
            type: 'referenced_item_update',
            data: { component, changeAnalysis },
            execute: async () => {
              console.log(`🔄 Transaction: Updating referenced comment for component ${component.id}`);
              const commentData = {
                title: component.title || '',
                content: JSON.stringify(component.content || []),
                allowmainresponse: component.settings?.allowMainResponse ?? true,
                allowcommentresponse: component.settings?.allowCommentResponse ?? true,
                notifymeonresponses: component.settings?.notifyMeOnResponses ?? false,
                allowassigneesviewresponses: component.settings?.allowAssigneesViewResponses ?? true,
                hideresponsesuntilresponded: component.settings?.hideresponsesuntilresponded ?? true
              };
              
              const response = await axios.put(`/api/comments/${component.reference_id}`, commentData, {
                headers: { Authorization: `Bearer ${token}` }
              });
              return response.data;
            }
          });
        } else if (component.reference_id && component.reference_table === 'assignments') {
          transactionOperations.operations.push({
            id: `referenced_${operationIndex++}`,
            type: 'referenced_item_update',
            data: { component, changeAnalysis },
            execute: async () => {
              console.log(`🔄 Transaction: Updating referenced assignment for component ${component.id}`);
              
              // For Assignment components, we need to update the assignment with data from the newly selected assignment
              // based on the selected assignment in the assignments array
              let assignmentData = {};
              
              if (component.assignments && component.assignments.length > 0) {
                const selectedAssignment = component.assignments[0];
                
                // Copy all the data from the newly selected assignment
                assignmentData = {
                  title: selectedAssignment.title || "Unknown Assignment",
                  gradingresulttype: selectedAssignment.gradingresulttype || "Points",
                  initialdirections: selectedAssignment.initialdirections || [],
                  questions: selectedAssignment.questions || [],
                  hours: selectedAssignment.hours || 0,
                  maxpoints: selectedAssignment.maxpoints || null,
                  concentration: selectedAssignment.concentration || null,
                  otherconcentrations: selectedAssignment.otherconcentrations || [],
                  taxonomy: selectedAssignment.taxonomy || [],
                  // Set the original field to point to the newly selected assignment
                  original: selectedAssignment.id
                };
                
                console.log('🔄 Updating assignment with data from newly selected assignment:', {
                    componentId: component.id,
                    assignmentId: component.reference_id,
                    newOriginalId: selectedAssignment.id,
                    newTitle: selectedAssignment.title,
                  currentLocalTimestamp: component.assignmentData?.updateddate,
                  databaseTimestamp: null // Will be updated after API call
                });
              }
              
              const response = await axios.put(`/api/assignments/${component.reference_id}`, assignmentData, {
                headers: { Authorization: `Bearer ${token}` }
              });
              
                            // Update the local component's assignmentData with the updated assignment data
              // This ensures the referenced_item.original field shows the correct value in the JSON
              if (response.data && response.data.assignment) {
                const updatedAssignment = response.data.assignment;
                // Ensure the original field is preserved from the newly selected assignment
                const currentSelectedAssignment = component.assignments[0];
                component.assignmentData = {
                  ...component.assignmentData,
                  ...updatedAssignment,
                  // Preserve the original field to point to the newly selected assignment
                  original: currentSelectedAssignment.id,
                  // Update the timestamp to match the database version
                  updateddate: updatedAssignment.updateddate
                };
                
                console.log('🔄 Updated component assignmentData after assignment update:', {
                componentId: component.id,
                  assignmentId: updatedAssignment.id,
                  newTitle: updatedAssignment.title,
                  newOriginal: currentSelectedAssignment.id,
                  newUpdatedDate: updatedAssignment.updateddate,
                  oldLocalTimestamp: component.assignmentData?.updateddate,
                  databaseTimestamp: updatedAssignment.updateddate
                });
              }
              
              return response.data;
            }
          });
        }
      });
      
      // Note: Draft Assignment creation is now handled by createAssignmentsForComponents function
      // which runs before the transaction system, similar to how comments are created
      
      // 5. Component updates/inserts
      componentsToSave.forEach((component, index) => {
        transactionOperations.operations.push({
          id: `component_${operationIndex++}`,
          type: 'component_update',
          data: component,
          execute: async () => {
            console.log(`🔄 Transaction: Processing component ${component.id} (${component.type}):`, {
              status: component.status,
              referenceTable: component.reference_table,
              referenceId: component.reference_id,
              hasAssignmentData: !!component.assignmentData,
              hasCommentData: !!component.commentData
            });
            
          // Update parent references to use the course component ID (either existing or pre-generated)
          // For Draft Course components, use the pre-generated UUID from courseComponent.id
          // For Active Course components, use the existing currentCourseId
          const effectiveCourseId = courseId || courseComponent?.id;
          
          if (component.level === 'section') {
            component.parent = effectiveCourseId;
            component.superparent = effectiveCourseId;
          } else if (component.level === 'subsection') {
            const parentSection = sections.find(s => s.id === component.parent);
            if (parentSection) {
              component.parent = parentSection.id;
              component.superparent = effectiveCourseId;
            }
          } else if (component.level === 'nested') {
            let parentSubsection = null;
            for (const section of sections) {
              if (section.subsections) {
                parentSubsection = section.subsections.find(ss => ss.id === component.parent);
                if (parentSubsection) break;
              }
            }
            if (parentSubsection) {
              component.parent = parentSubsection.id;
              component.superparent = effectiveCourseId;
            }
          }
          
          // Set status to Active BEFORE saving to database
          component.status = 'Active';
          
          // Create a database-ready object with proper field mapping
          const databaseComponent = {
            id: component.id,
            title: component.text || component.title || '',
            type: component.type,
            level: component.level,
            sort_index: component.index || 0,
            reference_id: component.reference_id || null,
            reference_table: component.reference_table || null,
            concentration: component.primaryConcentration || null,
            otherconcentrations: component.otherConcentrations || [],
            taxonomy: component.taxonomy || [],
            hourawards: component.hourawards || [],
              grade_settings: component.gradeSettings || null,
            parent: component.parent || null,
              superparent: component.superparent || null,
            original: component.original || null,
            draft: component.draft || null,
            status: component.status,
            ...(canComponentNotificationSettings
              ? { notification_settings: component.notification_settings ?? null }
              : {})
          };
          
            // Determine if this is an UPDATE or INSERT
          if (component.status === 'Active' && component.createddate && component.updateddate) {
              // UPDATE existing component
              console.log(`🔄 Transaction: Updating existing component ${component.id}`);
              const response = await axios.put(`/api/components/${component.id}`, databaseComponent, {
              headers: { Authorization: `Bearer ${token}` }
            });
              return {
                component: response.data.component,
                courseUpdatedDate: response.data.courseUpdatedDate || null
              };
            } else {
              // INSERT new component
              console.log(`🔄 Transaction: Creating new component ${component.id}:`, {
                type: component.type,
                referenceTable: component.reference_table,
                referenceId: component.reference_id,
                hasAssignmentData: !!component.assignmentData,
                hasCommentData: !!component.commentData
              });
                const response = await axios.post('/api/components', databaseComponent, {
                headers: { Authorization: `Bearer ${token}` }
              });
              return {
                component: response.data.component,
                courseUpdatedDate: response.data.courseUpdatedDate || null
              };
            }
          }
        });
      });
      
      console.log(`🚀 Transaction prepared with ${transactionOperations.operations.length} operations:`, {
        courseUpdate: !!transactionOperations.courseUpdate,
        componentUpdates: transactionOperations.componentUpdates.length,
        referencedItemUpdates: transactionOperations.referencedItemUpdates.length,
        totalOperations: transactionOperations.operations.length
      });
      console.log(
        '📋 Transaction operation queue (order = execution order):',
        transactionOperations.operations.map((op, i) => {
          const row = { step: i + 1, id: op.id, type: op.type };
          if (op.type === 'course_update' || op.type === 'course_create') {
            row.targetComponentId = op.data?.id;
            row.preview = { title: op.data?.title, concentration: op.data?.concentration };
          } else if (op.type === 'component_update') {
            row.targetComponentId = op.data?.id;
            row.preview = {
              type: op.data?.type,
              level: op.data?.level,
              textOrTitle: op.data?.text || op.data?.title,
              primaryConcentration: op.data?.primaryConcentration,
            };
          } else if (op.type === 'component_delete') {
            row.targetComponentId = op.data?.id;
          } else if (op.type === 'referenced_item_update') {
            const c = op.data?.component;
            row.targetComponentId = c?.id;
            row.reference = c ? { reference_table: c.reference_table, reference_id: c.reference_id } : null;
          }
          return row;
        })
      );

      // Execute all operations in sequence
      setSuccess('Executing transaction...');
      const transactionResults = [];
      let currentOperationIndex = 0;
      
      try {
        for (let opIdx = 0; opIdx < transactionOperations.operations.length; opIdx++) {
          currentOperationIndex = opIdx;
          const operation = transactionOperations.operations[opIdx];
          setSuccess(`Executing operation ${opIdx + 1} of ${transactionOperations.operations.length}...`);

          console.log(`🔄 Executing operation ${opIdx + 1}/${transactionOperations.operations.length}: ${operation.id} (${operation.type})`);
          const result = await operation.execute();
          transactionResults.push({ operation, result, success: true });

          console.log(`✅ Operation ${operation.id} completed successfully`);
        }
        
        // All operations succeeded - transaction completed
        console.log(`🎉 Transaction completed successfully! All ${transactionOperations.operations.length} operations succeeded.`);
        
        // Process results and update local state
        for (const { operation, result } of transactionResults) {
          if (operation.type === 'course_update') {
            // Course component was updated
            console.log(`✅ Course component updated successfully in transaction`);
            const savedCourse = result;
            
            // Update the course component with real database values
            courseComponent.createddate = savedCourse.createddate;
            courseComponent.updateddate = savedCourse.updateddate;
            courseComponent.grade_settings = savedCourse.grade_settings;
            
            // Update local state
            setCurrentCourseUpdatedDate(savedCourse.updateddate);
            setCurrentCourseComponent({ ...courseComponent });
            updateLocalComponentWithDatabaseData(courseComponent.id, savedCourse);
            setCourseComponentState(prev => ({ ...prev, ...savedCourse, hasChanges: false }));
            
            console.log('🔄 Updated course component after successful update:', {
              id: savedCourse.id,
              title: savedCourse.title,
              updateddate: savedCourse.updateddate
            });
          } else if (operation.type === 'course_create') {
            // Course component was created
            console.log(`✅ Course component created successfully in transaction`);
            const savedCourse = result;
            
            // Update the course component with real database values
            courseComponent.id = savedCourse.id;
            courseComponent.createddate = savedCourse.createddate;
            courseComponent.updateddate = savedCourse.updateddate;
            courseComponent.grade_settings = savedCourse.grade_settings;
            
            // Update local state
            setCurrentCourseId(savedCourse.id);
            setCurrentCourseUpdatedDate(savedCourse.updateddate);
            setCurrentCourseComponent({ ...courseComponent });
            updateLocalComponentWithDatabaseData(courseComponent.id, savedCourse);
            setCourseComponentState(prev => ({ ...prev, ...savedCourse, hasChanges: false }));
            if (
              userData?.account_id &&
              savedCourse.createdby &&
              String(savedCourse.createdby) === String(userData.account_id)
            ) {
              const n = [userData.first_name, userData.last_name].filter(Boolean).join(' ').trim();
              setCourseCreatorLabelFromServer(n || userData.primary_email || null);
            }
            
            console.log('🆕 Created new course component:', {
              id: savedCourse.id,
              title: savedCourse.title,
              createddate: savedCourse.createddate
            });
          } else if (operation.type === 'component_delete') {
            // Component was cascade deleted
            console.log(`✅ Component cascade deleted successfully in transaction: ${result.componentId}`);
            console.log(`  - Total components deleted: ${result.totalComponentsDeleted}`);
            console.log(`  - Total referenced items deleted: ${result.totalReferencedItemsDeleted}`);
            if (result.courseUpdatedDate) {
              setCurrentCourseUpdatedDate(result.courseUpdatedDate);
              courseComponent.updateddate = result.courseUpdatedDate;
              setCurrentCourseComponent(prev => (prev ? { ...prev, updateddate: result.courseUpdatedDate } : prev));
              setCourseComponentState(prev => (prev ? { ...prev, updateddate: result.courseUpdatedDate } : prev));
            }
          } else if (operation.type === 'component_update') {
            const component = operation.data;
            const savedComponent = result?.component || result;
            const updatedCourseVersion = result?.courseUpdatedDate || null;
            
          if (savedComponent) {
            component.createddate = savedComponent.createddate;
            component.updateddate = savedComponent.updateddate;
            component.grade_settings = savedComponent.grade_settings;
            if (savedComponent.status) component.status = savedComponent.status;
            
              // Preserve the comment reference data
            const componentWithReferences = {
              ...savedComponent,
              reference_table: component.reference_table,
              reference_id: component.reference_id,
              commentData: component.commentData
            };
            
              // Update the local sections state
            updateLocalComponentWithDatabaseData(component.id, componentWithReferences);
            if (updatedCourseVersion) {
              setCurrentCourseUpdatedDate(updatedCourseVersion);
              courseComponent.updateddate = updatedCourseVersion;
              setCurrentCourseComponent(prev => (prev ? { ...prev, updateddate: updatedCourseVersion } : prev));
              setCourseComponentState(prev => (prev ? { ...prev, updateddate: updatedCourseVersion } : prev));
            }
            }
          } else if (operation.type === 'draft_assignment_create') {
            // Draft assignment creation is now handled by createAssignmentsForComponents function
            // This operation type should no longer be used
            console.log(`⚠️ Draft assignment create operation detected - this should not happen anymore`);
            console.log(`   Draft assignments are now created by createAssignmentsForComponents function`);
          } else if (operation.type === 'referenced_item_update') {
            // Referenced item was updated
            console.log(`✅ Referenced item updated successfully in transaction`);
            
            // Update the component's referenced data if it's an assignment
            if (operation.data.component.reference_table === 'assignments') {
              const component = operation.data.component;
              const updatedAssignment = result;
              
              // Update the component's assignmentData with the new assignment data
              component.assignmentData = updatedAssignment;
              
              console.log('🔄 Updated component assignmentData after assignment update:', {
                componentId: component.id,
                assignmentId: updatedAssignment.id,
                newTitle: updatedAssignment.title,
                newOriginal: updatedAssignment.original
              });
            }
          }
        }
        
        // Transaction completed successfully
        console.log(`🎉 All transaction operations completed successfully!`);
        
      } catch (transactionError) {
        // Transaction failed - show error and retry popup
        const failedOp = transactionOperations.operations[currentOperationIndex];
        console.error(`❌ Transaction failed at operation ${currentOperationIndex + 1}:`, transactionError);
        console.error('❌ Failed operation (from queue):', failedOp ? { id: failedOp.id, type: failedOp.type } : '(unknown)');
        if (transactionError?.response) {
          const res = transactionError.response;
          console.error('❌ Response status:', res.status, res.statusText);
          console.error('❌ Response data:', res.data);
        }
        if (transactionError?.config) {
          const cfg = transactionError.config;
          console.error('❌ Request:', (cfg.method || '').toUpperCase(), cfg.url, {
            baseURL: cfg.baseURL || '(default)',
            params: cfg.params || undefined,
          });
          try {
            const raw = cfg.data;
            const parsed =
              typeof raw === 'string' ? (raw.length > 2000 ? `${raw.slice(0, 2000)}…` : JSON.parse(raw)) : raw;
            console.error(
              '❌ Request payload (truncated/summary):',
              typeof parsed === 'object' && parsed !== null
                ? {
                    ...(parsed.id !== undefined ? { id: parsed.id } : {}),
                    ...(parsed.title !== undefined ? { title: parsed.title } : {}),
                    ...(parsed.type !== undefined ? { type: parsed.type } : {}),
                    ...(parsed.level !== undefined ? { level: parsed.level } : {}),
                    ...(parsed.concentration !== undefined ? { concentration: parsed.concentration } : {}),
                    ...(parsed.primaryConcentration !== undefined ? { primaryConcentration: parsed.primaryConcentration } : {}),
                    ...(parsed.reference_table !== undefined ? { reference_table: parsed.reference_table } : {}),
                    ...(parsed.reference_id !== undefined ? { reference_id: parsed.reference_id } : {}),
                  }
                : parsed
            );
          } catch (_) {
            console.error('❌ Request payload (raw string, truncated):', String(cfg.data).slice(0, 500));
          }
        }

        // Clear any success messages and show critical error overlay
        setSuccess('');
        setCriticalError(`Save operation failed. Please try again or contact support if the problem persists.`);
        
        // Don't update any local state since transaction failed
        throw new Error(`Transaction failed: ${transactionError.message}`, {
          cause: transactionError,
        });
      }
      
      // Update course status based on all components
      const finalCourseStatus = courseComponent.status;
      setCurrentCourseStatus(finalCourseStatus);
      
      // Update the original state for course component if it was updated or created
      if ((courseHasChanges || !courseId) && originalComponentsState) {
        setOriginalComponentsState(prev => ({
          ...prev,
          course: { ...courseComponent }
        }));
      }
      
      // Log summary of save operations
      const updatedCount = componentsToSave.filter(comp => comp.status === 'Active' && comp.createddate && comp.updateddate).length;
      const insertedCount = componentsToSave.length - updatedCount;
      const courseWasSaved = courseComponent && (courseHasChanges || !courseId);
      const referencedItemsUpdated = componentsNeedingReferencedItemUpdates.length;
      const deletedCount = deletedComponents.length;
      
      // Calculate total cascade deletions (components + children + referenced items)
      let totalCascadeDeletions = 0;
      if (deletedCount > 0) {
        // This is a rough estimate - the actual count will be logged in the transaction results
        totalCascadeDeletions = deletedCount * 2; // Assume at least 2 items per deletion (component + referenced item)
      }
      
      console.log(`📊 Enhanced course save summary:`, {
        totalComponents: otherComponents.length,
        componentsWithChanges: componentsToSave.length,
        referencedItemUpdates: referencedItemsUpdated,
        courseWasSaved: courseWasSaved,
        updatedComponents: updatedCount,
        insertedComponents: insertedCount,
        deletedComponents: deletedCount,
        estimatedCascadeDeletions: totalCascadeDeletions,
        courseStatus: finalCourseStatus
      });
      
      // Determine the appropriate success message
      let successMessage;
      
      if (courseWasSaved && componentsToSave.length === 0 && referencedItemsUpdated === 0 && deletedCount === 0) {
        // Only course component was saved
        successMessage = `Course settings updated successfully! Status: ${finalCourseStatus}`;
      } else if (courseWasSaved && (componentsToSave.length > 0 || referencedItemsUpdated > 0 || deletedCount > 0)) {
        // Course + components and/or referenced items and/or deletions were saved
        const courseSaveText = ' + course';
        const referencedText = referencedItemsUpdated > 0 ? ` + ${referencedItemsUpdated} referenced items` : '';
        const deletedText = deletedCount > 0 ? ` + ${deletedCount} deleted` : '';
        successMessage = `Course saved successfully! Status: ${finalCourseStatus} (${componentsToSave.length} components${referencedText}${deletedText}${courseSaveText} saved: ${updatedCount} updated, ${insertedCount} created, ${deletedCount} deleted)`;
      } else if (componentsToSave.length > 0 || referencedItemsUpdated > 0 || deletedCount > 0) {
        // Only components and/or referenced items and/or deletions were saved
        const referencedText = referencedItemsUpdated > 0 ? ` + ${referencedItemsUpdated} referenced items` : '';
        const deletedText = deletedCount > 0 ? ` + ${deletedCount} deleted` : '';
        successMessage = `Course saved successfully! Status: ${finalCourseStatus} (${componentsToSave.length} components${referencedText}${deletedText} saved: ${updatedCount} updated, ${insertedCount} created, ${deletedCount} deleted)`;
      }
      
      setSuccess(successMessage);
      setTimeout(() => setSuccess(''), 3000);
      
      // Reset the original state after successful save to track future changes
      // Use setTimeout to ensure all setSections calls from updateLocalComponentWithDatabaseData have completed
      setTimeout(() => {
        const newOriginalState = {
          course: { ...courseComponent },
          sections: JSON.parse(JSON.stringify(sections)) // Deep copy of current state
        };
        
        console.log(`🔄 Resetting original state after save:`, {
          courseId: newOriginalState.course.id,
          sectionsCount: newOriginalState.sections.length,
          sections: newOriginalState.sections.map(s => ({
            id: s.id,
            type: s.type,
            subsectionsCount: s.subsections?.length || 0
          }))
        });
        
        setOriginalComponentsState(newOriginalState);
      }, 0);
      
      // Clear deleted components tracking after successful save
      setDeletedComponents([]);
      setDeletedComponentHierarchies([]);
      
    } catch (error) {
      console.error('Error saving course:', error);
      if (error?.cause) console.error('Error saving course (underlying cause — often Axios):', error.cause);
      if (/Transaction failed/i.test(String(error?.message || ''))) {
        console.warn('See logs above marked "❌ Transaction failed" for HTTP status, response body, and request URL.');
      }
      
      // ROLLBACK: Restore cascade deleted components if transaction failed
      if (deletedComponents.length > 0) {
        console.log(`🔄 Rolling back cascade deletions for ${deletedComponents.length} main components due to transaction failure`);
        
        // For each main component that was cascade deleted, restore the complete hierarchy
        deletedComponents.forEach(deletedComponent => {
          console.log(`🔄 Rolling back cascade deletion for component ${deletedComponent.id} (${deletedComponent.type})`);
          
          // Find the stored hierarchy for this component
          const storedHierarchy = deletedComponentHierarchies.find(h => h.mainComponent.id === deletedComponent.id);
          if (storedHierarchy) {
            console.log(`🔄 Restoring complete hierarchy for ${deletedComponent.id}:`, {
              mainComponent: storedHierarchy.mainComponent.id,
              childrenCount: storedHierarchy.children.length
            });
            
            // Restore the main component first
            if (storedHierarchy.mainComponent.level === 'section') {
              const restoredSection = {
                ...storedHierarchy.mainComponent,
                text: storedHierarchy.mainComponent.text || 'Untitled',
                index: sections.length,
                sort_index: sections.length
              };
              setSections(prev => [...prev, restoredSection]);
              console.log(`✅ Restored main section component ${storedHierarchy.mainComponent.id}`);
              
              // Now restore all children in the correct hierarchy
              const restoredSectionIndex = sections.length; // This will be the index after adding the section
              
              // Group children by their parent relationship
              const subsections = storedHierarchy.children.filter(c => c.level === 'subsection');
              const nestedSections = storedHierarchy.children.filter(c => c.level === 'nested');
              
              // Restore subsections
              if (subsections.length > 0) {
                const restoredSubsections = subsections.map(subsection => ({
                  ...subsection,
                  text: subsection.text || 'Untitled',
                  index: subsection.index,
                  sort_index: subsection.index
                }));
                
                setSections(prev => {
                  const newSections = [...prev];
                  newSections[restoredSectionIndex].subsections = restoredSubsections;
                  return newSections;
                });
                console.log(`✅ Restored ${subsections.length} subsection components`);
                
                // Restore nested sections within each subsection
                subsections.forEach((subsection, subIndex) => {
                  const subsectionNestedSections = nestedSections.filter(ns => 
                    ns.parentSubsectionIndex === subsection.index
                  );
                  
                  if (subsectionNestedSections.length > 0) {
                    const restoredNestedSections = subsectionNestedSections.map(nestedSection => ({
                      ...nestedSection,
                      text: nestedSection.text || 'Untitled',
                      index: nestedSection.index
                    }));
                    
                    setSections(prev => {
                      const newSections = [...prev];
                      if (!newSections[restoredSectionIndex].subsections[subIndex].nestedSections) {
                        newSections[restoredSectionIndex].subsections[subIndex].nestedSections = [];
                      }
                      newSections[restoredSectionIndex].subsections[subIndex].nestedSections = restoredNestedSections;
                      return newSections;
                    });
                    console.log(`✅ Restored ${subsectionNestedSections.length} nested section components in subsection ${subIndex}`);
                  }
                });
              }
              
            } else if (storedHierarchy.mainComponent.level === 'subsection') {
              // Find the parent section and restore subsection
              for (let i = 0; i < sections.length; i++) {
                const section = sections[i];
                if (section.subsections) {
                  const subsectionIndex = section.subsections.findIndex(ss => ss.id === storedHierarchy.mainComponent.id);
                  if (subsectionIndex === -1) {
                    // Subsection was deleted, restore it
                    const restoredSubsection = {
                      ...storedHierarchy.mainComponent,
                      text: storedHierarchy.mainComponent.text || 'Untitled',
                      index: section.subsections.length,
                      sort_index: section.subsections.length
                    };
                    
                    setSections(prev => {
                      const newSections = [...prev];
                      if (!newSections[i].subsections) {
                        newSections[i].subsections = [];
                      }
                      newSections[i].subsections.push(restoredSubsection);
                      return newSections;
                    });
                    console.log(`✅ Restored main subsection component ${storedHierarchy.mainComponent.id}`);
                    
                    // Restore nested sections within this subsection
                    const subsectionNestedSections = storedHierarchy.children.filter(c => c.level === 'nested');
                    if (subsectionNestedSections.length > 0) {
                      const restoredNestedSections = subsectionNestedSections.map(nestedSection => ({
                        ...nestedSection,
                        text: nestedSection.text || 'Untitled',
                        index: nestedSection.index
                      }));
                      
                      setSections(prev => {
                        const newSections = [...prev];
                        const newSubsectionIndex = newSections[i].subsections.length - 1;
                        if (!newSections[i].subsections[newSubsectionIndex].nestedSections) {
                          newSections[i].subsections[newSubsectionIndex].nestedSections = [];
                        }
                        newSections[i].subsections[newSubsectionIndex].nestedSections = restoredNestedSections;
                        return newSections;
                      });
                      console.log(`✅ Restored ${subsectionNestedSections.length} nested section components in restored subsection`);
                    }
                    break;
                  }
                }
              }
            } else if (storedHierarchy.mainComponent.level === 'nested') {
              // For nested sections, just restore the main component (no children)
              for (let i = 0; i < sections.length; i++) {
                const section = sections[i];
                if (section.subsections) {
                  for (let j = 0; j < section.subsections.length; j++) {
                    const subsection = section.subsections[j];
                    if (subsection.nestedSections) {
                      const nestedIndex = subsection.nestedSections.findIndex(ns => ns.id === storedHierarchy.mainComponent.id);
                      if (nestedIndex === -1) {
                        // Nested section was deleted, restore it
                        const restoredNestedSection = {
                          ...storedHierarchy.mainComponent,
                          text: storedHierarchy.mainComponent.text || 'Untitled',
                          index: subsection.nestedSections.length
                        };
                        
                        setSections(prev => {
                          const newSections = [...prev];
                          if (!newSections[i].subsections[j].nestedSections) {
                            newSections[i].subsections[j].nestedSections = [];
                          }
                          newSections[i].subsections[j].nestedSections.push(restoredNestedSection);
                          return newSections;
                        });
                        console.log(`✅ Restored main nested section component ${storedHierarchy.mainComponent.id}`);
                        break;
                      }
                    }
                  }
                }
              }
            }
          } else {
            console.warn(`⚠️ No stored hierarchy found for component ${deletedComponent.id} - cannot perform complete rollback`);
          }
        });
        
        // Clear deleted components tracking after rollback
        setDeletedComponents([]);
        setDeletedComponentHierarchies([]);
        console.log('✅ Rollback completed - complete component hierarchies restored');
      }
      
      // Clear any success messages when an error occurs
      setSuccess('');
      
      if (error.response?.status === 409) {
        // Version conflict - show critical error overlay
        setCriticalError('The course was updated while you were working. Save was cancelled to prevent conflicts. Please reload the page to see the latest version.');
      } else {
        // User-friendly error message that doesn't expose technical details
        let userFriendlyMessage = 'Failed to save course. Please try again or contact support if the problem persists.';
        
        // Log the technical error for debugging but don't show it to the user
        if (error.response?.data?.error) {
          console.log('Technical error details:', error.response.data.error);
        } else if (error.message) {
          console.log('Technical error details:', error.message);
        }
        
        setError(userFriendlyMessage);
        setTimeout(() => setError(''), 8000);
      }
    } finally {
      setIsSaving(false);
      setActionOverlay(false);
    }
  };

  // Start a new course (clear current state)
  const startNewCourse = () => {
    defaultCourseTrackAppliedRef.current = false;
    setSections([]);
    updateCourseTitle('');
    updateCourseConcentration('');
    setTk12Subject('');
    setGradeLevels([]);
    setCurrentCourseId(null);
    setCurrentCourseStatus(null);
    setCurrentCourseUpdatedDate(null);
    setCurrentCourseComponent(null);
    setCourseComponentState(null);
    setCourseCreatorLabelFromServer(null);
    
    // Clear the original state for change detection
    setOriginalComponentsState(null);
    
    // Clear deleted components tracking
    setDeletedComponents([]);
    setDeletedComponentHierarchies([]);
    
    // Reset modlock status for new courses
    window.courseModsLocked = false;
    
    setSuccess('Started new course');
    setTimeout(() => setSuccess(''), 3000);
  };

  // Check if course has been modified by another user/session
  const checkCourseVersion = async (courseId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      const response = await axios.get(`/api/components/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const currentVersion = response.data.component.updateddate;
      
      if (currentCourseUpdatedDate && currentVersion !== currentCourseUpdatedDate) {
        throw new Error('This course has been modified by another user or session. Please reload the course to see the latest changes.');
      }
      
      return true; // Version matches
    } catch (error) {
      console.error('Error checking course version:', error);
      throw error;
    }
  };

  // Load a specific saved course with transaction-based loading
  const loadCourse = async (courseComponent) => {
    try {
      // Show loading spinner during course loading transaction
      setActionOverlay(true);
      
      // Get the token for API calls
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

        // Extract course title and concentration from the Course component
      const courseTitle = courseComponent.title || '';
      const courseConcentration = courseComponent.concentration || '';

      // TRANSACTION-BASED LOADING: All operations must succeed or all must fail
      setSuccess('Preparing course loading transaction...');
      
      // Prepare all loading operations
      const loadOperations = {
        courseComponent: courseComponent,
        operations: []
      };
      
      let operationIndex = 0;

      // 1. Load course components (use viewer endpoint - does not filter by createdby, allows non-creators to open courses)
      loadOperations.operations.push({
        id: `load_components_${operationIndex++}`,
        type: 'load_components',
        execute: async () => {
          console.log(`🔄 Loading operation: Fetching course components for ${courseComponent.id}`);
          setSuccess('Loading course components...');
          
      const componentsResponse = await axios.get(`/api/components/viewer/${courseComponent.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!componentsResponse.data.components) {
        throw new Error('No components found for this course');
      }

      const allComponents = componentsResponse.data.components;
      // Check if course is locked for modifications
      if (courseComponent.modlock === true) {
        // Set global variable to disable save button
        window.courseModsLocked = true;
        console.log('🔒 Course modifications are locked - save button will be disabled');
      } else {
        // Ensure save button is enabled
        window.courseModsLocked = false;
        console.log('✅ Course modifications are unlocked - save button enabled');
      }
      // Additional validation: ensure all components belong to this course
      const courseId = courseComponent.id;
      const validatedComponents = allComponents.filter(comp => comp.superparent === courseId);
      
      if (validatedComponents.length !== allComponents.length) {
        console.warn(`⚠️ Found ${allComponents.length - validatedComponents.length} components with incorrect superparent. Filtering to only include course components.`);
      }
      
      console.log('🔍 Components loaded from database:', {
        totalComponents: allComponents.length,
        validatedComponents: validatedComponents.length,
        courseId: courseId,
        componentsWithDates: validatedComponents.filter(comp => comp.createddate && comp.updateddate).length,
            componentsWithoutDates: validatedComponents.filter(comp => !comp.createddate || !comp.updateddate).length
          });
          
          return {
            validatedComponents,
            courseId,
            rootCourse: componentsResponse.data.component || null
          };
        }
      });
        
      // 2. Load assignments (if any)
      loadOperations.operations.push({
        id: `load_assignments_${operationIndex++}`,
        type: 'load_assignments',
        execute: async (previousResults) => {
          // Access the first operation's result directly
          const firstResult = previousResults.find(r => r.operation.type === 'load_components');
          if (!firstResult) {
            throw new Error('Components must be loaded before assignments');
          }
          
          const { validatedComponents } = firstResult.result;
        
      // Get assignment IDs that need to be queried
      const assignmentIdsToQuery = validatedComponents
        .filter(comp => comp.reference_table === 'assignments' && comp.reference_id)
          .map(comp => comp.reference_id)
        .filter(id => id);
        
          if (assignmentIdsToQuery.length === 0) {
            console.log('📝 No assignments to load');
            return { assignmentsMap: {} };
          }
          
          console.log(`🔄 Loading operation: Fetching ${assignmentIdsToQuery.length} assignments`);
          setSuccess('Loading assignments...');
          
              const response = await axios.get(`/api/assignments?ids=${assignmentIdsToQuery.join(',')}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              
              // Create a map of assignment ID to assignment data
          const assignmentsMap = {};
              if (response.data.assignments) {
                response.data.assignments.forEach(assignment => {
                  assignmentsMap[assignment.id] = assignment;
                });
            }
          
          console.log(`✅ Assignments loaded successfully: ${Object.keys(assignmentsMap).length} assignments`);
          return { assignmentsMap };
        }
      });

      // 3. Load comments (if any)
      loadOperations.operations.push({
        id: `load_comments_${operationIndex++}`,
        type: 'load_comments',
        execute: async (previousResults) => {
          // Access the first operation's result directly
          const firstResult = previousResults.find(r => r.operation.type === 'load_components');
          if (!firstResult) {
            throw new Error('Components must be loaded before comments');
          }
          
          const { validatedComponents } = firstResult.result;
          
      // Get comment IDs for Content and Discussion Board components
      const commentTypes = ["Content", "Discussion Board"];
      const commentIdsToQuery = validatedComponents
        .filter(comp => commentTypes.includes(comp.type) && comp.reference_id)
        .map(comp => comp.reference_id)
        .filter(id => id);

          if (commentIdsToQuery.length === 0) {
            console.log('📝 No comments to load');
            return { commentsMap: {} };
          }

          console.log(`🔄 Loading operation: Fetching ${commentIdsToQuery.length} comments`);
          setSuccess('Loading content and discussion boards...');
          
          const commentsResponse = await axios.get(`/api/comments/ids?ids=${commentIdsToQuery.join(',')}`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          const commentsMap = {};
          if (commentsResponse.data.comments) {
            console.log('✅ Comments fetched successfully:', {
              commentsCount: commentsResponse.data.comments.length,
              commentIds: commentsResponse.data.comments.map(c => c.id)
            });
            commentsResponse.data.comments.forEach(comment => {
              commentsMap[comment.id] = comment;
            });
          }
          
          return { commentsMap };
        }
      });
        
      // 4. Build UI state structure
      loadOperations.operations.push({
        id: `build_ui_state_${operationIndex++}`,
        type: 'build_ui_state',
        execute: async (previousResults) => {
          // Access previous operation results by type
          const componentsResult = previousResults.find(r => r.operation.type === 'load_components');
          const assignmentsResult = previousResults.find(r => r.operation.type === 'load_assignments');
          const commentsResult = previousResults.find(r => r.operation.type === 'load_comments');
          
          if (!componentsResult) {
            throw new Error('Components must be loaded before building UI state');
          }
          
          const { validatedComponents } = componentsResult.result;
          const { assignmentsMap } = assignmentsResult?.result || { assignmentsMap: {} };
          const { commentsMap } = commentsResult?.result || { commentsMap: {} };
          
          console.log(`🔄 Loading operation: Building UI state structure`);
          setSuccess('Building course structure...');
        
        // Convert components array back to sections structure for the UI
        const sections = [];
      const sectionComponents = validatedComponents.filter(comp => comp.level === 'section');
        
        sectionComponents.forEach(sectionComp => {
          // Get associated comment data if this is a Content or Discussion Board component
          const commentData = (sectionComp.type === 'Content' || sectionComp.type === 'Discussion Board') && sectionComp.reference_id 
            ? commentsMap[sectionComp.reference_id] 
            : null;
          
          console.log(`🔍 Section ${sectionComp.id} (${sectionComp.type}):`, {
            hasReferenceId: !!sectionComp.reference_id,
            referenceId: sectionComp.reference_id,
            commentDataFound: !!commentData,
            commentTitle: commentData?.title || 'N/A',
            componentTitle: sectionComp.title || 'N/A',
            uiText: sectionComp.title || '',
            createddate: sectionComp.createddate,
            updateddate: sectionComp.updateddate
          });

                     const section = {
             id: sectionComp.id,
             index: sectionComp.sort_index,
             sort_index: sectionComp.sort_index, // Add sort_index for change detection
             color: `hsl(${Math.random() * 360}, 70%, 80%)`, // Generate new color
              text: sectionComp.title || '', // Use component title for UI display
            content: commentData ? JSON.parse(commentData.content || '[]') : [],
            title: commentData ? commentData.title : '', // Use comment title for post_title
             type: sectionComp.type,
             subsections: [],
             expanded: true,
             primaryConcentration: sectionComp.concentration || '',
             otherConcentrations: sectionComp.otherconcentrations || [],
             taxonomy: sectionComp.taxonomy || [],
             hourawards: sectionComp.hourawards || [],
             gradeSettings: sectionComp.grade_settings || null,
          settings: commentData ? {
            allowMainResponse: commentData.allowmainresponse,
            allowCommentResponse: commentData.allowcommentresponse,
            notifyMeOnResponses: commentData.notifymeonresponses,
            allowAssigneesViewResponses: commentData.allowassigneesviewresponses,
            hideresponsesuntilresponded: commentData.hideresponsesuntilresponded
          } : null,
          status: sectionComp.status || 'Draft',
          reference_table: sectionComp.reference_table,
          reference_id: sectionComp.reference_id,
          commentData: commentData,
          createddate: sectionComp.createddate || null,
          updateddate: sectionComp.updateddate || null,
          hasChanges: false, // Loaded components don't have changes
          assignments: sectionComp.type === 'Assignment' && sectionComp.reference_id ? [assignmentsMap[sectionComp.reference_id] || { id: sectionComp.reference_id }] : [],
          assignmentData: sectionComp.type === 'Assignment' && sectionComp.reference_id ? assignmentsMap[sectionComp.reference_id] || null : null,
          notification_settings: sectionComp.notification_settings ?? null
           };
          
          // Find subsections for this section
          const subsectionComponents = validatedComponents.filter(comp => 
            comp.level === 'subsection' && comp.parent === sectionComp.id
          );
          
          subsectionComponents.forEach(subsectionComp => {
              // Get associated comment data if this is a Content or Discussion Board component
              const subsectionCommentData = (subsectionComp.type === 'Content' || subsectionComp.type === 'Discussion Board') && subsectionComp.reference_id 
                ? commentsMap[subsectionComp.reference_id] 
                : null;
              
              console.log(`🔍 Subsection ${subsectionComp.id} (${subsectionComp.type}):`, {
                hasReferenceId: !!subsectionComp.reference_id,
                referenceId: subsectionComp.reference_id,
                commentDataFound: !!subsectionCommentData,
                commentTitle: subsectionCommentData?.title || 'N/A',
                componentTitle: subsectionComp.title || 'N/A',
                uiText: subsectionComp.title || '',
                createddate: subsectionComp.createddate,
                updateddate: subsectionComp.updateddate
              });

            const subsection = {
              id: subsectionComp.id,
              index: subsectionComp.sort_index,
              sort_index: subsectionComp.sort_index, // Add sort_index for change detection
              color: `hsl(${Math.random() * 360}, 60%, 85%)`, // Generate new color
              text: subsectionComp.title || '', // Use component title for UI display
              content: subsectionCommentData ? JSON.parse(subsectionCommentData.content || '[]') : [],
              title: subsectionCommentData ? subsectionCommentData.title : '', // Use comment title for post_title
              type: subsectionComp.type,
              nestedSections: [],
              expanded: true,
              primaryConcentration: subsectionComp.concentration || '',
              otherConcentrations: subsectionComp.otherconcentrations || [],
              taxonomy: subsectionComp.taxonomy || [],
              hourawards: subsectionComp.hourawards || [],
              gradeSettings: subsectionComp.grade_settings || null,
              settings: subsectionCommentData ? {
                allowMainResponse: subsectionCommentData.allowmainresponse,
                allowCommentResponse: subsectionCommentData.allowcommentresponse,
                notifyMeOnResponses: subsectionCommentData.notifymeonresponses,
                allowAssigneesViewResponses: subsectionCommentData.allowassigneesviewresponses,
                hideresponsesuntilresponded: subsectionCommentData.hideresponsesuntilresponded
              } : null,
              status: subsectionComp.status || 'Draft',
              reference_table: subsectionComp.reference_table,
              reference_id: subsectionComp.reference_id,
              commentData: subsectionCommentData,
              createddate: subsectionComp.createddate || null,
              updateddate: subsectionComp.updateddate || null,
              hasChanges: false, // Loaded components don't have changes
              assignments: subsectionComp.type === 'Assignment' && subsectionComp.reference_id ? [assignmentsMap[subsectionComp.reference_id] || { id: subsectionComp.reference_id }] : [],
              assignmentData: subsectionComp.type === 'Assignment' && subsectionComp.reference_id ? assignmentsMap[subsectionComp.reference_id] || null : null,
              notification_settings: subsectionComp.notification_settings ?? null
            };
            
            // Find nested sections for this subsection
            const nestedComponents = validatedComponents.filter(comp => 
              comp.level === 'nested' && comp.parent === subsectionComp.id
            );
            
            nestedComponents.forEach(nestedComp => {
              // Get associated comment data if this is a Content or Discussion Board component
              const nestedCommentData = (nestedComp.type === 'Content' || nestedComp.type === 'Discussion Board') && nestedComp.reference_id 
                ? commentsMap[nestedComp.reference_id] 
                : null;
              
              console.log(`🔍 Nested ${nestedComp.id} (${nestedComp.type}):`, {
                hasReferenceId: !!nestedComp.reference_id,
                referenceId: nestedComp.reference_id,
                commentDataFound: !!nestedCommentData,
                commentTitle: nestedCommentData?.title || 'N/A',
                componentTitle: nestedComp.title || 'N/A',
                uiText: nestedComp.title || '',
                createddate: nestedComp.createddate,
                updateddate: nestedComp.updateddate
              });
              
                           const nestedSection = {
               id: nestedComp.id,
               index: nestedComp.sort_index,
               sort_index: nestedComp.sort_index, // Add sort_index for change detection
               color: `hsl(${Math.random() * 360}, 50%, 90%)`, // Generate new color
                text: nestedComp.title || '', // Use component title for UI display
                content: nestedCommentData ? JSON.parse(nestedCommentData.content || '[]') : [],
                title: nestedCommentData ? nestedCommentData.title : '', // Use comment title for post_title
               type: nestedComp.type,
               primaryConcentration: nestedComp.concentration || '',
               otherConcentrations: nestedComp.otherconcentrations || [],
               taxonomy: nestedComp.taxonomy || [],
               hourawards: nestedComp.hourawards || [],
               gradeSettings: nestedComp.grade_settings || null,
                settings: nestedCommentData ? {
                  allowMainResponse: nestedCommentData.allowmainresponse,
                  allowCommentResponse: nestedCommentData.allowcommentresponse,
                  notifyMeOnResponses: nestedCommentData.notifymeonresponses,
                  allowAssigneesViewResponses: nestedCommentData.allowassigneesviewresponses,
                  hideresponsesuntilresponded: nestedCommentData.hideresponsesuntilresponded
                } : null,
                status: nestedComp.status || 'Draft',
                reference_table: nestedComp.reference_table,
                reference_id: nestedComp.reference_id,
                commentData: nestedCommentData,
                createddate: nestedComp.createddate || null,
                updateddate: nestedComp.updateddate || null,
                hasChanges: false, // Loaded components don't have changes
                assignments: nestedComp.type === 'Assignment' && nestedComp.reference_id ? [assignmentsMap[nestedComp.reference_id] || { id: nestedComp.reference_id }] : [],
                assignmentData: nestedComp.type === 'Assignment' && nestedComp.reference_id ? assignmentsMap[nestedComp.reference_id] || null : null,
                notification_settings: nestedComp.notification_settings ?? null
             };
              subsection.nestedSections.push(nestedSection);
            });
            
            section.subsections.push(subsection);
          });
          
          sections.push(section);
        });
        
        return { sections };
        }
      });
      
      console.log(`🚀 Course loading transaction prepared with ${loadOperations.operations.length} operations`);
      
      // Execute all loading operations in sequence
      setSuccess('Executing course loading transaction...');
      const loadResults = [];
      let currentOperationIndex = 0;
      
      try {
        for (const operation of loadOperations.operations) {
          currentOperationIndex = loadOperations.operations.indexOf(operation);
          setSuccess(`Loading operation ${currentOperationIndex + 1} of ${loadOperations.operations.length}...`);
          
          console.log(`🔄 Executing loading operation ${operation.id}: ${operation.type}`);
          const result = await operation.execute(loadResults);
          loadResults.push({ operation, result, success: true });
          
          console.log(`✅ Loading operation ${operation.id} completed successfully`);
        }
        
        // All loading operations succeeded - transaction completed
        console.log(`🎉 Course loading transaction completed successfully! All ${loadOperations.operations.length} operations succeeded.`);
        
        // Extract results and update UI state
        const uiStateResult = loadResults.find(r => r.operation.type === 'build_ui_state');
        if (!uiStateResult) {
          throw new Error('UI state building operation failed');
        }
        
        const { sections } = uiStateResult.result;

        const componentsLoadResult = loadResults.find((r) => r.operation.type === 'load_components');
        const rootFromViewer = componentsLoadResult?.result?.rootCourse;
        setCourseCreatorLabelFromServer(formatCourseCreatorFromViewerRow(rootFromViewer));
        
        // Update all UI state atomically
        setSections(sections);
        
        // Ensure all loaded sections have required properties
        ensureAllSectionsHaveProperties(sections);
        
        updateCourseTitle(courseTitle);
        updateCourseConcentration(courseConcentration);
        setCourseTrack(courseComponent.course_track === 'TK12' ? 'TK12' : 'CTE');
        setTk12Subject(courseComponent.subject || '');
        setGradeLevels(
          sortGradeLevelsSelected(
            Array.isArray(courseComponent.grade_levels) ? courseComponent.grade_levels : []
          )
        );
        setCurrentCourseId(courseComponent.id);
        setCurrentCourseStatus(courseComponent.status);
        setCurrentCourseUpdatedDate(courseComponent.updateddate);
        
        // Set the course component state for tracking changes
        setCourseComponentState({
          ...courseComponent,
          hasChanges: false
        });

        // Store the course component data for JSON generation
        setCurrentCourseComponent(courseComponent);
        
        // Store the original state for change detection
        setOriginalComponentsState({
          course: { ...courseComponent },
          sections: JSON.parse(JSON.stringify(sections))
        });
        
        // Clear deleted components tracking when loading a course
        setDeletedComponents([]);
        setDeletedComponentHierarchies([]);
        
        setSuccess('Course loaded successfully!');
        setTimeout(() => setSuccess(''), 3000);
        
      } catch (loadError) {
        // Loading transaction failed - show error and retry popup
        console.error(`❌ Course loading failed at operation ${currentOperationIndex + 1}:`, loadError);
        
        // Clear any success messages and show critical error overlay
        setSuccess('');
        setCriticalError(`Failed to load course. Please try again or contact support if the problem persists.`);
        
        // Don't update any local state since loading failed
        throw new Error(`Course loading failed: ${loadError.message}`);
      }
      
    } catch (error) {
      console.error('Error loading course:', error);
      
      // Clear any success messages when an error occurs
      setSuccess('');
      
      if (error.message.includes('Course loading failed')) {
        // This is a loading transaction error - already handled above
        return;
      } else {
        // This is a general error (auth, etc.)
        setError(`Error loading course: ${error.message}`);
        setTimeout(() => setError(''), 5000);
      }
    } finally {
      // Hide loading spinner when loading completes (success or failure)
      setActionOverlay(false);
      }
    };

  // Load course by id from URL parameter (?id=xxx) - same flow as Edit button in My Courses modal
  // Must be after loadCourse is defined
  useEffect(() => {
    const courseIdFromUrl = searchParams.get('id');
    if (!courseIdFromUrl || !userData?.account_id) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    const loadCourseById = async () => {
      try {
        const response = await axios.get(`/api/components/${courseIdFromUrl}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const courseComponent = response.data.component;
        if (!courseComponent || courseComponent.type !== 'Course') {
          setError('Invalid course ID or component is not a course.');
          return;
        }
        await loadCourse(courseComponent);
      } catch (err) {
        console.error('Error loading course by ID:', err);
        setError(err.response?.data?.error || 'Failed to load course. Please check the course ID.');
      }
    };

    loadCourseById();
  }, [searchParams, userData?.account_id]);

  // Duplicate course function
  const duplicateCourse = async (originalCourse, customSuffix = ' (Copy)') => {
    try {
      setIsDuplicating(true);
      setActionOverlay(true);
      setSuccess('Starting course duplication...');
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Step 1: Load the original course data (same as loadCourse)
      setSuccess('Loading original course data...');
      const courseResponse = await axios.get(`/api/components/${originalCourse.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const originalCourseData = courseResponse.data.component;
      
      // Step 2: Load all components for this course (same query as loadCourse)
      setSuccess('Loading course components...');
      const componentsResponse = await axios.get(`/api/components?superparent=${originalCourse.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!componentsResponse.data.components) {
        throw new Error('No components found for this course');
      }
      
      const originalComponents = componentsResponse.data.components;
      
      // Step 3: Load assignments (same as loadCourse)
      setSuccess('Loading assignments...');
      const assignmentIdsToQuery = originalComponents
        .filter(comp => comp.reference_table === 'assignments' && comp.reference_id)
        .map(comp => comp.reference_id)
        .filter(id => id);
      
      let assignmentsMap = {};
      if (assignmentIdsToQuery.length > 0) {
        const assignmentsResponse = await axios.get(`/api/assignments?ids=${assignmentIdsToQuery.join(',')}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (assignmentsResponse.data.assignments) {
          assignmentsResponse.data.assignments.forEach(assignment => {
            assignmentsMap[assignment.id] = assignment;
          });
        }
      }
      
      // Step 4: Load comments (same as loadCourse)
      setSuccess('Loading comments...');
      const commentTypes = ["Content", "Discussion Board"];
      const commentIdsToQuery = originalComponents
        .filter(comp => commentTypes.includes(comp.type) && comp.reference_id)
        .map(comp => comp.reference_id)
        .filter(id => id);
      
      let commentsMap = {};
      if (commentIdsToQuery.length > 0) {
        const commentsResponse = await axios.get(`/api/comments/ids?ids=${commentIdsToQuery.join(',')}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (commentsResponse.data.comments) {
          commentsResponse.data.comments.forEach(comment => {
            commentsMap[comment.id] = comment;
          });
        }
      }
      
      // Step 5: Generate new UUIDs for all entities
      setSuccess('Generating new IDs for duplicated entities...');
      const componentIdMap = new Map(); // Maps old ID to new ID
      const commentIdMap = new Map(); // Maps old comment ID to new comment ID
      const assignmentIdMap = new Map(); // Maps old assignment ID to new assignment ID
      
      // Generate new course ID
      const newCourseId = uuidv4();
      componentIdMap.set(originalCourse.id, newCourseId);
      
      // Generate new IDs for all components
      originalComponents.forEach(component => {
        const newId = uuidv4();
        componentIdMap.set(component.id, newId);
        
        // Generate new IDs for referenced items
        if (component.reference_table === 'comments' && component.reference_id) {
          const newCommentId = uuidv4();
          commentIdMap.set(component.reference_id, newCommentId);
        } else if (component.reference_table === 'assignments' && component.reference_id) {
          const newAssignmentId = uuidv4();
          assignmentIdMap.set(component.reference_id, newAssignmentId);
        }
      });
      
      // Step 6: Create the new course component
      setSuccess('Creating duplicated course...');
      const newCourseData = {
        ...originalCourseData,
        id: newCourseId,
        title: `${originalCourseData.title}${customSuffix}`,
        status: 'Active', // Set to Active instead of Draft
        createddate: null,
        updateddate: null,
        parent: null,
        superparent: null,
        original: originalCourseData.id // Track the original course this was copied from
      };
      
      await axios.post('/api/components', newCourseData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Step 7: Create all components with new IDs and relationships
      setSuccess('Creating duplicated components...');
      const componentsToCreate = [];
      
      for (const originalComponent of originalComponents) {
        const newComponentId = componentIdMap.get(originalComponent.id);
        const newParentId = originalComponent.parent ? componentIdMap.get(originalComponent.parent) : newCourseId;
        const newSuperparentId = originalComponent.superparent ? componentIdMap.get(originalComponent.superparent) : newCourseId;
        
        // Set up reference_id and reference_table based on the original component's references
        let newReferenceId = null;
        let newReferenceTable = null;
        
        if (originalComponent.reference_table === 'comments' && originalComponent.reference_id) {
          newReferenceId = commentIdMap.get(originalComponent.reference_id);
          newReferenceTable = 'comments';
        } else if (originalComponent.reference_table === 'assignments' && originalComponent.reference_id) {
          newReferenceId = assignmentIdMap.get(originalComponent.reference_id);
          newReferenceTable = 'assignments';
        }
        
        const newComponent = {
          ...originalComponent,
          id: newComponentId,
          title: originalComponent.title ? `${originalComponent.title}${customSuffix}` : originalComponent.title,
          parent: newParentId,
          superparent: newSuperparentId,
          createddate: null,
          updateddate: null,
          reference_id: newReferenceId,
          reference_table: newReferenceTable,
          original: originalComponent.id // Track the original component this was copied from
        };
        
        componentsToCreate.push(newComponent);
      }
      
      // Step 8: Create components in database
      for (const component of componentsToCreate) {
        await axios.post('/api/components', component, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      // Step 9: Create comments for Content and Discussion Board components
      setSuccess('Creating duplicated comments...');
      for (const originalComponent of originalComponents) {
        if ((originalComponent.type === 'Content' || originalComponent.type === 'Discussion Board') && 
            originalComponent.reference_table === 'comments' && originalComponent.reference_id) {
          
          const originalComment = commentsMap[originalComponent.reference_id];
          if (!originalComment) continue;
          
          // Use the pre-generated UUID from the map - this MUST be the same UUID set as reference_id on the component
          const newCommentId = commentIdMap.get(originalComponent.reference_id);
          const newComponentId = componentIdMap.get(originalComponent.id);
          
          // Create comment data following the same structure as createCommentForComponent
          const commentData = {
            id: newCommentId, // This MUST be the same UUID that was set as reference_id on the component
            title: `${originalComment.title}${customSuffix}`,
            content: originalComment.content || '',
            type: originalComponent.type,
            reference_id: newComponentId, // Points back to the component
            table: 'components',
            account: userData?.account_id,
            status: 'created',
            relevantRole: 'CourseBuilder', // Set to CourseBuilder for duplicated comments
            allowmainresponse: originalComment.allowmainresponse || false,
            allowcommentresponse: originalComment.allowcommentresponse || false,
            notifymeonresponses: originalComment.notifymeonresponses || false,
            allowassigneesviewresponses: originalComment.allowassigneesviewresponses || false,
            hideresponsesuntilresponded: originalComment.hideresponsesuntilresponded || false,
            initialnotification: false,
            createddate: null,
            updateddate: null
          };
          
          // Create the new comment with the pre-generated UUID using duplicate endpoint
          await axios.post('/api/comments/component-duplicate', commentData, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
      }
      
      // Step 10: Create assignments for Assignment components
      setSuccess('Creating duplicated assignments...');
      for (const originalComponent of originalComponents) {
        if (originalComponent.type === 'Assignment' && 
            originalComponent.reference_table === 'assignments' && originalComponent.reference_id) {
          
          const originalAssignment = assignmentsMap[originalComponent.reference_id];
          if (!originalAssignment) continue;
          
          // Use the pre-generated UUID from the map - this MUST be the same UUID set as reference_id on the component
          const newAssignmentId = assignmentIdMap.get(originalComponent.reference_id);
          const newComponentId = componentIdMap.get(originalComponent.id);
          
          // Create assignment data following the same structure as createAssignmentsForComponents
          const assignmentData = {
            id: newAssignmentId, // This MUST be the same UUID that was set as reference_id on the component
            title: `${originalAssignment.title}${customSuffix}`,
            gradingresulttype: originalAssignment.gradingresulttype || "Points",
            initialdirections: originalAssignment.initialdirections || [],
            questions: originalAssignment.questions || [],
            hours: originalAssignment.hours || 0,
            maxpoints: originalAssignment.maxpoints || null,
            concentration: originalAssignment.concentration || null,
            otherconcentrations: originalAssignment.otherconcentrations || [],
            taxonomy: originalAssignment.taxonomy || [],
            createdby: userData?.account_id,
            reference_id: newComponentId, // Points back to the component
            reference_table: 'components',
            original: originalAssignment.id, // Set original to the assignment being copied
            createddate: null,
            updateddate: null
          };
          
          // Create the new assignment with the pre-generated UUID using duplicate endpoint
          await axios.post('/api/assignments/component-duplicate', assignmentData, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
      }
      
      // Step 11: Refresh saved courses list
      setSuccess('Course duplicated successfully! Refreshing course list...');
      await loadSavedCourses();
      
      setSuccess(`Course "${originalCourseData.title}" duplicated successfully as "${originalCourseData.title}${customSuffix}"!`);
      setTimeout(() => setSuccess(''), 5000);
      
    } catch (error) {
      console.error('Error duplicating course:', error);
      setError(`Error duplicating course: ${error.message}`);
      setTimeout(() => setError(''), 5000);
    } finally {
      setIsDuplicating(false);
      setActionOverlay(false);
    }
  };

  // Simple status management - no complex validation
  // Components start as Draft and become Active when saved to database
  
  // Enhanced change detection function that distinguishes between component and referenced item changes
  const hasComponentChanged = (currentComponent, originalComponent) => {
    if (!originalComponent) return true; // New component
    
    // For Course components, we use different property names
    const isCourseComponent = currentComponent.type === 'Course' || currentComponent.level === 'top';
    
    if (isCourseComponent) {
      // Course components only have component-level properties
      const propertiesToCompare = [
        'title',
        'type',
        'concentration',
        'otherconcentrations',
        'taxonomy',
        'hourawards',
        'grade_settings',
        'course_track',
        'subject',
        'grade_levels'
      ];
      
      for (const prop of propertiesToCompare) {
        const currentValue = JSON.stringify(currentComponent[prop]);
        const originalValue = JSON.stringify(originalComponent[prop]);
        
        if (currentValue !== originalValue) {
          console.log(`🔄 Course property "${prop}" changed in component ${currentComponent.id}:`, {
            current: currentComponent[prop],
            original: originalComponent[prop]
          });
          return true;
        }
      }
    } else {
      // Regular components can have both component-level and referenced item changes
      
      // For Content, Discussion Board, and Assignment components, title is stored in the referenced item
      // For other components, title is stored in the component record
      const isContentOrDiscussion = currentComponent.type === 'Content' || currentComponent.type === 'Discussion Board';
      const isAssignment = currentComponent.type === 'Assignment';
      const isReferencedItemComponent = isContentOrDiscussion || isAssignment;
      
      // Component-level properties (update the component record)
      const componentProperties = isReferencedItemComponent 
        ? ['text', 'type', 'primaryConcentration', 'otherConcentrations', 'taxonomy', 'hourawards', 'gradeSettings', 'sort_index', 'reference_id']
        : ['text', 'title', 'type', 'primaryConcentration', 'otherConcentrations', 'taxonomy', 'hourawards', 'gradeSettings', 'sort_index'];
      
      // Referenced item properties (update the comment/assignment record)
      const referencedItemProperties = isReferencedItemComponent 
        ? ['title', 'content', 'settings', 'assignments']
        : ['content', 'settings'];
      
      // Check component-level changes
      for (const prop of componentProperties) {
        const currentValue = JSON.stringify(currentComponent[prop]);
        const originalValue = JSON.stringify(originalComponent[prop]);
        
        if (currentValue !== originalValue) {
          console.log(`🔄 Component property "${prop}" changed in component ${currentComponent.id}:`, {
            current: currentComponent[prop],
            original: originalComponent[prop]
          });
          return true;
        }
      }
      
      // Check referenced item changes (only for Content, Discussion Board, and Assignment components)
      if (isReferencedItemComponent) {
        for (const prop of referencedItemProperties) {
          const currentValue = JSON.stringify(currentComponent[prop]);
          const originalValue = JSON.stringify(originalComponent[prop]);
          
          if (currentValue !== originalValue) {
            console.log(`🔄 Referenced item property "${prop}" changed in component ${currentComponent.id}:`, {
              current: currentComponent[prop],
              original: originalComponent[prop]
            });
            return true;
          }
        }
      }
    }
    
    return false;
  };

  // Detailed change analysis function that returns what type of changes occurred
  const analyzeComponentChanges = (currentComponent, originalComponent) => {
    if (!originalComponent) {
      return {
        hasChanges: true,
        isNew: true,
        componentChanged: false,
        referencedItemChanged: false,
        changes: ['new_component']
      };
    }
    
    const isCourseComponent = currentComponent.type === 'Course' || currentComponent.level === 'top';
    const changes = [];
    let componentChanged = false;
    let referencedItemChanged = false;
    
    if (isCourseComponent) {
      // Course components only have component-level properties
      const propertiesToCompare = [
        'title',
        'type',
        'concentration',
        'otherconcentrations',
        'taxonomy',
        'hourawards',
        'grade_settings',
        'course_track',
        'subject',
        'grade_levels'
      ];
      
      for (const prop of propertiesToCompare) {
        const currentValue = JSON.stringify(currentComponent[prop]);
        const originalValue = JSON.stringify(originalComponent[prop]);
        
        if (currentValue !== originalValue) {
          changes.push(`course_${prop}`);
          componentChanged = true;
        }
      }
    } else {
      // Regular components can have both component-level and referenced item changes
      
      // For Content, Discussion Board, and Assignment components, title is stored in the referenced item
      // For other components, title is stored in the component record
      const isContentOrDiscussion = currentComponent.type === 'Content' || currentComponent.type === 'Discussion Board';
      const isAssignment = currentComponent.type === 'Assignment';
      const isReferencedItemComponent = isContentOrDiscussion || isAssignment;
      
      // Component-level properties (update the component record)
      const componentProperties = isReferencedItemComponent 
        ? ['text', 'type', 'primaryConcentration', 'otherConcentrations', 'taxonomy', 'hourawards', 'gradeSettings', 'sort_index', 'reference_id']
        : ['text', 'title', 'type', 'primaryConcentration', 'otherConcentrations', 'taxonomy', 'hourawards', 'gradeSettings', 'sort_index'];
      
      // Referenced item properties (update the comment/assignment record)
      const referencedItemProperties = isReferencedItemComponent 
        ? ['title', 'content', 'settings', 'assignments']
        : ['content', 'settings'];
      
      // Check component-level changes
      for (const prop of componentProperties) {
        const currentValue = JSON.stringify(currentComponent[prop]);
        const originalValue = JSON.stringify(originalComponent[prop]);
        
        if (currentValue !== originalValue) {
          changes.push(`component_${prop}`);
          componentChanged = true;
        }
      }
      
      // Check referenced item changes (only for Content, Discussion Board, and Assignment components)
      if (isReferencedItemComponent) {
        for (const prop of referencedItemProperties) {
          const currentValue = JSON.stringify(currentComponent[prop]);
          const originalValue = JSON.stringify(originalComponent[prop]);
          
          if (currentValue !== originalValue) {
            changes.push(`referenced_${prop}`);
            referencedItemChanged = true;
          }
        }
      }
    }
    
    return {
      hasChanges: changes.length > 0,
      isNew: false,
      componentChanged,
      referencedItemChanged,
      changes
    };
  };

  // Helper function to get a detailed breakdown of changes
  const getChangesBreakdown = () => {
    const analysis = getComponentsChangeAnalysis();
    const breakdown = {
      total: analysis.totalChanges,
      additions: analysis.componentAdditions,
      deletions: analysis.componentDeletions,
      modifications: analysis.componentUpdates + analysis.referencedItemUpdates,
      componentUpdates: analysis.componentUpdates,
      referencedItemUpdates: analysis.referencedItemUpdates,
      courseChanges: analysis.course?.hasChanges ? 1 : 0
    };
    
    // Add detailed breakdown by level
    breakdown.sections = {
      additions: 0,
      deletions: 0,
      modifications: 0
    };
    
    breakdown.subsections = {
      additions: 0,
      deletions: 0,
      modifications: 0
    };
    
    breakdown.nestedSections = {
      additions: 0,
      deletions: 0,
      modifications: 0
    };
    
    // Count changes by level
    analysis.sections.forEach(section => {
      if (section.isNew) {
        breakdown.sections.additions++;
      } else if (section.hasChanges) {
        breakdown.sections.modifications++;
      }
      
      section.subsections.forEach(subsection => {
        if (subsection.isNew) {
          breakdown.subsections.additions++;
        } else if (subsection.hasChanges) {
          breakdown.subsections.modifications++;
        }
        
        subsection.nestedSections.forEach(nestedSection => {
          if (nestedSection.isNew) {
            breakdown.nestedSections.additions++;
          } else if (nestedSection.hasChanges) {
            breakdown.nestedSections.modifications++;
          }
        });
      });
    });
    
    return breakdown;
  };

  // Helper function to count components with actual changes
  const getComponentsWithChangesCount = () => {
    let count = 0;
    
    // Check if course component has changes
    if (courseComponentState && hasComponentChanged(courseComponentState, originalComponentsState?.course)) {
      count++;
    }
    
    // Check for missing components (deletions) by comparing array lengths and structures
    const originalSections = originalComponentsState?.sections || [];
    
    // Check if any sections are missing
    if (sections.length < originalSections.length) {
      count += (originalSections.length - sections.length);
    }
    
    // Check existing sections and their children
    sections.forEach((section, sectionIndex) => {
      const originalSection = originalSections[sectionIndex];
      
      // If this section didn't exist in the original, it's new
      if (!originalSection) {
        count++;
      } else if (hasComponentChanged(section, originalSection)) {
        count++;
      }
      
      // Check subsections
      const originalSubsections = originalSection?.subsections || [];
      if (section.subsections) {
        // Check if any subsections are missing
        if (section.subsections.length < originalSubsections.length) {
          count += (originalSubsections.length - section.subsections.length);
        }
        
        section.subsections.forEach((subsection, subsectionIndex) => {
          const originalSubsection = originalSubsections[subsectionIndex];
          
          // If this subsection didn't exist in the original, it's new
          if (!originalSubsection) {
            count++;
          } else if (hasComponentChanged(subsection, originalSubsection)) {
            count++;
          }
          
          // Check nested sections
          const originalNestedSections = originalSubsection?.nestedSections || [];
          if (subsection.nestedSections) {
            // Check if any nested sections are missing
            if (subsection.nestedSections.length < originalNestedSections.length) {
              count += (originalNestedSections.length - subsection.nestedSections.length);
            }
            
            subsection.nestedSections.forEach((nestedSection, nestedIndex) => {
              const originalNestedSection = originalNestedSections[nestedIndex];
              
              // If this nested section didn't exist in the original, it's new
              if (!originalNestedSection) {
                count++;
              } else if (hasComponentChanged(nestedSection, originalNestedSection)) {
                count++;
              }
            });
          }
        });
      } else if (originalSubsections.length > 0) {
        // All subsections were deleted
        count += originalSubsections.length;
      }
    });
    
    return count;
  };

  // Enhanced function to get detailed change information for all components
  const getComponentsChangeAnalysis = () => {
    const analysis = {
      course: null,
      sections: [],
      totalChanges: 0,
      componentUpdates: 0,
      referencedItemUpdates: 0,
      componentDeletions: 0,
      componentAdditions: 0
    };
    
    // Check course component changes
    if (courseComponentState) {
      analysis.course = analyzeComponentChanges(courseComponentState, originalComponentsState?.course);
      if (analysis.course.hasChanges) {
        analysis.totalChanges++;
        if (analysis.course.componentChanged) analysis.componentUpdates++;
        if (analysis.course.isNew) analysis.componentAdditions++;
      }
    }
    
    // Check for missing components (deletions) by comparing array lengths and structures
    const originalSections = originalComponentsState?.sections || [];
    
    // Check if any sections are missing
    if (sections.length < originalSections.length) {
      const deletedSectionsCount = originalSections.length - sections.length;
      analysis.componentDeletions += deletedSectionsCount;
      analysis.totalChanges += deletedSectionsCount;
    }
    
    // Check existing sections and their children
    sections.forEach((section, sectionIndex) => {
      const originalSection = originalSections[sectionIndex];
      let sectionAnalysis;
      
      // If this section didn't exist in the original, it's new
      if (!originalSection) {
        sectionAnalysis = {
          hasChanges: true,
          isNew: true,
          componentChanged: false,
          referencedItemChanged: false,
          changes: ['new_section']
        };
        analysis.componentAdditions++;
        analysis.totalChanges++;
      } else {
        sectionAnalysis = analyzeComponentChanges(section, originalSection);
      if (sectionAnalysis.hasChanges) {
        analysis.totalChanges++;
        if (sectionAnalysis.componentChanged) analysis.componentUpdates++;
        if (sectionAnalysis.referencedItemChanged) analysis.referencedItemUpdates++;
          if (sectionAnalysis.isNew) analysis.componentAdditions++;
        }
      }
      
      analysis.sections.push({
        ...sectionAnalysis,
        subsections: []
      });
      
      // Check subsections
      const originalSubsections = originalSection?.subsections || [];
      if (section.subsections) {
        // Check if any subsections are missing
        if (section.subsections.length < originalSubsections.length) {
          const deletedSubsectionsCount = originalSubsections.length - section.subsections.length;
          analysis.componentDeletions += deletedSubsectionsCount;
          analysis.totalChanges += deletedSubsectionsCount;
        }
        
        section.subsections.forEach((subsection, subsectionIndex) => {
          const originalSubsection = originalSubsections[subsectionIndex];
          let subsectionAnalysis;
          
          // If this subsection didn't exist in the original, it's new
          if (!originalSubsection) {
            subsectionAnalysis = {
              hasChanges: true,
              isNew: true,
              componentChanged: false,
              referencedItemChanged: false,
              changes: ['new_subsection']
            };
            analysis.componentAdditions++;
            analysis.totalChanges++;
          } else {
            subsectionAnalysis = analyzeComponentChanges(subsection, originalSubsection);
          if (subsectionAnalysis.hasChanges) {
            analysis.totalChanges++;
            if (subsectionAnalysis.componentChanged) analysis.componentUpdates++;
            if (subsectionAnalysis.referencedItemChanged) analysis.referencedItemUpdates++;
              if (subsectionAnalysis.isNew) analysis.componentAdditions++;
            }
          }
          
          analysis.sections[sectionIndex].subsections.push({
            ...subsectionAnalysis,
            nestedSections: []
          });
          
          // Check nested sections
          const originalNestedSections = originalSubsection?.nestedSections || [];
          if (subsection.nestedSections) {
            // Check if any nested sections are missing
            if (subsection.nestedSections.length < originalNestedSections.length) {
              const deletedNestedSectionsCount = originalNestedSections.length - subsection.nestedSections.length;
              analysis.componentDeletions += deletedNestedSectionsCount;
              analysis.totalChanges += deletedNestedSectionsCount;
            }
            
            subsection.nestedSections.forEach((nestedSection, nestedIndex) => {
              const originalNestedSection = originalNestedSections[nestedIndex];
              let nestedAnalysis;
              
              // If this nested section didn't exist in the original, it's new
              if (!originalNestedSection) {
                nestedAnalysis = {
                  hasChanges: true,
                  isNew: true,
                  componentChanged: false,
                  referencedItemChanged: false,
                  changes: ['new_nested_section']
                };
                analysis.componentAdditions++;
                analysis.totalChanges++;
              } else {
                nestedAnalysis = analyzeComponentChanges(nestedSection, originalNestedSection);
              if (nestedAnalysis.hasChanges) {
                analysis.totalChanges++;
                if (nestedAnalysis.componentChanged) analysis.componentUpdates++;
                if (nestedAnalysis.referencedItemChanged) analysis.referencedItemUpdates++;
                  if (nestedAnalysis.isNew) analysis.componentAdditions++;
                }
              }
              
              analysis.sections[sectionIndex].subsections[subsectionIndex].nestedSections.push(nestedAnalysis);
            });
          } else if (originalNestedSections.length > 0) {
            // All nested sections were deleted
            analysis.componentDeletions += originalNestedSections.length;
            analysis.totalChanges += originalNestedSections.length;
          }
        });
      } else if (originalSubsections.length > 0) {
        // All subsections were deleted
        analysis.componentDeletions += originalSubsections.length;
        analysis.totalChanges += originalSubsections.length;
      }
    });
    
    return analysis;
  };
  
  // Wrapper functions to track course-level changes
  const updateCourseTitle = (newTitle) => {
    setCourseTitle(newTitle);
    
    // Update course component state to trigger change detection
    if (courseComponentState) {
      setCourseComponentState(prev => ({
        ...prev,
        title: newTitle
      }));
    }
    
    // Also update currentCourseComponent if it exists
    if (currentCourseComponent) {
      setCurrentCourseComponent(prev => ({
        ...prev,
        title: newTitle
      }));
    }
  };
  
  const updateCourseConcentration = (newConcentration) => {
    setDefaultPrimaryConcentration(newConcentration);
    
    // Update course component state to trigger change detection
    if (courseComponentState) {
      setCourseComponentState(prev => ({
        ...prev,
        concentration: newConcentration
      }));
    }
    
    // Also update currentCourseComponent if it exists
    if (currentCourseComponent) {
      setCurrentCourseComponent(prev => ({
        ...prev,
        concentration: newConcentration
      }));
    }
  };

  const patchTopLevelCourseRow = (patch) => {
    if (courseComponentState) {
      setCourseComponentState((prev) => ({ ...prev, ...patch }));
    }
    if (currentCourseComponent) {
      setCurrentCourseComponent((prev) => ({ ...prev, ...patch }));
    }
  };

  const updateCourseTrack = (track) => {
    const next = track === 'TK12' ? 'TK12' : 'CTE';
    setCourseTrack(next);
    if (next === 'CTE') {
      setTk12Subject('');
      setGradeLevels([]);
      patchTopLevelCourseRow({
        course_track: 'CTE',
        subject: null,
        grade_levels: []
      });
    } else {
      patchTopLevelCourseRow({
        course_track: 'TK12',
        concentration: null,
        otherconcentrations: []
      });
    }
  };

  const updateTk12Subject = (value) => {
    setTk12Subject(value);
    patchTopLevelCourseRow({ subject: value || null });
  };

  const toggleCourseGradeLevel = (code) => {
    const next = sortGradeLevelsSelected(
      gradeLevels.includes(code) ? gradeLevels.filter((g) => g !== code) : [...gradeLevels, code]
    );
    setGradeLevels(next);
    patchTopLevelCourseRow({ grade_levels: next });
  };

  const selectAllCourseGradeLevels = () => {
    const next = [...COURSE_GRADE_LEVEL_OPTIONS];
    setGradeLevels(next);
    patchTopLevelCourseRow({ grade_levels: next });
  };

  const deselectAllCourseGradeLevels = () => {
    setGradeLevels([]);
    patchTopLevelCourseRow({ grade_levels: [] });
  };

  // Default CTE vs General for new courses (same rules as AssignmentBuilder primary pathway tab).
  useEffect(() => {
    if (!pathwayCatalogReady || pathwayRoots.length === 0) return;
    if (searchParams.get('id')) return;
    if (currentCourseId) return;
    if (defaultCourseTrackAppliedRef.current) return;

    if (!hasCtePathways && hasSubjectPathways) {
      defaultCourseTrackAppliedRef.current = true;
      updateCourseTrack('TK12');
      return;
    }
    if (hasCtePathways && !hasSubjectPathways) {
      defaultCourseTrackAppliedRef.current = true;
      updateCourseTrack('CTE');
      return;
    }
    if (!hasCtePathways && !hasSubjectPathways) return;

    if (!userData?.account_id) return;

    defaultCourseTrackAppliedRef.current = true;
    const hasInstrConc = userData.has_instructor_concentration_relationships === true;
    updateCourseTrack(hasInstrConc ? 'CTE' : 'TK12');
  }, [
    pathwayCatalogReady,
    pathwayRoots.length,
    hasCtePathways,
    hasSubjectPathways,
    userData,
    currentCourseId,
    searchParams,
    updateCourseTrack,
  ]);
  
  // Helper function to update component with database values in local sections state
  const updateLocalComponentWithDatabaseData = (componentId, databaseData) => {
    
    setSections(prevSections => {
      const newSections = [...prevSections];
      
      // Search through sections, subsections, and nested sections
      for (let i = 0; i < newSections.length; i++) {
        const section = newSections[i];
        
        // Check if this section matches
        if (section.id === componentId) {
          // Update with database values
          section.status = databaseData.status || section.status;
          section.createddate = databaseData.createddate || section.createddate;
          section.updateddate = databaseData.updateddate || section.updateddate;
          // Map grade_settings from database to gradeSettings in UI state
          if (databaseData.grade_settings !== undefined) {
            section.gradeSettings = databaseData.grade_settings;
          }
          // Preserve reference data
          if (databaseData.reference_table !== undefined) {
            section.reference_table = databaseData.reference_table;
          }
          if (databaseData.reference_id !== undefined) {
            section.reference_id = databaseData.reference_id;
          }
          // Preserve comment data if available
          if (databaseData.commentData) {
            section.commentData = databaseData.commentData;
          }
          

          
          return newSections;
        }
        
        // Check subsections
        if (section.subsections) {
          for (let j = 0; j < section.subsections.length; j++) {
            const subsection = section.subsections[j];
            
            if (subsection.id === componentId) {
              // Update with database values
              subsection.status = databaseData.status || subsection.status;
              subsection.createddate = databaseData.createddate || subsection.createddate;
              subsection.updateddate = databaseData.updateddate || subsection.updateddate;
              // Map grade_settings from database to gradeSettings in UI state
              if (databaseData.grade_settings !== undefined) {
                subsection.gradeSettings = databaseData.grade_settings;
              }
              // Preserve reference data
              if (databaseData.reference_table !== undefined) {
                subsection.reference_table = databaseData.reference_table;
              }
              if (databaseData.reference_id !== undefined) {
                subsection.reference_id = databaseData.reference_id;
              }
              // Preserve comment data if available
              if (databaseData.commentData) {
                subsection.commentData = databaseData.commentData;
              }
              
              // Reset hasChanges flag after successful save
              subsection.hasChanges = false;
              
              return newSections;
            }
            
            // Check nested sections
            if (subsection.nestedSections) {
              for (let k = 0; k < subsection.nestedSections.length; k++) {
                const nestedSection = subsection.nestedSections[k];
                
                if (nestedSection.id === componentId) {
                  // Update with database values
                  nestedSection.status = databaseData.status || nestedSection.status;
                  nestedSection.createddate = databaseData.createddate || nestedSection.createddate;
                  nestedSection.updateddate = databaseData.updateddate || nestedSection.updateddate;
                  // Map grade_settings from database to gradeSettings in UI state
                  if (databaseData.grade_settings !== undefined) {
                    nestedSection.gradeSettings = databaseData.grade_settings;
                  }
                  // Preserve reference data
                  if (databaseData.reference_table !== undefined) {
                    nestedSection.reference_table = databaseData.reference_table;
                  }
                  if (databaseData.reference_id !== undefined) {
                    nestedSection.reference_id = databaseData.reference_id;
                  }
                  // Preserve comment data if available
                  if (databaseData.commentData) {
                    nestedSection.commentData = databaseData.commentData;
                  }
                  
                  // Reset hasChanges flag after successful save
                  nestedSection.hasChanges = false;
                  
                  return newSections;
                }
              }
            }
          }
        }
      }
      
      return newSections;
    });
  };

  // Helper function to update status in local sections state (kept for backward compatibility)
  const updateLocalSectionStatus = (componentId, newStatus) => {
    updateLocalComponentWithDatabaseData(componentId, { status: newStatus });
  };

  // Helper function to determine what needs to be updated based on component status
  const getUpdateTarget = (component) => {
    if (component.status === 'Draft') {
      return 'component'; // Component not in database yet, update component
    } else if (component.status === 'Active' && component.reference_table === 'comments') {
      return 'comment'; // Component in database with comment, update comment
    } else {
      return 'component'; // Component in database without comment, update component
    }
  };


  // Early returns - must be after all hooks (useEffect for load by id) to satisfy rules-of-hooks
  if (concentrationsError) {
    return (
      <div className="fullscreen-overlay" style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        zIndex: 9999
      }}>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '12px',
          textAlign: 'center',
          maxWidth: '500px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
          <h2 style={{ color: '#dc2626', marginBottom: '16px' }}>Critical Error</h2>
          <p style={{ color: '#374151', marginBottom: '24px', fontSize: '16px', lineHeight: '1.5' }}>
            {concentrationsError}
          </p>
          <button 
            onClick={() => window.location.reload()} 
            style={{
              background: '#dc2626',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  if (noPathwaysAvailable) {
    return (
      <div className="fullscreen-overlay" style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        zIndex: 9999
      }}>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '12px',
          textAlign: 'center',
          maxWidth: '520px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
          <h2 style={{ color: '#dc2626', marginBottom: '16px' }}>No pathways available</h2>
          <p style={{ color: '#374151', marginBottom: '24px', fontSize: '16px', lineHeight: '1.5' }}>
            There are no CTE concentrations and no course subjects (General) in taxonomy. Add at least one pathway or subject before building courses.
          </p>
          <button 
            onClick={() => window.location.reload()} 
            style={{
              background: '#dc2626',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  if (concentrationsLoading || (userData?.account_id && courseSubjectsLoading)) {
    return <LoadingSpinner />;
  }

  // Fallback copy function for older browsers
  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        setSuccess('JSON copied to clipboard!');
        setTimeout(() => setSuccess(''), 2000);
      } else {
        setSuccess('Copy failed. Please select and copy manually (Ctrl+C).');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Fallback copy failed:', err);
      setSuccess('Copy failed. Please select and copy manually (Ctrl+C).');
      setTimeout(() => setSuccess(''), 3000);
    }
    
    document.body.removeChild(textArea);
  };

  // Create course function


  return (
    <div className="drag-drop-positioner">
      <div className="header">
        <h1>Course Builder</h1>
        <div className="header-actions">
          <button 
            className="header-action-button"
            onClick={loadDefaultTemplate}
            disabled={!canLoadTemplate()}
            title={canLoadTemplate() 
              ? "Load a default course template with 1 module, 5 lessons, and 5 items per lesson"
              : "Cannot load template: there are unsaved changes or existing components. Save your changes or start a new course first."
            }
          >
            Template
          </button>
          <button 
            className="header-action-button"
            onClick={startNewCourse}
            title="Start a new course"
          >
            New Course
          </button>
          <button 
            className="header-action-button"
            onClick={() => setShowSavedCoursesModal(true)}
            title="View saved courses"
            disabled={!userData?.account_id}
          >
            My Courses
          </button>
          {currentCourseId && (
            <button 
              className="header-action-button"
              onClick={() => window.open(`/course-viewer?command=preview&id=${currentCourseId}`, '_blank')}
              title="View this course in Course Viewer"
            >
              Preview Course
            </button>
          )}
          {/* <button 
            className="header-action-button"
            onClick={() => setShowJsonModal(true)}
            title="Show JSON preview"
          >
            Show JSON
          </button> */}
        </div>
      </div>
      

      
      <div className="course-meta-panel">
        <header className="course-meta-panel__header">
          <div className="course-meta-panel__title-heading-row">
            <label className="course-meta-section__label course-meta-section__label--panel-title" htmlFor="course-meta-title-input">
              Title
            </label>
            {courseCreatorDisplay ? (
              <span className="course-meta-panel__creator" title="Creator of this course (top-level component)">
                <span className="course-meta-section__label course-meta-section__label--inline-meta">Creator</span>
                <span className="course-meta-panel__creator-name">{courseCreatorDisplay}</span>
                {canUpdateCourseCreator && currentCourseId ? (
                  <button
                    type="button"
                    className="course-meta-panel__creator-change-btn"
                    onClick={() => setShowCreatorTransferModal(true)}
                    title="Change course creator"
                  >
                    Change
                  </button>
                ) : null}
              </span>
            ) : null}
          </div>
          <div className="course-meta-panel__title-row">
            <input
              id="course-meta-title-input"
              type="text"
              className="course-meta-panel__title-input"
              placeholder="Title of Course"
              aria-label="Title"
              value={courseTitle}
              onChange={(e) => updateCourseTitle(e.target.value)}
            />
            {courseComponentState && courseComponentState.hasChanges ? (
              <span
                className="course-meta-panel__unsaved-dot"
                title="Unsaved course changes"
                aria-hidden
              >
                ●
              </span>
            ) : null}
          </div>
        </header>

          <div className="course-meta-section course-meta-section--after-title">
            <span className="course-meta-section__label" id="course-type-label">
              Type
            </span>
            <div
              className="course-track-segmented"
              role="group"
              aria-labelledby="course-type-label"
            >
              <button
                type="button"
                className={`course-track-segmented__btn${courseTrack === 'CTE' ? ' course-track-segmented__btn--active' : ''}`}
                onClick={() => updateCourseTrack('CTE')}
                aria-pressed={courseTrack === 'CTE'}
              >
                <span className="course-track-segmented__btn-title">CTE</span>
                <span className="course-track-segmented__btn-desc">Career pathway & concentration</span>
              </button>
              <button
                type="button"
                className={`course-track-segmented__btn${courseTrack === 'TK12' ? ' course-track-segmented__btn--active' : ''}`}
                onClick={() => updateCourseTrack('TK12')}
                aria-pressed={courseTrack === 'TK12'}
              >
                <span className="course-track-segmented__btn-title">General</span>
                <span className="course-track-segmented__btn-desc">
                  Regular K–12 subjects such as Math, English, and similar classes.
                </span>
              </button>
            </div>
          </div>

          {courseTrack === 'CTE' && (
            <div className="course-meta-section course-meta-section--bordered">
              <label className="course-meta-section__label" htmlFor="default-concentration">
                Primary concentration
              </label>
              <p className="course-meta-section__help">
                Pre-selects concentration in taxonomy popups for this course.
              </p>
              <div className="course-meta-field-row">
                <select
                  id="default-concentration"
                  value={defaultPrimaryConcentration}
                  onChange={(e) => updateCourseConcentration(e.target.value)}
                  className="default-concentration-select course-meta-select-grow"
                >
                  <option value="">Select a concentration</option>
                  {concentrations.map((concentration) => (
                    <option key={concentration.id} value={concentration.id}>
                      {concentration.title}
                    </option>
                  ))}
                </select>
                {courseComponentState && courseComponentState.hasChanges ? (
                  <span
                    className="course-meta-change-dot"
                    title="Unsaved course changes"
                    aria-hidden
                  >
                    ●
                  </span>
                ) : null}
              </div>
            </div>
          )}

          {courseTrack === 'TK12' && (
            <div className="course-meta-section course-meta-section--bordered">
              <label className="course-meta-section__label" htmlFor="tk12-subject">
                Subject
              </label>
              <div className="course-meta-subject-row">
                <select
                  id="tk12-subject"
                  value={tk12Subject}
                  onChange={(e) => updateTk12Subject(e.target.value)}
                  className="default-concentration-select course-meta-subject-select"
                  disabled={courseSubjectsLoading && courseSubjectsTaxonomy.length === 0}
                >
                  <option value="">
                    {courseSubjectsLoading ? 'Loading subjects…' : 'Select subject'}
                  </option>
                  {tk12Subject &&
                  !courseSubjectsTaxonomy.some((s) => s.id === tk12Subject) ? (
                    <option value={tk12Subject}>
                      (Not in list) {tk12Subject}
                    </option>
                  ) : null}
                  {courseSubjectsTaxonomy.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.title || item.id}
                    </option>
                  ))}
                </select>
                {canManageCourseSubjects ? (
                  <button
                    type="button"
                    className="btn-course-subject-manage-icon"
                    onClick={() => setShowCourseSubjectManager(true)}
                    title="Manage subject list"
                    aria-label="Manage subject list"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width={17} height={17} aria-hidden>
                      <path d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                      <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </button>
                ) : null}
              </div>
              {courseSubjectsFetchError ? (
                <p className="course-meta-inline-error" role="alert">
                  {courseSubjectsFetchError}
                </p>
              ) : null}

              <div className="course-meta-grade-heading-row">
                <span className="course-meta-section__label course-meta-section__label--grade-row" id="grade-levels-label">
                  Grade levels
                </span>
                <div
                  className="course-grade-preset-tags"
                  role="group"
                  aria-label="Grade level presets"
                >
                  <button
                    type="button"
                    className={`course-grade-preset-tag${gradeLevelsAllSelected ? ' course-grade-preset-tag--active' : ''}`}
                    onClick={selectAllCourseGradeLevels}
                    aria-pressed={gradeLevelsAllSelected}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    className={`course-grade-preset-tag${gradeLevelsNoneSelected ? ' course-grade-preset-tag--active' : ''}`}
                    onClick={deselectAllCourseGradeLevels}
                    aria-pressed={gradeLevelsNoneSelected}
                  >
                    None
                  </button>
                </div>
              </div>
              <div className="course-grade-chip-grid" role="group" aria-labelledby="grade-levels-label">
                {COURSE_GRADE_LEVEL_OPTIONS.map((code) => (
                  <label key={code} className="course-grade-chip course-grade-chip--pill">
                    <input
                      type="checkbox"
                      checked={gradeLevels.includes(code)}
                      onChange={() => toggleCourseGradeLevel(code)}
                    />
                    <span className="course-grade-chip__text">{code}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

        <div className="course-hours-summary course-hours-summary--in-panel">
          <span className="course-hours-summary__total">Total: {courseHoursBreakdown.total}h</span>
          {courseHoursBreakdown.types.map((t) => (
            <span key={t} className="course-hours-summary__part">
              {t}: {courseHoursBreakdown.breakdown[t]}h
            </span>
          ))}
        </div>
      </div>

      <CourseSubjectManagerModal
        isOpen={showCourseSubjectManager && canManageCourseSubjects}
        onClose={() => setShowCourseSubjectManager(false)}
        onSubjectsChanged={fetchCourseSubjectsTaxonomy}
      />

      {showCreatorTransferModal && canUpdateCourseCreator ? (
        <div className="creator-transfer-modal-backdrop" onClick={() => setShowCreatorTransferModal(false)}>
          <div className="creator-transfer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="creator-transfer-modal__header">
              <h3>Change Course Creator</h3>
              <button
                type="button"
                className="creator-transfer-modal__close"
                onClick={() => setShowCreatorTransferModal(false)}
                disabled={creatorTransferSaving}
                aria-label="Close"
              >
                x
              </button>
            </div>
            <p className="creator-transfer-modal__help">
              Search by first name, last name, or email.
            </p>
            <div className="creator-transfer-modal__search-row">
              <input
                type="text"
                value={creatorSearchTerm}
                onChange={(e) => {
                  setCreatorSearchTerm(e.target.value);
                  setCreatorSearchHasRun(false);
                }}
                placeholder="Type at least 2 characters"
                className="creator-transfer-modal__search-input"
              />
              <button
                type="button"
                className="creator-transfer-modal__search-btn"
                onClick={runCreatorSearch}
                disabled={creatorSearchLoading || creatorTransferSaving}
              >
                {creatorSearchLoading ? 'Searching...' : 'Search'}
              </button>
            </div>
            <div className="creator-transfer-modal__results">
              {creatorSearchResults.map((r) => {
                const label = [r.first_name, r.last_name].filter(Boolean).join(' ').trim() || r.primary_email || r.account_id;
                return (
                  <div key={r.account_id} className="creator-transfer-modal__result-row">
                    <div className="creator-transfer-modal__result-main">
                      <strong>{label}</strong>
                      <span>{r.primary_email || 'No email'}</span>
                    </div>
                    <button
                      type="button"
                      className="creator-transfer-modal__select-btn"
                      onClick={() => transferCourseCreator(r.account_id)}
                      disabled={creatorTransferSaving}
                    >
                      Select
                    </button>
                  </div>
                );
              })}
              {!creatorSearchLoading && creatorSearchHasRun && creatorSearchResults.length === 0 ? (
                <p className="creator-transfer-modal__empty">No matching users found.</p>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {/* Success Message */}
      {success && (
        <div style={{
          backgroundColor: '#d4edda',
          color: '#155724',
          padding: '1rem',
          borderRadius: '8px',
          margin: '1rem 0',
          border: '1px solid #c3e6cb',
          textAlign: 'center',
          fontSize: '1rem',
          fontWeight: '500'
        }}>
          {success}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '1rem',
          borderRadius: '8px',
          margin: '1rem 0',
          border: '1px solid #f5c6cb',
          textAlign: 'center',
          fontSize: '1rem',
          fontWeight: '500'
        }}>
          {error}
        </div>
      )}
      
      <div className="sections-container">
        {sections.length === 0 ? (
          <div className="empty-state">
            <p>Click the + button to add sections</p>
            <p>Drag sections to reorder them</p>
            <div className="add-section-container">
              <button className="add-button" onClick={() => handleAddButtonClick('section')}>
                <span>+</span>
              </button>
            </div>
          </div>
        ) : (
          <>
            {sections.map((section, index) => (
                              <div
                  key={section.id}
                  ref={index === draggedIndex ? dragRef : null}
                  className={`section ${draggedIndex === index ? 'dragging' : ''} ${
                    dragOverIndex === index ? 'drag-over' : ''
                  }`}
                  style={{
                    order: index
                  }}
                  data-section={index}
                >
                                  <div className="section-header">
                    <button 
                      className="drag-handle"
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                      title="Drag to reorder"
                    >
                      ⋮⋮
                    </button>
                    <span className="section-type">
                      {section.type === 'Module' && '📚 '}
                      {section.type === 'Lesson' && '📖 '}
                      {section.type === 'Assignment' && '📝 '}
                      {section.type === 'Discussion Board' && '💬 '}
                      {section.type === 'Content' && '📄 '}
                      {section.type}
                    </span>
                    <button 
                      className="remove-button"
                      onClick={() => removeSection(index)}
                      title="Remove section"
                    >
                      ×
                    </button>
                  </div>
                <div className="section-content">
                  <div className="section-left">
                    {section.type === 'Module' && (
                      <button 
                        className="expand-button"
                        onClick={() => toggleExpand(index)}
                        title={section.expanded ? "Collapse subsections" : "Expand subsections"}
                      >
                        {section.expanded ? '▼' : '▶'}
                      </button>
                    )}
                    {section.type === 'Lesson' && (
                      <button 
                        className="expand-button"
                        onClick={() => toggleExpand(index)}
                        title={section.expanded ? "Collapse items" : "Expand items"}
                      >
                        {section.expanded ? '▼' : '▶'}
                      </button>
                    )}
                    <input
                      type="text"
                      className="section-input"
                      placeholder="Enter title here..."
                      value={section.text}
                      onChange={(e) => updateSectionText(index, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    
                                         {/* Component Status Indicator - Only show for Draft status */}
                     {section.status === 'Draft' && (
                       <div className="component-status" style={{
                         display: 'flex',
                         alignItems: 'center',
                         gap: '4px',
                         padding: '2px 6px',
                         backgroundColor: '#f59e0b',
                         color: 'white',
                         borderRadius: '4px',
                         fontSize: '11px',
                         fontWeight: '500',
                         marginLeft: '8px'
                       }}>
                         <span>⚠</span>
                         <span>Draft</span>
                       </div>
                     )}
                  </div>
                  
                  {/* Content Preview for Content components */}
                  {section.type === 'Content' && (
                    <div className="content-editor-container">
                      <div className="content-header">
                        <label className="content-label">
                          Content: {section.title && <em style={{ color: '#666' }}>{section.title}</em>}
                        </label>
                        <div className="editor-controls">
                          {section.content && section.content.length > 0 && (
                            <>
                              <HourAwardsTooltip>
                              <button 
                                className="hour-awards-button"
                                onClick={() => openHourAwardsModal('section', index)}
                                title=""
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '16px',
                                  color: '#1D4E89',
                                  marginRight: '8px'
                                }}
                              >
                                ⌚
                              </button>
                              </HourAwardsTooltip>
                              <TaxonomyTooltip>
                            <button 
                              className="taxonomy-button"
                                  onClick={() => openTaxonomyModal('section', index)}
                              title="Open taxonomy selector"
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '16px',
                                color: '#1D4E89',
                                marginRight: '8px'
                              }}
                            >
                              <img src="https://acuitycte-files.s3.us-west-2.amazonaws.com/public/miscellaneous/taxonomy.png" alt="Taxonomy" style={{ width: '16px', height: '16px' }} />
                            </button>
                              </TaxonomyTooltip>
                            </>
                          )}
                          <button 
                            className="fullscreen-button"
                            onClick={() => openContentEditor({
                              type: 'section',
                              index: index,
                              content: section.content || [],
                              title: section.title || '',
                              updateFunction: (content, title) => updateSectionContent(index, content, title)
                            })}
                            title="Open editor to edit content"
                          >
                            ✏️
                          </button>
                          <button 
                            className="editor-toggle-button"
                            onClick={() => toggleEditorExpansion(`section-${index}`)}
                            title={expandedEditors[`section-${index}`] ? "Minimize preview" : "Expand preview"}
                          >
                            {expandedEditors[`section-${index}`] ? '−' : '+'}
                          </button>
                        </div>
                      </div>
                      <div className={`rich-text-container ${expandedEditors[`section-${index}`] ? 'expanded' : 'collapsed'}`}>
                        <div className="content-preview">
                          {section.content && section.content.length > 0 ? (
                            section.content.map((item, itemIndex) => (
                              <div key={item._id || itemIndex} style={{ marginBottom: itemIndex < section.content.length - 1 ? 16 : 0 }}>
                                {item.element === 'richText' && <div dangerouslySetInnerHTML={{ __html: item.value }} />}
                                {item.element === 'image' && renderImageFromTag(item.value)}
                                {item.element === 'video' && renderVideoFromUrl(item.value)}
                                {item.element === 'embed' && <div dangerouslySetInnerHTML={{ __html: item.value }} />}
                              </div>
                            ))
                          ) : (
                            <div style={{ color: '#666', fontStyle: 'italic' }}>No content added yet. Click the edit button ✏️ to add content.</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Discussion Board Preview for Discussion Board sections */}
                  {section.type === 'Discussion Board' && (
                    <div className="content-editor-container">
                      <div className="content-header">
                        <label className="content-label">
                          Discussion Prompt: {section.title && <em style={{ color: '#666' }}>{section.title}</em>}
                        </label>
                        <div className="editor-controls">
                          {section.content && section.content.length > 0 && (
                            <>
                              <HourAwardsTooltip>
                              <button 
                                className="hour-awards-button"
                                onClick={() => openHourAwardsModal('section', index)}
                                title=""
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '16px',
                                  color: '#1D4E89',
                                  marginRight: '8px'
                                }}
                              >
                                ⌚
                              </button>
                              </HourAwardsTooltip>
                              <TaxonomyTooltip>
                                <button 
                                  className="taxonomy-button"
                                  onClick={() => openTaxonomyModal('section', index)}
                                  title="Open taxonomy selector"
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    color: '#1D4E89',
                                    marginRight: '8px'
                                  }}
                                >
                                  <img src="https://acuitycte-files.s3.us-west-2.amazonaws.com/public/miscellaneous/taxonomy.png" alt="Taxonomy" style={{ width: '16px', height: '16px' }} />
                                </button>
                              </TaxonomyTooltip>
                            </>
                          )}
                          <button 
                            className="fullscreen-button"
                            onClick={() => openDiscussionBoardEditor({
                              type: 'section',
                              sectionIndex: index,
                              content: section.content || [],
                              title: section.title || '',
                              settings: section.settings || { ...DEFAULT_DISCUSSION_BOARD_SETTINGS },
                              updateFunction: async (content, settings, title) => {
                                const newSections = [...sections];
                                newSections[index].content = content;
                                newSections[index].settings = settings;
                                newSections[index].discussionboard_settings = settings;
                                newSections[index].title = title;
                                
                                // For Active components, also update the comment in the database
                                if (section.status === 'Active' && section.reference_id) {
                                  try {
                                    const token = localStorage.getItem('token');
                                    await axios.put(`/api/comments/${section.reference_id}`, {
                                      content: JSON.stringify(content),
                                      title: title,
                                      settings: settings
                                    }, {
                                      headers: { Authorization: `Bearer ${token}` }
                                    });
                                    
                                    // Update the commentData with the new settings
                                    if (newSections[index].commentData) {
                                      newSections[index].commentData = {
                                        ...newSections[index].commentData,
                                        content: JSON.stringify(content),
                                        title: title,
                                        allowmainresponse: settings.allowMainResponse,
                                        allowcommentresponse: settings.allowCommentResponse,
                                        notifymeonresponses: settings.notifyMeOnResponses,
                                        allowassigneesviewresponses: settings.allowAssigneesViewResponses,
                                        hideresponsesuntilresponded: settings.hideresponsesuntilresponded
                                      };
                                    }
                                  } catch (error) {
                                    console.error('Error updating comment settings:', error);
                                  }
                                }
                                
                                setSections(newSections);
                              }
                            })}
                            title="Open editor to edit discussion board"
                          >
                            ✏️
                          </button>
                          <button 
                            className="editor-toggle-button"
                            onClick={() => toggleEditorExpansion(`section-${index}`)}
                            title={expandedEditors[`section-${index}`] ? "Minimize preview" : "Expand preview"}
                          >
                            {expandedEditors[`section-${index}`] ? '−' : '+'}
                          </button>
                        </div>
                      </div>
                      <div className={`rich-text-container ${expandedEditors[`section-${index}`] ? 'expanded' : 'collapsed'}`}>
                        <div className="content-preview">
                          {section.content && section.content.length > 0 ? (
                            section.content.map((item, itemIndex) => (
                              <div key={item._id || itemIndex} style={{ marginBottom: itemIndex < section.content.length - 1 ? 16 : 0 }}>
                                {item.element === 'richText' && <div dangerouslySetInnerHTML={{ __html: item.value }} />}
                                {item.element === 'image' && renderImageFromTag(item.value)}
                                {item.element === 'video' && renderVideoFromUrl(item.value)}
                                {item.element === 'embed' && <div dangerouslySetInnerHTML={{ __html: item.value }} />}
                              </div>
                            ))
                          ) : (
                            <div style={{ color: '#666', fontStyle: 'italic' }}>No discussion prompt added yet. Click the edit button ✏️ to add content.</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Assignment Preview for sections */}
                  {section.type === 'Assignment' && (
                    <div className="content-editor-container">
                      <div className="content-header">
                        <label className="content-label">Selected Assignment:</label>
                        <div className="editor-controls">
                          {section.assignments && section.assignments.length > 0 && (
                            <>
                              <HourAwardsTooltip>
                              <button 
                                className="hour-awards-button"
                                onClick={() => openHourAwardsModal('section', index)}
                                title=""
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '16px',
                                  color: '#1D4E89',
                                  marginRight: '8px'
                                }}
                              >
                                ⌚
                              </button>
                              </HourAwardsTooltip>
                              <TaxonomyTooltip>
                            <button
                              className="taxonomy-button"
                                  onClick={() => openTaxonomyModal('section', index)}
                              title="Open taxonomy selector"
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '16px',
                                color: '#1D4E89',
                                marginRight: '8px'
                              }}
                            >
                              <img src="https://acuitycte-files.s3.us-west-2.amazonaws.com/public/miscellaneous/taxonomy.png" alt="Taxonomy" style={{ width: '16px', height: '16px' }} />
                            </button>
                              </TaxonomyTooltip>
                            </>
                          )}
                          <button 
                            className="fullscreen-button"
                            onClick={() => openAssignmentModal(index, null, null, null)}
                            title="Select assignment"
                          >
                            ✏️
                          </button>
                          {section.assignments && section.assignments.length > 0 && section.reference_id && (
                            <button
                              onClick={() => openAssignmentPreview(section.reference_id)}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '16px',
                                color: '#1D4E89',
                                marginLeft: '8px'
                              }}
                              title="Edit in Assignment Builder"
                            >
                              ⇗
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="assignment-preview">
                        {section.assignments && section.assignments.length > 0 ? (
                          <div className="selected-assignments">
                            {section.assignments.map((assignment, assignmentIndex) => (
                              <div key={assignment.id || assignmentIndex} className="assignment-item">
                                <span className="assignment-title">{assignment.title}</span>
                                <span className="assignment-type">{assignment.type}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="cb-assignment-empty-hint">No assignment selected. Click the pencil to open the list, then click an assignment to select it.</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Subsections - Only show for Modules */}
                {section.type === 'Module' && section.expanded && (
                  <div className="subsections-container">
                    {section.subsections.map((subsection, subIndex) => (
                      <div 
                        key={subsection.id} 
                        className={`subsection ${
                          draggedSubsection?.sectionIndex === index && 
                          draggedSubsection?.subsectionIndex === subIndex ? 'dragging' : ''
                        } ${
                          dragOverSubsection?.sectionIndex === index && 
                          dragOverSubsection?.subsectionIndex === subIndex ? 'drag-over' : ''
                        }`}
                        data-section={index}
                        data-subsection={subIndex}
                      >
                        <div className="subsection-header">
                          <button 
                            className="drag-handle-subsection"
                            draggable
                            onDragStart={(e) => handleSubsectionDragStart(e, index, subIndex)}
                            onDragOver={(e) => handleSubsectionDragOver(e, index, subIndex)}
                            onDrop={(e) => handleSubsectionDrop(e, index, subIndex)}
                            onDragEnd={handleDragEnd}
                            title="Drag to reorder subsection"
                          >
                            ⋮⋮
                          </button>
                          <span className="section-type">
                            {subsection.type === 'Module' && '📚 '}
                            {subsection.type === 'Lesson' && '📖 '}
                            {subsection.type === 'Assignment' && '📝 '}
                            {subsection.type === 'Discussion Board' && '💬 '}
                            {subsection.type === 'Content' && '📄 '}
                            {subsection.type}
                          </span>
                          <button 
                            className="remove-subsection-button"
                            onClick={() => removeSubsection(index, subIndex)}
                            title="Remove subsection"
                          >
                            ×
                          </button>
                        </div>
                        <div className="subsection-content">
                          <div className="subsection-left">
                            {subsection.type === 'Lesson' && (
                              <button 
                                className="expand-button-subsection"
                                onClick={() => toggleSubsectionExpand(index, subIndex)}
                                title={subsection.expanded ? "Collapse nested sections" : "Expand nested sections"}
                              >
                                {subsection.expanded ? '▼' : '▶'}
                              </button>
                            )}
                            <input
                              type="text"
                              className="subsection-input"
                              placeholder="Enter title here..."
                              value={subsection.text}
                              onChange={(e) => updateSubsectionText(index, subIndex, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            
                                                         {/* Component Status Indicator - Only show for Draft status */}
                             {subsection.status === 'Draft' && (
                               <div className="component-status" style={{
                                 display: 'flex',
                                 alignItems: 'center',
                                 gap: '4px',
                                 padding: '2px 6px',
                                 backgroundColor: '#f59e0b',
                                 color: 'white',
                                 borderRadius: '4px',
                                 fontSize: '11px',
                                 fontWeight: '500',
                                 marginLeft: '8px'
                               }}>
                                 <span>⚠</span>
                                 <span>Draft</span>
                               </div>
                             )}
                          </div>
                          
                          {/* Content Preview for Content components */}
                          {subsection.type === 'Content' && (
                            <div className="content-editor-container">
                              <div className="content-header">
                                <label className="content-label">
                                  Content: {subsection.title && <em style={{ color: '#666' }}>{subsection.title}</em>}
                                </label>
                                <div className="editor-controls">
                                  {subsection.content && subsection.content.length > 0 && (
                                    <>
                                      <HourAwardsTooltip>
                                      <button 
                                        className="hour-awards-button"
                                        onClick={() => openHourAwardsModal('subsection', index, subIndex)}
                                        title=""
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          cursor: 'pointer',
                                          fontSize: '16px',
                                          color: '#1D4E89',
                                          marginRight: '8px'
                                        }}
                                      >
                                        ⌚
                                      </button>
                                      </HourAwardsTooltip>
                                      <TaxonomyTooltip>
                                    <button 
                                      className="taxonomy-button"
                                          onClick={() => openTaxonomyModal('subsection', index, subIndex)}
                                      title="Open taxonomy selector"
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        color: '#1D4E89',
                                        marginRight: '8px'
                                      }}
                                    >
                                      <img src="https://acuitycte-files.s3.us-west-2.amazonaws.com/public/miscellaneous/taxonomy.png" alt="Taxonomy" style={{ width: '16px', height: '16px' }} />
                                    </button>
                                      </TaxonomyTooltip>
                                    </>
                                  )}
                                  <button 
                                    className="fullscreen-button"
                                    onClick={() => openContentEditor({
                                      type: 'subsection',
                                      sectionIndex: index,
                                      subsectionIndex: subIndex,
                                      content: subsection.content || [],
                              title: subsection.title || '',
                              updateFunction: (content, title) => updateSubsectionContent(index, subIndex, content, title)
                                    })}
                                    title="Open editor to edit content"
                                  >
                                    ✏️
                                  </button>
                                  <button 
                                    className="editor-toggle-button"
                                    onClick={() => toggleEditorExpansion(`subsection-${index}-${subIndex}`)}
                                    title={expandedEditors[`subsection-${index}-${subIndex}`] ? "Minimize preview" : "Expand preview"}
                                  >
                                    {expandedEditors[`subsection-${index}-${subIndex}`] ? '−' : '+'}
                                  </button>
                                </div>
                              </div>
                              <div className={`rich-text-container ${expandedEditors[`subsection-${index}-${subIndex}`] ? 'expanded' : 'collapsed'}`}>
                                <div className="content-preview">
                                  {subsection.content && subsection.content.length > 0 ? (
                                    subsection.content.map((item, itemIndex) => (
                                      <div key={item._id || itemIndex} style={{ marginBottom: itemIndex < subsection.content.length - 1 ? 16 : 0 }}>
                                        {item.element === 'richText' && <div dangerouslySetInnerHTML={{ __html: item.value }} />}
                                        {item.element === 'image' && renderImageFromTag(item.value)}
                                        {item.element === 'video' && renderVideoFromUrl(item.value)}
                                      </div>
                                    ))
                                  ) : (
                                    <div style={{ color: '#666', fontStyle: 'italic' }}>No content added yet. Click the edit button ✏️ to add content.</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Discussion Board Preview */}
                          {subsection.type === 'Discussion Board' && (
                            <div className="content-editor-container">
                              <div className="content-header">
                                <label className="content-label">
                                  Discussion Prompt: {subsection.title && <em style={{ color: '#666' }}>{subsection.title}</em>}
                                </label>
                                <div className="editor-controls">
                                  {subsection.content && subsection.content.length > 0 && (
                                    <>
                                      <HourAwardsTooltip>
                                      <button 
                                        className="hour-awards-button"
                                        onClick={() => openHourAwardsModal('subsection', index, subIndex)}
                                        title=""
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          cursor: 'pointer',
                                          fontSize: '16px',
                                          color: '#1D4E89',
                                          marginRight: '16px'
                                        }}
                                      >
                                        ⌚
                                      </button>
                                      </HourAwardsTooltip>
                                      <TaxonomyTooltip>
                                    <button 
                                      className="taxonomy-button"
                                          onClick={() => openTaxonomyModal('subsection', index, subIndex)}
                                      title="Open taxonomy selector"
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        color: '#1D4E89',
                                        marginRight: '8px'
                                      }}
                                    >
                                      <img src="https://acuitycte-files.s3.us-west-2.amazonaws.com/public/miscellaneous/taxonomy.png" alt="Taxonomy" style={{ width: '16px', height: '16px' }} />
                                    </button>
                                      </TaxonomyTooltip>
                                    </>
                                  )}
                                  <button 
                                    className="fullscreen-button"
                                    onClick={() => openDiscussionBoardEditor({
                                      type: 'subsection',
                                      sectionIndex: index,
                                      subsectionIndex: subIndex,
                                      content: subsection.content || [],
                                      title: subsection.title || '',
                                      settings: subsection.settings || { ...DEFAULT_DISCUSSION_BOARD_SETTINGS },
                                      updateFunction: async (content, settings, title) => {
                                        const newSections = [...sections];
                                        newSections[index].subsections[subIndex].content = content;
                                        newSections[index].subsections[subIndex].settings = settings;
                                        newSections[index].subsections[subIndex].discussionboard_settings = settings;
                                        newSections[index].subsections[subIndex].title = title;
                                        
                                        // For Active components, also update the comment in the database
                                        if (subsection.status === 'Active' && subsection.reference_id) {
                                          try {
                                            const token = localStorage.getItem('token');
                                            await axios.put(`/api/comments/${subsection.reference_id}`, {
                                              content: JSON.stringify(content),
                                              title: title,
                                              settings: settings
                                            }, {
                                              headers: { Authorization: `Bearer ${token}` }
                                            });
                                            
                                            // Update the commentData with the new settings
                                            if (newSections[index].subsections[subIndex].commentData) {
                                              newSections[index].subsections[subIndex].commentData = {
                                                ...newSections[index].subsections[subIndex].commentData,
                                                content: JSON.stringify(content),
                                                title: title,
                                                allowmainresponse: settings.allowMainResponse,
                                                allowcommentresponse: settings.allowCommentResponse,
                                                notifymeonresponses: settings.notifyMeOnResponses,
                                                allowassigneesviewresponses: settings.allowAssigneesViewResponses,
                                                hideresponsesuntilresponded: settings.hideresponsesuntilresponded
                                              };
                                            }
                                          } catch (error) {
                                            console.error('Error updating comment settings:', error);
                                          }
                                        }
                                        
                                        setSections(newSections);
                                      }
                                    })}
                                    title="Open editor to edit discussion board"
                                  >
                                    ✏️
                                  </button>
                                  <button 
                                    className="editor-toggle-button"
                                    onClick={() => toggleEditorExpansion(`subsection-${index}-${subIndex}`)}
                                    title={expandedEditors[`subsection-${index}-${subIndex}`] ? "Minimize preview" : "Expand preview"}
                                  >
                                    {expandedEditors[`subsection-${index}-${subIndex}`] ? '−' : '+'}
                                  </button>
                                </div>
                              </div>
                              <div className={`rich-text-container ${expandedEditors[`subsection-${index}-${subIndex}`] ? 'expanded' : 'collapsed'}`}>
                                <div className="content-preview">
                                  {subsection.content && subsection.content.length > 0 ? (
                                    subsection.content.map((item, itemIndex) => (
                                      <div key={item._id || itemIndex} style={{ marginBottom: itemIndex < subsection.content.length - 1 ? 16 : 0 }}>
                                        {item.element === 'richText' && <div dangerouslySetInnerHTML={{ __html: item.value }} />}
                                        {item.element === 'image' && renderImageFromTag(item.value)}
                                        {item.element === 'video' && renderVideoFromUrl(item.value)}
                                      </div>
                                    ))
                                  ) : (
                                    <div style={{ color: '#666', fontStyle: 'italic' }}>No discussion prompt added yet. Click the edit button ✏️ to add content.</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Assignment Preview for subsections */}
                          {subsection.type === 'Assignment' && (
                            <div className="content-editor-container">
                              <div className="content-header">
                                <label className="content-label">Selected Assignment: </label>
                                <div className="editor-controls">
                                  {subsection.assignments && subsection.assignments.length > 0 && (
                                    <>
                                        <HourAwardsTooltip>
                                      <button 
                                        className="hour-awards-button"
                                        onClick={() => openHourAwardsModal('subsection', index, subIndex)}
                                        title=""
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          cursor: 'pointer',
                                          fontSize: '16px',
                                          color: '#1D4E89',
                                          marginRight: '8px'
                                        }}
                                      >
                                        ⌚
                                      </button>
                                      </HourAwardsTooltip>
                                      <TaxonomyTooltip>
                                    <button
                                      className="taxonomy-button"
                                          onClick={() => openTaxonomyModal('subsection', index, subIndex)}
                                      title="Open taxonomy selector"
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        color: '#1D4E89',
                                        marginRight: '8px'
                                      }}
                                    >
                                      <img src="https://acuitycte-files.s3.us-west-2.amazonaws.com/public/miscellaneous/taxonomy.png" alt="Taxonomy" style={{ width: '16px', height: '16px' }} />
                                    </button>
                                      </TaxonomyTooltip>
                                    </>
                                  )}
                                  <button 
                                    className="fullscreen-button"
                                    onClick={() => openAssignmentModal(index, subIndex, null, null)}
                                    title="Select assignment"
                                  >
                                    ✏️
                                  </button>
                                  {subsection.assignments && subsection.assignments.length > 0 && subsection.reference_id && (
                                    <button
                                      onClick={() => openAssignmentPreview(subsection.reference_id)}
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        color: '#1D4E89',
                                        marginLeft: '8px'
                                      }}
                                      title="Edit in Assignment Builder"
                                    >
                                      ⇗
                                    </button>
                                  )}
                                </div>
                              </div>
                              <div className="assignment-preview">
                                {subsection.assignments && subsection.assignments.length > 0 ? (
                                  <div className="selected-assignments">
                                    {subsection.assignments.map((assignment, assignmentIndex) => (
                                      <div key={assignment.id || assignmentIndex} className="assignment-item">
                                        <span className="assignment-title">{assignment.title}</span>
                                        <span className="assignment-type">{assignment.type}</span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="cb-assignment-empty-hint">No assignment selected. Click the pencil to open the list, then click an assignment to select it.</div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Nested Sections - Only show for Lessons */}
                        {subsection.type === 'Lesson' && subsection.expanded && (
                          <div className="nested-sections-container">
                            {subsection.nestedSections.map((nestedSection, nestedIndex) => (
                              <div 
                                key={nestedSection.id} 
                                className={`nested-section ${
                                  draggedNestedSection?.sectionIndex === index && 
                                  draggedNestedSection?.subsectionIndex === subIndex &&
                                  draggedNestedSection?.nestedSectionIndex === nestedIndex ? 'dragging' : ''
                                } ${
                                  dragOverNestedSection?.sectionIndex === index && 
                                  dragOverNestedSection?.subsectionIndex === subIndex &&
                                  dragOverNestedSection?.nestedSectionIndex === nestedIndex ? 'drag-over' : ''
                                }`}
                                data-section={index}
                                data-subsection={subIndex}
                                data-nested={nestedIndex}
                              >
                                <div className="nested-section-header">
                                  <button 
                                    className="drag-handle-nested-section"
                                    draggable
                                    onDragStart={(e) => handleNestedSectionDragStart(e, index, subIndex, nestedIndex)}
                                    onDragOver={(e) => handleNestedSectionDragOver(e, index, subIndex, nestedIndex)}
                                    onDrop={(e) => handleNestedSectionDrop(e, index, subIndex, nestedIndex)}
                                    onDragEnd={handleDragEnd}
                                    title="Drag to reorder nested section"
                                  >
                                    ⋮⋮
                                  </button>
                                  <span className="section-type">
                                    {nestedSection.type === 'Module' && '📚 '}
                                    {nestedSection.type === 'Lesson' && '📖 '}
                                    {nestedSection.type === 'Assignment' && '📝 '}
                                    {nestedSection.type === 'Discussion Board' && '💬 '}
                                    {nestedSection.type === 'Content' && '📄 '}
                                    {nestedSection.type}
                                  </span>
                                  <button 
                                    className="remove-nested-section-button"
                                    onClick={() => removeNestedSection(index, subIndex, nestedIndex)}
                                    title="Remove nested section"
                                  >
                                    ×
                                  </button>
                                </div>
                                <div className="nested-section-content">
                                                                  <div className="nested-section-left">
                                  <input
                                    type="text"
                                    className="nested-section-input"
                                    placeholder="Enter title here..."
                                    value={nestedSection.text}
                                    onChange={(e) => updateNestedSectionText(index, subIndex, nestedIndex, e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  
                                                                     {/* Component Status Indicator - Only show for Draft status */}
                                   {nestedSection.status === 'Draft' && (
                                     <div className="component-status" style={{
                                       display: 'flex',
                                       alignItems: 'center',
                                       gap: '4px',
                                       padding: '2px 6px',
                                       backgroundColor: '#f59e0b',
                                       color: 'white',
                                       borderRadius: '4px',
                                       fontSize: '11px',
                                       fontWeight: '500',
                                       marginLeft: '8px'
                                     }}>
                                       <span>⚠</span>
                                       <span>Draft</span>
                                     </div>
                                   )}
                                </div>
                                  
                                  {/* Content Preview for Content components */}
                                  {nestedSection.type === 'Content' && (
                                    <div className="content-editor-container">
                                      <div className="content-header">
                                        <label className="content-label">
                                          Content: {nestedSection.title && <em style={{ color: '#666' }}>{nestedSection.title}</em>}
                                        </label>
                                        <div className="editor-controls">
                                          {nestedSection.content && nestedSection.content.length > 0 && (
                                            <>
                                              <HourAwardsTooltip>
                                              <button 
                                                className="hour-awards-button"
                                                onClick={() => openHourAwardsModal('nested', index, subIndex, nestedIndex)}
                                                title=""
                                                style={{
                                                  background: 'none',
                                                  border: 'none',
                                                  cursor: 'pointer',
                                                  fontSize: '16px',
                                                  color: '#1D4E89',
                                                  marginRight: '8px'
                                                }}
                                              >
                                                ⌚
                                              </button>
                                              </HourAwardsTooltip>
                                              <TaxonomyTooltip>
                                            <button 
                                              className="taxonomy-button"
                                                  onClick={() => openTaxonomyModal('nested', index, subIndex, nestedIndex)}
                                              title="Open taxonomy selector"
                                              style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: '16px',
                                                color: '#1D4E89',
                                                marginRight: '8px'
                                              }}
                                            >
                                              <img src="https://acuitycte-files.s3.us-west-2.amazonaws.com/public/miscellaneous/taxonomy.png" alt="Taxonomy" style={{ width: '16px', height: '16px' }} />
                                            </button>
                                              </TaxonomyTooltip>
                                            </>
                                          )}
                                          <button 
                                            className="fullscreen-button"
                                            onClick={() => openContentEditor({
                                              type: 'nested',
                                              sectionIndex: index,
                                              subsectionIndex: subIndex,
                                              nestedIndex: nestedIndex,
                                              content: nestedSection.content || [],
                              title: nestedSection.title || '',
                              updateFunction: (content, title) => updateNestedSectionContent(index, subIndex, nestedIndex, content, title)
                                            })}
                                            title="Open editor to edit content"
                                          >
                                            ✏️
                                          </button>
                                          <button 
                                            className="editor-toggle-button"
                                            onClick={() => toggleEditorExpansion(`nested-${index}-${subIndex}-${nestedIndex}`)}
                                            title={expandedEditors[`nested-${index}-${subIndex}-${nestedIndex}`] ? "Minimize preview" : "Expand preview"}
                                          >
                                            {expandedEditors[`nested-${index}-${subIndex}-${nestedIndex}`] ? '−' : '+'}
                                          </button>
                                        </div>
                                      </div>
                                      <div className={`rich-text-container ${expandedEditors[`nested-${index}-${subIndex}-${nestedIndex}`] ? 'expanded' : 'collapsed'}`}>
                                        <div className="content-preview">
                                          {nestedSection.content && nestedSection.content.length > 0 ? (
                                            nestedSection.content.map((item, itemIndex) => (
                                              <div key={item._id || itemIndex} style={{ marginBottom: itemIndex < nestedSection.content.length - 1 ? 16 : 0 }}>
                                                {item.element === 'richText' && <div dangerouslySetInnerHTML={{ __html: item.value }} />}
                                                {item.element === 'image' && renderImageFromTag(item.value)}
                                                {item.element === 'video' && renderVideoFromUrl(item.value)}
                                              </div>
                                            ))
                                          ) : (
                                            <div style={{ color: '#666', fontStyle: 'italic' }}>No content added yet. Click the edit button ✏️ to add content.</div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Discussion Board Preview for nested sections */}
                                  {nestedSection.type === 'Discussion Board' && (
                                    <div className="content-editor-container">
                                      <div className="content-header">
                                        <label className="content-label">
                                          Discussion Prompt: {nestedSection.title && <em style={{ color: '#666' }}>{nestedSection.title}</em>}
                                        </label>
                                        <div className="editor-controls">
                                          {nestedSection.content && nestedSection.content.length > 0 && (
                                            <>
                                              <HourAwardsTooltip>
                                              <button 
                                                className="hour-awards-button"
                                                onClick={() => openHourAwardsModal('nested', index, subIndex, nestedIndex)}
                                                title=""
                                                style={{
                                                  background: 'none',
                                                  border: 'none',
                                                  cursor: 'pointer',
                                                  fontSize: '16px',
                                                  color: '#1D4E89',
                                                  marginRight: '8px'
                                                }}
                                              >
                                                ⌚
                                              </button>
                                              </HourAwardsTooltip>
                                              <TaxonomyTooltip>
                                            <button 
                                              className="taxonomy-button"
                                                  onClick={() => openTaxonomyModal('nested', index, subIndex, nestedIndex)}
                                              title="Open taxonomy selector"
                                              style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: '16px',
                                                color: '#1D4E89',
                                                marginRight: '8px'
                                              }}
                                            >
                                              <img src="https://acuitycte-files.s3.us-west-2.amazonaws.com/public/miscellaneous/taxonomy.png" alt="Taxonomy" style={{ width: '16px', height: '16px' }} />
                                            </button>
                                              </TaxonomyTooltip>
                                            </>
                                          )}
                                          <button 
                                            className="fullscreen-button"
                                            onClick={() => openDiscussionBoardEditor({
                                              type: 'nested',
                                              sectionIndex: index,
                                              subsectionIndex: subIndex,
                                              nestedIndex: nestedIndex,
                                              content: nestedSection.content || [],
                                              title: nestedSection.title || '',
                                              settings: nestedSection.settings || { ...DEFAULT_DISCUSSION_BOARD_SETTINGS },
                                              updateFunction: async (content, settings, title) => {
                                                const newSections = [...sections];
                                                newSections[index].subsections[subIndex].nestedSections[nestedIndex].content = content;
                                                newSections[index].subsections[subIndex].nestedSections[nestedIndex].settings = settings;
                                                newSections[index].subsections[subIndex].nestedSections[nestedIndex].discussionboard_settings = settings;
                                                newSections[index].subsections[subIndex].nestedSections[nestedIndex].title = title;
                                                
                                                // For Active components, also update the comment in the database
                                                if (nestedSection.status === 'Active' && nestedSection.reference_id) {
                                                  try {
                                                    const token = localStorage.getItem('token');
                                                    await axios.put(`/api/comments/${nestedSection.reference_id}`, {
                                                      content: JSON.stringify(content),
                                                      title: title,
                                                      settings: settings
                                                    }, {
                                                      headers: { Authorization: `Bearer ${token}` }
                                                    });
                                                    
                                                    // Update the commentData with the new settings
                                                    if (newSections[index].subsections[subIndex].nestedSections[nestedIndex].commentData) {
                                                      newSections[index].subsections[subIndex].nestedSections[nestedIndex].commentData = {
                                                        ...newSections[index].subsections[subIndex].nestedSections[nestedIndex].commentData,
                                                        content: JSON.stringify(content),
                                                        title: title,
                                                        allowmainresponse: settings.allowMainResponse,
                                                        allowcommentresponse: settings.allowCommentResponse,
                                                        notifymeonresponses: settings.notifyMeOnResponses,
                                                        allowassigneesviewresponses: settings.allowAssigneesViewResponses,
                                                        hideresponsesuntilresponded: settings.hideresponsesuntilresponded
                                                      };
                                                    }
                                                  } catch (error) {
                                                    console.error('Error updating comment settings:', error);
                                                  }
                                                }
                                                
                                                setSections(newSections);
                                              }
                                            })}
                                            title="Open editor to edit discussion board"
                                          >
                                            ✏️
                                          </button>
                                          <button 
                                            className="editor-toggle-button"
                                            onClick={() => toggleEditorExpansion(`nested-${index}-${subIndex}-${nestedIndex}`)}
                                            title={expandedEditors[`nested-${index}-${subIndex}-${nestedIndex}`] ? "Minimize preview" : "Expand preview"}
                                          >
                                            {expandedEditors[`nested-${index}-${subIndex}-${nestedIndex}`] ? '−' : '+'}
                                          </button>
                                        </div>
                                      </div>
                                      <div className={`rich-text-container ${expandedEditors[`nested-${index}-${subIndex}-${nestedIndex}`] ? 'expanded' : 'collapsed'}`}>
                                        <div className="content-preview">
                                          {nestedSection.content && nestedSection.content.length > 0 ? (
                                            nestedSection.content.map((item, itemIndex) => (
                                              <div key={item._id || itemIndex} style={{ marginBottom: itemIndex < nestedSection.content.length - 1 ? 16 : 0 }}>
                                                {item.element === 'richText' && <div dangerouslySetInnerHTML={{ __html: item.value }} />}
                                                {item.element === 'image' && renderImageFromTag(item.value)}
                                                {item.element === 'video' && renderVideoFromUrl(item.value)}
                                              </div>
                                            ))
                                          ) : (
                                            <div style={{ color: '#666', fontStyle: 'italic' }}>No discussion prompt added yet. Click the edit button ✏️ to add content.</div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Assignment Preview for nested sections */}
                                  {nestedSection.type === 'Assignment' && (
                                    <div className="content-editor-container">
                                      <div className="content-header">
                                        <label className="content-label">Selected Assignment:</label>
                                                                            <div className="editor-controls">
                                                                            {nestedSection.assignments && nestedSection.assignments.length > 0 && (
                                            <>
                                                <HourAwardsTooltip>
                                              <button 
                                                className="hour-awards-button"
                                                onClick={() => openHourAwardsModal('nested', index, subIndex, nestedIndex)}
                                                title=""
                                                style={{
                                                  background: 'none',
                                                  border: 'none',
                                                  cursor: 'pointer',
                                                  fontSize: '16px',
                                                  color: '#1D4E89',
                                                  marginRight: '8px'
                                                }}
                                              >
                                                ⌚
                                              </button>
                                              </HourAwardsTooltip>
                                              <TaxonomyTooltip>
                                        <button
                                          className="taxonomy-button"
                                                  onClick={() => openTaxonomyModal('nested', index, subIndex, nestedIndex)}
                                          title="Open taxonomy selector"
                                          style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: '16px',
                                            color: '#1D4E89',
                                            marginRight: '8px'
                                          }}
                                        >
                                          <img src="https://acuitycte-files.s3.us-west-2.amazonaws.com/public/miscellaneous/taxonomy.png" alt="Taxonomy" style={{ width: '16px', height: '16px' }} />
                                        </button>
                                              </TaxonomyTooltip>
                                            </>
                                      )}
                                      <button
                                        className="fullscreen-button"
                                        onClick={() => openAssignmentModal(index, subIndex, nestedIndex, null)}
                                        title="Select assignment"
                                      >
                                        ✏️
                                      </button>
                                      {nestedSection.assignments && nestedSection.assignments.length > 0 && nestedSection.reference_id && (
                                        <button
                                          onClick={() => openAssignmentPreview(nestedSection.reference_id)}
                                          style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: '16px',
                                            color: '#1D4E89',
                                            marginLeft: '8px'
                                          }}
                                          title="Edit in Assignment Builder"
                                        >
                                          ⇗
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  <div className="assignment-preview">
                                    {nestedSection.assignments && nestedSection.assignments.length > 0 ? (
                                      <div className="selected-assignments">
                                        {nestedSection.assignments.map((assignment, assignmentIndex) => (
                                          <div key={assignment.id || assignmentIndex} className="assignment-item">
                                            <span className="assignment-title">{assignment.title}</span>
                                            <span className="assignment-type">{assignment.type}</span>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="cb-assignment-empty-hint">No assignment selected. Click the pencil to open the list, then click an assignment to select it.</div>
                                    )}
                                  </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                            
                            <div className="add-nested-section-container">
                              <button 
                                className="add-nested-section-button" 
                                onClick={() => handleAddButtonClick('nested', { sectionIndex: index, subsectionIndex: subIndex })}
                              >
                                <span>+</span>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    
                    <div className="add-subsection-container">
                      <button 
                        className="add-subsection-button" 
                        onClick={() => handleAddButtonClick('subsection', index)}
                      >
                        <span>+</span>
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Items for Lessons at section level */}
                {section.type === 'Lesson' && section.expanded && (
                  <div className="subsections-container">
                    {section.subsections.map((item, itemIndex) => (
                      <div 
                        key={item.id} 
                        className={`subsection ${
                          draggedSubsection?.sectionIndex === index && 
                          draggedSubsection?.subsectionIndex === itemIndex ? 'dragging' : ''
                        } ${
                          dragOverSubsection?.sectionIndex === index && 
                          dragOverSubsection?.subsectionIndex === itemIndex ? 'drag-over' : ''
                        }`}
                        data-section={index}
                        data-subsection={itemIndex}
                      >
                        <div className="subsection-header">
                          <button 
                            className="drag-handle-subsection"
                            draggable
                            onDragStart={(e) => handleSubsectionDragStart(e, index, itemIndex)}
                            onDragOver={(e) => handleSubsectionDragOver(e, index, itemIndex)}
                            onDrop={(e) => handleSubsectionDrop(e, index, itemIndex)}
                            onDragEnd={handleDragEnd}
                            title="Drag to reorder item"
                          >
                            ⋮⋮
                          </button>
                          <span className="section-type">
                            {item.type === 'Module' && '📚 '}
                            {item.type === 'Lesson' && '📖 '}
                            {item.type === 'Assignment' && '📝 '}
                            {item.type === 'Discussion Board' && '💬 '}
                            {item.type === 'Content' && '📄 '}
                            {item.type}
                          </span>
                          <button 
                            className="remove-subsection-button"
                            onClick={() => removeSubsection(index, itemIndex)}
                            title="Remove item"
                          >
                            ×
                          </button>
                        </div>
                        <div className="subsection-content">
                          <div className="subsection-left">
                            <input
                              type="text"
                              className="subsection-input"
                              placeholder="Enter title here..."
                              value={item.text}
                              onChange={(e) => updateSubsectionText(index, itemIndex, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            
                                                         {/* Component Status Indicator - Only show for Draft status */}
                             {item.status === 'Draft' && (
                               <div className="component-status" style={{
                                 display: 'flex',
                                 alignItems: 'center',
                                 gap: '4px',
                                 padding: '2px 6px',
                                 backgroundColor: '#f59e0b',
                                 color: 'white',
                                 borderRadius: '4px',
                                 fontSize: '11px',
                                 fontWeight: '500',
                                 marginLeft: '8px'
                               }}>
                                 <span>⚠</span>
                                 <span>Draft</span>
                               </div>
                             )}
                          </div>
                          
                          {/* Content Preview for Content components */}
                          {item.type === 'Content' && (
                            <div className="content-editor-container">
                              <div className="content-header">
                                <label className="content-label">
                                  Content: {item.title && <em style={{ color: '#666' }}>{item.title}</em>}
                                </label>
                                <div className="editor-controls">
                                  {item.content && item.content.length > 0 && (
                                    <>
                                      <HourAwardsTooltip>
                                      <button 
                                        className="hour-awards-button"
                                        onClick={() => openHourAwardsModal('lesson-item', index, itemIndex, null, null)}
                                        title=""
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          cursor: 'pointer',
                                          fontSize: '16px',
                                          color: '#1D4E89',
                                          marginRight: '8px'
                                        }}
                                      >
                                        ⌚
                                      </button>
                                      </HourAwardsTooltip>
                                      <TaxonomyTooltip>
                                    <button 
                                      className="taxonomy-button"
                                          onClick={() => openTaxonomyModal('lesson-item', index, itemIndex)}
                                      title="Open taxonomy selector"
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        color: '#1D4E89',
                                        marginRight: '8px'
                                      }}
                                    >
                                      <img src="https://acuitycte-files.s3.us-west-2.amazonaws.com/public/miscellaneous/taxonomy.png" alt="Taxonomy" style={{ width: '16px', height: '16px' }} />
                                    </button>
                                      </TaxonomyTooltip>
                                    </>
                                  )}
                                  <button 
                                    className="fullscreen-button"
                                    onClick={() => openContentEditor({
                                      type: 'lesson-item',
                                      sectionIndex: index,
                                      itemIndex: itemIndex,
                                      content: item.content || [],
                              title: item.title || '',
                              updateFunction: (content, title) => updateSubsectionContent(index, itemIndex, content, title)
                                    })}
                                    title="Open editor to edit content"
                                  >
                                    ✏️
                                  </button>
                                  <button 
                                    className="editor-toggle-button"
                                    onClick={() => toggleEditorExpansion(`lesson-item-${index}-${itemIndex}`)}
                                    title={expandedEditors[`lesson-item-${index}-${itemIndex}`] ? "Minimize preview" : "Expand preview"}
                                  >
                                    {expandedEditors[`lesson-item-${index}-${itemIndex}`] ? '−' : '+'}
                                  </button>
                                </div>
                              </div>
                              <div className={`rich-text-container ${expandedEditors[`lesson-item-${index}-${itemIndex}`] ? 'expanded' : 'collapsed'}`}>
                                <div className="content-preview">
                                  {item.content && item.content.length > 0 ? (
                                    item.content.map((contentItem, itemIndex) => (
                                      <div key={contentItem._id || itemIndex} style={{ marginBottom: itemIndex < item.content.length - 1 ? 16 : 0 }}>
                                        {contentItem.element === 'richText' && <div dangerouslySetInnerHTML={{ __html: contentItem.value }} />}
                                        {contentItem.element === 'image' && renderImageFromTag(contentItem.value)}
                                        {contentItem.element === 'video' && renderVideoFromUrl(contentItem.value)}
                                      </div>
                                    ))
                                  ) : (
                                    <div style={{ color: '#666', fontStyle: 'italic' }}>No content added yet. Click the edit button ✏️ to add content.</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Discussion Board Preview for lesson items */}
                          {item.type === 'Discussion Board' && (
                            <div className="content-editor-container">
                              <div className="content-header">
                                <label className="content-label">
                                  Discussion Prompt: {item.title && <em style={{ color: '#666' }}>{item.title}</em>}
                                </label>
                                <div className="editor-controls">
                                  {item.content && item.content.length > 0 && (
                                    <>
                                      <HourAwardsTooltip>
                                      <button 
                                        className="hour-awards-button"
                                        onClick={() => openHourAwardsModal('lesson-item', index, itemIndex, null, null)}
                                        title=""
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          cursor: 'pointer',
                                          fontSize: '16px',
                                          color: '#1D4E89',
                                          marginRight: '8px'
                                        }}
                                      >
                                        ⌚
                                      </button>
                                      </HourAwardsTooltip>
                                      <TaxonomyTooltip>
                                    <button 
                                      className="taxonomy-button"
                                          onClick={() => openTaxonomyModal('lesson-item', index, itemIndex)}
                                      title="Open taxonomy selector"
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        color: '#1D4E89',
                                        marginRight: '8px'
                                      }}
                                    >
                                      <img src="https://acuitycte-files.s3.us-west-2.amazonaws.com/public/miscellaneous/taxonomy.png" alt="Taxonomy" style={{ width: '16px', height: '16px' }} />
                                    </button>
                                      </TaxonomyTooltip>
                                    </>
                                  )}
                                  <button 
                                    className="fullscreen-button"
                                    onClick={() => openDiscussionBoardEditor({
                                      type: 'lesson-item',
                                      sectionIndex: index,
                                      itemIndex: itemIndex,
                                      content: item.content || [],
                                      title: item.title || '',
                                      settings: item.settings || { ...DEFAULT_DISCUSSION_BOARD_SETTINGS },
                                      updateFunction: (content, settings, title) => {
                                        const newSections = [...sections];
                                        newSections[index].subsections[itemIndex].content = content;
                                        newSections[index].subsections[itemIndex].settings = settings;
                                        newSections[index].subsections[itemIndex].discussionboard_settings = settings;
                                        newSections[index].subsections[itemIndex].title = title;
                                        setSections(newSections);
                                      }
                                    })}
                                    title="Open editor to edit discussion board"
                                  >
                                    ✏️
                                  </button>
                                  <button 
                                    className="editor-toggle-button"
                                    onClick={() => toggleEditorExpansion(`lesson-item-${index}-${itemIndex}`)}
                                    title={expandedEditors[`lesson-item-${index}-${itemIndex}`] ? "Minimize preview" : "Expand preview"}
                                  >
                                    {expandedEditors[`lesson-item-${index}-${itemIndex}`] ? '−' : '+'}
                                  </button>
                                </div>
                              </div>
                              <div className={`rich-text-container ${expandedEditors[`lesson-item-${index}-${itemIndex}`] ? 'expanded' : 'collapsed'}`}>
                                <div className="content-preview">
                                  {item.content && item.content.length > 0 ? (
                                    item.content.map((contentItem, itemIndex) => (
                                      <div key={contentItem._id || itemIndex} style={{ marginBottom: itemIndex < item.content.length - 1 ? 16 : 0 }}>
                                        {contentItem.element === 'richText' && <div dangerouslySetInnerHTML={{ __html: contentItem.value }} />}
                                        {contentItem.element === 'image' && renderImageFromTag(contentItem.value)}
                                        {contentItem.element === 'video' && renderVideoFromUrl(contentItem.value)}
                                      </div>
                                    ))
                                  ) : (
                                    <div style={{ color: '#666', fontStyle: 'italic' }}>No discussion prompt added yet. Click the edit button ✏️ to add content.</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Assignment Preview for lesson items */}
                          {item.type === 'Assignment' && (
                            <div className="content-editor-container">
                              <div className="content-header">
                                <label className="content-label">Selected Assignment:</label>
                                <div className="editor-controls">
                                                                    {item.assignments && item.assignments.length > 0 && (
                                    <>
                                      <HourAwardsTooltip>
                                      <button 
                                        className="hour-awards-button"
                                        onClick={() => openHourAwardsModal('lesson-item', index, itemIndex, null, null)}
                                        title=""
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          cursor: 'pointer',
                                          fontSize: '16px',
                                          color: '#1D4E89',
                                          marginRight: '8px'
                                        }}
                                      >
                                        ⌚
                                      </button>
                                      </HourAwardsTooltip>
                                      <TaxonomyTooltip>
                                    <button
                                      className="taxonomy-button"
                                          onClick={() => openTaxonomyModal('lesson-item', index, itemIndex)}
                                      title="Open taxonomy selector"
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        color: '#1D4E89',
                                        marginRight: '8px'
                                      }}
                                    >
                                      <img src="https://acuitycte-files.s3.us-west-2.amazonaws.com/public/miscellaneous/taxonomy.png" alt="Taxonomy" style={{ width: '16px', height: '16px' }} />
                                    </button>
                                      </TaxonomyTooltip>
                                    </>
                                  )}
                                  <button
                                    className="fullscreen-button"
                                    onClick={() => openAssignmentModal(index, null, null, itemIndex)}
                                    title="Select assignment"
                                  >
                                    ✏️
                                  </button>
                                  {item.assignments && item.assignments.length > 0 && item.reference_id && (
                                    <button
                                      onClick={() => openAssignmentPreview(item.reference_id)}
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        color: '#1D4E89',
                                        marginLeft: '8px'
                                      }}
                                      title="Edit in Assignment Builder"
                                    >
                                      ⇗
                                    </button>
                                  )}
                                </div>
                              </div>
                              <div className="assignment-preview">
                                {item.assignments && item.assignments.length > 0 ? (
                                  <div className="selected-assignments">
                                    {item.assignments.map((assignment, assignmentIndex) => (
                                      <div key={assignment.id || assignmentIndex} className="assignment-item">
                                        <span className="assignment-title">{assignment.title}</span>
                                        <span className="assignment-type">{assignment.type}</span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="cb-assignment-empty-hint">No assignment selected. Click the pencil to open the list, then click an assignment to select it.</div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    <div className="add-subsection-container">
                      <button 
                        className="add-subsection-button" 
                        onClick={() => handleAddButtonClick('subsection', index)}
                      >
                        <span>+</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            <div className="add-section-container">
                              <button className="add-button" onClick={() => handleAddButtonClick('section')}>
                <span>+</span>
              </button>
            </div>
          </>
        )}
      </div>
      


      {/* Sticky Total Sections Container */}
      {!showPopup && !showItemSelector && !contentEditor && !discussionBoardEditor && (
        <div className={`sticky-container ${stickyContainerExpanded ? 'expanded' : 'collapsed'}`}>
          <div className="sticky-header">
            <div className="sticky-header-left" onClick={() => setStickyContainerExpanded(!stickyContainerExpanded)}>
              <span className="sticky-title">
                📋 Outline
              </span>
              <span className="sticky-toggle">
                {stickyContainerExpanded ? '−' : '+'}
              </span>
            </div>
            <div className="sticky-header-actions">
              <button 
                className={`sticky-save-button ${window.courseModsLocked ? 'locked' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSaveCourse();
                }}
                title={window.courseModsLocked ? "Course modifications are locked" : !courseTitle.trim() ? "Course title is required" : (currentCourseStatus === 'Active' && getComponentsWithChangesCount() === 0 ? "No changes to save" : "Save Course Components")}
                disabled={isSaving || !courseTitle.trim() || (currentCourseStatus === 'Active' && getComponentsWithChangesCount() === 0) || window.courseModsLocked}
              >
                💾 {isSaving ? 'Saving...' : window.courseModsLocked ? 'Locked' : !courseTitle.trim() ? 'Enter Title' : (currentCourseStatus === 'Active' && getComponentsWithChangesCount() === 0) ? 'No Changes' : 'Save'}
                {!isSaving && getComponentsWithChangesCount() > 0 && (
                  <span className="changes-indicator" style={{
                    marginLeft: '8px',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    borderRadius: '50%',
                    padding: '2px 6px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    minWidth: '20px',
                    display: 'inline-block',
                    textAlign: 'center'
                  }}>
                    {getComponentsWithChangesCount()}
                  </span>
                )}
              </button>
              
              {/* Course Status Indicator - Only show for Draft status */}
              {currentCourseStatus === 'Draft' && (
                <div className="course-status-indicator" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  <span>📊</span>
                  <span>Status: Draft</span>
                </div>
              )}
            </div>
          </div>
          {stickyContainerExpanded && (
            <div className="sticky-content">
              <div className="sections-overview">
             {sections.map((section, index) => (
                  <div key={section.id} className="outline-section-item">
                    <div className="outline-section-header">
                      <button 
                        className="outline-expand-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleOutlineSectionExpansion(index);
                        }}
                        title={outlineExpandedSections[index] ? "Collapse subsections" : "Expand subsections"}
                      >
                        {outlineExpandedSections[index] ? '▼' : '▶'}
                      </button>
                      <span className="outline-section-index">{index + 1}.</span>
                      <span className="outline-section-type">{section.type}</span>
                      <span 
                        className="outline-section-text clickable"
                        onClick={() => scrollToSection(index)}
                        title="Click to scroll to this section"
                      >
                        {section.text || <em>Untitled {section.type}</em>}
                        {section.hasChanges && (
                          <span style={{
                            marginLeft: '8px',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            borderRadius: '50%',
                            padding: '1px 4px',
                            fontSize: '10px',
                            fontWeight: 'bold'
                          }}>
                            ●
                          </span>
                        )}
                   </span>
                    </div>
                    {section.subsections && section.subsections.length > 0 && outlineExpandedSections[index] && (
                      <div className="outline-subsections-container">
                 {section.subsections.map((subsection, subIndex) => (
                          <div key={subsection.id} className="outline-subsection-item">
                            <div className="outline-subsection-header">
                              <button 
                                className="outline-expand-button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleOutlineSubsectionExpansion(index, subIndex);
                                }}
                                title={outlineExpandedSubsections[`${index}-${subIndex}`] ? "Collapse nested sections" : "Expand nested sections"}
                              >
                                {outlineExpandedSubsections[`${index}-${subIndex}`] ? '▼' : '▶'}
                              </button>
                              <span className="outline-subsection-index">{index + 1}.{subIndex + 1}.</span>
                              <span className="outline-subsection-type">{subsection.type}</span>
                              <span 
                                className="outline-subsection-text clickable"
                                onClick={() => scrollToSection(index, subIndex)}
                                title="Click to scroll to this subsection"
                              >
                                {subsection.text || <em>Untitled {subsection.type}</em>}
                                {subsection.hasChanges && (
                                  <span style={{
                                    marginLeft: '8px',
                                    backgroundColor: '#ef4444',
                                    color: 'white',
                                    borderRadius: '50%',
                                    padding: '1px 4px',
                                    fontSize: '10px',
                                    fontWeight: 'bold'
                                  }}>
                                    ●
                                  </span>
                                )}
                       </span>
                            </div>
                            {subsection.nestedSections && subsection.nestedSections.length > 0 && outlineExpandedSubsections[`${index}-${subIndex}`] && (
                              <div className="outline-nested-sections-container">
                                {subsection.nestedSections.map((nestedSection, nestedIndex) => (
                                  <div key={nestedSection.id} className="outline-nested-section-item">
                                    <div className="outline-nested-section-header">
                                      <span className="outline-nested-section-index">{index + 1}.{subIndex + 1}.{nestedIndex + 1}.</span>
                                      <span className="outline-nested-section-type">{nestedSection.type}</span>
                                      <span 
                                        className="outline-nested-section-text clickable"
                                        onClick={() => scrollToSection(index, subIndex, nestedIndex)}
                                        title="Click to scroll to this nested section"
                                      >
                                        {nestedSection.text || <em>Untitled {nestedSection.type}</em>}
                                        {nestedSection.hasChanges && (
                                          <span style={{
                                            marginLeft: '8px',
                                            backgroundColor: '#ef4444',
                                            color: 'white',
                                            borderRadius: '50%',
                                            padding: '1px 4px',
                                            fontSize: '10px',
                                            fontWeight: 'bold'
                                          }}>
                                            ●
                                          </span>
                                        )}
                         </span>
                                    </div>
                                  </div>
                     ))}
                              </div>
                            )}
                   </div>
                 ))}
                      </div>
                    )}
               </div>
             ))}
           </div>
            </div>
          )}
         </div>
       )}

      {/* Flag Container for Deployment Readiness */}
      {!showPopup && !showItemSelector && !contentEditor && !discussionBoardEditor && (
        <div className={`flag-container ${flagContainerExpanded ? 'expanded' : 'collapsed'} ${(() => {
          const readinessStatus = getDeploymentReadinessStatus();
          return !readinessStatus.isReady ? 'has-issues' : '';
        })()}`}>
          <div className="flag-header">
            <div className="flag-header-left" onClick={() => setFlagContainerExpanded(!flagContainerExpanded)}>
              <span className="flag-title">
                {(() => {
                  const readinessStatus = getDeploymentReadinessStatus();
                  return readinessStatus.isReady 
                    ? '🚩 Deployment Flag' 
                    : `⚠️${readinessStatus.issuesCount} Issue${readinessStatus.issuesCount !== 1 ? 's' : ''} Found`;
                })()}
              </span>
              <span className="flag-toggle">
                {flagContainerExpanded ? '−' : '+'}
              </span>
            </div>
            {canComponentNotificationSettings && (
              <div className="flag-header-right">
                <button
                  type="button"
                  className="lms-notify-settings-btn"
                  title="LMS completion emails — who is notified when learners complete items (separate from discussion reply notifications)"
                  aria-label="Bulk LMS completion email settings"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowLmsEmailNotifyModal(true);
                  }}
                >
                  📧
                </button>
              </div>
            )}
          </div>
          {flagContainerExpanded && (
            <div className="flag-content">
              {(() => {
                const readinessStatus = getDeploymentReadinessStatus();
                return (
                  <div className="deployment-status">
                    {!readinessStatus.isReady && (
                      <div className="issues-list">
                        <div className="issues-header">
                          Components missing required settings:
                        </div>
                        <div className="issues-items">
                          {readinessStatus.issues.map((issue, index) => (
                            <div key={index} className="issue-item">
                              <div className="issue-path">{issue.path}</div>
                              <div className="issue-title">{issue.title}</div>
                              <div className="issue-type">{issue.componentType}</div>
                              <div className="issue-details">
                                Missing: {issue.issueTypes.join(', ')}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {readinessStatus.isReady && (
                      <div className="ready-message">
                        <div className="ready-icon">🎉</div>
                        <div className="ready-text">
                          All Content, Discussion Board, and Assignment components have hour awards and grade settings configured.
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {showLmsEmailNotifyModal &&
        canComponentNotificationSettings &&
        (() => {
          const LMS_NOTIFY_TYPE_ORDER = ['Assignment', 'Content', 'Discussion Board'];
          const notifyItems = walkSectionsForLmsNotifyItems(sections);
          const assignmentNotifyItems = notifyItems.filter((i) => i.type === 'Assignment');
          const grouped = LMS_NOTIFY_TYPE_ORDER.map((t) => ({
            type: t,
            items: notifyItems.filter((i) => i.type === t)
          }));
          return (
            <div
              className="popup-overlay"
              onClick={() => !lmsNotifySaving && setShowLmsEmailNotifyModal(false)}
            >
              <div
                className="popup lms-notify-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="popup-header">
                  <h3>LMS completion emails</h3>
                  <button
                    type="button"
                    className="popup-close"
                    disabled={lmsNotifySaving}
                    onClick={() => setShowLmsEmailNotifyModal(false)}
                  >
                    ×
                  </button>
                </div>
                <div className="popup-content lms-notify-modal-content">
                  <p className="lms-notify-help">
                    These checkboxes control who receives <strong>LMS staff</strong> emails when a
                    learner completes an item. They are separate from Discussion Board “notify me on
                    responses” (managed on each discussion item). <strong>Content</strong> and{' '}
                    <strong>Discussion Board</strong> completion emails are not available in the UI
                    yet (disabled until further notice). For <strong>Assignments</strong>, if nothing
                    is stored in the database, the superparent creator is notified by default. Learner
                    and account notification preferences still apply on top of this.
                  </p>
                  {notifyItems.length === 0 ? (
                    <p className="lms-notify-empty">
                      No Assignment, Content, or Discussion Board components in this course yet.
                    </p>
                  ) : (
                    grouped.map(({ type, items: typeItems }) => {
                      if (
                        LMS_NOTIFY_BULK_UI_DISABLED_TYPES.includes(type) &&
                        typeItems.length === 0
                      ) {
                        return null;
                      }
                      return LMS_NOTIFY_BULK_UI_DISABLED_TYPES.includes(type) ? (
                        <div
                          key={type}
                          className="lms-notify-type-block lms-notify-type-block-disabled"
                        >
                          <div className="lms-notify-type-header-static">
                            <span className="lms-notify-type-label">
                              {type}
                              <span className="lms-notify-type-count">
                                {' '}
                                ({typeItems.length})
                              </span>
                            </span>
                          </div>
                          <p className="lms-notify-disabled-msg">
                            Disabled until further notice.
                          </p>
                        </div>
                      ) : (
                        <div key={type} className="lms-notify-type-block">
                          <button
                            type="button"
                            className="lms-notify-type-header"
                            onClick={() =>
                              setLmsNotifyExpandedTypes((prev) => ({
                                ...prev,
                                [type]: !prev[type]
                              }))
                            }
                          >
                            <span className="lms-notify-type-toggle">
                              {lmsNotifyExpandedTypes[type] ? '−' : '+'}
                            </span>
                            <span className="lms-notify-type-label">
                              {type}
                              <span className="lms-notify-type-count">
                                {' '}
                                ({typeItems.length})
                              </span>
                            </span>
                          </button>
                          {lmsNotifyExpandedTypes[type] && (
                            <ul className="lms-notify-item-list">
                              {typeItems.map((item) => {
                                const d =
                                  lmsNotifyDraft[item.id] ??
                                  mergeLmsNotificationSettingsFromStored(
                                    item.notification_settings,
                                    item.type
                                  );
                                return (
                                  <li key={item.id} className="lms-notify-item-row">
                                    <div className="lms-notify-item-title">
                                      <span className="lms-notify-path">{item.path}</span>
                                      {item.title}
                                    </div>
                                    <div className="lms-notify-checks">
                                      <label className="lms-notify-check">
                                        <input
                                          type="checkbox"
                                          checked={d.notify_superparent_creator}
                                          onChange={(e) =>
                                            setLmsNotifyDraft((prev) => ({
                                              ...prev,
                                              [item.id]: {
                                                ...(prev[item.id] ??
                                                  mergeLmsNotificationSettingsFromStored(
                                                    item.notification_settings,
                                                    item.type
                                                  )),
                                                notify_superparent_creator: e.target.checked
                                              }
                                            }))
                                          }
                                        />
                                        <span title="User who owns the top-level course component (superparent)">
                                          Superparent creator
                                        </span>
                                      </label>
                                      <label className="lms-notify-check">
                                        <input
                                          type="checkbox"
                                          checked={d.notify_creator}
                                          onChange={(e) =>
                                            setLmsNotifyDraft((prev) => ({
                                              ...prev,
                                              [item.id]: {
                                                ...(prev[item.id] ??
                                                  mergeLmsNotificationSettingsFromStored(
                                                    item.notification_settings,
                                                    item.type
                                                  )),
                                                notify_creator: e.target.checked
                                              }
                                            }))
                                          }
                                        />
                                        <span title="Account that created this course component row">
                                          Component creator
                                        </span>
                                      </label>
                                      <label className="lms-notify-check">
                                        <input
                                          type="checkbox"
                                          checked={d.notify_item_creator}
                                          onChange={(e) =>
                                            setLmsNotifyDraft((prev) => ({
                                              ...prev,
                                              [item.id]: {
                                                ...(prev[item.id] ??
                                                  mergeLmsNotificationSettingsFromStored(
                                                    item.notification_settings,
                                                    item.type
                                                  )),
                                                notify_item_creator: e.target.checked
                                              }
                                            }))
                                          }
                                        />
                                        <span title="Author of the linked assignment or discussion (when applicable)">
                                          Item creator
                                        </span>
                                      </label>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      );
                    })
                  )}
                  <div className="lms-notify-modal-actions">
                    <button
                      type="button"
                      className="lms-notify-btn lms-notify-btn-cancel"
                      disabled={lmsNotifySaving}
                      onClick={() => setShowLmsEmailNotifyModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="lms-notify-btn lms-notify-btn-save"
                      disabled={lmsNotifySaving || assignmentNotifyItems.length === 0}
                      onClick={saveLmsEmailNotifySettings}
                    >
                      {lmsNotifySaving ? 'Saving…' : 'Save all'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

      {/* Main Popup */}
      {showPopup && !showItemSelector && (
        <div className="popup-overlay" onClick={closePopup}>
          <div className="popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>
                {popupType === 'section' && "What would you like to add to this layer?"}
                {popupType === 'subsection' && "What would you like to add to this layer?"}
                {popupType === 'nested' && "Select an item type"}
              </h3>
              <button className="popup-close" onClick={closePopup}>×</button>
            </div>
            <div className="popup-content">
              {popupType === 'section' && (
                <>
                  <div className="popup-option" onClick={() => handlePopupOption('module')}>
                    <div className="option-icon">📚</div>
                    <div className="option-content">
                      <div className="option-title">Module</div>
                      <div className="option-subtext">Top layer that holds lessons and items (assignments, discussion boards, etc)</div>
                    </div>
                  </div>
                  <div className="popup-option" onClick={() => handlePopupOption('lesson')}>
                    <div className="option-icon">📖</div>
                    <div className="option-content">
                      <div className="option-title">Lesson</div>
                      <div className="option-subtext">Holds items (assignments, discussion boards, etc)</div>
                    </div>
                  </div>
                  <div className="popup-option" onClick={() => handlePopupOption('item')}>
                    <div className="option-icon">📄</div>
                    <div className="option-content">
                      <div className="option-title">Item</div>
                      <div className="option-subtext">Assignment, Discussion Board, etc</div>
                    </div>
                  </div>
                </>
              )}
              {popupType === 'subsection' && (
                <>
                  <div className="popup-option" onClick={() => handlePopupOption('lesson')}>
                    <div className="option-icon">📖</div>
                    <div className="option-content">
                      <div className="option-title">Lesson</div>
                      <div className="option-subtext">Holds items (assignments, discussion boards, etc)</div>
                    </div>
                  </div>
                  <div className="popup-option" onClick={() => handlePopupOption('item')}>
                    <div className="option-icon">📄</div>
                    <div className="option-content">
                      <div className="option-title">Item</div>
                      <div className="option-subtext">Assignment, Discussion Board, etc</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Item Selector Popup */}
      {showItemSelector && (
        <div className="popup-overlay" onClick={closePopup}>
          <div className="popup item-selector-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>Select Item Type</h3>
              <button className="popup-close" onClick={closePopup}>×</button>
            </div>
            <div className="popup-content">
            <div className="popup-option" onClick={() => handleItemSelection('Content')}>
                <div className="option-icon">📄</div>
                <div className="option-content">
                  <div className="option-title">Content</div>
                  <div className="option-subtext">Add rich text, images, videos, links</div>
                </div>
              </div>
              <div className="popup-option" onClick={() => handleItemSelection('Discussion Board')}>
                <div className="option-icon">💬</div>
                <div className="option-content">
                  <div className="option-title">Discussion Board</div>
                  <div className="option-subtext">Allow students to reply to a post and each other</div>
                </div>
              </div>
              
              <div className="popup-option" onClick={() => handleItemSelection('Assignment')}>
                <div className="option-icon">📝</div>
                <div className="option-content">
                  <div className="option-title">Assignment</div>
                  <div className="option-subtext">Assessment or Survey questions (manual/auto-graded)</div>
                </div>
              </div>

            </div>
          </div>
                 </div>
       )}



      {/* Content Editor Popup */}
      {contentEditor && (
        <div className="fullscreen-overlay">
          <div className="fullscreen-editor" onClick={(e) => e.stopPropagation()}>
            <div className="fullscreen-header">
              <h3>Content Editor</h3>
              <button 
                className="fullscreen-close" 
                onClick={closeContentEditor}
                style={{
                  background: 'transparent',
                  color: '#374151',
                  border: 'none'
                }}
              >×</button>
            </div>
                        <div className="fullscreen-content">
              {/* Add New Content */}
              <div style={{ marginBottom: 24, maxWidth: '600px' }}>
                <h4 style={{ marginBottom: 16 }}>
                  {editingContentId ? 'Edit Content' : 'Add New Content'}:
                </h4>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    fontWeight: '600',
                    fontSize: '14px',
                    color: '#333'
                  }}>
                    Content Title:
                  </label>
                  <input
                    type="text"
                    value={contentTitle}
                    onChange={(e) => setContentTitle(e.target.value)}
                    placeholder="Enter content title..."
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #ddd',
                      background: 'white',
                      fontSize: '14px'
                    }}
                  />
                </div>
                          <div style={{ marginBottom: 16 }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              fontSize: '14px',
              color: '#333'
            }}>
              Content Type:
            </label>
            <div style={{ display: 'flex', gap: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="discussionContentType"
                  value="richText"
                  checked={directionDraft.element === 'richText'}
                  onChange={e => setDirectionDraft(d => ({ 
                    ...d, 
                    element: e.target.value, 
                    value: '', 
                    imageUrl: '', 
                    imageWidth: '', 
                    imageHeight: '' 
                  }))}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px' }}>📝 Rich Text</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="discussionContentType"
                  value="image"
                  checked={directionDraft.element === 'image'}
                  onChange={e => setDirectionDraft(d => ({ 
                    ...d, 
                    element: e.target.value, 
                    value: '', 
                    imageUrl: '', 
                    imageWidth: '', 
                    imageHeight: '' 
                  }))}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px' }}>🖼️ Image</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="discussionContentType"
                  value="video"
                  checked={directionDraft.element === 'video'}
                  onChange={e => setDirectionDraft(d => ({ 
                    ...d, 
                    element: e.target.value, 
                    value: '', 
                    imageUrl: '', 
                    imageWidth: '', 
                    imageHeight: '' 
                  }))}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px' }}>🎬 Video</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="discussionContentType"
                  value="embed"
                  checked={directionDraft.element === 'embed'}
                  onChange={e => setDirectionDraft(d => ({ 
                    ...d, 
                    element: e.target.value, 
                    value: '', 
                    imageUrl: '', 
                    imageWidth: '', 
                    imageHeight: '' 
                  }))}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px' }}>📺 Embed</span>
              </label>
            </div>
          </div>
              </div>

              {directionDraft.element === 'richText' && (
                <div style={{ marginBottom: 24 }}>
                  <label>Content:</label>
                  <div style={{ marginTop: 8 }}>
                    <ReactQuill 
                      theme="snow" 
                      value={directionDraft.value} 
                      onChange={val => setDirectionDraft(d => ({ ...d, value: val }))} 
                      style={{ minHeight: 200 }}
                      modules={{
                        toolbar: [
                          [{ 'header': [1, 2, 3, false] }],
                          ['bold', 'italic', 'underline', 'strike'],
                          [{ 'color': [] }, { 'background': [] }],
                          [{ 'align': [] }],
                          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                          ['link'],
                          ['clean']
                        ]
                      }}
                    />
                  </div>
                </div>
              )}

              {directionDraft.element === 'image' && (
                <div style={{ marginBottom: 24, maxWidth: '600px' }}>
                  {/* Upload Section */}
                  <div style={{ 
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '16px',
                    border: '1px solid #e9ecef'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '16px' }}>📁</span>
                        <span style={{ fontWeight: '500', fontSize: '14px', color: '#495057' }}>Upload New Image</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => fetchUserUploads('image')}
                        disabled={uploadsLoading}
                        style={{
                          background: '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '6px 12px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#5a6268'}
                        onMouseLeave={(e) => e.target.style.background = '#6c757d'}
                      >
                        <span style={{ fontSize: '14px' }}>🔍</span>
                        {uploadsLoading ? 'Loading...' : 'Find Images'}
                      </button>
                    </div>
                    
                    <div style={{
                      border: '2px dashed #dee2e6',
                      borderRadius: '6px',
                      padding: '16px',
                      textAlign: 'center',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      position: 'relative',
                      background: 'white'
                    }}
                    onMouseEnter={(e) => e.target.style.borderColor = '#adb5bd'}
                    onMouseLeave={(e) => e.target.style.borderColor = '#dee2e6'}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageFileUpload}
                        disabled={imageUploading}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          opacity: 0,
                          cursor: 'pointer'
                        }}
                      />
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>📸</div>
                      <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#495057' }}>
                        {imageUploading ? 'Uploading...' : 'Click to upload or drag image here'}
                      </div>

                    </div>
                    
                    {imageUploading && (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px', 
                        marginTop: '12px',
                        padding: '8px',
                        background: '#e9ecef',
                        borderRadius: '6px'
                      }}>
                        <div style={{ 
                          width: '16px', 
                          height: '16px', 
                          border: '2px solid #adb5bd', 
                          borderTop: '2px solid #495057', 
                          borderRadius: '50%', 
                          animation: 'spin 1s linear infinite' 
                        }}></div>
                        <span style={{ fontSize: '12px', color: '#495057' }}>Uploading image...</span>
                      </div>
                    )}
                    
                    {imageUploadError && (
                      <div style={{ 
                        color: '#721c24', 
                        fontSize: '12px', 
                        marginTop: '8px',
                        padding: '8px',
                        background: '#f8d7da',
                        borderRadius: '6px',
                        border: '1px solid #f5c6cb'
                      }}>
                        ⚠️ {imageUploadError}
                      </div>
                    )}
                  </div>
                  
                  {/* URL Section */}
                  <div style={{ 
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '16px',
                    border: '1px solid #e9ecef'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
                      <span style={{ fontSize: '16px' }}>🔗</span>
                      <span style={{ fontWeight: '500', fontSize: '14px', color: '#495057' }}>Or Use Image URL</span>
                    </div>
                    
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '6px', 
                        fontWeight: '500',
                        color: '#495057',
                        fontSize: '12px'
                      }}>
                        Image URL:
                      </label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input 
                          value={directionDraft.imageUrl} 
                          onChange={e => setDirectionDraft(d => ({ ...d, imageUrl: e.target.value }))} 
                          placeholder="Enter image URL here..."
                          style={{ 
                            flex: 1,
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: '1px solid #ced4da',
                            background: 'white',
                            color: '#495057',
                            fontSize: '14px'
                          }}
                        />
                        {directionDraft.imageUrl && (
                          <button
                            type="button"
                            onClick={async () => {
                              if (directionDraft.imageUrl) {
                                try {
                                  const dimensions = await getImageDimensionsFromUrl(directionDraft.imageUrl);
                                  setDirectionDraft(prev => ({
                                    ...prev,
                                    imageWidth: dimensions.webWidth.toString(),
                                    imageHeight: dimensions.webHeight.toString()
                                  }));
                                } catch (error) {
                                  console.error('Error detecting image dimensions:', error);
                                  alert('Could not detect image dimensions. Please enter them manually.');
                                }
                              }
                            }}
                            style={{
                              padding: '8px 12px',
                              borderRadius: '6px',
                              border: '1px solid #1976d2',
                              background: '#1976d2',
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '12px',
                              whiteSpace: 'nowrap'
                            }}
                            title="Automatically detect image dimensions"
                          >
                            📏 Detect
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ 
                          display: 'block', 
                          marginBottom: '6px', 
                          fontWeight: '500',
                          color: '#495057',
                          fontSize: '12px'
                        }}>
                          Width (px):
                        </label>
                        <input 
                          type="number" 
                          min={1} 
                          value={directionDraft.imageWidth} 
                          onChange={e => setDirectionDraft(d => ({ ...d, imageWidth: e.target.value }))} 
                          style={{ 
                            width: '100%',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: '1px solid #ced4da',
                            background: 'white',
                            color: '#495057',
                            fontSize: '14px'
                          }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ 
                          display: 'block', 
                          marginBottom: '6px', 
                          fontWeight: '500',
                          color: '#495057',
                          fontSize: '12px'
                        }}>
                          Height (px):
                        </label>
                        <input 
                          type="number" 
                          min={1} 
                          value={directionDraft.imageHeight} 
                          onChange={e => setDirectionDraft(d => ({ ...d, imageHeight: e.target.value }))} 
                          style={{ 
                            width: '100%',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: '1px solid #ced4da',
                            background: 'white',
                            color: '#495057',
                            fontSize: '14px'
                          }}
                        />
                      </div>
                    </div>
                    
                    {/* Image dimension info and preview */}
                    {directionDraft.imageUrl && directionDraft.imageWidth && directionDraft.imageHeight && (
                      <div style={{ 
                        marginTop: '12px',
                        padding: '12px',
                        background: '#e3f2fd',
                        borderRadius: '6px',
                        border: '1px solid #90caf9'
                      }}>
                        <div style={{ 
                          fontSize: '12px',
                          color: '#1565c0',
                          marginBottom: '8px'
                        }}>
                          📐 Image dimensions set to {directionDraft.imageWidth} × {directionDraft.imageHeight} pixels
                        </div>
                        <div style={{ 
                          textAlign: 'center',
                          padding: '8px',
                          background: 'white',
                          borderRadius: '4px',
                          border: '1px solid #90caf9'
                        }}>
                          <img 
                            src={directionDraft.imageUrl} 
                            alt="Preview" 
                            style={{ 
                              maxWidth: '100%',
                              maxHeight: '200px',
                              objectFit: 'contain'
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {directionDraft.element === 'video' && (
                <div style={{ marginBottom: 24, maxWidth: '600px' }}>
                  {/* Upload Section */}
                  <div style={{ 
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '16px',
                    border: '1px solid #e9ecef'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '16px' }}>🎬</span>
                        <span style={{ fontWeight: '500', fontSize: '14px', color: '#495057' }}>Upload New Video</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => fetchUserUploads('video')}
                        disabled={uploadsLoading}
                        style={{
                          background: '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '6px 12px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#5a6268'}
                        onMouseLeave={(e) => e.target.style.background = '#6c757d'}
                      >
                        <span style={{ fontSize: '14px' }}>🔍</span>
                        {uploadsLoading ? 'Loading...' : 'Find Videos'}
                      </button>
                    </div>
                    
                    <div style={{
                      border: '2px dashed #dee2e6',
                      borderRadius: '6px',
                      padding: '16px',
                      textAlign: 'center',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      position: 'relative',
                      background: 'white'
                    }}
                    onMouseEnter={(e) => e.target.style.borderColor = '#adb5bd'}
                    onMouseLeave={(e) => e.target.style.borderColor = '#dee2e6'}
                    >
                      <input
                        type="file"
                        accept="video/*"
                        onChange={handleVideoFileUpload}
                        disabled={videoUploading}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          opacity: 0,
                          cursor: 'pointer'
                        }}
                      />
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>🎥</div>
                      <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#495057' }}>
                        {videoUploading ? 'Uploading...' : 'Click to upload or drag video here'}
                      </div>

                    </div>
                    
                    {videoUploading && (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px', 
                        marginTop: '12px',
                        padding: '8px',
                        background: '#e9ecef',
                        borderRadius: '6px'
                      }}>
                        <div style={{ 
                          width: '16px', 
                          height: '16px', 
                          border: '2px solid #adb5bd', 
                          borderTop: '2px solid #495057', 
                          borderRadius: '50%', 
                          animation: 'spin 1s linear infinite' 
                        }}></div>
                        <span style={{ fontSize: '12px', color: '#495057' }}>Uploading video...</span>
                      </div>
                    )}
                    
                    {videoUploadError && (
                      <div style={{ 
                        color: '#721c24', 
                        fontSize: '12px', 
                        marginTop: '8px',
                        padding: '8px',
                        background: '#f8d7da',
                        borderRadius: '6px',
                        border: '1px solid #f5c6cb'
                      }}>
                        ⚠️ {videoUploadError}
                      </div>
                    )}
                  </div>
                  
                  {/* URL Section */}
                  <div style={{ 
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '16px',
                    border: '1px solid #e9ecef'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
                      <span style={{ fontSize: '16px' }}>🔗</span>
                      <span style={{ fontWeight: '500', fontSize: '14px', color: '#495057' }}>Or Use Video URL</span>
                    </div>
                    
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '6px', 
                        fontWeight: '500',
                        color: '#495057',
                        fontSize: '12px'
                      }}>
                        Video URL:
                      </label>
                      <input 
                        value={directionDraft.value} 
                        onChange={e => setDirectionDraft(d => ({ ...d, value: e.target.value }))} 

                        style={{ 
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid #ced4da',
                          background: 'white',
                          color: '#495057',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    

                  </div>
                </div>
              )}

              {directionDraft.element === 'embed' && (
                <div style={{ marginBottom: 24, maxWidth: '600px' }}>
                  <div style={{ 
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '16px',
                    border: '1px solid #e9ecef'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
                      <span style={{ fontSize: '16px' }}>📺</span>
                      <span style={{ fontWeight: '500', fontSize: '14px', color: '#495057' }}>Embed Code</span>
                    </div>
                    
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '6px', 
                        fontWeight: '500',
                        color: '#495057',
                        fontSize: '12px'
                      }}>
                        Embed Code (iframe, embed, object, or script):
                      </label>
                      <textarea 
                        value={directionDraft.value} 
                        onChange={e => setDirectionDraft(d => ({ ...d, value: e.target.value }))} 
                        placeholder="Paste your embed code here...&#10;&#10;Examples:&#10;&lt;iframe src=&quot;https://example.com&quot; width=&quot;560&quot; height=&quot;315&quot;&gt;&lt;/iframe&gt;&#10;&lt;embed src=&quot;https://example.com/video.mp4&quot; type=&quot;video/mp4&quot;&gt;"
                        style={{ 
                          width: '100%',
                          minHeight: '120px',
                          padding: '12px',
                          borderRadius: '6px',
                          border: '1px solid #ced4da',
                          background: 'white',
                          color: '#495057',
                          fontSize: '14px',
                          fontFamily: 'monospace',
                          resize: 'vertical'
                        }}
                      />
                    </div>
                    
                    {directionDraft.value && (
                      <div style={{ 
                        marginTop: '12px',
                        padding: '12px',
                        background: '#e3f2fd',
                        borderRadius: '6px',
                        border: '1px solid #90caf9'
                      }}>
                        <div style={{ 
                          fontSize: '12px',
                          color: '#1565c0',
                          marginBottom: '8px'
                        }}>
                          📺 Embed Preview
                        </div>
                        <div style={{ 
                          textAlign: 'center',
                          padding: '8px',
                          background: 'white',
                          borderRadius: '4px',
                          border: '1px solid #90caf9',
                          minHeight: '100px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <div 
                            style={{ 
                              width: '100%',
                              maxWidth: '100%',
                              overflow: 'hidden'
                            }}
                            dangerouslySetInnerHTML={{ __html: directionDraft.value }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="button-row">
                <button 
                  onClick={addOrUpdateContentElement} 
                  disabled={
                    (directionDraft.element === 'richText' && !directionDraft.value.trim()) ||
                    (directionDraft.element === 'image' && (!directionDraft.imageUrl || !directionDraft.imageWidth || !directionDraft.imageHeight)) ||
                    (directionDraft.element === 'video' && !directionDraft.value.trim()) ||
                    (directionDraft.element === 'embed' && !directionDraft.value.trim())
                  }
                  style={{ 
                    background: '#1976d2', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: 4, 
                    padding: '8px 16px', 
                    cursor: 'pointer' 
                  }}
                >
                  {editingContentId ? 'Update Content' : 'Add Content'}
                </button>
                {editingContentId && (
                  <button 
                    onClick={() => {
                      setEditingContentId(null);
                      setDirectionDraft({ 
                        element: 'richText', 
                        value: '', 
                        imageUrl: '', 
                        imageWidth: '', 
                        imageHeight: '' 
                      });
                    }}
                    style={{ 
                      background: '#666', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: 4, 
                      padding: '8px 16px', 
                      cursor: 'pointer' 
                    }}
                  >
                    Cancel Edit
                  </button>
                )}
              </div>

              {/* Current Content Preview */}
              {(contentEditor?.content || []).length > 0 && (
                <div style={{ marginTop: 24, borderTop: '1px solid #eee', paddingTop: 24 }}>
                  <h4 style={{ marginBottom: 16 }}>Current Content:</h4>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {(contentEditor?.content || []).map((item, idx) => (
                      <li key={item._id} style={{ marginBottom: 16, padding: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            {item.element === 'richText' && <div dangerouslySetInnerHTML={{ __html: item.value }} />}
                            {item.element === 'image' && renderImageFromTag(item.value)}
                            {item.element === 'video' && renderVideoFromUrl(item.value)}
                            {item.element === 'embed' && <div dangerouslySetInnerHTML={{ __html: item.value }} />}
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button 
                              onClick={() => editContentElement(item)} 
                              title="Edit" 
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
                            >
                              ✏️
                            </button>
                            <button 
                              onDoubleClick={() => removeContentElement(item._id)} 
                              title="Double-click to remove" 
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
                            >
                              🗑️
                            </button>
                            <button 
                              onClick={() => moveContentUp(idx)} 
                              disabled={idx === 0} 
                              title="Move Up"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
                            >
                              ↑
                            </button>
                            <button 
                              onClick={() => moveContentDown(idx)} 
                              disabled={idx === (contentEditor?.content || []).length - 1} 
                              title="Move Down"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
                            >
                              ↓
                            </button>
                          </div>
                        </div>
                        {idx < (contentEditor?.content || []).length - 1 && <hr style={{ border: 0, borderTop: '1px solid #eee', margin: '16px 0' }} />}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

{/* Discussion Board Editor Popup */}
{discussionBoardEditor && (
  <div className="fullscreen-overlay">
    <div className="fullscreen-editor" onClick={(e) => e.stopPropagation()}>
      <div className="fullscreen-header">
        <h3>Discussion Board Editor</h3>
        <button 
          className="fullscreen-close" 
          onClick={closeDiscussionBoardEditor}
          style={{
            background: 'transparent',
            color: '#374151',
            border: 'none'
          }}
        >×</button>
      </div>
      <div className="fullscreen-content">
        {/* Title Input */}
        <div style={{ marginBottom: '24px', maxWidth: '600px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            fontWeight: '600',
            fontSize: '14px',
            color: '#333'
          }}>
            Discussion Title:
          </label>
          <input
            type="text"
            value={discussionBoardTitle}
            onChange={(e) => setDiscussionBoardTitle(e.target.value)}
            placeholder="Enter a title for this discussion..."
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid #ddd',
              background: 'white',
              fontSize: '14px',
              outline: 'none'
            }}
          />
        </div>

        {/* Settings Panel */}
        <div style={{ 
          background: '#f8f9fa', 
          borderRadius: '8px', 
          padding: '16px', 
          marginBottom: '24px',
          border: '1px solid #e9ecef'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: showDiscussionSettings ? '16px' : '0',
            cursor: 'pointer'
          }}
          onClick={() => setShowDiscussionSettings(!showDiscussionSettings)}
          >
            <h4 style={{ margin: 0, color: '#495057' }}>Discussion Settings</h4>
            <button
              style={{
                background: 'none',
                border: 'none',
                fontSize: '18px',
                color: '#495057',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {showDiscussionSettings ? '−' : '+'}
            </button>
          </div>
          {showDiscussionSettings && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <label style={{ fontWeight: '600', fontSize: '14px', color: '#495057' }}>
                    Allow Main Response
                  </label>
                  <p style={{ fontSize: '12px', color: '#6c757d', margin: '4px 0 0 0' }}>
                    Allow students to reply directly to the discussion prompt
                  </p>
                </div>
                <div 
                  style={{
                    position: 'relative',
                    width: '40px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer'
                  }}
                  onClick={() => setDiscussionBoardSettings(prev => ({
                    ...prev,
                    allowMainResponse: !prev.allowMainResponse
                  }))}
                >
                  <input
                    type="checkbox"
                    checked={discussionBoardSettings.allowMainResponse}
                    onChange={(e) => setDiscussionBoardSettings(prev => ({
                      ...prev,
                      allowMainResponse: e.target.checked
                    }))}
                    style={{ opacity: 0, width: 0, height: 0, position: 'absolute', zIndex: -1 }}
                  />
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: discussionBoardSettings.allowMainResponse ? '#2563eb' : '#e5e7eb',
                    borderRadius: '20px',
                    transition: 'background-color 0.3s'
                  }}>
                    <div style={{
                      position: 'absolute',
                      width: '16px',
                      height: '16px',
                      left: discussionBoardSettings.allowMainResponse ? '22px' : '2px',
                      top: '2px',
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      transition: 'transform 0.3s'
                    }}></div>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <label style={{ fontWeight: '600', fontSize: '14px', color: '#495057' }}>
                    Allow Comment Response
                  </label>
                  <p style={{ fontSize: '12px', color: '#6c757d', margin: '4px 0 0 0' }}>
                    Allow students to reply to other students' responses
                  </p>
                </div>
                <div 
                  style={{
                    position: 'relative',
                    width: '40px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer'
                  }}
                  onClick={() => setDiscussionBoardSettings(prev => ({
                    ...prev,
                    allowCommentResponse: !prev.allowCommentResponse
                  }))}
                >
                  <input
                    type="checkbox"
                    checked={discussionBoardSettings.allowCommentResponse}
                    onChange={(e) => setDiscussionBoardSettings(prev => ({
                      ...prev,
                      allowCommentResponse: e.target.checked
                    }))}
                    style={{ opacity: 0, width: 0, height: 0, position: 'absolute', zIndex: -1 }}
                  />
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: discussionBoardSettings.allowCommentResponse ? '#2563eb' : '#e5e7eb',
                    borderRadius: '20px',
                    transition: 'background-color 0.3s'
                  }}>
                    <div style={{
                      position: 'absolute',
                      width: '16px',
                      height: '16px',
                      left: discussionBoardSettings.allowCommentResponse ? '22px' : '2px',
                      top: '2px',
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      transition: 'transform 0.3s'
                    }}></div>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', opacity: DISCUSSION_NOTIFY_ON_RESPONSES_UI_ENABLED ? 1 : 0.55 }}>
                <div>
                  <label style={{ fontWeight: '600', fontSize: '14px', color: '#495057' }}>
                    Notify Me on Responses
                  </label>
                  <p style={{ fontSize: '12px', color: '#6c757d', margin: '4px 0 0 0' }}>
                    {DISCUSSION_NOTIFY_ON_RESPONSES_UI_ENABLED
                      ? 'Receive email notifications when students respond'
                      : '***Not available*** — Receive email notifications when students respond.'}
                  </p>
                </div>
                <div 
                  style={{
                    position: 'relative',
                    width: '40px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: DISCUSSION_NOTIFY_ON_RESPONSES_UI_ENABLED ? 'pointer' : 'not-allowed'
                  }}
                  onClick={() => {
                    if (!DISCUSSION_NOTIFY_ON_RESPONSES_UI_ENABLED) return;
                    setDiscussionBoardSettings(prev => ({
                      ...prev,
                      notifyMeOnResponses: !prev.notifyMeOnResponses
                    }));
                  }}
                >
                  <input
                    type="checkbox"
                    checked={discussionBoardSettings.notifyMeOnResponses}
                    disabled={!DISCUSSION_NOTIFY_ON_RESPONSES_UI_ENABLED}
                    onChange={(e) => {
                      if (!DISCUSSION_NOTIFY_ON_RESPONSES_UI_ENABLED) return;
                      setDiscussionBoardSettings(prev => ({
                        ...prev,
                        notifyMeOnResponses: e.target.checked
                      }));
                    }}
                    style={{ opacity: 0, width: 0, height: 0, position: 'absolute', zIndex: -1 }}
                  />
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: discussionBoardSettings.notifyMeOnResponses ? '#2563eb' : '#e5e7eb',
                    borderRadius: '20px',
                    transition: 'background-color 0.3s'
                  }}>
                    <div style={{
                      position: 'absolute',
                      width: '16px',
                      height: '16px',
                      left: discussionBoardSettings.notifyMeOnResponses ? '22px' : '2px',
                      top: '2px',
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      transition: 'transform 0.3s'
                    }}></div>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <label style={{ fontWeight: '600', fontSize: '14px', color: '#495057' }}>
                    Allow Students View Responses
                  </label>
                  <p style={{ fontSize: '12px', color: '#6c757d', margin: '4px 0 0 0' }}>
                    Students may see responses from other students.
                  </p>
                </div>
                <div 
                  style={{
                    position: 'relative',
                    width: '40px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer'
                  }}
                  onClick={() => setDiscussionBoardSettings(prev => {
                    const next = !prev.allowAssigneesViewResponses;
                    return {
                      ...prev,
                      allowAssigneesViewResponses: next,
                      ...(next ? {} : { hideresponsesuntilresponded: false })
                    };
                  })}
                >
                  <input
                    type="checkbox"
                    checked={discussionBoardSettings.allowAssigneesViewResponses}
                    onChange={(e) => setDiscussionBoardSettings(prev => ({
                      ...prev,
                      allowAssigneesViewResponses: e.target.checked,
                      ...(e.target.checked ? {} : { hideresponsesuntilresponded: false })
                    }))}
                    style={{ opacity: 0, width: 0, height: 0, position: 'absolute', zIndex: -1 }}
                  />
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: discussionBoardSettings.allowAssigneesViewResponses ? '#2563eb' : '#e5e7eb',
                    borderRadius: '20px',
                    transition: 'background-color 0.3s'
                  }}>
                    <div style={{
                      position: 'absolute',
                      width: '16px',
                      height: '16px',
                      left: discussionBoardSettings.allowAssigneesViewResponses ? '22px' : '2px',
                      top: '2px',
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      transition: 'transform 0.3s'
                    }}></div>
                  </div>
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                opacity: discussionBoardSettings.allowAssigneesViewResponses ? 1 : 0.55
              }}>
                <div>
                  <label style={{ fontWeight: '600', fontSize: '14px', color: '#495057' }}>
                    Hide Responses Until Responded
                  </label>
                  <p style={{ fontSize: '12px', color: '#6c757d', margin: '4px 0 0 0' }}>
                    Hide other students responses until the student responds to the main post.
                  </p>
                </div>
                <div 
                  style={{
                    position: 'relative',
                    width: '40px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: discussionBoardSettings.allowAssigneesViewResponses ? 'pointer' : 'not-allowed'
                  }}
                  onClick={() => {
                    if (!discussionBoardSettings.allowAssigneesViewResponses) return;
                    setDiscussionBoardSettings(prev => ({
                      ...prev,
                      hideresponsesuntilresponded: !prev.hideresponsesuntilresponded
                    }));
                  }}
                >
                  <input
                    type="checkbox"
                    checked={discussionBoardSettings.allowAssigneesViewResponses && discussionBoardSettings.hideresponsesuntilresponded}
                    disabled={!discussionBoardSettings.allowAssigneesViewResponses}
                    onChange={(e) => {
                      if (!discussionBoardSettings.allowAssigneesViewResponses) return;
                      setDiscussionBoardSettings(prev => ({
                        ...prev,
                        hideresponsesuntilresponded: e.target.checked
                      }));
                    }}
                    style={{ opacity: 0, width: 0, height: 0, position: 'absolute', zIndex: -1 }}
                  />
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: (discussionBoardSettings.allowAssigneesViewResponses && discussionBoardSettings.hideresponsesuntilresponded) ? '#2563eb' : '#e5e7eb',
                    borderRadius: '20px',
                    transition: 'background-color 0.3s'
                  }}>
                    <div style={{
                      position: 'absolute',
                      width: '16px',
                      height: '16px',
                      left: (discussionBoardSettings.allowAssigneesViewResponses && discussionBoardSettings.hideresponsesuntilresponded) ? '22px' : '2px',
                      top: '2px',
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      transition: 'transform 0.3s'
                    }}></div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Add New Content */}
        <div style={{ marginBottom: 24, maxWidth: '600px' }}>
          <h4 style={{ marginBottom: 16 }}>
            {editingContentId ? 'Edit Discussion Prompt' : 'Discussion Prompt'}:
          </h4>
          <div style={{ marginBottom: 16 }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              fontSize: '14px',
              color: '#333'
            }}>
              Content Type:
            </label>
            <div style={{ display: 'flex', gap: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="contentType"
                  value="richText"
                  checked={directionDraft.element === 'richText'}
                  onChange={e => setDirectionDraft(d => ({ 
                    ...d, 
                    element: e.target.value, 
                    value: '', 
                    imageUrl: '', 
                    imageWidth: '', 
                    imageHeight: '' 
                  }))}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px' }}>📝 Rich Text</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="contentType"
                  value="image"
                  checked={directionDraft.element === 'image'}
                  onChange={e => setDirectionDraft(d => ({ 
                    ...d, 
                    element: e.target.value, 
                    value: '', 
                    imageUrl: '', 
                    imageWidth: '', 
                    imageHeight: '' 
                  }))}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px' }}>🖼️ Image</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="contentType"
                  value="video"
                  checked={directionDraft.element === 'video'}
                  onChange={e => setDirectionDraft(d => ({ 
                    ...d, 
                    element: e.target.value, 
                    value: '', 
                    imageUrl: '', 
                    imageWidth: '', 
                    imageHeight: '' 
                  }))}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px' }}>🎬 Video</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="contentType"
                  value="embed"
                  checked={directionDraft.element === 'embed'}
                  onChange={e => setDirectionDraft(d => ({ 
                    ...d, 
                    element: e.target.value, 
                    value: '', 
                    imageUrl: '', 
                    imageWidth: '', 
                    imageHeight: '' 
                  }))}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px' }}>📺 Embed</span>
              </label>
            </div>
          </div>
        </div>

        {directionDraft.element === 'richText' && (
          <div style={{ marginBottom: 24 }}>
            <label>Discussion Prompt:</label>
            <div style={{ marginTop: 8 }}>
              <ReactQuill 
                theme="snow" 
                value={directionDraft.value} 
                onChange={val => setDirectionDraft(d => ({ ...d, value: val }))} 
                style={{ minHeight: 120 }}
                modules={{
                  toolbar: [
                    [{ 'header': [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ 'color': [] }, { 'background': [] }],
                    [{ 'align': [] }],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    ['link'],
                    ['clean']
                  ]
                }}
              />
            </div>
          </div>
        )}

        {directionDraft.element === 'image' && (
          <div style={{ marginBottom: 24, maxWidth: '600px' }}>
            {/* Upload Section */}
            <div style={{ 
              background: '#f8f9fa',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px',
              border: '1px solid #e9ecef'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '16px' }}>📁</span>
                  <span style={{ fontWeight: '500', fontSize: '14px', color: '#495057' }}>Upload New Image</span>
                </div>
                <button
                  type="button"
                  onClick={() => fetchUserUploads('image')}
                  disabled={uploadsLoading}
                  style={{
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '6px 12px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#5a6268'}
                  onMouseLeave={(e) => e.target.style.background = '#6c757d'}
                >
                  <span style={{ fontSize: '14px' }}>🔍</span>
                  {uploadsLoading ? 'Loading...' : 'Find Images'}
                </button>
              </div>
              
              <div style={{
                border: '2px dashed #dee2e6',
                borderRadius: '6px',
                padding: '16px',
                textAlign: 'center',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                position: 'relative',
                background: 'white'
              }}
              onMouseEnter={(e) => e.target.style.borderColor = '#adb5bd'}
              onMouseLeave={(e) => e.target.style.borderColor = '#dee2e6'}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageFileUpload}
                  disabled={imageUploading}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer'
                  }}
                />
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>📸</div>
                <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#495057' }}>
                  {imageUploading ? 'Uploading...' : 'Click to upload or drag image here'}
                </div>
              </div>
              
              {imageUploading && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  marginTop: '12px',
                  padding: '8px',
                  background: '#e9ecef',
                  borderRadius: '6px'
                }}>
                  <div style={{ 
                    width: '16px', 
                    height: '16px', 
                    border: '2px solid #adb5bd', 
                    borderTop: '2px solid #495057', 
                    borderRadius: '50%', 
                    animation: 'spin 1s linear infinite' 
                  }}></div>
                  <span style={{ fontSize: '12px', color: '#495057' }}>Uploading image...</span>
                </div>
              )}
              
              {imageUploadError && (
                <div style={{ 
                  color: '#721c24', 
                  fontSize: '12px', 
                  marginTop: '8px',
                  padding: '8px',
                  background: '#f8d7da',
                  borderRadius: '6px',
                  border: '1px solid #f5c6cb'
                }}>
                  ⚠️ {imageUploadError}
                </div>
              )}
            </div>
            
            {/* URL Section */}
            <div style={{ 
              background: '#f8f9fa',
              borderRadius: '8px',
              padding: '16px',
              border: '1px solid #e9ecef'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
                <span style={{ fontSize: '16px' }}>🔗</span>
                <span style={{ fontWeight: '500', fontSize: '14px', color: '#495057' }}>Or Use Image URL</span>
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px', 
                  fontWeight: '500',
                  color: '#495057',
                  fontSize: '12px'
                }}>
                  Image URL:
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input 
                    value={directionDraft.imageUrl} 
                    onChange={e => setDirectionDraft(d => ({ ...d, imageUrl: e.target.value }))} 
                    placeholder="Enter image URL here..."
                    style={{ 
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #ced4da',
                      background: 'white',
                      color: '#495057',
                      fontSize: '14px'
                    }}
                  />
                  {directionDraft.imageUrl && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (directionDraft.imageUrl) {
                          try {
                            const dimensions = await getImageDimensionsFromUrl(directionDraft.imageUrl);
                            setDirectionDraft(prev => ({
                              ...prev,
                              imageWidth: dimensions.webWidth.toString(),
                              imageHeight: dimensions.webHeight.toString()
                            }));
                          } catch (error) {
                            console.error('Error detecting image dimensions:', error);
                            alert('Could not detect image dimensions. Please enter them manually.');
                          }
                        }
                      }}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid #1976d2',
                        background: '#1976d2',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '12px',
                        whiteSpace: 'nowrap'
                      }}
                      title="Automatically detect image dimensions"
                    >
                      📏 Detect
                    </button>
                  )}
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '6px', 
                    fontWeight: '500',
                    color: '#495057',
                    fontSize: '12px'
                  }}>
                    Width (px):
                  </label>
                  <input 
                    type="number" 
                    min={1} 
                    value={directionDraft.imageWidth} 
                    onChange={e => setDirectionDraft(d => ({ ...d, imageWidth: e.target.value }))} 
                    style={{ 
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #ced4da',
                      background: 'white',
                      color: '#495057',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '6px', 
                    fontWeight: '500',
                    color: '#495057',
                    fontSize: '12px'
                  }}>
                    Height (px):
                  </label>
                  <input 
                    type="number" 
                    min={1} 
                    value={directionDraft.imageHeight} 
                    onChange={e => setDirectionDraft(d => ({ ...d, imageHeight: e.target.value }))} 
                    style={{ 
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #ced4da',
                      background: 'white',
                      color: '#495057',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>
              
              {/* Image dimension info and preview */}
              {directionDraft.imageUrl && directionDraft.imageWidth && directionDraft.imageHeight && (
                <div style={{ 
                  marginTop: '12px',
                  padding: '12px',
                  background: '#e3f2fd',
                  borderRadius: '6px',
                  border: '1px solid #90caf9'
                }}>
                  <div style={{ 
                    fontSize: '12px',
                    color: '#1565c0',
                    marginBottom: '8px'
                  }}>
                    📐 Image dimensions set to {directionDraft.imageWidth} × {directionDraft.imageHeight} pixels
                  </div>
                  <div style={{ 
                    textAlign: 'center',
                    padding: '8px',
                    background: 'white',
                    borderRadius: '4px',
                    border: '1px solid #90caf9'
                  }}>
                    <img 
                      src={directionDraft.imageUrl} 
                      alt="Preview" 
                      style={{ 
                        maxWidth: '100%',
                        maxHeight: '200px',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {directionDraft.element === 'video' && (
          <div style={{ marginBottom: 24, maxWidth: '600px' }}>
            {/* Upload Section */}
            <div style={{ 
              background: '#f8f9fa',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px',
              border: '1px solid #e9ecef'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '16px' }}>🎬</span>
                  <span style={{ fontWeight: '500', fontSize: '14px', color: '#495057' }}>Upload New Video</span>
                </div>
                <button
                  type="button"
                  onClick={() => fetchUserUploads('video')}
                  disabled={uploadsLoading}
                  style={{
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '6px 12px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#5a6268'}
                  onMouseLeave={(e) => e.target.style.background = '#6c757d'}
                >
                  <span style={{ fontSize: '14px' }}>🔍</span>
                  {uploadsLoading ? 'Loading...' : 'Find Videos'}
                </button>
              </div>
              
              <div style={{
                border: '2px dashed #dee2e6',
                borderRadius: '6px',
                padding: '16px',
                textAlign: 'center',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                position: 'relative',
                background: 'white'
              }}
              onMouseEnter={(e) => e.target.style.borderColor = '#adb5bd'}
              onMouseLeave={(e) => e.target.style.borderColor = '#dee2e6'}
              >
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleVideoFileUpload}
                  disabled={videoUploading}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer'
                  }}
                />
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>🎥</div>
                <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#495057' }}>
                  {videoUploading ? 'Uploading...' : 'Click to upload or drag video here'}
                </div>
              </div>
              
              {videoUploading && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  marginTop: '12px',
                  padding: '8px',
                  background: '#e9ecef',
                  borderRadius: '6px'
                }}>
                  <div style={{ 
                    width: '16px', 
                    height: '16px', 
                    border: '2px solid #adb5bd', 
                    borderTop: '2px solid #495057', 
                    borderRadius: '50%', 
                    animation: 'spin 1s linear infinite' 
                  }}></div>
                  <span style={{ fontSize: '12px', color: '#495057' }}>Uploading video...</span>
                </div>
              )}
              
              {videoUploadError && (
                <div style={{ 
                  color: '#721c24', 
                  fontSize: '12px', 
                  marginTop: '8px',
                  padding: '8px',
                  background: '#f8d7da',
                  borderRadius: '6px',
                  border: '1px solid #f5c6cb'
                }}>
                  ⚠️ {videoUploadError}
                </div>
              )}
            </div>
            
            {/* URL Section */}
            <div style={{ 
              background: '#f8f9fa',
              borderRadius: '8px',
              padding: '16px',
              border: '1px solid #e9ecef'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
                <span style={{ fontSize: '16px' }}>🔗</span>
                <span style={{ fontWeight: '500', fontSize: '14px', color: '#495057' }}>Or Use Video URL</span>
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px', 
                  fontWeight: '500',
                  color: '#495057',
                  fontSize: '12px'
                }}>
                  Video URL:
                </label>
                <input 
                  value={directionDraft.value} 
                  onChange={e => setDirectionDraft(d => ({ ...d, value: e.target.value }))} 
                  style={{ 
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #ced4da',
                    background: 'white',
                    color: '#495057',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {directionDraft.element === 'embed' && (
          <div style={{ marginBottom: 24, maxWidth: '600px' }}>
            <div style={{ 
              background: '#f8f9fa',
              borderRadius: '8px',
              padding: '16px',
              border: '1px solid #e9ecef'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
                <span style={{ fontSize: '16px' }}>📺</span>
                <span style={{ fontWeight: '500', fontSize: '14px', color: '#495057' }}>Embed Code</span>
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px', 
                  fontWeight: '500',
                  color: '#495057',
                  fontSize: '12px'
                }}>
                  Embed Code (iframe, embed, object, or script):
                </label>
                <textarea 
                  value={directionDraft.value} 
                  onChange={e => setDirectionDraft(d => ({ ...d, value: e.target.value }))} 
                  placeholder="Paste your embed code here...&#10;&#10;Examples:&#10;&lt;iframe src=&quot;https://example.com&quot; width=&quot;560&quot; height=&quot;315&quot;&gt;&lt;/iframe&gt;&#10;&lt;embed src=&quot;https://example.com/video.mp4&quot; type=&quot;video/mp4&quot;&gt;"
                  style={{ 
                    width: '100%',
                    minHeight: '120px',
                    padding: '12px',
                    borderRadius: '6px',
                    border: '1px solid #ced4da',
                    background: 'white',
                    color: '#495057',
                    fontSize: '14px',
                    fontFamily: 'monospace',
                    resize: 'vertical'
                  }}
                />
              </div>
              
              {directionDraft.value && (
                <div style={{ 
                  marginTop: '12px',
                  padding: '12px',
                  background: '#e3f2fd',
                  borderRadius: '6px',
                  border: '1px solid #90caf9'
                }}>
                  <div style={{ 
                    fontSize: '12px',
                    color: '#1565c0',
                    marginBottom: '8px'
                  }}>
                    📺 Embed Preview
                  </div>
                  <div style={{ 
                    textAlign: 'center',
                    padding: '8px',
                    background: 'white',
                    borderRadius: '4px',
                    border: '1px solid #90caf9',
                    minHeight: '100px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <div 
                      style={{ 
                        width: '100%',
                        maxWidth: '100%',
                        overflow: 'hidden'
                      }}
                      dangerouslySetInnerHTML={{ __html: directionDraft.value }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="button-row">
          <button 
            onClick={addOrUpdateDiscussionBoardElement} 
            disabled={
              (directionDraft.element === 'richText' && !directionDraft.value.trim()) ||
              (directionDraft.element === 'image' && (!directionDraft.imageUrl || !directionDraft.imageWidth || !directionDraft.imageHeight)) ||
              (directionDraft.element === 'video' && !directionDraft.value.trim()) ||
              (directionDraft.element === 'embed' && !directionDraft.value.trim())
            }
            style={{ 
              background: '#1976d2', 
              color: 'white', 
              border: 'none', 
              borderRadius: 4, 
              padding: '8px 16px', 
              cursor: 'pointer' 
            }}
          >
            {editingContentId ? 'Update Discussion Prompt' : 'Add to Discussion Prompt'}
          </button>
          {editingContentId && (
            <button 
              onClick={() => {
                setEditingContentId(null);
                setDirectionDraft({ 
                  element: 'richText', 
                  value: '', 
                  imageUrl: '', 
                  imageWidth: '', 
                  imageHeight: '' 
                });
              }}
              style={{ 
                background: '#666', 
                color: 'white', 
                border: 'none', 
                borderRadius: 4, 
                padding: '8px 16px', 
                cursor: 'pointer' 
              }}
            >
              Cancel Edit
            </button>
          )}
        </div>

        {/* Current Content Preview */}
        {(discussionBoardEditor?.content || []).length > 0 && (
          <div style={{ marginTop: 24, borderTop: '1px solid #eee', paddingTop: 24 }}>
            <h4 style={{ marginBottom: 16 }}>Current Discussion Prompt:</h4>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {(discussionBoardEditor?.content || []).map((item, idx) => (
                <li key={item._id} style={{ marginBottom: 16, padding: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      {item.element === 'richText' && <div dangerouslySetInnerHTML={{ __html: item.value }} />}
                      {item.element === 'image' && renderImageFromTag(item.value)}
                      {item.element === 'video' && renderVideoFromUrl(item.value)}
                      {item.element === 'embed' && <div dangerouslySetInnerHTML={{ __html: item.value }} />}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button 
                        onClick={() => editContentElement(item)} 
                        title="Edit" 
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
                      >
                        ✏️
                      </button>
                      <button 
                        onDoubleClick={() => removeDiscussionBoardElement(item._id)} 
                        title="Double-click to remove" 
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
                      >
                        🗑️
                      </button>
                      <button 
                        onClick={() => moveDiscussionBoardUp(idx)} 
                        disabled={idx === 0} 
                        title="Move Up"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
                      >
                        ↑
                      </button>
                      <button 
                        onClick={() => moveDiscussionBoardDown(idx)} 
                        disabled={idx === (discussionBoardEditor?.content || []).length - 1} 
                        title="Move Down"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                  {idx < (discussionBoardEditor?.content || []).length - 1 && <hr style={{ border: 0, borderTop: '1px solid #eee', margin: '16px 0' }} />}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  </div>
)}

      {/* User Uploads Modal */}
      {showUploadsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10001
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            width: 'min(1100px, min(94vw, 100vw - 32px))',
            maxWidth: '94vw',
            maxHeight: '80vh',
            overflow: 'auto',
            position: 'relative',
            boxSizing: 'border-box'
          }}>
            <button
              onClick={() => setShowUploadsModal(false)}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: '#666'
              }}
            >
              ×
            </button>

            <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>
              Your {directionDraft.element === 'image' ? 'Images' : 'Videos'}
            </h3>

            {uploadsLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Loading...</div>
              </div>
            ) : userUploads.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                <p>No {directionDraft.element === 'image' ? 'images' : 'videos'} found.</p>
                <p>Upload some files first to see them here.</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
                {userUploads.map((upload) => (
                  <div
                    key={upload.id}
                    style={{
                      position: 'relative',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '1rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={async () => {
                      if (directionDraft.element === 'image') {
                        // For images, get actual dimensions and construct the img tag
                        try {
                          const dimensions = await getImageDimensionsFromUrl(upload.link);
                          setDirectionDraft(prev => ({
                            ...prev,
                            element: 'image',
                            value: `<img src="${upload.link}" width="${dimensions.webWidth}" height="${dimensions.webHeight}" alt="Image">`,
                            imageUrl: upload.link,
                            imageWidth: dimensions.webWidth.toString(),
                            imageHeight: dimensions.webHeight.toString()
                          }));
                        } catch (error) {
                          console.error('Error getting image dimensions:', error);
                          // Fallback to default dimensions
                          setDirectionDraft(prev => ({
                            ...prev,
                            element: 'image',
                            value: `<img src="${upload.link}" width="800" height="600" alt="Image">`,
                            imageUrl: upload.link,
                            imageWidth: '800',
                            imageHeight: '600'
                          }));
                        }
                      } else {
                        // For videos, just use the URL
                        setDirectionDraft(prev => ({
                          ...prev,
                          element: 'video',
                          value: upload.link
                        }));
                      }
                      setShowUploadsModal(false);
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#1976d2';
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#ddd';
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    <UploadMediaOpenInNewTabLink href={upload.link} />
                    {directionDraft.element === 'image' ? (
                      <img
                        src={upload.link}
                        alt={upload.filename}
                        style={{
                          width: '100%',
                          height: '120px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                          marginBottom: '0.5rem'
                        }}
                      />
                    ) : (
                      <video
                        src={upload.link}
                        style={{
                          width: '100%',
                          height: '120px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                          marginBottom: '0.5rem'
                        }}
                        muted
                      />
                    )}
                    <div style={{ fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                      {upload.filename}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>
                      {new Date(upload.createddate).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Assignment Selector Modal */}
      {assignmentModalConfig.isOpen && (
        <AssignmentSelectorModal
          isOpen={assignmentModalConfig.isOpen}
          onClose={closeAssignmentModal}
          onSelectionChange={assignmentModalConfig.onSelectionChange}
          userAssignments={allAssignments}
          selectedAssignments={assignmentModalConfig.selectedAssignments}
          userData={userData}
          onRefreshAssignments={fetchUserAssignments}
          onTaxonomyInheritance={handleTaxonomyInheritance}
          isProjectBuilder={false}
          allowMultiple={false}
        />
      )}

      {/* Taxonomy Modal */}
       <CourseBuilderTaxonomyModal
         isOpen={taxonomyModalConfig.isOpen}
         onClose={closeTaxonomyModal}
         onTaxonomyChange={taxonomyModalConfig.onTaxonomyChange}
         currentTaxonomy={taxonomyModalConfig.currentTaxonomy}
                  primaryConcentration={taxonomyModalConfig.primaryConcentration}
         otherConcentrations={taxonomyModalConfig.otherConcentrations}
         concentrations={pathwayRoots}
         currentHourAwards={[]}
                />

      {/* Hour Awards Modal */}
      {hourAwardsModalConfig.isOpen && (
        <div className="fullscreen-overlay">
          <div className="fullscreen-editor" onClick={(e) => e.stopPropagation()}>
            <div className="fullscreen-header">
              <h3>Hour Awards & Grade Settings</h3>
              <button 
                className="fullscreen-close" 
                onClick={closeHourAwardsModal}
                style={{
                  background: 'transparent',
                  color: '#374151',
                  border: 'none'
                }}
              >×</button>
            </div>
            <div className="fullscreen-content">
              <div className="form-section">
                <h2>Type of Hours</h2>
                <div style={{ color: '#1D4E89', fontSize: '0.98rem', marginBottom: 12, fontWeight: 500 }}>
                  Set the hours and type for this item. Each type can only be selected once.
                </div>
                
                {/* Hour Awards List */}
                <div className="hour-awards-list">
                  {hourAwardsModalConfig.currentHourAwards.map((award, index) => (
                    <div key={index} className="hour-award-item">
                      <div className="hour-award-row">
                        <div className="form-group">
                          <label>Hours:</label>
                          <input
                            type="number"
                            min="0"
                            step="0.5"
                            value={award.hours}
                            onChange={(e) => {
                              const newHourAwards = [...hourAwardsModalConfig.currentHourAwards];
                              newHourAwards[index].hours = parseFloat(e.target.value) || 0;
                              setHourAwardsModalConfig({
                                ...hourAwardsModalConfig,
                                currentHourAwards: newHourAwards
                              });
                              
                              // Also immediately save to sections state for JSON preview
                              saveHourAwardsAndGradeSettings(newHourAwards, hourAwardsModalConfig.currentGradeSettings);
                            }}
                            className="form-control"
                            style={{ width: '80px' }}
                          />
                        </div>
                        <div className="form-group">
                          <label>Type:</label>
                          <select
                            value={award.type}
                            onChange={(e) => {
                              const newHourAwards = [...hourAwardsModalConfig.currentHourAwards];
                              newHourAwards[index].type = e.target.value;
                              setHourAwardsModalConfig({
                                ...hourAwardsModalConfig,
                                currentHourAwards: newHourAwards
                              });
                              
                              // Also immediately save to sections state for JSON preview
                              saveHourAwardsAndGradeSettings(newHourAwards, hourAwardsModalConfig.currentGradeSettings);
                            }}
                            className="form-control"
                            style={{ width: '200px' }}
                          >
                            <option value="">Select type</option>
                            {projectTypes
                              .filter(type => !hourAwardsModalConfig.currentHourAwards.some((award, i) => i !== index && award.type === type.value))
                              .map(type => (
                                <option key={type.value} value={type.value}>
                                  {type.label}
                                </option>
                              ))}
                          </select>
                        </div>
                        <button
                          className="remove-hour-award-button"
                          onClick={() => {
                            const newHourAwards = hourAwardsModalConfig.currentHourAwards.filter((_, i) => i !== index);
                            setHourAwardsModalConfig({
                              ...hourAwardsModalConfig,
                              currentHourAwards: newHourAwards
                            });
                            
                            // Also immediately save to sections state for JSON preview
                            saveHourAwardsAndGradeSettings(newHourAwards, hourAwardsModalConfig.currentGradeSettings);
                          }}
                          title="Remove this hour award"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add New Hour Award Button */}
                <button
                  className="add-hour-awards-button"
                  onClick={() => {
                    const newHourAwards = [...hourAwardsModalConfig.currentHourAwards, { hours: 0, type: '' }];
                    setHourAwardsModalConfig({
                      ...hourAwardsModalConfig,
                      currentHourAwards: newHourAwards
                    });
                    
                    // Also immediately save to sections state for JSON preview
                    saveHourAwardsAndGradeSettings(newHourAwards, hourAwardsModalConfig.currentGradeSettings);
                  }}
                  disabled={hourAwardsModalConfig.currentHourAwards.length >= projectTypes.length}
                >
                  <span style={{ fontSize: '18px', fontWeight: 'bold' }}>+</span>
                  <span>Add Hour Award</span>
                </button>

                {/* Validation Message */}
                {hourAwardsModalConfig.currentHourAwards.some(award => !award.type || (award.hours === null || award.hours === undefined)) && (
                  <div style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '12px' }}>
                    ⚠️ Please select a type for all hour awards and ensure hours are set (0 is allowed).
                  </div>
                )}

                {/* Action Buttons */}
                <div className="hour-awards-actions">

                </div>
              </div>

{/* Grade Settings Section */}
<div className="form-section" style={{ marginTop: '32px', borderTop: '1px solid #e2e8f0', paddingTop: '32px' }}>
  <h2>Grade Settings</h2>
  <div style={{ color: '#1D4E89', fontSize: '0.98rem', marginBottom: 24, fontWeight: 500 }}>
    Configure how students earn credit for this item.
  </div>

  {/* Content Grade Settings */}
  {hourAwardsModalConfig.type === 'Content' && (
    <div className="grade-settings-content">
      <div style={{ 
        background: 'white',
        border: '1px solid #e1e5e9',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{ 
          background: '#f0fff4',
          border: '1px solid #c6f6d5',
          borderRadius: '8px',
          padding: '20px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
          <h5 style={{ 
            margin: '0 0 12px 0', 
            color: '#22543d',
            fontSize: '18px',
            fontWeight: '600'
          }}>
            Automatic Completion
          </h5>
          <p style={{ 
            margin: '0', 
            color: '#2f855a',
            fontSize: '15px',
            lineHeight: '1.6'
          }}>
            Content components are automatically marked complete when students click "Complete" in the student interface.
          </p>
        </div>
      </div>
    </div>
  )}

  {/* Assignment Grade Settings */}
  {hourAwardsModalConfig.type === 'Assignment' && (
    <div className="grade-settings-assignment">
      <div style={{ 
        background: 'white',
        border: '1px solid #e1e5e9',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ 
            display: 'block',
            fontSize: '16px',
            fontWeight: '600',
            color: '#2d3748',
            marginBottom: '12px'
          }}>
            🎯 Minimum Score Required:
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              type="number"
              min="0"
              max="100"
              value={hourAwardsModalConfig.currentGradeSettings?.criteria?.min_score || 70}
              onChange={(e) => {
                const newGradeSettings = {
                  type: 'score_threshold',
                  criteria: {
                    min_score: parseInt(e.target.value),
                    max_score: 100
                  }
                };
                // Update the modal config state immediately for real-time updates
                setHourAwardsModalConfig(prev => ({
                  ...prev,
                  currentGradeSettings: newGradeSettings
                }));
                
                // Also immediately save to sections state for JSON preview
                saveHourAwardsAndGradeSettings(hourAwardsModalConfig.currentHourAwards, newGradeSettings);
              }}
              style={{ 
                width: '120px',
                padding: '14px 16px',
                fontSize: '16px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                backgroundColor: 'white',
                color: '#2d3748',
                outline: 'none',
                transition: 'all 0.2s ease',
                textAlign: 'center'
              }}
              onFocus={(e) => e.target.style.borderColor = '#ed8936'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
            <span style={{ 
              fontSize: '18px', 
              fontWeight: '600',
              color: '#2d3748'
            }}>%</span>
          </div>
        </div>

        <div style={{ 
          background: '#fffaf0',
          border: '1px solid #fbd38d',
          borderRadius: '8px',
          padding: '16px'
        }}>
          <div style={{ 
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <div style={{ 
              fontSize: '20px',
              color: '#c05621'
            }}>💡</div>
            <div>
              <h6 style={{ 
                margin: '0 0 8px 0',
                color: '#c05621',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                How it works:
              </h6>
              <p style={{ 
                margin: '0', 
                color: '#744210',
                fontSize: '14px',
                lineHeight: '1.5'
              }}>
                Students must achieve the minimum score or higher to receive credit. Survey-type assignments are automatically marked as 100% on submission.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )}

  {/* Discussion Board Grade Settings */}
  {hourAwardsModalConfig.type === 'Discussion Board' && (
    <div className="grade-settings-discussion">
      <div style={{ 
        background: 'white',
        border: '1px solid #e1e5e9',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ 
            display: 'block',
            fontSize: '16px',
            fontWeight: '600',
            color: '#2d3748',
            marginBottom: '12px'
          }}>
            To get credit, students must:
          </label>
          <select
            value={hourAwardsModalConfig.currentGradeSettings?.criteria?.type || 'respond_to_main'}
            onChange={(e) => {
              const currentSettings = hourAwardsModalConfig.currentGradeSettings || getDefaultGradeSettings('Discussion Board');
              const newGradeSettings = {
                ...currentSettings,
                criteria: {
                  type: e.target.value,
                  min_responses: e.target.value.includes('students') ? (currentSettings?.criteria?.min_responses || 2) : null
                }
              };
              // Update the modal config state immediately for real-time updates
              setHourAwardsModalConfig(prev => ({
                ...prev,
                currentGradeSettings: newGradeSettings
              }));
            }}
            style={{ 
              width: '100%',
              padding: '14px 16px',
              fontSize: '15px',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              backgroundColor: 'white',
              color: '#2d3748',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              outline: 'none'
            }}
            onFocus={(e) => e.target.style.borderColor = '#667eea'}
            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
          >
            <option value="respond_to_main">Respond to main post</option>
            <option value="respond_to_main_and_students">Respond to main post and to x students</option>
            {/* <option value="manual_after_main">Instructor must manually grade after student response to main</option> */}
            {/* <option value="manual_after_main_and_students">Instructor must manually grade after student responds to main post and responds to x students</option> */}
          </select>
</div>

        {/* Show input for student count when needed */}
        {(hourAwardsModalConfig.currentGradeSettings?.criteria?.type === 'respond_to_main_and_students') && (
          <div style={{ 
            background: '#f7fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px'
          }}>
            <label style={{ 
              display: 'block',
              fontSize: '15px',
              fontWeight: '600',
              color: '#2d3748',
              marginBottom: '12px'
            }}>
              📊 Number of students to respond to:
            </label>
            <input
              type="number"
              min="1"
              value={hourAwardsModalConfig.currentGradeSettings?.criteria?.min_responses || 2}
              onChange={(e) => {
                const currentSettings = hourAwardsModalConfig.currentGradeSettings || getDefaultGradeSettings('Discussion Board');
                const newGradeSettings = {
                  ...currentSettings,
                  criteria: {
                    ...currentSettings.criteria,
                    min_responses: parseInt(e.target.value)
                  }
                };
                // Update the modal config state immediately for real-time updates
                setHourAwardsModalConfig(prev => ({
                  ...prev,
                  currentGradeSettings: newGradeSettings
                }));
              }}
              style={{ 
                width: '120px',
                padding: '14px 16px',
                fontSize: '16px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                backgroundColor: 'white',
                color: '#2d3748',
                outline: 'none',
                transition: 'all 0.2s ease',
                textAlign: 'center'
              }}
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>
        )}

        <div style={{ 
          background: '#f0f4ff',
          border: '1px solid #c6dafe',
          borderRadius: '8px',
          padding: '16px'
        }}>
          <div style={{ 
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <div style={{ 
              fontSize: '20px',
              color: '#4c51bf'
            }}>💡</div>
            <div>
              <h6 style={{ 
                margin: '0 0 8px 0',
                color: '#4c51bf',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                What this means:
              </h6>
              <p style={{ 
                margin: '0', 
                color: '#434190',
                fontSize: '14px',
                lineHeight: '1.5'
              }}>
                Students must meet these requirements to receive credit for this discussion board. The system will automatically track and validate student participation based on your settings.
              </p>
            </div>
</div>
</div>
</div>
</div>
)}

  {/* Module/Lesson Grade Settings (no specific settings needed) */}
  {(hourAwardsModalConfig.type === 'Module' || hourAwardsModalConfig.type === 'Lesson') && (
    <div className="grade-settings-module">
      <div style={{ 
        background: 'white',
        border: '1px solid #e1e5e9',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{ 
          background: '#faf5ff',
          border: '1px solid #e9d8fd',
          borderRadius: '8px',
          padding: '20px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
          <h5 style={{ 
            margin: '0 0 12px 0', 
            color: '#553c9a',
            fontSize: '18px',
            fontWeight: '600'
          }}>
            No Specific Grading
          </h5>
          <p style={{ 
            margin: '0', 
            color: '#6b46c1',
            fontSize: '15px',
            lineHeight: '1.6'
          }}>
            {hourAwardsModalConfig.type === 'Module' ? 'Modules' : 'Lessons'} are container components that organize content. Students earn credit by completing the individual items within them.
          </p>
        </div>
      </div>
    </div>
  )}
</div>
            </div>
          </div>
        </div>
      )}

      {/* Saved Courses Modal */}
      {showSavedCoursesModal && (
        <div className="fullscreen-overlay">
          <div className="fullscreen-editor saved-courses-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fullscreen-header saved-courses-modal-header">
              <div className="saved-courses-modal-header-text">
                <h3>My Courses</h3>
                <p className="saved-courses-modal-help">
                  Click a course row or the pencil icon to edit in Course Builder. The video icon
                  opens a preview in a new tab. The duplicate icon copies the course.
                </p>
              </div>
              <div className="saved-courses-header-actions">
                <button
                  type="button"
                  className={`saved-courses-icon-btn${isLoadingCourses ? ' is-spinning' : ''}`}
                  onClick={loadSavedCourses}
                  disabled={isLoadingCourses}
                  title="Refresh course list"
                  aria-label="Refresh course list"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width={16} height={16} aria-hidden>
                    <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                    <path d="M21 3v5h-5" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="fullscreen-close saved-courses-modal-close"
                  onClick={() => setShowSavedCoursesModal(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="fullscreen-content">
              <div className="saved-courses-modal-content">
                {isLoadingCourses ? (
                  <div className="loading-courses">
                    <span>Loading saved courses...</span>
                  </div>
                ) : savedCourses.length > 0 ? (
                  <div className="saved-courses-list">
                    {savedCourses.map((course) => (
                      <div key={course.id} className={`saved-course-item ${currentCourseId === course.id ? 'current-course' : ''}`}>
                        <button
                          type="button"
                          className="saved-course-main-btn"
                          onClick={() => {
                            loadCourse(course);
                            setShowSavedCoursesModal(false);
                          }}
                          title={currentCourseId === course.id ? 'Reload in Course Builder' : 'Edit in Course Builder'}
                        >
                          <span className="saved-course-title">
                            {course.draft?.courseTitle || course.title || 'Untitled Course'}
                          </span>
                          <div className="saved-course-meta">
                            {course.createddate && (
                              <span className="saved-course-created">
                                <span className="saved-course-meta-label">Created</span>
                                <time dateTime={new Date(course.createddate).toISOString()}>
                                  {new Date(course.createddate).toLocaleString()}
                                </time>
                              </span>
                            )}
                            {currentCourseId === course.id && (
                              <span className="current-course-badge">Currently loaded</span>
                            )}
                          </div>
                        </button>
                        <div className="saved-course-actions">
                          <button
                            type="button"
                            className="saved-courses-icon-btn"
                            onClick={() => {
                              loadCourse(course);
                              setShowSavedCoursesModal(false);
                            }}
                            title={currentCourseId === course.id ? 'Reload in Course Builder' : 'Edit in Course Builder'}
                            aria-label={currentCourseId === course.id ? 'Reload course' : 'Edit course'}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width={16} height={16} aria-hidden>
                              <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                              <path d="m15 5 4 4" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="saved-courses-icon-btn"
                            onClick={() => {
                              window.open(`/course-viewer?command=preview&id=${course.id}`, '_blank');
                            }}
                            title="Preview in Course Viewer"
                            aria-label="Preview in Course Viewer"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width={16} height={16} aria-hidden>
                              <path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5" />
                              <rect x="2" y="6" width="14" height="12" rx="2" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="saved-courses-icon-btn"
                            onClick={() => {
                              const courseTitle = course.draft?.courseTitle || course.title || 'Untitled Course';
                              const defaultSuffix = ' (Copy)';
                              const customSuffix = window.prompt(
                                `Enter the suffix to append to duplicated components (e.g., " (Copy)", " - Duplicate", " v2"):\n\nCourse: "${courseTitle}"`,
                                defaultSuffix
                              );
                              if (customSuffix !== null) {
                                if (window.confirm(`Are you sure you want to duplicate "${courseTitle}"?\n\nThis will create a complete copy with all components, comments, and assignments.\n\nSuffix: "${customSuffix}"`)) {
                                  duplicateCourse(course, customSuffix);
                                }
                              }
                            }}
                            title="Duplicate course"
                            aria-label="Duplicate course"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width={16} height={16} aria-hidden>
                              <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                              <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-saved-courses">
                    <span>No saved courses yet. Save your first course to see it here!</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* JSON Preview Modal */}
      {showJsonModal && (
        <div className="fullscreen-overlay">
          <div className="fullscreen-editor" onClick={(e) => e.stopPropagation()}>
            <div className="fullscreen-header">
              <h3>Course Structure JSON</h3>
              <div className="header-actions">
                <button 
                  className="copy-json-button"
                  onClick={() => {
                    const jsonText = JSON.stringify(memoizedJsonStructure, null, 2);
                    
                    // Try modern clipboard API first
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                      navigator.clipboard.writeText(jsonText).then(() => {
                        setSuccess('JSON copied to clipboard!');
                        setTimeout(() => setSuccess(''), 2000);
                      }).catch(err => {
                        console.error('Failed to copy JSON:', err);
                        fallbackCopyTextToClipboard(jsonText);
                      });
                    } else {
                      // Fallback for older browsers
                      fallbackCopyTextToClipboard(jsonText);
                    }
                  }}
                  title="Copy JSON to clipboard"
                >
                  📋 Copy JSON
                </button>
                <button 
                  className="fullscreen-close" 
                  onClick={() => setShowJsonModal(false)}
                  style={{
                    background: 'transparent',
                    color: '#374151',
                    border: 'none'
                  }}
                >×</button>
              </div>
            </div>
            <div className="fullscreen-content">
              <div className="json-modal-content">
                <pre className="json-preview">
                  {JSON.stringify(memoizedJsonStructure, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Overlay for Save/Create Operations */}
      {actionOverlay && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255,255,255,0.8)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <LoadingSpinner />
          {isDuplicating && (
            <div style={{
              marginTop: '20px',
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#d32f2f',
              textAlign: 'center',
              padding: '10px 20px',
              backgroundColor: '#ffebee',
              borderRadius: '8px',
              border: '2px solid #f44336'
            }}>
              ⚠️ PLEASE DO NOT RELOAD THE PAGE ⚠️<br/>
              <span style={{ fontSize: '14px', fontWeight: 'normal' }}>
                Course duplication in progress...
              </span>
            </div>
          )}
        </div>
      )}

      {/* Critical Error Overlay */}
      {criticalError && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255,255,255,0.95)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 32,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            textAlign: 'center',
            maxWidth: 500,
            border: '1px solid #e3e8f0'
          }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#d32f2f', marginBottom: 16 }}>
              ⚠️ Critical Error
            </div>
            <div style={{ fontSize: 16, color: '#666', marginBottom: 24, lineHeight: 1.5 }}>
              {typeof criticalError === 'string' ? criticalError : criticalError?.message}
            </div>
            {((typeof criticalError === 'string' && criticalError !== 'Modifications are locked.') || 
              (typeof criticalError === 'object' && criticalError?.showReload !== false)) && (
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                <button
                  onClick={() => window.location.reload()}
                  style={{
                    background: '#d32f2f',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    padding: '12px 24px',
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  🔄 Reload Page
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {canControlModlock && currentCourseId && (courseComponentState || currentCourseComponent)?.status === 'Active' ? (
        <div className="course-builder-modlock-bar">
          <label className="course-builder-modlock-label">
            <span className="course-builder-modlock-text">Modlock</span>
            <input
              type="checkbox"
              className="course-builder-modlock-switch"
              checked={!!(courseComponentState || currentCourseComponent)?.modlock}
              onChange={(e) => handleModlockToggle(e.target.checked)}
              disabled={modlockSaving}
              aria-label="Course modlock (locks course for enrollment when on)"
            />
          </label>
        </div>
      ) : null}

      {/* Deletion Confirmation Modal */}
      {deletionModalConfig.isOpen && (
        <div className="fullscreen-overlay">
          <div className="deletion-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="deletion-modal-header">
              <h3>Confirm Deletion</h3>
              <button 
                className="deletion-modal-close" 
                onClick={() => setDeletionModalConfig({ ...deletionModalConfig, isOpen: false })}
                style={{
                  background: 'transparent',
                  color: '#374151',
                  border: 'none'
                }}
              >×</button>
            </div>
            <div className="deletion-modal-body">
              <div className="deletion-modal-content">
                <p>
                  Are you sure you want to remove this item? It will delete the following:
                </p>
                <ul>
                  {deletionModalConfig.componentTitles.map((title, index) => (
                    <li key={index}>{title}</li>
                  ))}
                </ul>
                <p>
                  These updates will only take effect after you click Save.
                </p>
                <div className="deletion-modal-actions">
                  <button
                    onClick={() => {
                      deletionModalConfig.onConfirm();
                      setDeletionModalConfig({ ...deletionModalConfig, isOpen: false });
                    }}
                  >
                    Confirm
                  </button>
                  <button
                    onClick={() => setDeletionModalConfig({ ...deletionModalConfig, isOpen: false })}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {aiCapabilities?.UseAssistant === true && (
        <CourseAssistant
          courseTitle={courseTitle}
          setCourseTitle={setCourseTitle}
          courseTrack={courseTrack}
          gradeLevels={gradeLevels}
          defaultPrimaryConcentration={defaultPrimaryConcentration}
          currentCourseId={currentCourseId}
          sections={sections}
          setSections={setSectionsWithProperties}
          aiCapabilities={aiCapabilities}
          onOutlineInserted={(mergedSections) => {
            const slots = collectAiAssignmentSlots(mergedSections);
            if (slots.length > 0) setAssignmentDraftModalOpen(true);
          }}
        />
      )}

      <CourseAssignmentDraftModal
        open={assignmentDraftModalOpen}
        onClose={() => setAssignmentDraftModalOpen(false)}
        sections={sections}
        setSections={setSectionsWithProperties}
        defaultPrimaryConcentration={defaultPrimaryConcentration}
        canGenerate={
          aiCapabilities?.GenerateAssignmentDraft === true ||
          aiCapabilities?.assignmentBuilderGenerateDraft === true
        }
      />

    </div>
  );
};

export default CourseBuilder; 